import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import FixedProgramBulkTable, { type FixedProgramBulkHandle } from '../components/aiPreset/FixedProgramBulkTable';
import {
  draftApplySig,
  draftPeriodName,
  isDraftConfirmed,
  loadFixedDrafts,
  saveFixedDrafts,
  subscribeFixedDrafts,
  type FixedProgramDraft,
} from '../utils/fixedProgramDrafts';
import { mergeFixedDraftsFromCellSnapshots, seedFixedDraftsFromAnnualCells } from '../utils/fixedProgramsFromAnnualCells';
import { createPortal, flushSync } from 'react-dom';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AutoStatusToast from '../components/common/AutoStatusToast';
import BasicSettingIcon from '../components/common/BasicSettingIcon';
import ModalDocumentActionButtons from '../components/common/ModalDocumentActionButtons';
import AnnualTimetableClassActionBar from '../components/annual/AnnualTimetableClassActionBar';
import AnnualTimetableHoursAdjustModal from '../components/annual/AnnualTimetableHoursAdjustModal';
import HoursAdjustChecklistFloatingModal from '../components/annual/HoursAdjustChecklistFloatingModal';
import type {
  HoursAdjustChecklist,
  HoursAdjustPhase2Result,
} from '../utils/annualTimetableHoursAdjustPlan';
import { planHoursAdjustChecklistCellUpdates } from '../utils/annualTimetableHoursAdjustApply';
import AnnualOperationPlanTableColGroup from '../components/annual/AnnualOperationPlanTableColGroup';
import AnnualTimetableLeaderImportSourcePreview from '../components/annual/AnnualTimetableLeaderImportSourcePreview';
import {
  AnnualTimetableClassSectionTitle,
  AnnualTimetableClassShellPreview,
  AnnualTimetableShellPreview,
} from '../components/annual/AnnualTimetableShellPreview';
import {
  AnnualTimetablePreviewMainTable,
  createAnnualShellWeekRows,
} from '../components/annual/AnnualTimetablePreviewMainTable';
import { AnnualPeriodHeadLabel } from '../components/annual/AnnualPeriodHeadLabel';
import ProgramColorPickModal, { type ProgramColorPickModalType } from '../components/ProgramColorPickModal';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { useAnnualTimetablePageQueries } from '../hooks/useAnnualTimetablePageQueries';
import { useSchoolScopedCacheSyncReady } from '../hooks/useSchoolScopedCacheSyncReady';
import { addDays, mondayOf, parseISODate, toISODate } from '../utils/date';
import { saveAnnualOperationPlanAsExcel } from '../utils/annualOperationPlanExcelExport';
import {
  buildOperationPlanExportSubtitle,
  computeOperationPlanTotals,
  getOperationPlanFooterRowClassName,
  getOperationPlanVacationText,
  mergeOperationPlanTotals,
  type OperationPlanFooterRow,
  type OperationPlanTermSelection,
  type OperationPlanWeekRow,
} from '../utils/annualOperationPlanTableHelpers';
import {
  openDocumentExportPreviewFailureMessage,
  openDocumentExportPreviewInNewTab,
  openDocumentExportPreviewTabPlaceholder,
  type AnnualOperationPlanDocumentPayload,
} from '../utils/documentExportPreviewStorage';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useCalendarLinkedContext } from '../hooks/useCalendarLinkedContext';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import {
  ANNUAL_PREREQUISITE_REFRESH_EVENT,
  emitAnnualPrerequisiteRefresh,
  type AnnualPrerequisiteScope,
} from '../utils/annualPrerequisiteRefresh';
import { createDefaultGradeProgramColors, PROJECT_PROGRAM_COLOR_PALETTE, REGULAR_PROGRAM_COLOR_PALETTE } from '../constants/programColorPalettes';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import {
  getAnnualProjectPrograms,
  getUsedProjectColorHexSetForTerm,
  isOtherProjectUsingColorForAnyOfTerms,
  loadAnnualProjectPrograms,
  matchesAnnualProjectProgramCell,
  normalizeHexColorForProjectCompare,
  saveAnnualProjectPrograms,
  type AnnualProjectProgram,
} from '../utils/annualProjectProgramsStorage';
import {
  applyAnnualProgramsToGradeProgramColors,
  gradeProgramColorsNeedAnnualProjectSync,
} from '../utils/annualProjectColorSync';
import {
  BaseTimetableByTerm,
  BaseTimetableGrid,
  ClassScheduleRow,
  ClassScheduleSettings,
  GradeProgramColors,
  getClassScheduleSettings,
  getGradeTrackSections,
  getGradeTrackProgramPreferences,
  getBaseTimetableGridForTerm,
  normalizeBaseTimetableByTerm,
  PageSectionLayout,
  getProgramColorSettings,
  ProgramColorSettings,
  resolveGradeProgramColors,
  saveAcademicCalendarSections,
  saveClassScheduleSettings,
  saveGradeTrackProgramPreferences,
  saveGradeTrackSections,
  saveProgramColorSettings,
  getAcademicCalendarSections,
} from '../services/sectionLayoutService';
import {
  CalendarDay,
  AnnualTimetableCell,
  AnnualTimetableCellUpsert,
  AnnualTimetableSlotKind,
  AnnualWeekdayKey,
  DayKind,
  Grade,
  ProgramFixedContentRowUpsert,
  ProgramProgressCellUpsert,
  Track,
} from '../types/domain';
import { upsertAnnualTimetableWeekMemo } from '../services/annualTimetableWeekMemoService';
import { getSchoolGradeRepresentatives, getTracks } from '../services/gradeTrackService';
import {
  ANNUAL_WEEKDAY_KEYS as WEEKDAY_KEYS,
  AnnualClassEntry,
  AnnualEffectiveCell,
  MAX_ANNUAL_TIMETABLE_SLOTS as MAX_PERIODS,
  buildAnnualCellKey,
  buildAnnualCellMap,
  buildAnnualClassEntries,
  buildAnnualPeriodLabels,
  baseGridClassPeriodIndexForSectionSlot,
  buildProgramGrouping,
  createAnnualCellUpsert,
  annualPersistSlotKindFromEffective,
  deriveAnnualEffectiveCell,
  getSeedSlotKind,
  listAnnualKeysWithStaleCalendarSlotOverrides,
  getBaseGridCell,
  gradeIdFromAnnualCellKey,
  parseAnnualCellKey,
  ANNUAL_SHORT_DAY_TAIL_MEMO,
  isAnnualShortDayTailMemo,
  isBaseExcludeColor,
  isDedicatedColor,
  isFixedProgramColor,
} from '../utils/annualTimetable';
import { markAnnualEntryFocusClick } from '../utils/annualProgressEntryFocus';
import { upsertAnnualTimetableCells } from '../services/annualTimetableService';
import { getProgramFixedContentRows, upsertProgramFixedContentRows } from '../services/programFixedContentService';
import {
  applyFixedProgramUnfixedSlotCleanup,
  deleteProgramProgressCellsForClearedAnnualSlots,
  getProgramProgressCells,
  upsertProgramProgressCells,
} from '../services/programProgressService';
import { ensureProjectProgramScopedContentRows, ensureProjectProgramScopedContentRowsForProjects } from '../services/programScopedContentService';
import { parseGradeTermScopeKey } from '../utils/annualClearedSlotSync';
import { emitAnnualTimetableSaved } from '../utils/annualTimetableSavedEvents';
import { emitProgramProgressSaved } from '../utils/programProgressSavedEvents';
import { shouldIgnoreAppNavigationShortcut } from '../utils/appNavigationShortcutGuard';
import { releaseTrappedKeepAliveFocus } from '../utils/keepAliveFocusRelease';
import { clampCalendarHolidayNote } from '../utils/calendarHolidayNote';
import { loadTrackProgramModeMap, type TrackProgramMode } from '../utils/trackProgramModeStorage';
import { applyGradeTrackProgramPrefsToLocalStores } from '../utils/gradeTrackProgramPrefsLocalSeed';
import { loadAnnualShowSaturday, saveAnnualShowSaturday } from '../utils/annualSaturdayViewStorage';
import {
  loadAnnualShowAllClasses,
  saveAnnualShowAllClasses,
} from '../utils/annualShowAllClassesStorage';
import {
  loadAnnualSwapTermOrder,
  orderedAcademicTerms,
  saveAnnualSwapTermOrder,
} from '../utils/annualSwapTermOrderStorage';
import { loadTrackProgramSyncRepMap } from '../utils/trackProgramSyncRepStorage';
import { TRACK_PROGRAM_SPLIT_CHANGE_EVENT } from '../utils/trackProgramSplitStorage';
import {
  buildWildcardCharsByGrade,
  isTimetableTextMatchedByRegisteredWildcard,
  matchesTrackWildcardSuffix,
  sanitizeWildcardChar,
} from '../utils/trackWildcard';
import { shrinkToFitElementFont } from '../utils/shrinkToFitInputFont';
import {
  computeAnnualCellColorHoverTooltipStyle,
  computeProgramProgressHoverTooltipStyle,
  computeProgramTableFloatingPopupStyle,
  computeWeekTrackHoursHoverTooltipStyle,
  getProgramProgressOverlayWidth,
  layoutWidthFromClientRect,
  measureWeekTrackHoursPreviewLayoutWidth,
  resolveProgramTableFloatingHostRect,
} from '../utils/programProgressHoverTooltip';
import { hasSubstantialFixedEventContentElsewhere, alignFixedContentRowToScheduleSlot } from '../utils/programCellDisplayText';
import {
  annualSlotHasTimetableProgramAssignment,
  normalizeFixedEventName,
} from '../utils/annualProgressSlotSource';
import { buildTrackNameByTimetableTextByGrade } from '../utils/programProgressRows';
import { getOrderedTrackNamesForGrade } from '../utils/trackOrderForGrade';
import { orderedTrackAliasesForGrade } from '../utils/trackTimetableAlias';
import TimetableSettingsPage from './TimetableSettingsPage';
import { useTeamIdForLeaderImport } from '../hooks/useTeamIdForLeaderImport';
import { supabase } from '../supabaseClient';
import {
  deleteCalendarDay,
  getCalendarDays,
  upsertAcademicYearSettings,
  upsertCalendarDay,
} from '../services/calendarService';
import { getTeamLeaderGradeTrackSectionsPreview } from '../services/gradeTrackTeamLeaderImportService';
import { getTeamLeaderTimetableSettingsPreview } from '../services/programColorTeamLeaderImportService';
import {
  getTeamLeaderClassScheduleSectionsPreview,
} from '../services/classScheduleTeamLeaderImportService';
import {
  getTeamLeaderAcademicCalendarSectionsPreview,
} from '../services/academicCalendarTeamLeaderImportService';
import {
  writeAcademicSectionCalendarDaysToLocalStorage,
  writeAcademicSectionSettingsToLocalStorage,
  stripAutoBreakVacationCalendarDays,
  isDateInSchoolBreak,
} from '../utils/calendarSectionAcademicYear';
import { fetchTeamLeaderAnnualTimetableBundle } from '../services/annualTimetableTeamLeaderImportService';

interface WeekInfo {
  weekIndex: number;
  mondayIso: string;
  monthLabel: string; // 숫자만 예: "3"
  dateRangeLabel: string; // "M.D-M.D" 예: "3.2-3.7"
  datesByWeekday: string[]; // [0]=Mon, ..., [5]=Sat
}

interface WeekActivityMemoModalState {
  termKey: '1' | '2';
  termNumber: 1 | 2;
  gradeId: string;
  mondayIso: string;
  weekIndex: number;
  dateRangeDisplay: string;
  activityLinesDraft: string[];
  generalMemoDraft: string;
  baselineSnapshot: string;
}

interface WeeklyClipboardCell {
  weekIdx: number;
  dayIdx: number;
  periodIdx: number;
  value: string;
  memo: string;
  colorHex: string;
  slotKind: AnnualTimetableSlotKind;
  isBaseExcluded: boolean;
}

type TeamLeaderImportMode = 'annual_only' | 'annual_all';

type TeamLeaderGradeCandidate = {
  gradeId: string;
  rawName: string;
  title: string;
  grades: number[];
};

type TeamLeaderImportModalState = {
  targetGradeId: string;
  targetClassTitle: string;
  targetClassGrades: number[];
  selectedSourceGradeId: string;
  mode: TeamLeaderImportMode;
  sourceCandidates: TeamLeaderGradeCandidate[];
};

function generateLocalSectionId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function remapImportedProjectPrograms(
  rows: AnnualProjectProgram[],
  gradeId: string,
  academicYear: number
): AnnualProjectProgram[] {
  return rows.map((row, index) => ({
    ...row,
    // 팀장 워크스페이스 id와 충돌하면 upsert(id)에서 타 워크스페이스 행을 건드리려다 RLS가 난다.
    id: `import-${gradeId}-${academicYear}-${row.termNumber}-${Date.now()}-${index}`,
  }));
}

function normalizeTrackColumnsForCompare(columns: PageSectionLayout['trackColumns']): string {
  const rows = (columns ?? []).map((col) => ({
    name: String(col.name ?? '').trim(),
    aliases: [...(col.aliases ?? [])].map((a) => a.trim()).filter(Boolean).sort(),
    criteriaHours: Number(col.criteriaHours ?? 0) || 0,
    trackCategory: col.trackCategory === 'other' ? 'other' : 'regular',
    wildcardChar: String(col.wildcardChar ?? '').trim(),
  }));
  rows.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  return JSON.stringify(rows);
}

function normalizeClassScheduleSectionForCompare(
  rows: ClassScheduleRow[],
  weekdayMax: ClassScheduleSettings['sectionWeekdayMax'][string]
): string {
  return JSON.stringify({
    rows: rows.map((row) => ({
      kind: row.kind,
      name: row.name.trim(),
      startTime: row.startTime.trim(),
      durationMinutes: row.durationMinutes.trim(),
      endTime: row.endTime.trim(),
    })),
    weekdayMax: {
      mon: Number(weekdayMax?.mon ?? 0) || 0,
      tue: Number(weekdayMax?.tue ?? 0) || 0,
      wed: Number(weekdayMax?.wed ?? 0) || 0,
      thu: Number(weekdayMax?.thu ?? 0) || 0,
      fri: Number(weekdayMax?.fri ?? 0) || 0,
    },
  });
}

function isClassScheduleSectionContentEmpty(
  rows: ClassScheduleRow[] | undefined,
  weekdayMax: ClassScheduleSettings['sectionWeekdayMax'][string] | undefined
): boolean {
  let classCount = 0;
  let afterSchoolCount = 0;
  /** 저장 시 자동 채워지는 `1교시`·`방과후N`만 있고 시각·시수가 없으면 빈 섹션으로 본다 */
  const hasMeaningfulRow = (rows ?? []).some((row) => {
    if (
      (row.startTime ?? '').trim() ||
      (row.durationMinutes ?? '').trim() ||
      (row.endTime ?? '').trim()
    ) {
      return true;
    }
    const kind = row.kind ?? 'class';
    const name = (row.name ?? '').trim();
    if (kind === 'class') {
      classCount += 1;
      return Boolean(name && name !== `${classCount}교시`);
    }
    if (kind === 'after_school') {
      afterSchoolCount += 1;
      return Boolean(name && name !== `방과후${afterSchoolCount}`);
    }
    return Boolean(name);
  });
  if (hasMeaningfulRow) return false;
  const max = weekdayMax ?? { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0 };
  return !(
    Number(max.mon) > 0 ||
    Number(max.tue) > 0 ||
    Number(max.wed) > 0 ||
    Number(max.thu) > 0 ||
    Number(max.fri) > 0
  );
}

function normalizeAcademicSectionForCompare(section: PageSectionLayout | undefined): string {
  const settings = section?.academicCalendarSettings;
  const days = [...(section?.academicCalendarDays ?? [])]
    .map((d) => ({
      day: d.day,
      dayKind: d.day_kind,
      note: (d.note ?? '').trim(),
    }))
    .sort((a, b) => a.day.localeCompare(b.day));
  return JSON.stringify({
    settings: {
      academicYear: Number(settings?.academicYear ?? 0) || 0,
      yearStartsOn: String(settings?.yearStartsOn ?? ''),
      yearEndsOn: String(settings?.yearEndsOn ?? ''),
      summerBreakStartsOn: String(settings?.summerBreakStartsOn ?? ''),
      summerBreakEndsOn: String(settings?.summerBreakEndsOn ?? ''),
      winterBreakStartsOn: String(settings?.winterBreakStartsOn ?? ''),
      winterBreakEndsOn: String(settings?.winterBreakEndsOn ?? ''),
    },
    days,
  });
}

/** 휴일·토일수업·겨울방학 없으면 빈 섹션으로 보고 가져오기 시 덮어씀 */
function isAcademicSectionContentEmpty(section: PageSectionLayout | undefined): boolean {
  if (!section) return true;
  if ((section.academicCalendarDays ?? []).length > 0) return false;
  const winterFrom = (section.academicCalendarSettings?.winterBreakStartsOn ?? '').trim();
  const winterTo = (section.academicCalendarSettings?.winterBreakEndsOn ?? '').trim();
  return !(winterFrom && winterTo);
}

/** 가져오기 후 의미 없는 빈 공유 섹션 제거(기록한 섹션·내용/클래스 있는 섹션만 유지, 최소 1개) */
function pruneEmptySharedSectionsAfterImport<T extends { id: string; gradeIds: string[] }>(
  sections: T[],
  keepSectionId: string,
  isEmpty: (section: T) => boolean
): T[] {
  const kept = sections.filter(
    (section) =>
      section.id === keepSectionId ||
      section.gradeIds.length > 0 ||
      !isEmpty(section)
  );
  if (kept.length > 0) return kept;
  const fallback = sections.find((section) => section.id === keepSectionId);
  return fallback ? [fallback] : sections.slice(0, 1);
}

function isGradeTrackSectionContentEmpty(section: PageSectionLayout | undefined): boolean {
  return !(section?.trackColumns ?? []).some((column) => (column.name ?? '').trim().length > 0);
}

interface FixedProgramSummary {
  id: string;
  dateIso: string;
  weekdayKey: AnnualWeekdayKey;
  mondayIso: string;
  eventName: string;
  colorHex: string;
  /** 고정 프로그램 팔레트가 아닌 셀의 메모(운영계획에서 '일반 메모' 토글로만 표시) */
  isGeneralMemo: boolean;
  gradeIds: string[];
  /** 집계·표시용: `class:1`·`after_school:2` 또는 활동 등은 `raw:slotIndex` */
  periodKeys: string[];
  timetableByPeriodKey: Record<string, string>;
  cellKeys: string[];
}

interface ShortDaySummary {
  id: string;
  dateIso: string;
  weekdayKey: AnnualWeekdayKey;
  mondayIso: string;
  gradeIds: string[];
  periodChips: Array<{ key: string; inactive: boolean }>;
  endSlotTarget: EventSlotTargetKey | null;
  increaseCount: number;
  decreaseCount: number;
  cellKeys: string[];
}

/** 고정 프로그램 모달: 시간표명을 비우고 반영할 때 셀에 들어가는 기본 텍스트 */
const DEFAULT_EVENT_TIMETABLE_CELL_LABEL = '교과';

/** 고정 프로그램 관리 목록: `YYYY년 MM월 DD일` */
function formatFixedProgramManageDate(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}년 ${mo}월 ${day}일`;
}

function formatIsoWithDots(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${mo}.${day}`;
}

/** 연간 운영계획 주요 교육 활동: `03.15 (월)` — 자리 맞춤용 월·일 2자리 */
function formatOperationPlanActivityDate(iso: string, weekdayLabel: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return `${iso} (${weekdayLabel})`;
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${mo}.${day} (${weekdayLabel})`;
}

const ALL_ELEMENTARY_GRADE_NUMS = [1, 2, 3, 4, 5, 6] as const;

function inferGradeNumbersFromLabel(rawLabel: string): number[] {
  const parsed = parseGradeDisplayName(rawLabel).grades;
  if (parsed.length > 0) return [...parsed].sort((a, b) => a - b);
  const nums = new Set<number>();
  // `1학년`, `2 학년` 같은 일반 라벨도 인식한다.
  const gradeMatch = rawLabel.match(/([1-6])\s*학년/g) ?? [];
  gradeMatch.forEach((chunk) => {
    const m = chunk.match(/[1-6]/);
    if (!m) return;
    nums.add(Number(m[0]));
  });
  if (nums.size > 0) return Array.from(nums).sort((a, b) => a - b);
  const bareNums = rawLabel.match(/[1-6]/g) ?? [];
  bareNums.forEach((n) => nums.add(Number(n)));
  return Array.from(nums).sort((a, b) => a - b);
}

function gradeNumbersFromClassGradeIds(
  gradeIds: readonly string[],
  entries: ReadonlyArray<{ gradeId: string; label: string }>
): number[] {
  const nums = new Set<number>();
  const byId = new Map(entries.map((e) => [e.gradeId, e] as const));
  gradeIds.forEach((id) => {
    const entry = byId.get(id);
    if (!entry) return;
    inferGradeNumbersFromLabel(entry.label).forEach((g) => nums.add(g));
  });
  return Array.from(nums).sort((a, b) => a - b);
}

function schoolWideGradeNumbersFromEntries(
  entries: ReadonlyArray<{ gradeId: string; label: string }>
): number[] {
  const nums = new Set<number>();
  entries.forEach((entry) => {
    inferGradeNumbersFromLabel(entry.label).forEach((g) => nums.add(g));
  });
  return Array.from(nums).sort((a, b) => a - b);
}

function isAllSixElementaryGrades(gradeNums: readonly number[]): boolean {
  return (
    gradeNums.length === ALL_ELEMENTARY_GRADE_NUMS.length &&
    ALL_ELEMENTARY_GRADE_NUMS.every((n) => gradeNums.includes(n))
  );
}

/** 대상 학년이 등록된 전체 학년 집합과 같으면(또는 1~6 전체) [공통] 태그 */
function isOperationPlanCommonGradeTag(
  affectedGradeNums: readonly number[],
  registeredSchoolGradeNums: readonly number[]
): boolean {
  if (affectedGradeNums.length === 0) return true;
  if (isAllSixElementaryGrades(affectedGradeNums)) return true;
  if (registeredSchoolGradeNums.length === 0) return false;
  if (affectedGradeNums.length !== registeredSchoolGradeNums.length) return false;
  return registeredSchoolGradeNums.every((g) => affectedGradeNums.includes(g));
}

function formatShortDayTargetLabel(target: EventSlotTargetKey | null): string {
  if (!target) return '';
  const parsed = parseEventSlotTargetKey(target);
  if (!parsed) return '';
  if (parsed.kind === 'class') return `${parsed.order}교시`;
  return `방과후${parsed.order}`;
}

/** 연간시간표 교시 헤더 툴팁: `HH:MM`, 비어 있으면 `00:00` */
function normalizeScheduleTimeForTooltip(raw: string | undefined): string {
  const t = (raw ?? '').trim();
  if (!t) return '00:00';
  const m = t.match(/^(\d{1,2})\s*:\s*(\d{2})$/);
  if (!m) return t;
  const h = Math.min(23, Math.max(0, parseInt(m[1], 10) || 0));
  const min = Math.min(59, Math.max(0, parseInt(m[2], 10) || 0));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** 교시 헤더 호버: `09:00 - 09:45` 한 줄 */
function formatPeriodHeaderTimeRange(row: ClassScheduleRow | undefined): string {
  const start = normalizeScheduleTimeForTooltip(row?.startTime);
  const end = normalizeScheduleTimeForTooltip(row?.endTime);
  return `${start} - ${end}`;
}

function buildPeriodLabels(rows: ClassScheduleRow[]): string[] {
  return buildAnnualPeriodLabels(rows);
}

/** 연간 표 헤더에서 월·일만 입력할 때 (`3.15`, `03/15` 등) */
function parseMonthDayDotInput(raw: string): { month: number; day: number } | null {
  const s = raw.trim().replace(/\s+/g, '').replace(/,/g, '.');
  if (!s) return null;
  const norm = s.replace(/\//g, '.');
  const parts = norm.split('.').filter((p) => p.length > 0);
  if (parts.length < 2) return null;
  const month = Number(parts[0]);
  const day = Number(parts[1]);
  if (!Number.isFinite(month) || !Number.isFinite(day)) return null;
  const mi = Math.trunc(month);
  const di = Math.trunc(day);
  if (mi < 1 || mi > 12 || di < 1 || di > 31) return null;
  return { month: mi, day: di };
}

type EventSlotGroupKind = 'class' | 'after_school';
type EventSlotTargetKey = `${EventSlotGroupKind}:${number}`;

/** 고정 프로그램 모달: 선택된 교시·방과후 + 구간별 시간표 이름(메모) */
interface EventModalPeriodState {
  selected: EventSlotTargetKey[];
  namesByTarget: Partial<Record<EventSlotTargetKey, string>>;
}

function parseEventSlotTargetKey(key: string): { kind: EventSlotGroupKind; order: number } | null {
  const [kindRaw, orderRaw] = key.split(':');
  if ((kindRaw !== 'class' && kindRaw !== 'after_school') || !orderRaw) return null;
  const order = Number(orderRaw);
  if (!Number.isInteger(order) || order < 1) return null;
  return { kind: kindRaw, order };
}

function eventSlotTargetLabel(kind: EventSlotGroupKind, order: number): string {
  return kind === 'class' ? `${order}교시` : `방과후${order}`;
}

function eventSlotTargetKeyBySlotIndex(sectionRows: ClassScheduleRow[], slotIndex: number): EventSlotTargetKey | null {
  let classCount = 0;
  let afterSchoolCount = 0;
  for (let i = 0; i <= slotIndex && i < sectionRows.length; i += 1) {
    const kind = sectionRows[i]?.kind;
    if (kind === 'class') classCount += 1;
    else if (kind === 'after_school') afterSchoolCount += 1;
  }
  const kind = sectionRows[slotIndex]?.kind;
  if (kind === 'class' && classCount > 0) return `class:${classCount}`;
  if (kind === 'after_school' && afterSchoolCount > 0) return `after_school:${afterSchoolCount}`;
  return null;
}

function findSlotIndexByEventSlotTarget(sectionRows: ClassScheduleRow[], target: EventSlotTargetKey): number {
  const parsed = parseEventSlotTargetKey(target);
  if (!parsed) return -1;
  let classCount = 0;
  let afterSchoolCount = 0;
  for (let i = 0; i < sectionRows.length; i += 1) {
    const kind = sectionRows[i]?.kind;
    if (kind === 'class') {
      classCount += 1;
      if (parsed.kind === 'class' && classCount === parsed.order) return i;
    } else if (kind === 'after_school') {
      afterSchoolCount += 1;
      if (parsed.kind === 'after_school' && afterSchoolCount === parsed.order) return i;
    }
  }
  return -1;
}

/** 고정 프로그램 집계/목록용: 수업·방과후는 논리 순번 키, 그 외(활동 등)는 열 인덱스 */
function fixedProgramPeriodKeyFromCell(sectionRows: ClassScheduleRow[], slotIndex: number): string {
  const t = eventSlotTargetKeyBySlotIndex(sectionRows, slotIndex);
  if (t) return t;
  return `raw:${slotIndex}`;
}

function formatFixedProgramPeriodLabel(key: string): string {
  const parsed = parseEventSlotTargetKey(key);
  if (parsed) return eventSlotTargetLabel(parsed.kind, parsed.order);
  if (key.startsWith('raw:')) {
    const idx = Number(key.slice(4));
    if (Number.isInteger(idx) && idx >= 0) return `${idx + 1}타임`;
  }
  return key;
}

function compareFixedProgramPeriodKeys(a: string, b: string): number {
  const rank = (key: string) => {
    const p = parseEventSlotTargetKey(key);
    if (p?.kind === 'class') return p.order;
    if (p?.kind === 'after_school') return 100 + p.order;
    if (key.startsWith('raw:')) return 200 + Number(key.slice(4));
    return 999;
  };
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;
  return a.localeCompare(b);
}

function buildOrderedEventSlotTargetKeys(classMax: number, afterSchoolMax: number): EventSlotTargetKey[] {
  const keys: EventSlotTargetKey[] = [];
  for (let i = 1; i <= classMax; i += 1) keys.push(`class:${i}`);
  for (let j = 1; j <= afterSchoolMax; j += 1) keys.push(`after_school:${j}`);
  return keys;
}

function computeSlotOrderLimitsFromEntries(
  entries: AnnualClassEntry[],
  selectedGradeIds: string[]
): { classMax: number; afterSchoolMax: number } {
  const targetGradeIds = selectedGradeIds.length > 0 ? selectedGradeIds : entries.map((e) => e.gradeId);
  const targetEntries =
    targetGradeIds.length > 0 ? entries.filter((e) => targetGradeIds.includes(e.gradeId)) : entries;
  let classMax = 0;
  let afterSchoolMax = 0;
  targetEntries.forEach((entry) => {
    let classCount = 0;
    let afterSchoolCount = 0;
    entry.sectionRows.forEach((row) => {
      if (row.kind === 'class') classCount += 1;
      else if (row.kind === 'after_school') afterSchoolCount += 1;
    });
    classMax = Math.max(classMax, classCount);
    afterSchoolMax = Math.max(afterSchoolMax, afterSchoolCount);
  });
  return { classMax, afterSchoolMax };
}

function weeklyCellKey(mondayIso: string, dayKey: string, periodIdx: number): string {
  return `${mondayIso}-${dayKey}-${periodIdx}`;
}

/** weeklyCellKey 문자열을 다시 (mondayIso, dayKey, periodIdx)로 파싱 (mondayIso에 하이픈 포함) */
function parseWeeklyCellKey(key: string): { mondayIso: string; dayKey: string; periodIdx: number } | null {
  const parts = key.split('-');
  if (parts.length < 3) return null;
  const periodIdx = Number(parts.pop());
  const dayKey = parts.pop();
  if (dayKey == null || !Number.isInteger(periodIdx) || periodIdx < 0 || periodIdx >= MAX_PERIODS) return null;
  const mondayIso = parts.join('-');
  if (!mondayIso || !WEEKDAY_KEYS.some((w) => w.key === dayKey)) return null;
  return { mondayIso, dayKey, periodIdx };
}

function buildWeeksFromRange(startsOn: string, endsOn: string): WeekInfo[] {
  if (!startsOn || !endsOn) return [];
  const formatMMDD = (date: Date) =>
    `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  const endDate = parseISODate(endsOn);
  const endMonday = mondayOf(endsOn);
  const endMondayIso = toISODate(endMonday);
  let cur = mondayOf(startsOn);
  const list: WeekInfo[] = [];
  let weekIndex = 0;
  while (cur.getTime() <= endDate.getTime()) {
    const mondayIso = toISODate(cur);
    const mon = parseISODate(mondayIso);
    const monthLabel = String(mon.getMonth() + 1);
    const sat = addDays(cur, 5);
    const dateRangeLabel = `${formatMMDD(mon)}-${formatMMDD(sat)}`;
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d++) {
      datesByWeekday.push(toISODate(addDays(cur, d)));
    }
    list.push({
      weekIndex: ++weekIndex,
      mondayIso,
      monthLabel,
      dateRangeLabel,
      datesByWeekday,
    });
    cur = addDays(cur, 7);
  }
  const hasEndWeek = list.some((w) => w.mondayIso === endMondayIso);
  if (!hasEndWeek) {
    const mon = parseISODate(endMondayIso);
    const sat = addDays(endMonday, 5);
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d++) {
      datesByWeekday.push(toISODate(addDays(endMonday, d)));
    }
    list.push({
      weekIndex: ++weekIndex,
      mondayIso: endMondayIso,
      monthLabel: String(mon.getMonth() + 1),
      dateRangeLabel: `${formatMMDD(mon)}-${formatMMDD(sat)}`,
      datesByWeekday,
    });
  }
  return list;
}

function annualCellDisplayFontSize(text: string): string {
  const t = text.trim();
  const len = t.length;
  if (len <= 2) return '0.62rem';
  if (len <= 4) return '0.5rem';
  if (len <= 6) return '0.4rem';
  if (len <= 8) return '0.34rem';
  return '0.28rem';
}

function normalizeProgramColorHex(hex: string): string {
  return hex.trim().toLowerCase();
}

function colorHexInPalette(hex: string, palette: string[]): boolean {
  const n = normalizeProgramColorHex(hex);
  if (!n) return false;
  return palette.some((c) => normalizeProgramColorHex(c) === n);
}

/** 배경 hex 밝기에 따라 숫자/글자용 검정·흰색 */
function contrastInkForBackgroundHex(hex: string): '#111111' | '#ffffff' {
  const n = normalizeHexColorForProjectCompare(hex);
  if (!n) return '#111111';
  const r = Number.parseInt(n.slice(1, 3), 16);
  const g = Number.parseInt(n.slice(3, 5), 16);
  const b = Number.parseInt(n.slice(5, 7), 16);
  if (![r, g, b].every((c) => Number.isFinite(c))) return '#111111';
  const linear = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * linear[0]! + 0.7152 * linear[1]! + 0.0722 * linear[2]!;
  return lum > 0.45 ? '#111111' : '#ffffff';
}

function findAnnualProjectProgramForCell(
  colorHex: string,
  programs: AnnualProjectProgram[],
  termNumber: 1 | 2
): AnnualProjectProgram | undefined {
  const nc = normalizeHexColorForProjectCompare(colorHex);
  if (!nc) return undefined;
  return programs.find((p) => {
    if (p.termNumber !== termNumber) return false;
    return normalizeHexColorForProjectCompare(p.colorHex) === nc;
  });
}

/** 편제에 등록된 시간표명이 아니면 true (빈 값·「교과」는 미표시용으로 유효 처리) */
function isAnnualTimetableNameUnregistered(
  gradeId: string,
  text: string,
  namesByGrade: Record<string, Set<string>>,
  wildcardCharsByGrade: Record<string, Set<string>>
): boolean {
  const raw = text.trim();
  if (!raw) return false;
  if (raw === DEFAULT_EVENT_TIMETABLE_CELL_LABEL) return false;
  const set = namesByGrade[gradeId];
  if (set?.has(raw)) return false;
  const wildcards = wildcardCharsByGrade[gradeId];
  if (wildcards?.size && isTimetableTextMatchedByRegisteredWildcard(raw, wildcards)) return false;
  if (!set || set.size === 0) return true;
  return true;
}

/**
 * 고정 프로그램 모달 시간표명 입력: 미등록이면 true (빈 값·교과는 무시).
 * 적용 클래스를 아직 고르지 않았을 때는 편제에 등록된 이름의 합집합으로 검사한다.
 */
function isEventModalTimetableTextUnregistered(
  raw: string,
  selectedGradeIds: string[],
  namesByGrade: Record<string, Set<string>>,
  fallbackGradeIds: string[],
  wildcardCharsByGrade: Record<string, Set<string>>
): boolean {
  const t = raw.trim();
  if (!t) return false;
  if (t === DEFAULT_EVENT_TIMETABLE_CELL_LABEL) return false;

  const useIds = selectedGradeIds.length > 0 ? selectedGradeIds : fallbackGradeIds;
  if (!useIds.length) return false;

  if (selectedGradeIds.length > 0) {
    return selectedGradeIds.some((gid) =>
      isAnnualTimetableNameUnregistered(gid, t, namesByGrade, wildcardCharsByGrade)
    );
  }

  const union = new Set<string>();
  const unionWildcards = new Set<string>();
  useIds.forEach((gid) => {
    namesByGrade[gid]?.forEach((n) => union.add(n));
    wildcardCharsByGrade[gid]?.forEach((wc) => unionWildcards.add(wc));
  });
  if (unionWildcards.size > 0 && isTimetableTextMatchedByRegisteredWildcard(t, unionWildcards)) {
    return false;
  }
  if (union.size === 0) {
    return true;
  }
  return !union.has(t);
}

function resolveTrackNameForTimetableText(
  gradeId: string,
  timetableText: string,
  trackNameByTimetableTextByGrade: Record<string, Record<string, string>>,
  wildcardCharByGradeTrackName: Record<string, Record<string, string>>
): string {
  const text = timetableText.trim();
  if (!text) return '';
  const byText = trackNameByTimetableTextByGrade[gradeId] ?? {};
  const exact = byText[text];
  if (exact) return exact;
  const wildcardByTrack = wildcardCharByGradeTrackName[gradeId] ?? {};
  for (const [trackName, wildcardChar] of Object.entries(wildcardByTrack)) {
    if (matchesTrackWildcardSuffix(text, wildcardChar)) return trackName;
  }
  return '';
}

/** 기초시간표와 연간 입력이 같은 편제가 아니면 true (텍스트가 같거나 같은 편제 별칭이면 false) */
function isAnnualTrackMismatchAgainstBase(
  gradeId: string,
  baseText: string,
  annualText: string,
  trackNameByTimetableTextByGrade: Record<string, Record<string, string>>,
  wildcardCharByGradeTrackName: Record<string, Record<string, string>>
): boolean {
  const baseT = baseText.trim();
  const annT = annualText.trim();
  if (!baseT || !annT) return false;
  if (annT === baseT) return false;
  const baseTrack = resolveTrackNameForTimetableText(
    gradeId,
    baseT,
    trackNameByTimetableTextByGrade,
    wildcardCharByGradeTrackName
  );
  const annTrack = resolveTrackNameForTimetableText(
    gradeId,
    annT,
    trackNameByTimetableTextByGrade,
    wildcardCharByGradeTrackName
  );
  if (baseTrack && annTrack && baseTrack === annTrack) return false;
  return true;
}

/** 고정 프로그램 색으로 모달 열 때와 동일: 선택 키 순서대로 첫 유효 셀의 dateIso */
function dateIsoFromAnnualCellSelectionLikeColorModal(
  keys: string[],
  cellsByKey: Record<string, AnnualEffectiveCell>
): string | null {
  const parsed = keys
    .map((key) => {
      const cell = cellsByKey[key];
      return cell ? { cell } : null;
    })
    .filter((item): item is NonNullable<typeof item> => item != null);
  if (!parsed.length) return null;
  const d = parsed[0]!.cell.dateIso?.trim();
  return d || null;
}

/** 모달 초기 날짜: 선택 셀 날짜 우선, 없으면 학기 시작일(또는 달력 첫 일) */
function resolveAnnualModalInitialDateIso(
  keys: string[],
  cellsByKey: Record<string, AnnualEffectiveCell>,
  eventDateTargets: Record<string, unknown>,
  semesterStartIso: string
): string {
  const fromCell = dateIsoFromAnnualCellSelectionLikeColorModal(keys, cellsByKey);
  if (fromCell) return fromCell;
  const start = (semesterStartIso ?? '').trim();
  if (start && eventDateTargets[start]) return start;
  const sorted = Object.keys(eventDateTargets).sort((a, b) => a.localeCompare(b));
  return sorted[0] ?? '';
}

function normalizeIsoDateInput(raw: string): string | null {
  const value = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() + 1 !== m || date.getDate() !== d) return null;
  return toISODate(date);
}

function areAllKeysInWeeks(keys: string[], weeks: WeekInfo[], dayKeys: string[]): boolean {
  if (keys.length === 0) return false;
  return keys.every((k) => weeklyKeyToIndicesCompound(k, weeks, dayKeys) != null);
}

/** 주당 테이블에서 키를 (weekIdx, dayIdx, periodIdx)로 파싱 (mondayIso에 하이픈 포함) */
function weeklyKeyToIndices(
  key: string,
  weeks: WeekInfo[],
  dayKeys: string[]
): { weekIdx: number; dayIdx: number; periodIdx: number } | null {
  const parsed = parseWeeklyCellKey(key);
  if (!parsed) return null;
  const weekIdx = weeks.findIndex((w) => w.mondayIso === parsed.mondayIso);
  const dayIdx = dayKeys.indexOf(parsed.dayKey);
  if (weekIdx < 0 || dayIdx < 0) return null;
  return { weekIdx, dayIdx, periodIdx: parsed.periodIdx };
}

/** 두 키로 만든 직사각형 영역(행=주, 열=요일×교시) 안의 셀 키 */
function weeklyRectKeys(
  anchorKey: string,
  currentKey: string,
  weeks: WeekInfo[],
  dayKeys: string[]
): string[] {
  const a = weeklyKeyToIndices(anchorKey, weeks, dayKeys);
  const b = weeklyKeyToIndices(currentKey, weeks, dayKeys);
  if (!a || !b) return [];
  const minWi = Math.min(a.weekIdx, b.weekIdx);
  const maxWi = Math.max(a.weekIdx, b.weekIdx);
  const aFlatCol = a.dayIdx * MAX_PERIODS + a.periodIdx;
  const bFlatCol = b.dayIdx * MAX_PERIODS + b.periodIdx;
  const minFlatCol = Math.min(aFlatCol, bFlatCol);
  const maxFlatCol = Math.max(aFlatCol, bFlatCol);
  const keys: string[] = [];
  for (let wi = minWi; wi <= maxWi; wi += 1) {
    const week = weeks[wi];
    if (!week) continue;
    for (let flatCol = minFlatCol; flatCol <= maxFlatCol; flatCol += 1) {
      const di = Math.floor(flatCol / MAX_PERIODS);
      const pi = flatCol % MAX_PERIODS;
      const dk = dayKeys[di];
      if (!dk) continue;
      keys.push(weeklyCellKey(week.mondayIso, dk, pi));
    }
  }
  return keys;
}

function weeklyRectKeysCompound(
  anchorCompound: string,
  currentCompound: string,
  weeks: WeekInfo[],
  dayKeys: string[]
): string[] {
  const ga = gradeIdFromCompound(anchorCompound);
  const gc = gradeIdFromCompound(currentCompound);
  const wa = weeklyPartFromCompound(anchorCompound);
  const wc = weeklyPartFromCompound(currentCompound);
  if (!ga || !wa || !wc) return [];
  if (ga !== gc) return [anchorCompound];
  const inner = weeklyRectKeys(wa, wc, weeks, dayKeys);
  return inner.map((wk) => `${ga}|${wk}`);
}

function compoundCellKey(gradeId: string, mondayIso: string, dayKey: string, periodIdx: number): string {
  return buildAnnualCellKey(gradeId, mondayIso, dayKey, periodIdx);
}

function annualCellDomId(compoundKey: string): string {
  return `cp-annual-cell-${compoundKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

function annualWeekMemoAnchorDomId(termKey: '1' | '2', gradeId: string, mondayIso: string): string {
  return `cp-annual-week-memo-${termKey}-${gradeId.replace(/[^a-zA-Z0-9_-]/g, '_')}-${mondayIso}`;
}

function annualWeekHoursAnchorDomId(termKey: '1' | '2', gradeId: string, mondayIso: string): string {
  return `cp-annual-week-hours-${termKey}-${gradeId.replace(/[^a-zA-Z0-9_-]/g, '_')}-${mondayIso}`;
}

function annualTermHoursAnchorDomId(termKey: '1' | '2', gradeId: string): string {
  return `cp-annual-term-hours-${termKey}-${gradeId.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

function deriveUniformSelectedCellText(
  editableKeys: string[],
  gradeId: string,
  wks: WeekInfo[],
  dayKeys: string[],
  effectiveCellsByKey: Record<string, AnnualEffectiveCell>,
  editingCell: {
    gradeId: string;
    mondayIso: string;
    dayKey: string;
    periodIdx: number;
    draft: string;
  } | null
): string {
  if (editableKeys.length === 0) return '';
  if (editingCell && editingCell.gradeId === gradeId) {
    const editingKey = compoundCellKey(
      editingCell.gradeId,
      editingCell.mondayIso,
      editingCell.dayKey,
      editingCell.periodIdx
    );
    if (editableKeys.includes(editingKey)) {
      return editingCell.draft ?? '';
    }
  }
  const values = editableKeys.map((compoundKey) => {
    const pos = weeklyKeyToIndicesCompound(compoundKey, wks, dayKeys);
    const isEditingThis =
      editingCell &&
      editingCell.gradeId === gradeId &&
      pos != null &&
      wks[pos.weekIdx]?.mondayIso === editingCell.mondayIso &&
      dayKeys[pos.dayIdx] === editingCell.dayKey &&
      editingCell.periodIdx === pos.periodIdx;
    if (isEditingThis) return editingCell.draft ?? '';
    return effectiveCellsByKey[compoundKey]?.textValue ?? '';
  });
  const first = values[0];
  if (values.every((value) => value === first)) return first;
  return '';
}

type AnnualTermToolbarSelectionInputProps = {
  gradeId: string;
  selInTerm: boolean;
  selectedCells: string[];
  wks: WeekInfo[];
  dayKeysForSelection: string[];
  effectiveCellsByKey: Record<string, AnnualEffectiveCell>;
  editingCell: {
    gradeId: string;
    mondayIso: string;
    dayKey: string;
    periodIdx: number;
    draft: string;
  } | null;
  editingInputRef: React.RefObject<HTMLInputElement | null>;
  filterEditableSelectionKeys: (keys: string[]) => string[];
  applyCellUpdates: (
    updates: Array<{
      key: string;
      textValue?: string;
      memo?: string;
      colorHex?: string;
      slotKind?: AnnualTimetableSlotKind;
      isBaseExcluded?: boolean;
    }>
  ) => void;
  setEditingCell: React.Dispatch<
    React.SetStateAction<{
      gradeId: string;
      mondayIso: string;
      dayKey: string;
      periodIdx: number;
      draft: string;
    } | null>
  >;
  moveSingleSelectionByFlatStep: (
    step: number,
    options?: { skipAutoEditOnce?: boolean }
  ) => boolean;
};

const AnnualTermToolbarSelectionInput: React.FC<AnnualTermToolbarSelectionInputProps> = ({
  gradeId,
  selInTerm,
  selectedCells,
  wks,
  dayKeysForSelection,
  effectiveCellsByKey,
  editingCell,
  editingInputRef,
  filterEditableSelectionKeys,
  applyCellUpdates,
  setEditingCell,
  moveSingleSelectionByFlatStep,
}) => {
  const editableKeys = React.useMemo(
    () =>
      filterEditableSelectionKeys(selectedCells).filter((key) =>
        areAllKeysInWeeks([key], wks, dayKeysForSelection)
      ),
    [dayKeysForSelection, filterEditableSelectionKeys, selectedCells, wks]
  );
  const uniformText = React.useMemo(
    () =>
      deriveUniformSelectedCellText(
        editableKeys,
        gradeId,
        wks,
        dayKeysForSelection,
        effectiveCellsByKey,
        editingCell
      ),
    [dayKeysForSelection, editableKeys, editingCell, effectiveCellsByKey, gradeId, wks]
  );
  const [draft, setDraft] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const composingRef = React.useRef(false);
  const suppressBlurCommitRef = React.useRef(false);
  const pendingTabRefocusRef = React.useRef(false);
  const lastCommittedRef = React.useRef('');
  const perWeekFlatStep = dayKeysForSelection.length * MAX_PERIODS;
  const editingCompoundKey = React.useMemo(() => {
    if (!editingCell) return null;
    return compoundCellKey(
      editingCell.gradeId,
      editingCell.mondayIso,
      editingCell.dayKey,
      editingCell.periodIdx
    );
  }, [editingCell?.dayKey, editingCell?.gradeId, editingCell?.mondayIso, editingCell?.periodIdx]);
  const canEdit = selInTerm && editableKeys.length > 0;
  const displayedValue = focused ? draft : uniformText;

  React.useEffect(() => {
    if (focused) return;
    lastCommittedRef.current = uniformText;
  }, [focused, uniformText, editableKeys.join('|')]);

  const beginToolbarEdit = React.useCallback(() => {
    const seed =
      editingCell && editingCompoundKey && editableKeys.includes(editingCompoundKey)
        ? (editingInputRef.current?.value ?? editingCell.draft ?? '')
        : uniformText;
    setDraft(seed);
    if (editingCell) setEditingCell(null);
    setFocused(true);
  }, [editableKeys, editingCell, editingCompoundKey, editingInputRef, setEditingCell, uniformText]);

  const commitDraftValue = React.useCallback(
    (value: string) => {
      if (!canEdit) return;
      if (value === lastCommittedRef.current) return;
      applyCellUpdates(editableKeys.map((key) => ({ key, textValue: value })));
      setEditingCell(null);
      lastCommittedRef.current = value;
    },
    [applyCellUpdates, canEdit, editableKeys, setEditingCell]
  );

  const commitToolbarDraft = React.useCallback(() => {
    if (!canEdit) {
      setFocused(false);
      return;
    }
    const value = draft;
    setFocused(false);
    commitDraftValue(value);
  }, [canEdit, commitDraftValue, draft]);

  const canNavigateSelection = selectedCells.length === 1 && editableKeys.length > 0;

  React.useEffect(() => {
    if (!pendingTabRefocusRef.current) return;
    if (selectedCells.length !== 1) return;
    const nextEditableKeys = filterEditableSelectionKeys(selectedCells).filter((key) =>
      areAllKeysInWeeks([key], wks, dayKeysForSelection)
    );
    if (nextEditableKeys.length === 0) {
      pendingTabRefocusRef.current = false;
      setFocused(false);
      return;
    }
    const nextText = deriveUniformSelectedCellText(
      nextEditableKeys,
      gradeId,
      wks,
      dayKeysForSelection,
      effectiveCellsByKey,
      null
    );
    pendingTabRefocusRef.current = false;
    setDraft(nextText);
    lastCommittedRef.current = nextText;
    setFocused(true);
    const raf = window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    dayKeysForSelection,
    effectiveCellsByKey,
    filterEditableSelectionKeys,
    gradeId,
    selectedCells,
    wks,
  ]);

  return (
    <input
      ref={inputRef}
      type="text"
      className="cp-annual-term-toolbar-selection-input"
      value={displayedValue}
      disabled={!canEdit}
      onChange={(e) => {
        if (!focused) return;
        setDraft(e.target.value);
      }}
      onFocus={() => {
        if (!canEdit) return;
        beginToolbarEdit();
      }}
      onBlur={() => {
        if (composingRef.current || suppressBlurCommitRef.current) return;
        commitToolbarDraft();
      }}
      onCompositionStart={() => {
        composingRef.current = true;
      }}
      onCompositionEnd={(e) => {
        composingRef.current = false;
        if (!focused) return;
        setDraft(e.currentTarget.value);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          if (composingRef.current) return;
          e.preventDefault();
          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
          if (!canEdit) {
            inputRef.current?.blur();
            return;
          }
          suppressBlurCommitRef.current = true;
          window.setTimeout(() => {
            suppressBlurCommitRef.current = false;
          }, 0);
          if (focused) commitDraftValue(draft);
          setFocused(false);
          if (canNavigateSelection) {
            moveSingleSelectionByFlatStep(perWeekFlatStep, { skipAutoEditOnce: true });
          }
          inputRef.current?.blur();
          return;
        }
        if (e.key === 'Tab') {
          if (composingRef.current) return;
          e.preventDefault();
          e.stopPropagation();
          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
          if (!canEdit || !canNavigateSelection) return;
          suppressBlurCommitRef.current = true;
          window.setTimeout(() => {
            suppressBlurCommitRef.current = false;
          }, 0);
          if (focused) commitDraftValue(draft);
          const moved = moveSingleSelectionByFlatStep(e.shiftKey ? -1 : 1, { skipAutoEditOnce: true });
          if (moved) {
            pendingTabRefocusRef.current = true;
          } else {
            setFocused(false);
            inputRef.current?.blur();
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setFocused(false);
          inputRef.current?.blur();
        }
      }}
      onMouseDown={(e) => e.stopPropagation()}
      aria-label="선택한 셀 시간표명 입력"
    />
  );
};

function weekMemoStateKey(termNumber: 1 | 2, gradeId: string, mondayIso: string): string {
  return `${termNumber}|${gradeId}|${mondayIso}`;
}

function serializeWeekActivityMemoDraft(activityLines: string[], generalMemo: string): string {
  return JSON.stringify({ a: activityLines, g: generalMemo });
}

function weekMemoRowToHoverPayload(row: { activityLines: string[]; generalMemo: string }): {
  activityLines: string[];
  generalMemo: string;
} {
  const lines = (row.activityLines ?? []).map((s) => s.trim()).filter((s) => s.length > 0);
  return { activityLines: lines, generalMemo: (row.generalMemo ?? '').trim() };
}

function weeklyPartFromCompound(compoundKey: string): string | null {
  const i = compoundKey.indexOf('|');
  if (i < 0) return null;
  return compoundKey.slice(i + 1);
}

function gradeIdFromCompound(compoundKey: string): string | null {
  return gradeIdFromAnnualCellKey(compoundKey);
}

/** 연간시간표 시수 요약: 편제별 프로그램 생성 방식(편제 및 기준 시수와 동일 출처) */
function resolveTrackProgramModeForSummary(
  workspaceId: string,
  gradeId: string,
  trackName: string
): TrackProgramMode {
  if (!workspaceId || !gradeId) return 'unified';
  return loadTrackProgramModeMap(workspaceId, gradeId)[trackName] ?? 'unified';
}

function resolveSyncRepresentativeAlias(
  workspaceId: string,
  gradeId: string,
  trackName: string,
  aliases: string[]
): string {
  const cleaned = aliases.map((a) => a.trim()).filter(Boolean);
  if (cleaned.length === 0) return '';
  const fromMap = loadTrackProgramSyncRepMap(workspaceId, gradeId)[trackName]?.trim();
  if (fromMap && cleaned.includes(fromMap)) return fromMap;
  return cleaned[0] ?? '';
}

function weeklyKeyToIndicesCompound(
  compoundKey: string,
  weeks: WeekInfo[],
  dayKeys: string[]
): { weekIdx: number; dayIdx: number; periodIdx: number } | null {
  const wk = weeklyPartFromCompound(compoundKey);
  if (!wk) return null;
  return weeklyKeyToIndices(wk, weeks, dayKeys);
}

type AnnualCellPersistSnapshot = {
  textValue: string;
  memo: string;
  colorHex: string;
  slotKind: AnnualTimetableSlotKind;
  isBaseExcluded: boolean;
};

function annualCellNextSnapshotFromUpdate(
  current: AnnualCellPersistSnapshot,
  update: {
    textValue?: string;
    memo?: string;
    colorHex?: string;
    slotKind?: AnnualTimetableSlotKind;
    isBaseExcluded?: boolean;
  }
): AnnualCellPersistSnapshot {
  const textValue = update.textValue !== undefined ? update.textValue : current.textValue;
  let memo = current.memo;
  if (update.memo !== undefined) {
    memo = (update.memo ?? '').trim();
  }
  let colorHex = current.colorHex;
  if (update.colorHex !== undefined) {
    colorHex = (update.colorHex ?? '').trim();
  }
  const slotKind = update.slotKind ?? current.slotKind;
  let isBaseExcluded = current.isBaseExcluded;
  if (update.isBaseExcluded !== undefined) {
    isBaseExcluded = update.isBaseExcluded;
  } else if (update.colorHex !== undefined) {
    const nextColor = (update.colorHex ?? '').trim();
    if (!nextColor) isBaseExcluded = false;
  }
  return { textValue: textValue ?? '', memo, colorHex, slotKind, isBaseExcluded };
}

function annualCellSnapshotsEqual(a: AnnualCellPersistSnapshot, b: AnnualCellPersistSnapshot): boolean {
  return (
    a.textValue === b.textValue &&
    a.memo === b.memo &&
    a.colorHex === b.colorHex &&
    a.slotKind === b.slotKind &&
    a.isBaseExcluded === b.isBaseExcluded
  );
}

function annualPersistSnapshotFromEffective(cell: AnnualEffectiveCell): AnnualCellPersistSnapshot {
  return {
    textValue: cell.textValue ?? '',
    memo: (cell.memo ?? '').trim(),
    colorHex: (cell.colorHex ?? '').trim(),
    slotKind: annualPersistSlotKindFromEffective(cell),
    isBaseExcluded: cell.isBaseExcluded,
  };
}

/** 프로젝트 색 변경·삭제 시, 해당 학기·학년에 칠해 둔 동일 hex 셀 배경을 맞춘다(제외 색 단색 셀은 건드리지 않음) */
function collectTimetableCellUpdatesForProjectPaintHexChange(
  effectiveByKey: Record<string, AnnualEffectiveCell>,
  params: { gradeId: string; termNumber: 1 | 2; oldColorHex: string; newColorHex: string }
): Array<{ key: string; colorHex: string; isBaseExcluded: boolean }> {
  const oldNorm = normalizeHexColorForProjectCompare(params.oldColorHex);
  if (!oldNorm) return [];
  const nextHex = params.newColorHex.trim();
  const out: Array<{ key: string; colorHex: string; isBaseExcluded: boolean }> = [];
  Object.entries(effectiveByKey).forEach(([key, cell]) => {
    if (cell.gradeId !== params.gradeId || cell.termNumber !== params.termNumber) return;
    if (!cell.isEditable) return;
    if (cell.slotKind === 'holiday' || cell.slotKind === 'out_of_term') return;
    if (cell.isBaseExcluded) return;
    const cn = normalizeHexColorForProjectCompare(cell.colorHex);
    if (!cn || cn !== oldNorm) return;
    out.push({ key, colorHex: nextHex, isBaseExcluded: false });
  });
  return out;
}

interface AnnualTimetablePageProps {
  workspaceId: string;
  grades: Grade[];
  /** '' = 네비바 통합 클래스 보기 */
  currentGradeId?: string;
  isActive?: boolean;
}

/** 기초시간표 반영 버튼 활성 조건: 해당 학년 id 키가 `baseTimetableByGrade`에 있을 것 */
export function gradeHasBaseTimetableForApply(
  baseTimetableByGrade: Record<string, BaseTimetableByTerm>,
  gradeId: string
): boolean {
  return Object.prototype.hasOwnProperty.call(baseTimetableByGrade, gradeId);
}

const ANNUAL_LOCAL_EDIT_HISTORY_MAX = 50;

/** 연간 셀 맵 + dirty 목록 — 로컬 실행 취소용 스냅샷 */
type AnnualLocalHistoryFrame = {
  cellData: Record<string, string>;
  cellMemos: Record<string, string>;
  cellColors: Record<string, string>;
  cellSlotKinds: Record<string, AnnualTimetableSlotKind>;
  cellRecordIds: Record<string, string>;
  cellBaseExcluded: Record<string, boolean>;
  dirtyCellKeys: string[];
};

const AnnualTimetablePage: React.FC<AnnualTimetablePageProps> = ({
  workspaceId,
  grades,
  currentGradeId = '',
  isActive = true,
}) => {
  const {
    setAnnualTimetableDirty,
    saveAnnualTimetableRef,
    timetableSettingsDirty,
    saveTimetableSettingsRef,
  } = useContext(GradeTrackLeaveContext);

  /** keep-alive 프로그램 표 포커스·전역 단축키가 연간시간표 화살표 이동을 가로채지 않게 */
  useLayoutEffect(() => {
    releaseTrappedKeepAliveFocus('/annual-timetable');
  }, []);

  const [holidaySet, setHolidaySet] = useState<Set<string>>(new Set());
  const [calendarDayKindByDate, setCalendarDayKindByDate] = useState<Record<string, DayKind>>({});
  const [calendarDayNoteByDate, setCalendarDayNoteByDate] = useState<Record<string, string>>({});
  const [classSchedule, setClassSchedule] = useState<ClassScheduleSettings | null>(null);
  const classEntries = React.useMemo(
    () => buildAnnualClassEntries(classSchedule, grades),
    [classSchedule, grades]
  );
  const timetableGradeIds = React.useMemo(
    () => classEntries.map((entry) => entry.gradeId).filter(Boolean),
    [classEntries]
  );
  const { schoolScopedCacheSyncReady } = useSchoolScopedCacheSyncReady(workspaceId, isActive);
  const gradeIdForCalendarYear = (currentGradeId || classEntries[0]?.gradeId || '').trim();
  const calendarLinkedContext = useCalendarLinkedContext(workspaceId, gradeIdForCalendarYear);
  const academicYear = calendarLinkedContext.academicYear;
  const terms = calendarLinkedContext.terms;
  const [tracks, setTracks] = useState<Track[]>([]);
  const [gradeTrackSections, setGradeTrackSections] = useState<PageSectionLayout[]>([]);
  /** 편제 프로그램 모드·동기화 대표(localStorage) 변경 시 시수 요약 재계산 */
  const [trackProgramPrefsRev, setTrackProgramPrefsRev] = useState(0);
  const [representativeGradeIdByGradeNumber, setRepresentativeGradeIdByGradeNumber] = useState<Record<number, string>>(
    {}
  );
  const [programColorSettingsFull, setProgramColorSettingsFull] = useState<ProgramColorSettings | null>(null);
  /** 연간 툴바 색상 등록/수정/삭제 — DB는 「저장하기」 시 반영 */
  const [programColorsDirty, setProgramColorsDirty] = useState(false);
  const programColorSettingsBaselineRef = useRef<ProgramColorSettings | null>(null);
  const programColorSettingsFullRef = useRef<ProgramColorSettings | null>(null);
  const programColorsDirtyRef = useRef(false);
  programColorSettingsFullRef.current = programColorSettingsFull;
  programColorsDirtyRef.current = programColorsDirty;
  const [programColorPickModal, setProgramColorPickModal] = useState<{
    gradeId: string;
    type: ProgramColorPickModalType;
    mode: 'create' | 'edit';
    initialHex?: string;
    initialLabel?: string;
  } | null>(null);
  const [regularColors, setRegularColors] = useState<string[]>([]);
  /** 고정 프로그램 관리(일괄 관리 표) 모달 — 명시 저장 */
  const [bulkFixedModalOpen, setBulkFixedModalOpen] = useState(false);
  const [bulkFixedDirty, setBulkFixedDirty] = useState(false);
  const [bulkFixedCloseAsk, setBulkFixedCloseAsk] = useState(false);
  const bulkFixedRef = useRef<FixedProgramBulkHandle | null>(null);
  const [dedicatedColors, setDedicatedColors] = useState<string[]>([]);
  const [baseExcludeColors, setBaseExcludeColors] = useState<string[]>([]);
  const [baseTimetableByGrade, setBaseTimetableByGrade] = useState<Record<string, BaseTimetableByTerm>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** 토요일 열 표시 (기본 off) — 워크스페이스별 localStorage 유지 */
  const [showSaturday, setShowSaturday] = useState(() => loadAnnualShowSaturday(workspaceId));
  /** 네비 선택과 무관하게 등록된 모든 클래스 섹션 표시 (기본 off) */
  const [showAllClasses, setShowAllClasses] = useState(() => loadAnnualShowAllClasses(workspaceId));
  /** 1학기·2학기 테이블·사이드 메뉴 순서 바꾸기 (기본 off) */
  const [swapTermOrder, setSwapTermOrder] = useState(() => loadAnnualSwapTermOrder(workspaceId));
  useEffect(() => {
    if (!workspaceId) return;
    setShowSaturday(loadAnnualShowSaturday(workspaceId));
    setShowAllClasses(loadAnnualShowAllClasses(workspaceId));
    setSwapTermOrder(loadAnnualSwapTermOrder(workspaceId));
  }, [workspaceId]);
  /** 셀 텍스트/색상/종류/저장 상태 */
  const [cellData, setCellData] = useState<Record<string, string>>({});
  const [cellMemos, setCellMemos] = useState<Record<string, string>>({});
  const [cellColors, setCellColors] = useState<Record<string, string>>({});
  const [cellSlotKinds, setCellSlotKinds] = useState<Record<string, AnnualTimetableSlotKind>>({});
  const [cellRecordIds, setCellRecordIds] = useState<Record<string, string>>({});
  /** 제외 색(단색) vs 동일 hex 프로젝트(무늈) 구분 — DB `is_base_excluded`와 동기 */
  const [cellBaseExcluded, setCellBaseExcluded] = useState<Record<string, boolean>>({});
  const [loadedCellScopeKey, setLoadedCellScopeKey] = useState('');
  const [dirtyCellKeys, setDirtyCellKeys] = useState<string[]>([]);
  const [annualUndoRedoAvail, setAnnualUndoRedoAvail] = useState({ canUndo: false, canRedo: false });
  const [savingAll, setSavingAll] = useState(false);
  const savingAllRef = useRef(false);
  savingAllRef.current = savingAll;
  const { toastState: saveToastState, showSaving, showSuccess, showError, showInfo } = useAutoStatusToast(3000);
  const [operationPlanModalOpen, setOperationPlanModalOpen] = useState(false);
  /** 운영계획 모달: 열린 클래스 블럭(gradeId)만 표시 */
  const [operationPlanScopeGradeId, setOperationPlanScopeGradeId] = useState<string | null>(null);
  const [operationPlanTerm, setOperationPlanTerm] = useState<OperationPlanTermSelection>(1);
  /** 운영계획 모달: 연간 일반(비고정색) 셀 메모를 주요 교육 활동에 표시 */
  const [operationPlanShowGeneralMemos, setOperationPlanShowGeneralMemos] = useState(false);
  const operationPlanExportRootRef = useRef<HTMLDivElement | null>(null);
  const [operationPlanExportBusy, setOperationPlanExportBusy] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [manageShortDayModalOpen, setManageShortDayModalOpen] = useState(false);
  const [shortDayModalOpen, setShortDayModalOpen] = useState(false);
  const [shortDayDateIso, setShortDayDateIso] = useState('');
  const [shortDayEndSlotTarget, setShortDayEndSlotTarget] = useState<EventSlotTargetKey | null>(null);
  const [shortDaySelectedGradeIds, setShortDaySelectedGradeIds] = useState<string[]>([]);
  const [shortDayModalError, setShortDayModalError] = useState<string | null>(null);
  /** 클래스(gradeId)별 연간시간표 전용 프로젝트 프로그램 (로컬 저장) */
  const [annualProjectProgramsByGradeId, setAnnualProjectProgramsByGradeId] = useState<
    Record<string, AnnualProjectProgram[]>
  >({});
  const [projectCreateModalGradeId, setProjectCreateModalGradeId] = useState<string | null>(null);
  /** 수정 모드일 때 대상 프로젝트 id (없으면 신규 등록) */
  const [projectFormEditingId, setProjectFormEditingId] = useState<string | null>(null);
  const [projectManageModalGradeId, setProjectManageModalGradeId] = useState<string | null>(null);
  const [projectDeleteTargetId, setProjectDeleteTargetId] = useState<string | null>(null);
  /** 생성 시 1·2학기 동시 선택 가능. 수정 시에는 단일 학기만 유지 */
  const [projectFormTerms, setProjectFormTerms] = useState<(1 | 2)[]>([]);
  const [projectFormName, setProjectFormName] = useState('');
  const [projectFormColorHex, setProjectFormColorHex] = useState<string | null>(null);
  const [projectCustomColorModalOpen, setProjectCustomColorModalOpen] = useState(false);
  const [projectFormCustomColor, setProjectFormCustomColor] = useState('#2e7d32');
  const [projectFormError, setProjectFormError] = useState<string | null>(null);
  const [projectDuplicateColorWarnOpen, setProjectDuplicateColorWarnOpen] = useState(false);
  const [eventColorHex, setEventColorHex] = useState('');
  const [eventEditingSourceKeys, setEventEditingSourceKeys] = useState<string[]>([]);
  const [eventEditingProgramId, setEventEditingProgramId] = useState<string | null>(null);
  const [eventDateIso, setEventDateIso] = useState('');
  const [eventModalPeriodState, setEventModalPeriodState] = useState<EventModalPeriodState>({
    selected: [],
    namesByTarget: {},
  });
  const eventSelectedSlotTargets = eventModalPeriodState.selected;
  const eventNamesBySlotTarget = eventModalPeriodState.namesByTarget;
  /** 고정 프로그램 셀 memo·말풍선·목록에 쓰는 공통 행사명 (시간표명과 별도) */
  const [eventMemoText, setEventMemoText] = useState('');
  const [eventSelectedGradeIds, setEventSelectedGradeIds] = useState<string[]>([]);
  const [eventModalError, setEventModalError] = useState<string | null>(null);
  /** 편집 중인 셀 */
  const [editingCell, setEditingCell] = useState<{
    gradeId: string;
    mondayIso: string;
    dayKey: string;
    periodIdx: number;
    draft: string;
  } | null>(null);
  const editingInputRef = useRef<HTMLInputElement | null>(null);
  /** 고정 프로그램 모달: 교시·방과후 블록 라벨 버튼 */
  const eventPeriodLabelBtnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const eventModalApplyBtnRef = useRef<HTMLButtonElement | null>(null);
  /** 선택된 셀 키 목록 */
  const [selectedCells, setSelectedCells] = useState<string[]>([]);
  /** 일자 헤더(월·일 표시 칸)에서 월.일 입력 후 해당 날짜 열(교시 전체)로 점프 */
  const [annualDateHeadPicker, setAnnualDateHeadPicker] = useState<{
    gradeId: string;
    termKey: '1' | '2';
    draft: string;
  } | null>(null);
  const annualDateHeadInputRef = useRef<HTMLInputElement | null>(null);
  const annualDateHeadActiveRef = useRef(false);
  const annualDateHeadPickerRef = useRef<{
    gradeId: string;
    termKey: '1' | '2';
    draft: string;
  } | null>(null);
  annualDateHeadPickerRef.current = annualDateHeadPicker;
  /** 기초시간표 반영: 확인 후 적용 */
  const [baseTimetableApplyConfirm, setBaseTimetableApplyConfirm] = useState<{
    entry: AnnualClassEntry;
    termNum: 1 | 2;
  } | null>(null);
  const [timetableSettingsModal, setTimetableSettingsModal] = useState<{
    gradeId: string;
    classTitle: string;
  } | null>(null);
  const [hoursAdjustModal, setHoursAdjustModal] = useState<{
    gradeId: string;
    classTitle: string;
  } | null>(null);
  const [hoursAdjustChecklistFloat, setHoursAdjustChecklistFloat] = useState<{
    gradeId: string;
    classTitle: string;
    checklist: HoursAdjustChecklist;
    phase2: HoursAdjustPhase2Result;
    applySkipColorHexes: string[];
    checklistChecked: Record<string, boolean>;
    fillFromCurriculumEventCells?: boolean;
  } | null>(null);
  const [timetableSettingsCloseConfirm, setTimetableSettingsCloseConfirm] = useState(false);
  const [timetableSettingsClosing, setTimetableSettingsClosing] = useState(false);
  const [timetableSettingsToolbarEl, setTimetableSettingsToolbarEl] = useState<HTMLElement | null>(null);
  const teamIdForLeaderImport = useTeamIdForLeaderImport(workspaceId);
  const [teamLeaderImportModal, setTeamLeaderImportModal] = useState<TeamLeaderImportModalState | null>(null);
  const [teamLeaderImportBusy, setTeamLeaderImportBusy] = useState(false);
  const [teamLeaderImportError, setTeamLeaderImportError] = useState<string | null>(null);
  const [teamLeaderImportAllConfirmOpen, setTeamLeaderImportAllConfirmOpen] = useState(false);
  /** 드래그로 영역 선택 */
  const [dragState, setDragState] = useState<{
    active: boolean;
    anchorKey: string;
    currentKey: string;
  } | null>(null);
  /** 연간시간표 셀: 길게 눌러 메모 편집 모달 */
  const [annualCellMemoModal, setAnnualCellMemoModal] = useState<{
    compoundKey: string;
    cellDisplay: string;
    memoDraft: string;
  } | null>(null);
  const annualCellMemoModalRef = useRef<{
    compoundKey: string;
    cellDisplay: string;
    memoDraft: string;
  } | null>(null);
  annualCellMemoModalRef.current = annualCellMemoModal;
  const [annualCellMemoAnchorStyle, setAnnualCellMemoAnchorStyle] = useState<React.CSSProperties | null>(null);
  const [annualWeekMemosByKey, setAnnualWeekMemosByKey] = useState<
    Record<string, { id?: string; activityLines: string[]; generalMemo: string }>
  >({});
  const annualWeekMemosByKeyRef = useRef(annualWeekMemosByKey);
  annualWeekMemosByKeyRef.current = annualWeekMemosByKey;
  const [weekActivityMemoModal, setWeekActivityMemoModal] = useState<WeekActivityMemoModalState | null>(null);
  const weekActivityMemoModalRef = useRef<WeekActivityMemoModalState | null>(null);
  weekActivityMemoModalRef.current = weekActivityMemoModal;
  const [weekActivityMemoAnchorStyle, setWeekActivityMemoAnchorStyle] = useState<React.CSSProperties | null>(null);
  const [weekMemoHoverPreview, setWeekMemoHoverPreview] = useState<{
    anchorId: string;
    style: React.CSSProperties;
    activityLines: string[];
    generalMemo: string;
  } | null>(null);
  const [weekHoursHoverPreview, setWeekHoursHoverPreview] = useState<{
    anchorId: string;
    style: React.CSSProperties;
    tracks: { trackName: string; hours: number }[];
  } | null>(null);
  const [termHoursHoverPreview, setTermHoursHoverPreview] = useState<{
    anchorId: string;
    style: React.CSSProperties;
    tracks: { trackName: string; hours: number }[];
  } | null>(null);
  const [cellColorHoverPreview, setCellColorHoverPreview] = useState<{
    anchorId: string;
    style: React.CSSProperties;
    label: string;
    kind: 'project' | 'plain';
  } | null>(null);
  const annualCellMemoLongPressTimerRef = useRef<number | null>(null);
  const annualCellMemoLongPressAnchorRef = useRef<string | null>(null);
  /** 복사/잘라내기된 셀 (상대 인덱스 + 값) */
  const copiedCellsRef = useRef<{
    cells: WeeklyClipboardCell[];
    isCut?: boolean;
    sourceKeys?: string[];
    sourceGradeId?: string;
  } | null>(null);
  const [hasCopiedData, setHasCopiedData] = useState(false);
  /** 복사/잘라낸 영역 표시 */
  const [copiedSourceRegion, setCopiedSourceRegion] = useState<string[] | null>(null);
  /** 맞교환 영역 A, B */
  const [swapRegionA, setSwapRegionA] = useState<string[] | null>(null);
  const [swapRegionB, setSwapRegionB] = useState<string[] | null>(null);
  const cellDataRef = useRef<Record<string, string>>({});
  cellDataRef.current = cellData;
  const cellMemosRef = useRef<Record<string, string>>({});
  cellMemosRef.current = cellMemos;
  const cellColorsRef = useRef<Record<string, string>>({});
  cellColorsRef.current = cellColors;
  const cellSlotKindsRef = useRef<Record<string, AnnualTimetableSlotKind>>({});
  cellSlotKindsRef.current = cellSlotKinds;
  const cellRecordIdsRef = useRef<Record<string, string>>({});
  cellRecordIdsRef.current = cellRecordIds;
  const cellBaseExcludedRef = useRef<Record<string, boolean>>({});
  cellBaseExcludedRef.current = cellBaseExcluded;
  const dirtyCellKeysRef = useRef<string[]>([]);
  dirtyCellKeysRef.current = dirtyCellKeys;
  const annualApplyingLocalHistoryRef = useRef(false);
  const annualLocalUndoStackRef = useRef<AnnualLocalHistoryFrame[]>([]);
  const annualLocalRedoStackRef = useRef<AnnualLocalHistoryFrame[]>([]);
  const cloneAnnualLocalHistoryFrameRef = useRef<() => AnnualLocalHistoryFrame>(() => ({
    cellData: {},
    cellMemos: {},
    cellColors: {},
    cellSlotKinds: {},
    cellRecordIds: {},
    cellBaseExcluded: {},
    dirtyCellKeys: [],
  }));
  const notifyAnnualUndoRedoStacksRef = useRef<() => void>(() => {});
  const pushAnnualLocalUndoSnapshotFromCurrentRef = useRef<() => void>(() => {});
  cloneAnnualLocalHistoryFrameRef.current = () =>
    JSON.parse(
      JSON.stringify({
        cellData: cellDataRef.current,
        cellMemos: cellMemosRef.current,
        cellColors: cellColorsRef.current,
        cellSlotKinds: cellSlotKindsRef.current,
        cellRecordIds: cellRecordIdsRef.current,
        cellBaseExcluded: cellBaseExcludedRef.current,
        dirtyCellKeys: dirtyCellKeysRef.current,
      })
    ) as AnnualLocalHistoryFrame;
  notifyAnnualUndoRedoStacksRef.current = () => {
    const next = {
      canUndo: annualLocalUndoStackRef.current.length > 0,
      canRedo: annualLocalRedoStackRef.current.length > 0,
    };
    setAnnualUndoRedoAvail((prev) =>
      prev.canUndo === next.canUndo && prev.canRedo === next.canRedo ? prev : next
    );
  };
  pushAnnualLocalUndoSnapshotFromCurrentRef.current = () => {
    if (annualApplyingLocalHistoryRef.current) return;
    const undo = annualLocalUndoStackRef.current;
    undo.push(cloneAnnualLocalHistoryFrameRef.current());
    if (undo.length > ANNUAL_LOCAL_EDIT_HISTORY_MAX) undo.shift();
    annualLocalRedoStackRef.current = [];
    notifyAnnualUndoRedoStacksRef.current();
  };
  /** 마지막 로드/저장 시점 셀 값 — 동일하면 dirty 아님 */
  const annualCellSavedBaselineRef = useRef<Record<string, AnnualCellPersistSnapshot>>({});
  /** 연간시간표만 가져오기: 저장하기 전까지 프로그램 색·프로젝트 보류 */
  const pendingLeaderAnnualOnlyPersistRef = useRef<{
    gradeId: string;
    programSettings: ProgramColorSettings;
    projects: AnnualProjectProgram[];
  } | null>(null);
  const editingCellRef = useRef(editingCell);
  editingCellRef.current = editingCell;
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  /** Shift+클릭 범위 선택 앵커 (기초시간표와 동일) */
  const selectionRangeAnchorRef = useRef<string | null>(null);
  const selectedCellsRef = useRef<string[]>([]);
  selectedCellsRef.current = selectedCells;
  /** Enter/Tab 확정 직후 단일 선택 자동 재편집을 1회 막는다. */
  const skipAutoEditOnceRef = useRef(false);
  /** 입력창 Tab 처리 직후 같은 키 이벤트의 전역 Tab 이동 중복 실행 방지 */
  const suppressGlobalTabOnceRef = useRef(false);
  /** 키보드 이동으로 이미 커밋한 직후 발생하는 input blur 커밋 1회 무시 */
  const suppressInputBlurCommitOnceRef = useRef(false);
  /** 한글 IME 조합 중 Enter/Tab 이동 방지 (기초시간표와 동일) */
  const isComposingRef = useRef(false);
  /** 학년도·워크스페이스가 바뀌며 겹쳐 도는 `loadTermsAndSchedule` 중 오래된 응답이 상태를 덮지 않게 함 */
  const termsScheduleLoadGenRef = useRef(0);
  /** `calendarStartsOn`/`EndsOn`이 바뀔 때 늦게 도착한 `getCalendarDays` 결과 무시 */
  const calendarDaysLoadGenRef = useRef(0);
  const openEventModalFromSelectedCellsRef = useRef<(colorHex: string) => boolean>(() => false);
  const term1Def = terms.find((t) => t.term_number === 1);
  const term2Def = terms.find((t) => t.term_number === 2);
  const weeksTerm1 = React.useMemo(
    () => buildWeeksFromRange(term1Def?.starts_on ?? '', term1Def?.ends_on ?? ''),
    [term1Def?.starts_on, term1Def?.ends_on]
  );
  const weeksTerm2 = React.useMemo(
    () => buildWeeksFromRange(term2Def?.starts_on ?? '', term2Def?.ends_on ?? ''),
    [term2Def?.starts_on, term2Def?.ends_on]
  );
  const mergedWeeks = React.useMemo(() => [...weeksTerm1, ...weeksTerm2], [weeksTerm1, weeksTerm2]);
  const calendarStartsOn = React.useMemo(() => {
    const s1 = term1Def?.starts_on;
    const s2 = term2Def?.starts_on;
    if (s1 && s2) return s1 < s2 ? s1 : s2;
    return s1 || s2 || '';
  }, [term1Def?.starts_on, term2Def?.starts_on]);
  const calendarEndsOn = React.useMemo(() => {
    const e1 = term1Def?.ends_on;
    const e2 = term2Def?.ends_on;
    if (e1 && e2) return e1 > e2 ? e1 : e2;
    return e1 || e2 || '';
  }, [term1Def?.ends_on, term2Def?.ends_on]);
  const hasAnyTermWeeks = weeksTerm1.length > 0 || weeksTerm2.length > 0;

  const adoptProgramColorSettingsFromServer = useCallback((settings: ProgramColorSettings) => {
    const cloned = structuredClone(settings);
    setProgramColorSettingsFull(cloned);
    programColorSettingsBaselineRef.current = structuredClone(settings);
    setProgramColorsDirty(false);
  }, []);

  /** 연간 프로젝트 → 기초시간표 프로젝트 팔레트 동기화 후 저장 */
  const syncProgramColorsFromAnnualProjects = useCallback(
    async (gradeId: string, programs: AnnualProjectProgram[], options?: { persist?: boolean }) => {
      const settings = programColorSettingsFullRef.current;
      if (!settings || !workspaceId) return;
      const current = resolveGradeProgramColors(settings, gradeId);
      if (!gradeProgramColorsNeedAnnualProjectSync(current, programs)) {
        return;
      }
      const nextColors = applyAnnualProgramsToGradeProgramColors(current, programs);
      const updated: ProgramColorSettings = {
        ...settings,
        colorsByGrade: {
          ...settings.colorsByGrade,
          [gradeId]: nextColors,
        },
      };
      setProgramColorSettingsFull(updated);
      if (options?.persist === false) {
        setProgramColorsDirty(true);
        return;
      }
      try {
        await saveProgramColorSettings(workspaceId, updated);
        programColorSettingsBaselineRef.current = structuredClone(updated);
        setProgramColorsDirty(false);
        emitAnnualPrerequisiteRefresh(workspaceId, ['timetable_settings']);
      } catch {
        setProgramColorsDirty(true);
      }
    },
    [workspaceId]
  );

  const loadTermsAndSchedule = useCallback(async (options?: { silent?: boolean }) => {
    if (!workspaceId) return;
    const runId = (termsScheduleLoadGenRef.current += 1);
    if (!options?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [scheduleData, colorSettings, trackRows, gradeTrackSectionRows] = await Promise.all([
        getClassScheduleSettings(workspaceId),
        getProgramColorSettings(workspaceId),
        getTracks(workspaceId),
        getGradeTrackSections(workspaceId),
      ]);
      if (runId !== termsScheduleLoadGenRef.current) return;
      setClassSchedule(scheduleData);
      adoptProgramColorSettingsFromServer(colorSettings);
      setBaseTimetableByGrade(colorSettings.baseTimetableByGrade);
      setTracks(trackRows);
      setGradeTrackSections(gradeTrackSectionRows);
    } catch (e) {
      if (runId !== termsScheduleLoadGenRef.current) return;
      if (!options?.silent) {
        setError(e instanceof Error ? e.message : String(e));
      }
      setClassSchedule(null);
      setTracks([]);
      setGradeTrackSections([]);
      setProgramColorSettingsFull(null);
      programColorSettingsBaselineRef.current = null;
      setProgramColorsDirty(false);
      setRegularColors([]);
      setDedicatedColors([]);
      setBaseExcludeColors([]);
      setBaseTimetableByGrade({});
    } finally {
      if (!options?.silent && runId === termsScheduleLoadGenRef.current) {
        setLoading(false);
      }
    }
  }, [workspaceId, adoptProgramColorSettingsFromServer]);

  const applyPrerequisiteRefresh = useCallback(
    async (scopes: AnnualPrerequisiteScope[]) => {
      if (!workspaceId || scopes.length === 0) return;
      const runId = (termsScheduleLoadGenRef.current += 1);
      /* 학사일정: `useCalendarLinkedContext`·`ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT`가 주차·공휴일 처리 */
      const needsSchedule = scopes.includes('class_schedule');
      const needsColors = scopes.includes('timetable_settings');
      const needsTracks = scopes.includes('grade_track');
      const needsTrackSections = scopes.includes('grade_track');
      try {
        const [scheduleResult, colorResult, trackResult, trackSectionResult] = await Promise.all([
          needsSchedule ? getClassScheduleSettings(workspaceId) : Promise.resolve(null),
          needsColors ? getProgramColorSettings(workspaceId) : Promise.resolve(null),
          needsTracks ? getTracks(workspaceId) : Promise.resolve(null),
          needsTrackSections ? getGradeTrackSections(workspaceId) : Promise.resolve(null),
        ]);
        if (runId !== termsScheduleLoadGenRef.current) return;
        if (needsSchedule && scheduleResult) {
          setClassSchedule(scheduleResult);
        }
        if (needsColors && colorResult) {
          adoptProgramColorSettingsFromServer(colorResult);
          setBaseTimetableByGrade(colorResult.baseTimetableByGrade);
        }
        if (needsTracks && trackResult) {
          setTracks(trackResult);
          setTrackProgramPrefsRev((r) => r + 1);
        }
        if (needsTrackSections && trackSectionResult) {
          setGradeTrackSections(trackSectionResult);
        }
      } catch {
        /* 사전 설정 동기화 실패는 조용히 무시(연간 편집 데이터는 유지) */
      }
    },
    [workspaceId, adoptProgramColorSettingsFromServer]
  );

  useEffect(() => {
    void loadTermsAndSchedule();
    return () => {
      termsScheduleLoadGenRef.current += 1;
    };
  }, [loadTermsAndSchedule]);

  const ttPageData = useAnnualTimetablePageQueries({
    workspaceId: schoolScopedCacheSyncReady ? workspaceId : '',
    academicYear,
    gradeIds: timetableGradeIds,
    isActive,
    schoolScopedCacheSyncReady,
  });

  /** 셀이 캐시·조회에서 오는 즉시 고정 프로그램 목록에 반영(모달을 열기 전에 채워 둠) */
  useEffect(() => {
    if (!workspaceId) return;
    const cells = Object.values(ttPageData.cellsByKey);
    if (cells.length === 0) return;
    seedFixedDraftsFromAnnualCells(workspaceId, cells, { insertOnly: true });
  }, [workspaceId, ttPageData.cellsByKey, programColorSettingsFull]);

  /** 사전 설정 저장 시에만 LS(또는 캐시 미스 시 1회 네트워크)로 연간 화면 동기화 */
  useEffect(() => {
    const onPrerequisiteRefresh = (e: Event) => {
      const detail = (e as CustomEvent<{ workspaceId: string; scopes: AnnualPrerequisiteScope[] }>).detail;
      if (!detail || detail.workspaceId !== workspaceId) return;
      void applyPrerequisiteRefresh(detail.scopes);
    };
    window.addEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onPrerequisiteRefresh);
    return () => window.removeEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onPrerequisiteRefresh);
  }, [workspaceId, applyPrerequisiteRefresh]);

  useEffect(() => {
    const onPrefs = () => setTrackProgramPrefsRev((r) => r + 1);
    window.addEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onPrefs);
    return () => window.removeEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onPrefs);
  }, []);

  useEffect(() => {
    if (!workspaceId || tracks.length === 0) return;
    let cancelled = false;
    void getGradeTrackProgramPreferences(workspaceId)
      .then((prefs) => {
        if (cancelled) return;
        const gradeIds = new Set<string>();
        tracks.forEach((t) => {
          if (t.grade_id) gradeIds.add(t.grade_id);
        });
        /** 엄격 모드: DB prefs 거울로 교체(병합 시 편제 및 기준 시수에서 지운 설정이 되살아남) */
        let touched = false;
        gradeIds.forEach((gid) => {
          if (applyGradeTrackProgramPrefsToLocalStores(workspaceId, gid, prefs)) touched = true;
        });
        if (touched) setTrackProgramPrefsRev((r) => r + 1);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [workspaceId, tracks]);

  useEffect(() => {
    if (!workspaceId) {
      setRepresentativeGradeIdByGradeNumber({});
      return;
    }
    getSchoolGradeRepresentatives(workspaceId)
      .then((rows) => {
        const next: Record<number, string> = {};
        rows.forEach((row) => {
          const n = Number(row.grade_number);
          if (n < 1 || n > 6) return;
          next[n] = row.grade_id;
        });
        setRepresentativeGradeIdByGradeNumber(next);
      })
      .catch(() => {
        setRepresentativeGradeIdByGradeNumber({});
      });
  }, [workspaceId, grades]);

  const applyCalendarDaysToState = useCallback((days: CalendarDay[]) => {
    const dayKinds: Record<string, DayKind> = {};
    const dayNotes: Record<string, string> = {};
    days.forEach((d) => {
      dayKinds[d.day] = d.day_kind;
      const note = (d.note ?? '').trim();
      if (note) {
        dayNotes[d.day] = d.day_kind === 'class' ? note : clampCalendarHolidayNote(note);
      }
    });
    const holidays = new Set(
      days.filter((d) => d.day_kind === 'holiday' || d.day_kind === 'closure').map((d) => d.day)
    );
    setHolidaySet(holidays);
    setCalendarDayKindByDate(dayKinds);
    setCalendarDayNoteByDate(dayNotes);
  }, []);

  useEffect(() => {
    const from = calendarLinkedContext.calendarStartsOn;
    const to = calendarLinkedContext.calendarEndsOn;
    if (!workspaceId || !from || !to) {
      setHolidaySet(new Set());
      setCalendarDayKindByDate({});
      setCalendarDayNoteByDate({});
      return;
    }
    const runId = (calendarDaysLoadGenRef.current += 1);
    const normalizeDays = (days: CalendarDay[]) => stripAutoBreakVacationCalendarDays(days);
    if (!calendarLinkedContext.useDbCalendarDays) {
      applyCalendarDaysToState(normalizeDays(calendarLinkedContext.localCalendarDays));
      return () => {
        calendarDaysLoadGenRef.current += 1;
      };
    }
    getCalendarDays(workspaceId, from, to)
      .then((days) => {
        if (runId !== calendarDaysLoadGenRef.current) return;
        applyCalendarDaysToState(normalizeDays(days));
      })
      .catch(() => {
        if (runId !== calendarDaysLoadGenRef.current) return;
        setHolidaySet(new Set());
        setCalendarDayKindByDate({});
        setCalendarDayNoteByDate({});
      });
    return () => {
      calendarDaysLoadGenRef.current += 1;
    };
  }, [
    workspaceId,
    calendarLinkedContext.calendarStartsOn,
    calendarLinkedContext.calendarEndsOn,
    calendarLinkedContext.useDbCalendarDays,
    calendarLinkedContext.localCalendarDays,
    calendarLinkedContext.sectionId,
    calendarLinkedContext.sectionSettings,
    applyCalendarDaysToState,
  ]);

  const expectedCellScopeKey = React.useMemo(() => ttPageData.scopeKey, [ttPageData.scopeKey]);
  const waitingForCellScopeHydration =
    expectedCellScopeKey !== '' && expectedCellScopeKey !== loadedCellScopeKey;
  const loadingCells =
    classEntries.length > 0 && (ttPageData.loading || waitingForCellScopeHydration);
  const visibleClassEntries = React.useMemo(() => {
    if (!currentGradeId) return classEntries;
    return classEntries.filter((e) => e.gradeId === currentGradeId);
  }, [classEntries, currentGradeId]);

  React.useEffect(() => {
    if (!programColorSettingsFull) return;
    const gid = (currentGradeId || classEntries[0]?.gradeId || '').trim();
    if (!gid) return;
    const c = resolveGradeProgramColors(programColorSettingsFull, gid);
    setRegularColors(c.regular);
    setDedicatedColors(c.dedicated);
    setBaseExcludeColors(c.baseExclude);
  }, [programColorSettingsFull, currentGradeId, classEntries]);

  const buildColorPaletteGroups = useCallback(
    (gradeId: string) => {
      const colors = programColorSettingsFull
        ? resolveGradeProgramColors(programColorSettingsFull, gradeId)
        : {
            ...createDefaultGradeProgramColors(),
            regular: regularColors,
            dedicated: dedicatedColors,
            baseExclude: baseExcludeColors,
          };
      return [
        { label: '고정(행사)', colors: colors.regular, modalType: 'regular' as const },
        { label: '전담', colors: colors.dedicated, modalType: 'dedicated' as const },
        { label: '라벨링 색상', colors: colors.baseExclude, modalType: 'baseExclude' as const },
      ];
    },
    [programColorSettingsFull, regularColors, dedicatedColors, baseExcludeColors]
  );

  const getProgramColorLabel = useCallback(
    (gradeId: string, type: ProgramColorPickModalType, hex: string): string => {
      if (!programColorSettingsFull) return '';
      const current = resolveGradeProgramColors(programColorSettingsFull, gradeId);
      const key = normalizeProgramColorHex(hex);
      if (!key) return '';
      return current.labels?.[type]?.[key] ?? '';
    },
    [programColorSettingsFull]
  );

  const handleSubmitProgramColorFromToolbar = useCallback(
    (
      gradeId: string,
      type: ProgramColorPickModalType,
      payload: { hex: string; label: string; mode: 'create' | 'edit'; originalHex?: string }
    ) => {
      if (!programColorSettingsFull) return;
      const current = resolveGradeProgramColors(programColorSettingsFull, gradeId);
      const nextHex = normalizeProgramColorHex(payload.hex);
      if (!nextHex) return;
      const currentLabels = {
        regular: { ...(current.labels?.regular ?? {}) },
        dedicated: { ...(current.labels?.dedicated ?? {}) },
        baseExclude: { ...(current.labels?.baseExclude ?? {}) },
        project: { ...(current.labels?.project ?? {}) },
        projectTerm1: { ...(current.labels?.projectTerm1 ?? {}) },
        projectTerm2: { ...(current.labels?.projectTerm2 ?? {}) },
      };

      let nextColors: GradeProgramColors = { ...current, labels: currentLabels };
      if (type === 'regular') {
        if (payload.mode === 'edit') {
          const original = normalizeProgramColorHex(payload.originalHex ?? '');
          if (!original) return;
          if (!current.regular.some((hex) => normalizeProgramColorHex(hex) === original)) return;
          if (nextHex !== original && current.regular.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return;
          nextColors = {
            ...nextColors,
            regular: current.regular.map((hex) => (normalizeProgramColorHex(hex) === original ? nextHex : hex)),
          };
          delete currentLabels.regular[original];
        } else {
          if (current.regular.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return;
          nextColors = { ...nextColors, regular: [...current.regular, nextHex] };
        }
        if (payload.label.trim()) currentLabels.regular[nextHex] = payload.label.trim();
        else delete currentLabels.regular[nextHex];
      } else if (type === 'dedicated') {
        if (payload.mode === 'edit') {
          const original = normalizeProgramColorHex(payload.originalHex ?? '');
          if (!original) return;
          if (!current.dedicated.some((hex) => normalizeProgramColorHex(hex) === original)) return;
          if (nextHex !== original && current.dedicated.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return;
          nextColors = {
            ...nextColors,
            dedicated: current.dedicated.map((hex) => (normalizeProgramColorHex(hex) === original ? nextHex : hex)),
          };
          delete currentLabels.dedicated[original];
        } else {
          if (current.dedicated.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return;
          nextColors = { ...nextColors, dedicated: [...current.dedicated, nextHex] };
        }
        if (payload.label.trim()) currentLabels.dedicated[nextHex] = payload.label.trim();
        else delete currentLabels.dedicated[nextHex];
      } else if (type === 'project') {
        const projectByTerm = {
          term1: [...(current.projectByTerm?.term1 ?? [])],
          term2: [...(current.projectByTerm?.term2 ?? [])],
        };
        const mapTermList = (list: string[]) => {
          if (payload.mode === 'edit') {
            const original = normalizeProgramColorHex(payload.originalHex ?? '');
            if (!original) return list;
            return list.map((hex) => (normalizeProgramColorHex(hex) === original ? nextHex : hex));
          }
          if (list.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return list;
          return [...list, nextHex];
        };
        if (payload.mode === 'edit') {
          const original = normalizeProgramColorHex(payload.originalHex ?? '');
          if (!original) return;
          const projectList = current.project ?? [];
          if (!projectList.some((hex) => normalizeProgramColorHex(hex) === original)) return;
          if (nextHex !== original && projectList.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return;
          projectByTerm.term1 = mapTermList(projectByTerm.term1);
          projectByTerm.term2 = mapTermList(projectByTerm.term2);
          delete currentLabels.project[original];
          delete currentLabels.projectTerm1[original];
          delete currentLabels.projectTerm2[original];
        } else {
          const projectList = current.project ?? [];
          if (projectList.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return;
          projectByTerm.term1 = mapTermList(projectByTerm.term1);
          projectByTerm.term2 = mapTermList(projectByTerm.term2);
        }
        const seen = new Set<string>();
        const project: string[] = [];
        [...projectByTerm.term1, ...projectByTerm.term2].forEach((hex) => {
          const n = normalizeProgramColorHex(hex);
          if (!n || seen.has(n)) return;
          seen.add(n);
          project.push(n);
        });
        if (payload.label.trim()) {
          currentLabels.project[nextHex] = payload.label.trim();
          currentLabels.projectTerm1[nextHex] = payload.label.trim();
          currentLabels.projectTerm2[nextHex] = payload.label.trim();
        } else {
          delete currentLabels.project[nextHex];
          delete currentLabels.projectTerm1[nextHex];
          delete currentLabels.projectTerm2[nextHex];
        }
        nextColors = { ...nextColors, project, projectByTerm };
      } else {
        if (payload.mode === 'edit') {
          const original = normalizeProgramColorHex(payload.originalHex ?? '');
          if (!original) return;
          if (!current.baseExclude.some((hex) => normalizeProgramColorHex(hex) === original)) return;
          if (
            nextHex !== original &&
            current.baseExclude.some((hex) => normalizeProgramColorHex(hex) === nextHex)
          ) {
            return;
          }
          nextColors = {
            ...nextColors,
            baseExclude: current.baseExclude.map((hex) => (normalizeProgramColorHex(hex) === original ? nextHex : hex)),
          };
          delete currentLabels.baseExclude[original];
        } else {
          if (current.baseExclude.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return;
          nextColors = { ...nextColors, baseExclude: [...current.baseExclude, nextHex] };
        }
        if (payload.label.trim()) currentLabels.baseExclude[nextHex] = payload.label.trim();
        else delete currentLabels.baseExclude[nextHex];
      }

      const updatedSettings: ProgramColorSettings = {
        ...programColorSettingsFull,
        colorsByGrade: {
          ...programColorSettingsFull.colorsByGrade,
          [gradeId]: nextColors,
        },
      };
      setProgramColorSettingsFull(updatedSettings);
      setProgramColorsDirty(true);
      setProgramColorPickModal(null);
    },
    [programColorSettingsFull]
  );

  const handleDeleteProgramColorFromToolbar = useCallback(
    (gradeId: string, type: ProgramColorPickModalType, hex: string) => {
      if (!programColorSettingsFull) return;
      const target = normalizeProgramColorHex(hex);
      if (!target) return;
      const current = resolveGradeProgramColors(programColorSettingsFull, gradeId);
      const currentLabels = {
        regular: { ...(current.labels?.regular ?? {}) },
        dedicated: { ...(current.labels?.dedicated ?? {}) },
        baseExclude: { ...(current.labels?.baseExclude ?? {}) },
        project: { ...(current.labels?.project ?? {}) },
        projectTerm1: { ...(current.labels?.projectTerm1 ?? {}) },
        projectTerm2: { ...(current.labels?.projectTerm2 ?? {}) },
      };
      if (type === 'project') {
        const projectByTerm = {
          term1: (current.projectByTerm?.term1 ?? []).filter(
            (c) => normalizeProgramColorHex(c) !== target
          ),
          term2: (current.projectByTerm?.term2 ?? []).filter(
            (c) => normalizeProgramColorHex(c) !== target
          ),
        };
        const seen = new Set<string>();
        const project: string[] = [];
        [...projectByTerm.term1, ...projectByTerm.term2].forEach((hex) => {
          const n = normalizeProgramColorHex(hex);
          if (!n || seen.has(n)) return;
          seen.add(n);
          project.push(n);
        });
        delete currentLabels.project[target];
        delete currentLabels.projectTerm1[target];
        delete currentLabels.projectTerm2[target];
        const nextColors: GradeProgramColors = {
          ...current,
          project,
          projectByTerm,
          labels: currentLabels,
        };
        const updatedSettings: ProgramColorSettings = {
          ...programColorSettingsFull,
          colorsByGrade: {
            ...programColorSettingsFull.colorsByGrade,
            [gradeId]: nextColors,
          },
        };
        setProgramColorSettingsFull(updatedSettings);
        setProgramColorsDirty(true);
        setProgramColorPickModal(null);
        return;
      }
      const list = current[type] ?? [];
      if (!list.some((c) => normalizeProgramColorHex(c) === target)) return;
      delete currentLabels[type][target];
      const nextColors: GradeProgramColors = {
        ...current,
        [type]: list.filter((c) => normalizeProgramColorHex(c) !== target),
        labels: currentLabels,
      };
      const updatedSettings: ProgramColorSettings = {
        ...programColorSettingsFull,
        colorsByGrade: {
          ...programColorSettingsFull.colorsByGrade,
          [gradeId]: nextColors,
        },
      };
      setProgramColorSettingsFull(updatedSettings);
      setProgramColorsDirty(true);
      setProgramColorPickModal(null);
    },
    [programColorSettingsFull]
  );

  const reloadAnnualProjectPrograms = useCallback(async () => {
    if (!workspaceId) {
      setAnnualProjectProgramsByGradeId({});
      return;
    }
    const next: Record<string, AnnualProjectProgram[]> = {};
    classEntries.forEach((e) => {
      next[e.gradeId] = loadAnnualProjectPrograms(workspaceId, e.gradeId, academicYear);
    });
    setAnnualProjectProgramsByGradeId(next);
    const fetched = await Promise.all(
      classEntries.map(async (entry) => ({
        gradeId: entry.gradeId,
        rows: await getAnnualProjectPrograms(workspaceId, entry.gradeId, academicYear),
      }))
    );
    const merged: Record<string, AnnualProjectProgram[]> = {};
    fetched.forEach(({ gradeId, rows }) => {
      merged[gradeId] = rows;
    });
    setAnnualProjectProgramsByGradeId((prev) => ({ ...prev, ...merged }));
    // 기존 연간 프로젝트 → 기초시간표 팔레트 보정(미연동 데이터)
    await Promise.all(
      Object.entries(merged).map(([gradeId, programs]) =>
        syncProgramColorsFromAnnualProjects(gradeId, programs)
      )
    );
  }, [workspaceId, classEntries, academicYear, syncProgramColorsFromAnnualProjects]);

  useEffect(() => {
    void reloadAnnualProjectPrograms();
  }, [reloadAnnualProjectPrograms]);

  const replaceImportedGradeCellsInState = useCallback(
    (gradeId: string, rows: AnnualTimetableCell[]) => {
      const byKey = buildAnnualCellMap(rows);
      setCellData((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = row.text_value ?? '';
        });
        return next;
      });
      setCellMemos((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = (row.memo ?? '').trim();
        });
        return next;
      });
      setCellColors((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = row.color_hex ?? '';
        });
        return next;
      });
      setCellSlotKinds((prev) => {
        const next: Record<string, AnnualTimetableSlotKind> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = row.slot_kind;
        });
        return next;
      });
      setCellRecordIds((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = row.id;
        });
        return next;
      });
      setCellBaseExcluded((prev) => {
        const next: Record<string, boolean> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          const ex =
            typeof row.is_base_excluded === 'boolean'
              ? row.is_base_excluded
              : isBaseExcludeColor((row.color_hex ?? '').trim(), baseExcludeColors);
          next[key] = ex;
        });
        return next;
      });
      setDirtyCellKeys((prev) => prev.filter((key) => gradeIdFromAnnualCellKey(key) !== gradeId));
      const baseline = { ...annualCellSavedBaselineRef.current };
      Object.keys(baseline).forEach((key) => {
        if (gradeIdFromAnnualCellKey(key) === gradeId) delete baseline[key];
      });
      Object.entries(byKey).forEach(([key, row]) => {
        baseline[key] = {
          textValue: row.text_value ?? '',
          memo: (row.memo ?? '').trim(),
          colorHex: (row.color_hex ?? '').trim(),
          slotKind: row.slot_kind,
          isBaseExcluded:
            typeof row.is_base_excluded === 'boolean'
              ? row.is_base_excluded
              : isBaseExcludeColor((row.color_hex ?? '').trim(), baseExcludeColors),
        };
      });
      annualCellSavedBaselineRef.current = baseline;
    },
    [baseExcludeColors]
  );

  /** 팀장 연간만 가져오기: UI 반영 + dirty (baseline 유지 → 저장하기 필요) */
  const applyImportedGradeCellsPendingSave = useCallback(
    (gradeId: string, rows: AnnualTimetableCell[]) => {
      const byKey = buildAnnualCellMap(rows);
      const importedKeys = Object.keys(byKey);
      setCellData((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = row.text_value ?? '';
        });
        return next;
      });
      setCellMemos((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = (row.memo ?? '').trim();
        });
        return next;
      });
      setCellColors((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = row.color_hex ?? '';
        });
        return next;
      });
      setCellSlotKinds((prev) => {
        const next: Record<string, AnnualTimetableSlotKind> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          next[key] = row.slot_kind;
        });
        return next;
      });
      setCellRecordIds((prev) => {
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
          else if (byKey[key]) next[key] = value;
        });
        return next;
      });
      setCellBaseExcluded((prev) => {
        const next: Record<string, boolean> = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (gradeIdFromAnnualCellKey(key) !== gradeId) next[key] = value;
        });
        Object.entries(byKey).forEach(([key, row]) => {
          const ex =
            typeof row.is_base_excluded === 'boolean'
              ? row.is_base_excluded
              : isBaseExcludeColor((row.color_hex ?? '').trim(), baseExcludeColors);
          next[key] = ex;
        });
        return next;
      });
      setDirtyCellKeys((prev) => {
        const next = new Set(prev.filter((key) => gradeIdFromAnnualCellKey(key) !== gradeId));
        importedKeys.forEach((key) => next.add(key));
        Object.keys(annualCellSavedBaselineRef.current).forEach((key) => {
          if (gradeIdFromAnnualCellKey(key) === gradeId && !byKey[key]) next.add(key);
        });
        return Array.from(next);
      });
    },
    [baseExcludeColors]
  );

  const handleOpenTeamLeaderImportModal = useCallback(
    async (targetGradeId: string, targetClassTitle: string, targetClassGrades: number[]) => {
      if (!teamIdForLeaderImport) return;
      setTeamLeaderImportError(null);
      setTeamLeaderImportModal({
        targetGradeId,
        targetClassTitle,
        targetClassGrades,
        selectedSourceGradeId: '',
        mode: 'annual_only',
        sourceCandidates: [],
      });
      setTeamLeaderImportBusy(true);
      try {
        const [trackPreview, timetablePreview] = await Promise.all([
          getTeamLeaderGradeTrackSectionsPreview(teamIdForLeaderImport).catch(() => null),
          getTeamLeaderTimetableSettingsPreview(teamIdForLeaderImport).catch(() => null),
        ]);
        const byGradeId = new Map<string, TeamLeaderGradeCandidate>();

        trackPreview?.sections.forEach((section) => {
          section.grade_slots.forEach((slot) => {
            const gid = (slot.grade_id ?? '').trim();
            const rawName = (slot.label ?? '').trim();
            if (!gid || !rawName || byGradeId.has(gid)) return;
            const parsed = parseGradeDisplayName(rawName);
            byGradeId.set(gid, {
              gradeId: gid,
              rawName,
              title: parsed.title.trim(),
              grades: parsed.grades,
            });
          });
        });
        timetablePreview?.entries.forEach((entry) => {
          const gid = (entry.grade_id ?? '').trim();
          const rawName = (entry.grade_name ?? '').trim();
          if (!gid || !rawName || byGradeId.has(gid)) return;
          const parsed = parseGradeDisplayName(rawName);
          byGradeId.set(gid, {
            gradeId: gid,
            rawName,
            title: parsed.title.trim(),
            grades: parsed.grades,
          });
        });
        const candidates = Array.from(byGradeId.values());
        if (candidates.length === 0) throw new Error('팀장 워크스페이스에 클래스가 없습니다.');
        const targetTitle = targetClassTitle.trim();
        const targetGradesKey = [...targetClassGrades].sort((a, b) => a - b).join(',');
        const exact = candidates.find(
          (row) =>
            row.title === targetTitle &&
            [...row.grades].sort((a, b) => a - b).join(',') === targetGradesKey
        );
        const titleOnly = candidates.find((row) => row.title === targetTitle);
        const selectedSourceGradeId = exact?.gradeId ?? titleOnly?.gradeId ?? candidates[0].gradeId;
        setTeamLeaderImportModal((prev) =>
          prev
            ? {
                ...prev,
                selectedSourceGradeId,
                sourceCandidates: candidates,
              }
            : {
                targetGradeId,
                targetClassTitle,
                targetClassGrades,
                selectedSourceGradeId,
                mode: 'annual_only',
                sourceCandidates: candidates,
              }
        );
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : '팀장 클래스 목록을 불러오지 못했습니다.';
        setTeamLeaderImportError(message);
        showError(message);
      } finally {
        setTeamLeaderImportBusy(false);
      }
    },
    [showError, teamIdForLeaderImport]
  );

  const handleExecuteTeamLeaderImport = useCallback(async () => {
    if (!teamLeaderImportModal || !workspaceId || !teamIdForLeaderImport || !programColorSettingsFull) return;
    if (!teamLeaderImportModal.selectedSourceGradeId) {
      setTeamLeaderImportError('가져올 팀장 클래스를 선택해 주세요.');
      return;
    }
    setTeamLeaderImportBusy(true);
    setTeamLeaderImportError(null);
    const mode = teamLeaderImportModal.mode;
    const persistImmediately = mode === 'annual_all';
    try {
      const sourceGradeId = teamLeaderImportModal.selectedSourceGradeId;
      const targetGradeId = teamLeaderImportModal.targetGradeId;
      const selectedCandidate =
        teamLeaderImportModal.sourceCandidates.find((row) => row.gradeId === sourceGradeId) ?? null;
      const selectedSourceGradeLabel = (selectedCandidate?.rawName ?? '').trim();
      /** 섹션에 등록할 클래스: 가져오기 대상(=현재 연간시간표 클래스) */
      const registerGradeId = targetGradeId.trim();

      if (persistImmediately) showSaving();

      const [timetablePreview, annualBundle] = await Promise.all([
        getTeamLeaderTimetableSettingsPreview(teamIdForLeaderImport),
        fetchTeamLeaderAnnualTimetableBundle(
          teamIdForLeaderImport,
          workspaceId,
          sourceGradeId,
          academicYear
        ),
      ]);
      const sourceEntry =
        timetablePreview.entries.find((entry) => entry.grade_id === sourceGradeId) ??
        timetablePreview.entries.find(
          (entry) => selectedSourceGradeLabel && entry.grade_name.trim() === selectedSourceGradeLabel
        ) ??
        null;
      const sourceColors = sourceEntry?.colors ?? resolveGradeProgramColors(programColorSettingsFull, targetGradeId);
      const sourceGrid = sourceEntry?.grid;
      const nextProgramSettings: ProgramColorSettings = {
        ...programColorSettingsFull,
        colorsByGrade: {
          ...programColorSettingsFull.colorsByGrade,
          [targetGradeId]: sourceColors,
        },
        baseTimetableByGrade: {
          ...programColorSettingsFull.baseTimetableByGrade,
          [targetGradeId]: sourceGrid
            ? normalizeBaseTimetableByTerm(sourceGrid)
            : programColorSettingsFull.baseTimetableByGrade[targetGradeId],
        },
      };

      const sourceAnnualYear = annualBundle.sourceAcademicYear ?? academicYear;
      const annualCells = annualBundle.cells;
      const leaderProjects = annualBundle.projects;
      if (annualCells.length === 0) {
        throw new Error(
          `선택한 소스 클래스(${selectedSourceGradeLabel || sourceGradeId})의 연간시간표 데이터를 찾지 못했습니다. 클래스명/학년 또는 팀장 학년도 데이터를 확인해 주세요.`
        );
      }
      if (sourceAnnualYear !== academicYear) {
        showInfo(`팀장 ${sourceAnnualYear}학년도 데이터를 현재 ${academicYear}학년도에 가져왔습니다.`);
      }
      const remappedProjects = remapImportedProjectPrograms(leaderProjects, targetGradeId, academicYear);
      const mappedRows: AnnualTimetableCellUpsert[] = annualCells.map((row) => ({
        workspace_id: workspaceId,
        grade_id: targetGradeId,
        academic_year: academicYear,
        term_number: row.term_number,
        monday_iso: row.monday_iso,
        weekday_key: row.weekday_key,
        slot_index: row.slot_index,
        text_value: row.text_value,
        memo: row.memo ?? null,
        color_hex: row.color_hex ?? null,
        slot_kind: row.slot_kind,
        is_editable: row.is_editable,
        is_dedicated: row.is_dedicated,
        is_base_excluded: row.is_base_excluded,
      }));
      const localDisplayRows: AnnualTimetableCell[] = mappedRows.map((row) => ({
        id: '',
        workspace_id: row.workspace_id,
        grade_id: row.grade_id,
        academic_year: row.academic_year,
        term_number: row.term_number,
        monday_iso: row.monday_iso,
        weekday_key: row.weekday_key,
        slot_index: row.slot_index,
        text_value: row.text_value ?? '',
        memo: row.memo ?? null,
        color_hex: row.color_hex ?? null,
        slot_kind: row.slot_kind,
        is_editable: row.is_editable,
        is_dedicated: row.is_dedicated,
        is_base_excluded: row.is_base_excluded,
      }));

      if (persistImmediately) {
        await saveProgramColorSettings(workspaceId, nextProgramSettings);
        adoptProgramColorSettingsFromServer(nextProgramSettings);
        setBaseTimetableByGrade(nextProgramSettings.baseTimetableByGrade);
        const savedRows = await upsertAnnualTimetableCells(mappedRows);
        await saveAnnualProjectPrograms(workspaceId, targetGradeId, academicYear, remappedProjects);
        try {
          await ensureProjectProgramScopedContentRowsForProjects(
            workspaceId,
            targetGradeId,
            academicYear,
            remappedProjects
          );
        } catch {
          /* 연간셀 가져오기는 유지 */
        }
        replaceImportedGradeCellsInState(targetGradeId, savedRows);
        setAnnualProjectProgramsByGradeId((prev) => ({
          ...prev,
          [targetGradeId]: remappedProjects,
        }));
        pendingLeaderAnnualOnlyPersistRef.current = null;
      } else {
        setProgramColorSettingsFull(nextProgramSettings);
        setBaseTimetableByGrade(nextProgramSettings.baseTimetableByGrade);
        applyImportedGradeCellsPendingSave(targetGradeId, localDisplayRows);
        setAnnualProjectProgramsByGradeId((prev) => ({
          ...prev,
          [targetGradeId]: remappedProjects,
        }));
        pendingLeaderAnnualOnlyPersistRef.current = {
          gradeId: targetGradeId,
          programSettings: nextProgramSettings,
          projects: remappedProjects,
        };
      }

      if (persistImmediately) {
        const [classPreview, currentClassSchedule] = await Promise.all([
          getTeamLeaderClassScheduleSectionsPreview(teamIdForLeaderImport),
          getClassScheduleSettings(workspaceId),
        ]);
        const sourceClassSection =
          classPreview.sections.find((section) =>
            selectedSourceGradeLabel ? section.grade_labels.includes(selectedSourceGradeLabel) : false
          ) ?? classPreview.sections[0];
        if (sourceClassSection) {
          const sourceRows = sourceClassSection.rows ?? [];
          const sourceWeekdayMax = sourceClassSection.weekday_max ?? { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0 };
          const sourceKey = normalizeClassScheduleSectionForCompare(sourceRows, sourceWeekdayMax);
          const matched = currentClassSchedule.sections.find((section) => {
            const rows = currentClassSchedule.sectionRows[section.id] ?? [];
            const max = currentClassSchedule.sectionWeekdayMax[section.id] ?? {
              mon: 0,
              tue: 0,
              wed: 0,
              thu: 0,
              fri: 0,
            };
            return normalizeClassScheduleSectionForCompare(rows, max) === sourceKey;
          });
          const emptySection = !matched
            ? currentClassSchedule.sections.find((section) =>
                isClassScheduleSectionContentEmpty(
                  currentClassSchedule.sectionRows[section.id],
                  currentClassSchedule.sectionWeekdayMax[section.id]
                )
              )
            : undefined;
          /** matched 우선, 없으면 빈 섹션 덮어쓰기, 둘 다 없으면 신규 추가 */
          const reuseSection = matched ?? emptySection;
          const nextSectionId = reuseSection?.id ?? generateLocalSectionId('cs');
          const shouldWriteContent = !matched;
          const nextSections = currentClassSchedule.sections.map((section) => ({
            ...section,
            gradeIds: section.gradeIds.filter((gid) => gid !== registerGradeId),
          }));
          if (registerGradeId) {
            if (reuseSection) {
              const idx = nextSections.findIndex((section) => section.id === nextSectionId);
              if (idx >= 0 && !nextSections[idx].gradeIds.includes(registerGradeId)) {
                nextSections[idx] = {
                  ...nextSections[idx],
                  gradeIds: [...nextSections[idx].gradeIds, registerGradeId],
                };
              }
            } else {
              nextSections.push({ id: nextSectionId, gradeIds: [registerGradeId] });
            }
          } else if (!reuseSection) {
            nextSections.push({ id: nextSectionId, gradeIds: [] });
          }
          const nextRows = { ...currentClassSchedule.sectionRows };
          const nextWeekdayMax = { ...currentClassSchedule.sectionWeekdayMax };
          if (shouldWriteContent) {
            nextRows[nextSectionId] = sourceRows.map((row) => ({ ...row }));
            nextWeekdayMax[nextSectionId] = { ...sourceWeekdayMax };
          }
          let sectionsToSave = nextSections;
          if (shouldWriteContent) {
            sectionsToSave = pruneEmptySharedSectionsAfterImport(
              nextSections,
              nextSectionId,
              (section) =>
                isClassScheduleSectionContentEmpty(nextRows[section.id], nextWeekdayMax[section.id])
            );
          }
          const rowsToSave: ClassScheduleSettings['sectionRows'] = {};
          const weekdayToSave: ClassScheduleSettings['sectionWeekdayMax'] = {};
          sectionsToSave.forEach((section) => {
            rowsToSave[section.id] = nextRows[section.id] ?? [];
            weekdayToSave[section.id] = nextWeekdayMax[section.id] ?? {
              mon: 0,
              tue: 0,
              wed: 0,
              thu: 0,
              fri: 0,
            };
          });
          await saveClassScheduleSettings(workspaceId, {
            sections: sectionsToSave,
            sectionRows: rowsToSave,
            sectionWeekdayMax: weekdayToSave,
          });
        }

        const [academicPreview, currentAcademicSections] = await Promise.all([
          getTeamLeaderAcademicCalendarSectionsPreview(teamIdForLeaderImport),
          getAcademicCalendarSections(workspaceId, { skipLocalCache: true }),
        ]);
        const sourceAcademicSection =
          academicPreview.sections.find((section) =>
            selectedSourceGradeLabel ? section.grade_labels.includes(selectedSourceGradeLabel) : false
          ) ?? academicPreview.sections[0];
        if (sourceAcademicSection?.settings) {
          const importedSettings = sourceAcademicSection.settings;
          const importedDays = sourceAcademicSection.days.map((d, i) => ({
            id: `import-cal-${Date.now()}-${i}`,
            workspace_id: workspaceId,
            day: d.day,
            day_kind: d.day_kind,
            note: d.note ?? null,
          }));
          const sourceAcademicLayout: PageSectionLayout = {
            id: 'source',
            gradeIds: [],
            academicCalendarSettings: {
              academicYear: importedSettings.academic_year,
              yearStartsOn: importedSettings.year_starts_on,
              yearEndsOn: importedSettings.year_ends_on,
              summerBreakStartsOn: importedSettings.summer_break_starts_on,
              summerBreakEndsOn: importedSettings.summer_break_ends_on,
              winterBreakStartsOn: importedSettings.winter_break_starts_on ?? '',
              winterBreakEndsOn: importedSettings.winter_break_ends_on ?? '',
            },
            academicCalendarDays: importedDays,
          };
          const sourceAcademicKey = normalizeAcademicSectionForCompare(sourceAcademicLayout);
          const matched = currentAcademicSections.find(
            (section) => normalizeAcademicSectionForCompare(section) === sourceAcademicKey
          );
          const emptySection = !matched
            ? currentAcademicSections.find((section) => isAcademicSectionContentEmpty(section))
            : undefined;
          const reuseSection = matched ?? emptySection;
          const nextAcademicSectionId = reuseSection?.id ?? generateLocalSectionId('ac');
          const shouldWriteContent = !matched;
          const nextAcademicSections = currentAcademicSections.map((section) => {
            const withoutGrade = {
              ...section,
              gradeIds: section.gradeIds.filter((gid) => gid !== registerGradeId),
            };
            if (section.id === nextAcademicSectionId && shouldWriteContent) {
              return {
                ...withoutGrade,
                academicCalendarSettings: sourceAcademicLayout.academicCalendarSettings,
                academicCalendarDays: sourceAcademicLayout.academicCalendarDays,
              };
            }
            return withoutGrade;
          });
          if (registerGradeId) {
            if (reuseSection) {
              const idx = nextAcademicSections.findIndex((section) => section.id === nextAcademicSectionId);
              if (idx >= 0 && !nextAcademicSections[idx].gradeIds.includes(registerGradeId)) {
                nextAcademicSections[idx] = {
                  ...nextAcademicSections[idx],
                  gradeIds: [...nextAcademicSections[idx].gradeIds, registerGradeId],
                };
              }
            } else {
              nextAcademicSections.push({
                id: nextAcademicSectionId,
                gradeIds: [registerGradeId],
                academicCalendarSettings: sourceAcademicLayout.academicCalendarSettings,
                academicCalendarDays: sourceAcademicLayout.academicCalendarDays,
              });
            }
          } else if (!reuseSection) {
            nextAcademicSections.push({
              id: nextAcademicSectionId,
              gradeIds: [],
              academicCalendarSettings: sourceAcademicLayout.academicCalendarSettings,
              academicCalendarDays: sourceAcademicLayout.academicCalendarDays,
            });
          }
          let academicSectionsToSave = nextAcademicSections;
          if (shouldWriteContent) {
            academicSectionsToSave = pruneEmptySharedSectionsAfterImport(
              nextAcademicSections,
              nextAcademicSectionId,
              (section) => isAcademicSectionContentEmpty(section)
            );
          }
          await saveAcademicCalendarSections(workspaceId, academicSectionsToSave);
          if (shouldWriteContent && sourceAcademicLayout.academicCalendarSettings) {
            const settings = sourceAcademicLayout.academicCalendarSettings;
            const days = stripAutoBreakVacationCalendarDays(
              sourceAcademicLayout.academicCalendarDays ?? []
            );
            if (nextAcademicSectionId === 'base') {
              await upsertAcademicYearSettings(workspaceId, {
                academicYear: settings.academicYear,
                yearStartsOn: settings.yearStartsOn,
                yearEndsOn: settings.yearEndsOn,
                summerBreakStartsOn: settings.summerBreakStartsOn,
                summerBreakEndsOn: settings.summerBreakEndsOn,
                winterBreakStartsOn: settings.winterBreakStartsOn || null,
                winterBreakEndsOn: settings.winterBreakEndsOn || null,
              });
              if (settings.yearStartsOn && settings.yearEndsOn) {
                const serverDays = await getCalendarDays(
                  workspaceId,
                  settings.yearStartsOn,
                  settings.yearEndsOn
                );
                const localDaySet = new Set(days.map((d) => d.day));
                for (const d of serverDays) {
                  if (!localDaySet.has(d.day)) {
                    await deleteCalendarDay(workspaceId, d.day);
                  }
                }
                for (const d of days) {
                  await upsertCalendarDay(workspaceId, {
                    day: d.day,
                    dayKind: d.day_kind,
                    note: d.note ?? undefined,
                  });
                }
              }
            } else {
              writeAcademicSectionSettingsToLocalStorage(workspaceId, nextAcademicSectionId, settings);
              writeAcademicSectionCalendarDaysToLocalStorage(workspaceId, nextAcademicSectionId, days);
            }
          }
        }

        const [trackPreview, currentTrackSections, currentPrefs] = await Promise.all([
          getTeamLeaderGradeTrackSectionsPreview(teamIdForLeaderImport),
          getGradeTrackSections(workspaceId, { skipLocalCache: true }),
          getGradeTrackProgramPreferences(workspaceId, { skipLocalCache: true }),
        ]);
        const sourceTrackSection =
          trackPreview.sections.find((section) =>
            section.grade_slots.some((slot) => (slot.grade_id ?? '').trim() === sourceGradeId)
          ) ??
          trackPreview.sections.find((section) =>
            selectedSourceGradeLabel ? section.grade_labels.includes(selectedSourceGradeLabel) : false
          ) ??
          trackPreview.sections[0];
        if (sourceTrackSection) {
          const sourceTrackSlot =
            sourceTrackSection.grade_slots.find((slot) => (slot.grade_id ?? '').trim() === sourceGradeId) ??
            sourceTrackSection.grade_slots[0];
          const sourceTracks = sourceTrackSlot?.tracks ?? [];
          const aliasesByName = new Map<string, string[]>();
          const criteriaByName = new Map<string, number>();
          const categoryByName = new Map<string, 'regular' | 'other'>();
          sourceTracks.forEach((row) => {
            const name = (row.name ?? '').trim();
            if (!name) return;
            const alias = (row.description ?? '').trim();
            const prevAliases = aliasesByName.get(name) ?? [];
            if (alias && !prevAliases.includes(alias)) prevAliases.push(alias);
            aliasesByName.set(name, prevAliases);
            criteriaByName.set(name, Number(row.criteria_hours ?? 0) || 0);
            categoryByName.set(name, row.track_category === 'other' ? 'other' : 'regular');
          });
          const sourceTrackColumns = sourceTrackSection.track_columns.map((col) => ({
            id: generateLocalSectionId('trk-col'),
            name: col.name,
            aliases: aliasesByName.get(col.name)?.length
              ? aliasesByName.get(col.name)!
              : [col.name],
            criteriaHours: criteriaByName.get(col.name) ?? 0,
            trackCategory: (categoryByName.get(col.name) ?? 'regular') as 'regular' | 'other',
            wildcardChar: (col.wildcardChar ?? '').trim() || undefined,
          }));
          const sourceTrackColumnsFinal =
            sourceTrackColumns.length > 0
              ? sourceTrackColumns
              : Array.from(aliasesByName.keys()).map((name) => ({
                  id: generateLocalSectionId('trk-col'),
                  name,
                  aliases: aliasesByName.get(name)?.length ? aliasesByName.get(name)! : [name],
                  criteriaHours: criteriaByName.get(name) ?? 0,
                  trackCategory: (categoryByName.get(name) ?? 'regular') as 'regular' | 'other',
                  wildcardChar: undefined,
                }));
          const sourceTrackKey = normalizeTrackColumnsForCompare(sourceTrackColumnsFinal);
          const preferredSectionId = `grade:${targetGradeId}`;
          const matchedTrackSection = currentTrackSections.find(
            (section) => normalizeTrackColumnsForCompare(section.trackColumns) === sourceTrackKey
          );
          const preferredTrackSection = currentTrackSections.find(
            (section) => section.id === preferredSectionId
          );
          const emptyTrackSection = currentTrackSections.find((section) =>
            isGradeTrackSectionContentEmpty(section)
          );
          let nextTrackSectionId: string;
          let trackWriteMode: 'match' | 'overwrite' | 'create';
          if (matchedTrackSection) {
            nextTrackSectionId = matchedTrackSection.id;
            trackWriteMode = 'match';
          } else if (preferredTrackSection) {
            /** 클래스 단독 섹션은 기존 내용이 있어도 덮어쓰기 */
            nextTrackSectionId = preferredTrackSection.id;
            trackWriteMode = 'overwrite';
          } else if (emptyTrackSection) {
            nextTrackSectionId = emptyTrackSection.id;
            trackWriteMode = 'overwrite';
          } else {
            nextTrackSectionId = preferredSectionId;
            trackWriteMode = 'create';
          }
          const nextTrackSections = currentTrackSections.map((section) => {
            if (section.id === nextTrackSectionId) {
              if (trackWriteMode === 'match') {
                return {
                  ...section,
                  gradeIds: [...section.gradeIds, targetGradeId].filter((v, i, arr) => arr.indexOf(v) === i),
                };
              }
              return {
                ...section,
                gradeIds: [targetGradeId],
                sourceGradeId: targetGradeId,
                trackColumns: sourceTrackColumnsFinal,
              };
            }
            return {
              ...section,
              gradeIds: section.gradeIds.filter((gid) => gid !== targetGradeId),
            };
          });
          if (trackWriteMode === 'create' && !nextTrackSections.some((section) => section.id === nextTrackSectionId)) {
            nextTrackSections.push({
              id: nextTrackSectionId,
              gradeIds: [targetGradeId],
              sourceGradeId: targetGradeId,
              trackColumns: sourceTrackColumnsFinal,
            });
          }
          const trackSectionsToSave =
            trackWriteMode === 'match'
              ? nextTrackSections
              : pruneEmptySharedSectionsAfterImport(
                  nextTrackSections,
                  nextTrackSectionId,
                  (section) => isGradeTrackSectionContentEmpty(section)
                );
          await saveGradeTrackSections(workspaceId, trackSectionsToSave);

          if (trackWriteMode !== 'match') {
            const { error: deleteTargetTracksError } = await supabase
              .from('tracks')
              .delete()
              .eq('workspace_id', workspaceId)
              .eq('grade_id', targetGradeId);
            if (deleteTargetTracksError) throw new Error(deleteTargetTracksError.message);
            if (sourceTracks.length > 0) {
              const { error: insertTracksError } = await supabase.from('tracks').insert(
                sourceTracks.map((row) => ({
                  workspace_id: workspaceId,
                  grade_id: targetGradeId,
                  name: row.name,
                  description: row.description,
                  criteria_hours: row.criteria_hours ?? 0,
                  track_category: row.track_category === 'other' ? 'other' : 'regular',
                }))
              );
              if (insertTracksError) throw new Error(insertTracksError.message);
            }

            const nextPrefs = {
              modes: { ...currentPrefs.modes },
              syncReps: { ...currentPrefs.syncReps },
              hoursAdjust: { ...(currentPrefs.hoursAdjust ?? {}) },
              learningPublishers: { ...(currentPrefs.learningPublishers ?? {}) },
            };
            if (trackPreview.program_preferences.modes[sourceGradeId]) {
              nextPrefs.modes[targetGradeId] = { ...trackPreview.program_preferences.modes[sourceGradeId] };
            }
            else delete nextPrefs.modes[targetGradeId];
            if (trackPreview.program_preferences.syncReps[sourceGradeId]) {
              nextPrefs.syncReps[targetGradeId] = { ...trackPreview.program_preferences.syncReps[sourceGradeId] };
            } else {
              delete nextPrefs.syncReps[targetGradeId];
            }
            const sourceHours = trackPreview.program_preferences.hoursAdjust?.[sourceGradeId];
            if (sourceHours) {
              nextPrefs.hoursAdjust[targetGradeId] = { ...sourceHours };
            } else {
              delete nextPrefs.hoursAdjust[targetGradeId];
            }
            const sourcePublishers = trackPreview.program_preferences.learningPublishers?.[sourceGradeId];
            if (sourcePublishers) {
              nextPrefs.learningPublishers[targetGradeId] = { ...sourcePublishers };
            } else {
              delete nextPrefs.learningPublishers[targetGradeId];
            }
            await saveGradeTrackProgramPreferences(workspaceId, nextPrefs);
          }
        }

        emitAnnualPrerequisiteRefresh(workspaceId, [
          'academic_calendar',
          'class_schedule',
          'grade_track',
          'timetable_settings',
        ]);
        await loadTermsAndSchedule({ silent: true });
        setTeamLeaderImportModal(null);
        showSuccess('팀장 데이터 구성 전체를 가져와 저장했습니다.');
      } else {
        setTeamLeaderImportModal(null);
        showInfo(
          '팀장 연간시간표 데이터를 화면에 가져왔습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.'
        );
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '팀장 데이터 가져오기에 실패했습니다.';
      setTeamLeaderImportError(message);
      showError(message);
    } finally {
      setTeamLeaderImportBusy(false);
    }
  }, [
    academicYear,
    adoptProgramColorSettingsFromServer,
    applyImportedGradeCellsPendingSave,
    loadTermsAndSchedule,
    programColorSettingsFull,
    replaceImportedGradeCellsInState,
    showError,
    showInfo,
    showSaving,
    showSuccess,
    teamIdForLeaderImport,
    teamLeaderImportModal,
    workspaceId,
  ]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const { programFocusGradeId, setProgramFocusGradeId } = useProgramSidebar();

  React.useEffect(() => {
    const g = searchParams.get('gradeId');
    if (g && classEntries.some((e) => e.gradeId === g)) {
      setProgramFocusGradeId(g);
    }
  }, [searchParams, classEntries, setProgramFocusGradeId]);

  /** 사이드바「고정 프로그램」학기 링크 → 해당 학기 표 블록으로 스크롤 */
  React.useEffect(() => {
    const pt = searchParams.get('programTerm');
    if (pt !== '1' && pt !== '2') return;
    if (searchParams.get('focusAnnualCellKey')?.trim()) return;
    if (loading || loadingCells) return;
    const gid =
      searchParams.get('gradeId') ||
      programFocusGradeId ||
      (visibleClassEntries.length === 1 ? visibleClassEntries[0]?.gradeId : '') ||
      '';
    if (!gid) return;
    const id = `cp-annual-fixed-term-${gid}-${pt}`;
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(t);
  }, [
    loading,
    loadingCells,
    programFocusGradeId,
    searchParams,
    visibleClassEntries,
  ]);

  React.useEffect(() => {
    if (currentGradeId && grades.some((g) => g.id === currentGradeId)) {
      setProgramFocusGradeId(currentGradeId);
      return;
    }
    if (selectedCells.length > 0) {
      const g0 = gradeIdFromCompound(selectedCells[0]);
      if (g0 && selectedCells.every((k) => gradeIdFromCompound(k) === g0)) {
        setProgramFocusGradeId(g0);
        return;
      }
    }
    if (visibleClassEntries.length === 1) {
      setProgramFocusGradeId(visibleClassEntries[0].gradeId);
    }
  }, [currentGradeId, grades, selectedCells, visibleClassEntries, setProgramFocusGradeId]);

  /** URL에 gradeId가 있을 때만 해당 클래스 카드로 스크롤. 메인「연간시간표」(/annual-timetable 단독)은 최상단 유지 */
  React.useEffect(() => {
    if (routerLocation.pathname !== '/annual-timetable') return;
    const pt = searchParams.get('programTerm');
    if (pt === '1' || pt === '2') return;
    if (searchParams.get('focusAnnualCellKey')?.trim()) return;
    const gid = searchParams.get('gradeId');
    if (!gid) return;
    if (loading || (loadingCells && hasAnyTermWeeks && classEntries.length > 0)) return;
    const el = document.getElementById(`cp-annual-class-${gid}`);
    if (!el) return;
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(t);
  }, [
    routerLocation.pathname,
    routerLocation.key,
    loading,
    loadingCells,
    hasAnyTermWeeks,
    classEntries.length,
    searchParams,
  ]);

  const focusAnnualCellKeyFromUrl = searchParams.get('focusAnnualCellKey')?.trim() ?? '';
  const focusedAnnualCellRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (routerLocation.pathname !== '/annual-timetable') return;
    const annualKey = focusAnnualCellKeyFromUrl;
    if (!annualKey) {
      focusedAnnualCellRef.current = null;
      return;
    }
    if (loading || (loadingCells && hasAnyTermWeeks && classEntries.length > 0)) return;
    const onceKey = `${routerLocation.key}|${annualKey}`;
    if (focusedAnnualCellRef.current === onceKey) return;
    focusedAnnualCellRef.current = onceKey;
    skipAutoEditOnceRef.current = true;
    dragStateRef.current = null;
    setSelectedCells([annualKey]);
    setDragState(null);
    setCopiedSourceRegion(null);
    const id = annualCellDomId(annualKey);
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }, 120);
    return () => window.clearTimeout(t);
  }, [
    classEntries.length,
    focusAnnualCellKeyFromUrl,
    hasAnyTermWeeks,
    loading,
    loadingCells,
    routerLocation.key,
    routerLocation.pathname,
  ]);

  const gradeLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};
    classEntries.forEach((entry) => {
      map[entry.gradeId] = entry.label;
    });
    return map;
  }, [classEntries]);
  const classEntryByGradeId = React.useMemo(() => {
    const map: Record<string, AnnualClassEntry> = {};
    classEntries.forEach((entry) => {
      map[entry.gradeId] = entry;
    });
    return map;
  }, [classEntries]);
  /** 통합 보기·동시보기 ON: 등록된 모든 클래스마다 연간시간표 섹션(클래스 블럭)을 세로로 나열 */
  const visibleAnnualBlocks = React.useMemo(() => {
    const targets =
      showAllClasses || !currentGradeId ? grades : grades.filter((g) => g.id === currentGradeId);
    return targets.map((grade) => ({
      grade,
      entry: classEntryByGradeId[grade.id] ?? null,
    }));
  }, [grades, currentGradeId, classEntryByGradeId, showAllClasses]);
  const classEntryOrderByGradeId = React.useMemo(() => {
    const out = new Map<string, number>();
    classEntries.forEach((entry, idx) => {
      out.set(entry.gradeId, idx);
    });
    return out;
  }, [classEntries]);
  const selectedAnnualProgressBrowseTargetByTerm = React.useMemo(() => {
    const out: Record<1 | 2, string | null> = { 1: null, 2: null };
    if (selectedCells.length === 0) return out;
    const grouped: Record<1 | 2, Array<{ annualKey: string; gradeId: string; mondayIso: string; dayIdx: number; periodIdx: number }>> = {
      1: [],
      2: [],
    };
    selectedCells.forEach((annualKey) => {
      const gradeId = gradeIdFromCompound(annualKey);
      const wk = weeklyPartFromCompound(annualKey);
      if (!gradeId || !wk) return;
      const parsed = parseWeeklyCellKey(wk);
      if (!parsed) return;
      const dayOffset = WEEKDAY_KEYS.findIndex((v) => v.key === parsed.dayKey);
      if (dayOffset < 0) return;
      const dateIso = toISODate(addDays(parseISODate(parsed.mondayIso), dayOffset));
      const matchedTerm = terms.find((term) => dateIso >= term.starts_on && dateIso <= term.ends_on)?.term_number;
      if (matchedTerm !== 1 && matchedTerm !== 2) return;
      grouped[matchedTerm].push({
        annualKey,
        gradeId,
        mondayIso: parsed.mondayIso,
        dayIdx: dayOffset,
        periodIdx: parsed.periodIdx,
      });
    });
    ([1, 2] as const).forEach((termNumber) => {
      const rows = grouped[termNumber];
      if (rows.length === 0) return;
      rows.sort((a, b) => {
        const ga = classEntryOrderByGradeId.get(a.gradeId) ?? Number.MAX_SAFE_INTEGER;
        const gb = classEntryOrderByGradeId.get(b.gradeId) ?? Number.MAX_SAFE_INTEGER;
        if (ga !== gb) return ga - gb;
        const mondayCmp = a.mondayIso.localeCompare(b.mondayIso);
        if (mondayCmp !== 0) return mondayCmp;
        if (a.dayIdx !== b.dayIdx) return a.dayIdx - b.dayIdx;
        return a.periodIdx - b.periodIdx;
      });
      const topLeft = rows[0];
      if (!topLeft) return;
      out[termNumber] = `/annual-progress-term${termNumber}?${createSearchParams({
        gradeId: topLeft.gradeId,
        focusAnnualCellKey: topLeft.annualKey,
      }).toString()}`;
    });
    return out;
  }, [classEntryOrderByGradeId, selectedCells, terms]);
  /** Shift+G: 학기 블록이 둘일 때 첫 선택 셀 학기 쪽 탐색 URL 우선 */
  const primaryAnnualProgressBrowseTarget = React.useMemo(() => {
    const t1 = selectedAnnualProgressBrowseTargetByTerm[1];
    const t2 = selectedAnnualProgressBrowseTargetByTerm[2];
    if (!t1 && !t2) return null;
    if (t1 && !t2) return t1;
    if (!t1 && t2) return t2;
    const firstKey = selectedCells[0];
    if (!firstKey) return t1 ?? t2;
    const gradeId = gradeIdFromCompound(firstKey);
    const wk = weeklyPartFromCompound(firstKey);
    if (!gradeId || !wk) return t1 ?? t2;
    const parsed = parseWeeklyCellKey(wk);
    if (!parsed) return t1 ?? t2;
    const dayOffset = WEEKDAY_KEYS.findIndex((v) => v.key === parsed.dayKey);
    if (dayOffset < 0) return t1 ?? t2;
    const dateIso = toISODate(addDays(parseISODate(parsed.mondayIso), dayOffset));
    const matchedTerm = terms.find((term) => dateIso >= term.starts_on && dateIso <= term.ends_on)?.term_number;
    if (matchedTerm === 1 && t1) return t1;
    if (matchedTerm === 2 && t2) return t2;
    return t1 ?? t2;
  }, [selectedAnnualProgressBrowseTargetByTerm, selectedCells, terms]);
  const registeredNamesByGrade = React.useMemo(() => {
    const byGrade: Record<string, Set<string>> = {};
    tracks.forEach((row) => {
      const gradeId = (row.grade_id ?? '').trim();
      if (!gradeId) return;
      if (!byGrade[gradeId]) byGrade[gradeId] = new Set<string>();
      const base = (row.name ?? '').trim();
      const alias = (row.description ?? '').trim();
      if (base) byGrade[gradeId].add(base);
      if (alias) byGrade[gradeId].add(alias);
    });
    return byGrade;
  }, [tracks]);
  const trackNameByTimetableTextByGrade = React.useMemo(
    () => buildTrackNameByTimetableTextByGrade(tracks),
    [tracks]
  );
  const eventSlotOrderLimits = React.useMemo(
    () => computeSlotOrderLimitsFromEntries(classEntries, eventSelectedGradeIds),
    [classEntries, eventSelectedGradeIds]
  );

  const shortDaySlotOrderLimits = React.useMemo(
    () => computeSlotOrderLimitsFromEntries(classEntries, shortDaySelectedGradeIds),
    [classEntries, shortDaySelectedGradeIds]
  );

  const eventOrderedSlotTargets = React.useMemo(
    () =>
      buildOrderedEventSlotTargetKeys(eventSlotOrderLimits.classMax, eventSlotOrderLimits.afterSchoolMax),
    [eventSlotOrderLimits]
  );

  useEffect(() => {
    if (!eventModalOpen) return;
    const allowed = new Set(eventOrderedSlotTargets);
    setEventModalPeriodState((prev) => {
      const removed = prev.selected.filter((t) => !allowed.has(t));
      if (removed.length === 0) return prev;
      const namesByTarget = { ...prev.namesByTarget };
      removed.forEach((t) => {
        delete namesByTarget[t];
      });
      return { selected: prev.selected.filter((t) => allowed.has(t)), namesByTarget };
    });
  }, [eventModalOpen, eventOrderedSlotTargets]);

  const shortDayOrderedSlotTargets = React.useMemo(
    () =>
      buildOrderedEventSlotTargetKeys(
        shortDaySlotOrderLimits.classMax,
        shortDaySlotOrderLimits.afterSchoolMax
      ),
    [shortDaySlotOrderLimits]
  );

  useEffect(() => {
    if (!shortDayModalOpen) return;
    setShortDayEndSlotTarget((prev) => {
      if (prev && shortDayOrderedSlotTargets.includes(prev)) return prev;
      return shortDayOrderedSlotTargets[0] ?? null;
    });
  }, [shortDayModalOpen, shortDayOrderedSlotTargets]);

  useLayoutEffect(() => {
    if (!eventModalOpen) return undefined;

    const runShrink = () => {
      eventPeriodLabelBtnRefs.current.forEach((el, key) => {
        const parsed = parseEventSlotTargetKey(key);
        const label = parsed ? eventSlotTargetLabel(parsed.kind, parsed.order) : key;
        shrinkToFitElementFont(el, label, {
          maxRem: 0.95,
          minRem: 0.44,
        });
      });
    };

    runShrink();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(runShrink);
    });
    eventPeriodLabelBtnRefs.current.forEach((el) => {
      ro.observe(el);
    });

    return () => {
      ro.disconnect();
    };
  }, [eventModalOpen, eventOrderedSlotTargets, eventSelectedSlotTargets]);

  const eventDateTargets = React.useMemo(() => {
    const map: Record<string, { termNumber: 1 | 2; mondayIso: string; weekdayKey: AnnualWeekdayKey }> = {};
    weeksTerm1.forEach((week) => {
      WEEKDAY_KEYS.forEach(({ key }, idx) => {
        const dateIso = week.datesByWeekday[idx] ?? '';
        if (!dateIso) return;
        map[dateIso] = { termNumber: 1, mondayIso: week.mondayIso, weekdayKey: key as AnnualWeekdayKey };
      });
    });
    weeksTerm2.forEach((week) => {
      WEEKDAY_KEYS.forEach(({ key }, idx) => {
        const dateIso = week.datesByWeekday[idx] ?? '';
        if (!dateIso) return;
        map[dateIso] = { termNumber: 2, mondayIso: week.mondayIso, weekdayKey: key as AnnualWeekdayKey };
      });
    });
    return map;
  }, [weeksTerm1, weeksTerm2]);

  const effectiveCellsByKey = React.useMemo<Record<string, AnnualEffectiveCell>>(() => {
    const next: Record<string, AnnualEffectiveCell> = {};
    const schoolBreakSettings = calendarLinkedContext.sectionSettings;
    const isSchoolBreakDate = schoolBreakSettings
      ? (iso: string) => isDateInSchoolBreak(schoolBreakSettings, iso)
      : undefined;
    const termBlocks: Array<{ tn: 1 | 2; weeks: WeekInfo[]; startsOn: string; endsOn: string }> = [];
    if (term1Def?.starts_on && term1Def?.ends_on && weeksTerm1.length) {
      termBlocks.push({ tn: 1, weeks: weeksTerm1, startsOn: term1Def.starts_on, endsOn: term1Def.ends_on });
    }
    if (term2Def?.starts_on && term2Def?.ends_on && weeksTerm2.length) {
      termBlocks.push({ tn: 2, weeks: weeksTerm2, startsOn: term2Def.starts_on, endsOn: term2Def.ends_on });
    }
    classEntries.forEach((entry) => {
      termBlocks.forEach(({ tn, weeks: wks, startsOn, endsOn }) => {
        wks.forEach((week) => {
          WEEKDAY_KEYS.forEach(({ key }, weekdayIdx) => {
            const weekdayKey = key as AnnualWeekdayKey;
            const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
            for (let slotIndex = 0; slotIndex < MAX_PERIODS; slotIndex += 1) {
              const compoundKey = compoundCellKey(entry.gradeId, week.mondayIso, weekdayKey, slotIndex);
              const hasStoredValue =
                cellRecordIds[compoundKey] !== undefined ||
                cellSlotKinds[compoundKey] !== undefined ||
                cellData[compoundKey] !== undefined ||
                cellMemos[compoundKey] !== undefined ||
                cellColors[compoundKey] !== undefined;
              const storedCell: AnnualTimetableCell | undefined = hasStoredValue
                ? {
                    id: cellRecordIds[compoundKey] || `virtual-${compoundKey}`,
                    workspace_id: workspaceId,
                    grade_id: entry.gradeId,
                    academic_year: academicYear,
                    term_number: tn,
                    monday_iso: week.mondayIso,
                    weekday_key: weekdayKey,
                    slot_index: slotIndex,
                    text_value: cellData[compoundKey] ?? '',
                    memo: cellMemos[compoundKey] ?? null,
                    color_hex: cellColors[compoundKey] || null,
                    slot_kind: cellSlotKinds[compoundKey] ?? 'class',
                    is_editable: true,
                    is_dedicated: false,
                    is_base_excluded: Object.prototype.hasOwnProperty.call(cellBaseExcluded, compoundKey)
                      ? cellBaseExcluded[compoundKey]
                      : isBaseExcludeColor((cellColors[compoundKey] ?? '').trim(), baseExcludeColors),
                  }
                : undefined;
              const derived = deriveAnnualEffectiveCell({
                workspaceId,
                academicYear,
                termNumber: tn,
                gradeId: entry.gradeId,
                mondayIso: week.mondayIso,
                weekdayKey,
                slotIndex,
                dateIso,
                sectionRows: entry.sectionRows,
                slotLimitByDay: entry.slotLimitByDay,
                startsOn,
                endsOn,
                holidaySet,
                storedCell,
                dedicatedColors,
                baseExcludeColors,
                calendarDayKindByDate,
                isSchoolBreakDate,
              });
              const storedText = (storedCell?.text_value ?? '').trim();
              const storedMemo = (storedCell?.memo ?? '').trim();
              const storedColor = (storedCell?.color_hex ?? '').trim();
              const hasImportedValue = Boolean(storedText || storedMemo || storedColor);
              const inSchoolBreak = Boolean(dateIso && isSchoolBreakDate?.(dateIso));
              const isCalendarBlockedNonClass =
                derived.slotKind === 'non_class' && derived.nonClassSource === 'calendar_schedule';
              const isImportConflict =
                hasStoredValue &&
                hasImportedValue &&
                (derived.slotKind === 'holiday' || isCalendarBlockedNonClass || inSchoolBreak);
              next[compoundKey] = isImportConflict
                ? {
                    ...derived,
                    ...(inSchoolBreak ? { slotKind: 'holiday' as const, nonClassSource: null } : {}),
                    textValue: storedCell?.text_value ?? '',
                    memo: storedCell?.memo ?? '',
                    colorHex: storedCell?.color_hex ?? '',
                    isEditable: true,
                    nonClassSource: isCalendarBlockedNonClass ? 'calendar_schedule' : derived.nonClassSource,
                  }
                : derived;
            }
          });
        });
      });
    });
    return next;
  }, [
    academicYear,
    baseExcludeColors,
    calendarDayKindByDate,
    cellBaseExcluded,
    cellColors,
    cellData,
    cellMemos,
    cellRecordIds,
    cellSlotKinds,
    classEntries,
    dedicatedColors,
    holidaySet,
    workspaceId,
    calendarLinkedContext.sectionSettings,
    term1Def?.ends_on,
    term1Def?.starts_on,
    term2Def?.ends_on,
    term2Def?.starts_on,
    weeksTerm1,
    weeksTerm2,
  ]);

  const effectiveCellsByKeyRef = useRef<Record<string, AnnualEffectiveCell>>({});
  effectiveCellsByKeyRef.current = effectiveCellsByKey;

  /** 학기·클래스별 라벨링/프로젝트 색상 셀 개수 (툴바 스와치 중앙 표시용) */
  const annualTermColorCellCounts = React.useMemo(() => {
    const map: Record<string, { labeling: Record<string, number>; project: Record<string, number> }> = {};
    const projectNormByGradeTerm = new Map<string, Set<string>>();
    Object.entries(annualProjectProgramsByGradeId).forEach(([gradeId, programs]) => {
      programs.forEach((p) => {
        const norm = normalizeHexColorForProjectCompare(p.colorHex);
        if (!norm) return;
        const key = `${gradeId}|${p.termNumber}`;
        let set = projectNormByGradeTerm.get(key);
        if (!set) {
          set = new Set();
          projectNormByGradeTerm.set(key, set);
        }
        set.add(norm);
      });
    });
    const labelingNormSet = new Set(
      baseExcludeColors
        .map((c) => normalizeHexColorForProjectCompare(c))
        .filter((n): n is string => Boolean(n))
    );
    Object.values(effectiveCellsByKey).forEach((cell) => {
      // 학기 밖·공휴만 제외 — 수업·활동·비수업(사용자/편제 자동) 모두 카운트
      if (cell.slotKind === 'out_of_term' || cell.slotKind === 'holiday') return;
      const norm = normalizeHexColorForProjectCompare(cell.colorHex);
      if (!norm) return;
      const bucketKey = `${cell.gradeId}|${cell.termNumber}`;
      let bucket = map[bucketKey];
      if (!bucket) {
        bucket = { labeling: {}, project: {} };
        map[bucketKey] = bucket;
      }
      if (cell.isBaseExcluded) {
        bucket.labeling[norm] = (bucket.labeling[norm] ?? 0) + 1;
        return;
      }
      if (projectNormByGradeTerm.get(bucketKey)?.has(norm)) {
        bucket.project[norm] = (bucket.project[norm] ?? 0) + 1;
        return;
      }
      // 제외 플래그가 없어도 라벨링 팔레트 색이면 카운트(활동·비수업 포함)
      if (labelingNormSet.has(norm)) {
        bucket.labeling[norm] = (bucket.labeling[norm] ?? 0) + 1;
      }
    });
    return map;
  }, [annualProjectProgramsByGradeId, baseExcludeColors, effectiveCellsByKey]);

  /** 학사일정·요일 시수 변경 시 DB·로컬에 남은 예전 공휴/비수업 slot_kind 흔적 제거 */
  useEffect(() => {
    if (loadingCells || classEntries.length === 0) return;
    const termBlocks: Array<{ termNumber: 1 | 2; weeks: WeekInfo[]; startsOn: string; endsOn: string }> = [];
    if (term1Def?.starts_on && term1Def?.ends_on && weeksTerm1.length) {
      termBlocks.push({ termNumber: 1, weeks: weeksTerm1, startsOn: term1Def.starts_on, endsOn: term1Def.ends_on });
    }
    if (term2Def?.starts_on && term2Def?.ends_on && weeksTerm2.length) {
      termBlocks.push({ termNumber: 2, weeks: weeksTerm2, startsOn: term2Def.starts_on, endsOn: term2Def.ends_on });
    }
    if (termBlocks.length === 0) return;
    const schoolBreakSettings = calendarLinkedContext.sectionSettings;
    const isSchoolBreakDate = schoolBreakSettings
      ? (iso: string) => isDateInSchoolBreak(schoolBreakSettings, iso)
      : undefined;
    const staleKeys = listAnnualKeysWithStaleCalendarSlotOverrides({
      classEntries,
      termBlocks,
      holidaySet,
      calendarDayKindByDate,
      storedSlotKinds: cellSlotKinds,
      isSchoolBreakDate,
    });
    if (staleKeys.length === 0) return;
    setCellSlotKinds((prev) => {
      let changed = false;
      const next = { ...prev };
      staleKeys.forEach((key) => {
        if (next[key] !== undefined) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    /**
     * 학사일정 자동 보정으로 localStorage/slotKind만 맞추고, 사용자 편집 dirty로 올리지 않는다.
     * (미저장 모달·이탈 확인이 편집 없이 반복되는 원인)
     * DB 정렬은 저장 시 `seedCalendarAlignKeys`로 함께 반영된다.
     */
  }, [
    calendarDayKindByDate,
    cellSlotKinds,
    classEntries,
    holidaySet,
    loadingCells,
    calendarLinkedContext.sectionSettings,
    term1Def?.ends_on,
    term1Def?.starts_on,
    term2Def?.ends_on,
    term2Def?.starts_on,
    weeksTerm1,
    weeksTerm2,
  ]);

  const filterEditableSelectionKeys = useCallback((keys: string[]) => {
    return keys.filter((key) => effectiveCellsByKeyRef.current[key]?.isEditable);
  }, []);

  const effectiveCellsByGrade = React.useMemo(() => {
    const buckets: Record<string, AnnualEffectiveCell[]> = {};
    classEntries.forEach((entry) => {
      buckets[entry.gradeId] = [];
    });
    Object.values(effectiveCellsByKey).forEach((cell) => {
      if (!buckets[cell.gradeId]) buckets[cell.gradeId] = [];
      buckets[cell.gradeId]!.push(cell);
    });
    return buckets;
  }, [classEntries, effectiveCellsByKey]);

  const programGroupsByGrade = React.useMemo(() => {
    const next: Record<string, ReturnType<typeof buildProgramGrouping>> = {};
    classEntries.forEach((entry) => {
      next[entry.gradeId] = buildProgramGrouping(effectiveCellsByGrade[entry.gradeId] ?? []);
    });
    return next;
  }, [classEntries, effectiveCellsByGrade]);

  const wildcardCharByGradeTrackName = React.useMemo(() => {
    const byGrade: Record<string, Record<string, string>> = {};
    gradeTrackSections.forEach((section) => {
      section.gradeIds.forEach((gradeId) => {
        const map = byGrade[gradeId] ?? {};
        (section.trackColumns ?? []).forEach((column) => {
          const name = (column.name ?? '').trim();
          if (!name) return;
          const wildcard = sanitizeWildcardChar(column.wildcardChar ?? '');
          if (!wildcard) return;
          map[name] = wildcard;
        });
        byGrade[gradeId] = map;
      });
    });
    return byGrade;
  }, [gradeTrackSections]);

  const wildcardCharsByGrade = React.useMemo(
    () => buildWildcardCharsByGrade(gradeTrackSections),
    [gradeTrackSections]
  );

  const applyAnnualLocalHistoryFrame = useCallback((frame: AnnualLocalHistoryFrame) => {
    annualApplyingLocalHistoryRef.current = true;
    try {
      const cloned = JSON.parse(JSON.stringify(frame)) as AnnualLocalHistoryFrame;
      setCellData(cloned.cellData);
      setCellMemos(cloned.cellMemos);
      setCellColors(cloned.cellColors);
      setCellSlotKinds(cloned.cellSlotKinds);
      setCellRecordIds(cloned.cellRecordIds);
      setCellBaseExcluded(cloned.cellBaseExcluded);
      setDirtyCellKeys([...cloned.dirtyCellKeys]);
      setEditingCell(null);
    } finally {
      annualApplyingLocalHistoryRef.current = false;
      notifyAnnualUndoRedoStacksRef.current();
    }
  }, []);

  const tryAnnualUndo = useCallback((): boolean => {
    if (savingAllRef.current) return false;
    if (editingCellRef.current) return false;
    if (annualCellMemoModalRef.current) return false;
    if (weekActivityMemoModalRef.current) return false;
    const st = annualLocalUndoStackRef.current;
    if (!st.length) return false;
    const previous = st.pop()!;
    const redo = annualLocalRedoStackRef.current;
    redo.push(cloneAnnualLocalHistoryFrameRef.current());
    if (redo.length > ANNUAL_LOCAL_EDIT_HISTORY_MAX) redo.shift();
    applyAnnualLocalHistoryFrame(previous);
    return true;
  }, [applyAnnualLocalHistoryFrame]);

  const tryAnnualRedo = useCallback((): boolean => {
    if (savingAllRef.current) return false;
    if (editingCellRef.current) return false;
    if (annualCellMemoModalRef.current) return false;
    if (weekActivityMemoModalRef.current) return false;
    const st = annualLocalRedoStackRef.current;
    if (!st.length) return false;
    const nextFrame = st.pop()!;
    const undo = annualLocalUndoStackRef.current;
    undo.push(cloneAnnualLocalHistoryFrameRef.current());
    if (undo.length > ANNUAL_LOCAL_EDIT_HISTORY_MAX) undo.shift();
    applyAnnualLocalHistoryFrame(nextFrame);
    return true;
  }, [applyAnnualLocalHistoryFrame]);

  const tryAnnualUndoRef = useRef(tryAnnualUndo);
  tryAnnualUndoRef.current = tryAnnualUndo;
  const tryAnnualRedoRef = useRef(tryAnnualRedo);
  tryAnnualRedoRef.current = tryAnnualRedo;

  useLayoutEffect(() => {
    const onWindowKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.isComposing) return;
      const zk = (e.key || '').toLowerCase();
      if (e.code !== 'KeyZ' && zk !== 'z') return;
      if (savingAllRef.current) return;
      if (editingCellRef.current) return;
      if (annualCellMemoModalRef.current) return;
      if (weekActivityMemoModalRef.current) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae) {
        if (ae.closest('[role="dialog"]') && !ae.closest('.cp-annual-timetable-table-wrap')) return;
        if (
          ae.closest('input, textarea, select, [contenteditable="true"]') &&
          !ae.closest('.cp-annual-timetable-table-wrap')
        ) {
          return;
        }
      }
      const inTable = !!(ae && ae.closest('.cp-annual-timetable-table-wrap'));
      const hasSel = selectedCellsRef.current.length > 0;
      if (!inTable && !hasSel) return;
      const canUndo = annualLocalUndoStackRef.current.length > 0;
      const canRedo = annualLocalRedoStackRef.current.length > 0;
      if (e.shiftKey ? !canRedo : !canUndo) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.shiftKey) tryAnnualRedoRef.current();
      else tryAnnualUndoRef.current();
    };
    window.addEventListener('keydown', onWindowKeyDown, true);
    return () => window.removeEventListener('keydown', onWindowKeyDown, true);
  }, []);

  const applyCellUpdates = useCallback(
    (
      updates: Array<{
        key: string;
        textValue?: string;
        memo?: string;
        colorHex?: string;
        slotKind?: AnnualTimetableSlotKind;
        isBaseExcluded?: boolean;
      }>
    ) => {
      const valid = updates
        .map((update) => {
          const current = effectiveCellsByKeyRef.current[update.key];
          if (!current || !current.isEditable) return null;
          return { current, update };
        })
        .filter((item): item is NonNullable<typeof item> => item != null);
      if (valid.length === 0) return;
      pushAnnualLocalUndoSnapshotFromCurrentRef.current();
      setCellData((prev) => {
        const next = { ...prev };
        valid.forEach(({ current, update }) => {
          if (update.textValue !== undefined) next[update.key] = update.textValue;
          else if (next[update.key] === undefined) next[update.key] = current.textValue;
        });
        return next;
      });
      setCellMemos((prev) => {
        const next = { ...prev };
        valid.forEach(({ current, update }) => {
          const memo = update.memo !== undefined ? update.memo : current.memo;
          if (memo && memo.trim()) next[update.key] = memo;
          else delete next[update.key];
        });
        return next;
      });
      setCellColors((prev) => {
        const next = { ...prev };
        valid.forEach(({ current, update }) => {
          const colorHex = update.colorHex !== undefined ? update.colorHex : current.colorHex;
          if (colorHex) next[update.key] = colorHex;
          else delete next[update.key];
        });
        return next;
      });
      setCellSlotKinds((prev) => {
        const next = { ...prev };
        valid.forEach(({ current, update }) => {
          next[update.key] = update.slotKind ?? current.slotKind;
        });
        return next;
      });
      setCellBaseExcluded((prev) => {
        const next = { ...prev };
        valid.forEach(({ current, update }) => {
          const key = update.key;
          if (update.isBaseExcluded !== undefined) {
            next[key] = update.isBaseExcluded;
          } else if (update.colorHex !== undefined) {
            const nextColor = (update.colorHex ?? '').trim();
            if (!nextColor) delete next[key];
            else next[key] = prev[key] !== undefined ? prev[key]! : false;
          }
        });
        return next;
      });
      setDirtyCellKeys((prev) => {
        const s = new Set(prev);
        valid.forEach(({ current, update }) => {
          const key = update.key;
          const currentSnap: AnnualCellPersistSnapshot = {
            textValue: current.textValue ?? '',
            memo: (current.memo ?? '').trim(),
            colorHex: (current.colorHex ?? '').trim(),
            slotKind: annualPersistSlotKindFromEffective(current),
            isBaseExcluded: current.isBaseExcluded,
          };
          const nextSnap = annualCellNextSnapshotFromUpdate(currentSnap, update);
          const saved = annualCellSavedBaselineRef.current[key];
          if (saved && annualCellSnapshotsEqual(nextSnap, saved)) {
            s.delete(key);
          } else {
            s.add(key);
          }
        });
        return Array.from(s);
      });
    },
    []
  );

  const clearAnnualCellMemoLongPressTimer = useCallback(() => {
    if (annualCellMemoLongPressTimerRef.current == null) return;
    window.clearTimeout(annualCellMemoLongPressTimerRef.current);
    annualCellMemoLongPressTimerRef.current = null;
    annualCellMemoLongPressAnchorRef.current = null;
  }, []);

  const openAnnualCellMemoModalForKey = useCallback((compoundKey: string) => {
    clearAnnualCellMemoLongPressTimer();
    const cur = effectiveCellsByKeyRef.current[compoundKey];
    if (!cur?.isEditable) return;
    setWeekActivityMemoModal(null);
    setWeekActivityMemoAnchorStyle(null);
    setEditingCell(null);
    setAnnualCellMemoModal({
      compoundKey,
      cellDisplay: cur.textValue ?? '',
      memoDraft: (cur.memo ?? '').trim(),
    });
    setDragState(null);
    dragStateRef.current = null;
    setSelectedCells([compoundKey]);
  }, [clearAnnualCellMemoLongPressTimer]);

  const closeAnnualCellMemoModal = useCallback(() => {
    setAnnualCellMemoModal(null);
  }, []);

  const saveAnnualCellMemoModal = useCallback(() => {
    if (!annualCellMemoModal) return;
    applyCellUpdates([
      { key: annualCellMemoModal.compoundKey, memo: annualCellMemoModal.memoDraft },
    ]);
    setAnnualCellMemoModal(null);
  }, [annualCellMemoModal, applyCellUpdates]);

  const recomputeAnnualCellMemoPopupLayout = useCallback(() => {
    if (!annualCellMemoModal) {
      setAnnualCellMemoAnchorStyle(null);
      return;
    }
    const el = document.getElementById(annualCellDomId(annualCellMemoModal.compoundKey));
    if (!(el instanceof HTMLElement)) {
      setAnnualCellMemoAnchorStyle(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const pad = 8;
    const hostRect =
      resolveProgramTableFloatingHostRect(el.closest('.cp-annual-timetable-table-wrap')) ??
      el.closest('.cp-annual-timetable-table-wrap')?.getBoundingClientRect() ??
      null;
    const { width: w, maxWidth: maxW } = getProgramProgressOverlayWidth(
      { width: layoutWidthFromClientRect(r) },
      'memo_narrow',
      pad
    );
    setAnnualCellMemoAnchorStyle(
      computeProgramTableFloatingPopupStyle({
        anchorRect: r,
        hostRect,
        preferredWidth: w,
        alignX: 'right',
        pad,
        maxWidthCss: maxW,
        zIndex: 10060,
      })
    );
  }, [annualCellMemoModal]);

  useLayoutEffect(() => {
    if (!annualCellMemoModal) {
      setAnnualCellMemoAnchorStyle(null);
      return;
    }
    recomputeAnnualCellMemoPopupLayout();
    const raf = window.requestAnimationFrame(() => {
      recomputeAnnualCellMemoPopupLayout();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [annualCellMemoModal, recomputeAnnualCellMemoPopupLayout]);

  useEffect(() => {
    if (!annualCellMemoModal) return;
    const el = document.getElementById(annualCellDomId(annualCellMemoModal.compoundKey));
    const host = el?.closest('.cp-annual-timetable-table-wrap');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => recomputeAnnualCellMemoPopupLayout()) : null;
    window.addEventListener('resize', recomputeAnnualCellMemoPopupLayout);
    window.addEventListener('scroll', recomputeAnnualCellMemoPopupLayout, true);
    if (host && ro) ro.observe(host);
    return () => {
      window.removeEventListener('resize', recomputeAnnualCellMemoPopupLayout);
      window.removeEventListener('scroll', recomputeAnnualCellMemoPopupLayout, true);
      if (host && ro) ro.unobserve(host);
      ro?.disconnect();
    };
  }, [annualCellMemoModal, recomputeAnnualCellMemoPopupLayout]);

  useEffect(() => {
    return () => clearAnnualCellMemoLongPressTimer();
  }, [clearAnnualCellMemoLongPressTimer]);

  useEffect(() => {
    if (!annualCellMemoModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeAnnualCellMemoModal();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [annualCellMemoModal, closeAnnualCellMemoModal]);

  const recomputeWeekActivityMemoPopupLayout = useCallback(() => {
    if (!weekActivityMemoModal) {
      setWeekActivityMemoAnchorStyle(null);
      return;
    }
    const el = document.getElementById(
      annualWeekMemoAnchorDomId(
        weekActivityMemoModal.termKey,
        weekActivityMemoModal.gradeId,
        weekActivityMemoModal.mondayIso
      )
    );
    if (!(el instanceof HTMLElement)) {
      setWeekActivityMemoAnchorStyle(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const pad = 8;
    const hostRect =
      resolveProgramTableFloatingHostRect(el.closest('.cp-annual-timetable-table-wrap')) ??
      el.closest('.cp-annual-timetable-table-wrap')?.getBoundingClientRect() ??
      null;
    const { width: w, maxWidth: maxW } = getProgramProgressOverlayWidth(
      { width: layoutWidthFromClientRect(r) },
      'memo_wide',
      pad
    );
    setWeekActivityMemoAnchorStyle(
      computeProgramTableFloatingPopupStyle({
        anchorRect: r,
        hostRect,
        preferredWidth: w,
        alignX: 'right',
        pad,
        minReadableHeight: 160,
        maxWidthCss: maxW,
        zIndex: 10060,
      })
    );
  }, [weekActivityMemoModal]);

  useLayoutEffect(() => {
    if (!weekActivityMemoModal) {
      setWeekActivityMemoAnchorStyle(null);
      return;
    }
    recomputeWeekActivityMemoPopupLayout();
    const raf = window.requestAnimationFrame(() => {
      recomputeWeekActivityMemoPopupLayout();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [weekActivityMemoModal, recomputeWeekActivityMemoPopupLayout]);

  useEffect(() => {
    if (!weekActivityMemoModal) return;
    const el = document.getElementById(
      annualWeekMemoAnchorDomId(
        weekActivityMemoModal.termKey,
        weekActivityMemoModal.gradeId,
        weekActivityMemoModal.mondayIso
      )
    );
    const host = el?.closest('.cp-annual-timetable-table-wrap');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => recomputeWeekActivityMemoPopupLayout()) : null;
    window.addEventListener('resize', recomputeWeekActivityMemoPopupLayout);
    window.addEventListener('scroll', recomputeWeekActivityMemoPopupLayout, true);
    if (host && ro) ro.observe(host);
    return () => {
      window.removeEventListener('resize', recomputeWeekActivityMemoPopupLayout);
      window.removeEventListener('scroll', recomputeWeekActivityMemoPopupLayout, true);
      if (host && ro) ro.unobserve(host);
      ro?.disconnect();
    };
  }, [weekActivityMemoModal, recomputeWeekActivityMemoPopupLayout]);

  useEffect(() => {
    if (!weekActivityMemoModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setWeekActivityMemoModal(null);
      setWeekActivityMemoAnchorStyle(null);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [weekActivityMemoModal]);

  const weekMemoHoverAnchorId = weekMemoHoverPreview?.anchorId;
  useEffect(() => {
    if (!weekMemoHoverAnchorId) return;
    const recalc = () => {
      const el = document.getElementById(weekMemoHoverAnchorId);
      if (!(el instanceof HTMLElement)) return;
      setWeekMemoHoverPreview((prev) => {
        if (!prev || prev.anchorId !== weekMemoHoverAnchorId) return prev;
        return {
          ...prev,
          style: computeProgramProgressHoverTooltipStyle(el.getBoundingClientRect(), 'memo_narrow'),
        };
      });
    };
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [weekMemoHoverAnchorId]);

  const weekHoursHoverAnchorId = weekHoursHoverPreview?.anchorId;
  useEffect(() => {
    if (!weekHoursHoverAnchorId) return;
    const recalc = () => {
      const el = document.getElementById(weekHoursHoverAnchorId);
      if (!(el instanceof HTMLElement)) return;
      const preview = document.querySelector('.cp-annual-week-hours-hover-preview');
      // layout px — GBR.width(시각)를 쓰면 네비 배율≠100%에서 합계 여백이 깨짐.
      const measuredWidth =
        preview instanceof HTMLElement
          ? measureWeekTrackHoursPreviewLayoutWidth(preview)
          : undefined;
      setWeekHoursHoverPreview((prev) => {
        if (!prev || prev.anchorId !== weekHoursHoverAnchorId) return prev;
        return {
          ...prev,
          style: computeWeekTrackHoursHoverTooltipStyle(el.getBoundingClientRect(), measuredWidth),
        };
      });
    };
    recalc();
    requestAnimationFrame(recalc);
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [weekHoursHoverAnchorId]);

  const termHoursHoverAnchorId = termHoursHoverPreview?.anchorId;
  useEffect(() => {
    if (!termHoursHoverAnchorId) return;
    const recalc = () => {
      const el = document.getElementById(termHoursHoverAnchorId);
      if (!(el instanceof HTMLElement)) return;
      const preview = document.querySelector(
        '.cp-annual-week-hours-hover-preview[aria-label="편제별 학기 시수"]'
      );
      const measuredWidth =
        preview instanceof HTMLElement
          ? measureWeekTrackHoursPreviewLayoutWidth(preview)
          : undefined;
      setTermHoursHoverPreview((prev) => {
        if (!prev || prev.anchorId !== termHoursHoverAnchorId) return prev;
        return {
          ...prev,
          style: computeWeekTrackHoursHoverTooltipStyle(el.getBoundingClientRect(), measuredWidth),
        };
      });
    };
    recalc();
    requestAnimationFrame(recalc);
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [termHoursHoverAnchorId]);

  const cellColorHoverAnchorId = cellColorHoverPreview?.anchorId;
  useEffect(() => {
    if (!cellColorHoverAnchorId) return;
    const recalc = () => {
      const el = document.getElementById(cellColorHoverAnchorId);
      if (!(el instanceof HTMLElement)) return;
      setCellColorHoverPreview((prev) => {
        if (!prev || prev.anchorId !== cellColorHoverAnchorId) return prev;
        return {
          ...prev,
          style: computeAnnualCellColorHoverTooltipStyle(el.getBoundingClientRect()),
        };
      });
    };
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [cellColorHoverAnchorId]);

  const flushWeekActivityMemoModalIfDirty = useCallback(async (): Promise<boolean> => {
    const m = weekActivityMemoModalRef.current;
    if (!m || !workspaceId) return false;
    const draftSnapshot = serializeWeekActivityMemoDraft(m.activityLinesDraft, m.generalMemoDraft);
    if (m.baselineSnapshot === draftSnapshot) {
      return false;
    }
    const trimmedLines = [...m.activityLinesDraft.map((s) => s.trim())];
    while (trimmedLines.length > 0 && trimmedLines[trimmedLines.length - 1] === '') {
      trimmedLines.pop();
    }
    const key = weekMemoStateKey(m.termNumber, m.gradeId, m.mondayIso);
    const existingId = annualWeekMemosByKeyRef.current[key]?.id;
    try {
      const saved = await upsertAnnualTimetableWeekMemo({
        ...(existingId ? { id: existingId } : {}),
        workspace_id: workspaceId,
        grade_id: m.gradeId,
        academic_year: academicYear,
        term_number: m.termNumber,
        monday_iso: m.mondayIso,
        activity_lines: trimmedLines,
        general_memo: m.generalMemoDraft.trim(),
      });
      setAnnualWeekMemosByKey((prev) => ({
        ...prev,
        [key]: {
          id: saved.id,
          activityLines: saved.activity_lines,
          generalMemo: saved.general_memo,
        },
      }));
      setWeekActivityMemoModal(null);
      setWeekActivityMemoAnchorStyle(null);
      return true;
    } catch (e) {
      showError(e instanceof Error ? e.message : '주 메모 저장에 실패했습니다.');
      return false;
    }
  }, [academicYear, workspaceId, showError]);

  const closeWeekActivityMemoModal = useCallback(() => {
    setWeekActivityMemoModal(null);
    setWeekActivityMemoAnchorStyle(null);
  }, []);

  const saveWeekActivityMemoModal = useCallback(async () => {
    const m = weekActivityMemoModalRef.current;
    if (!m || !workspaceId) {
      closeWeekActivityMemoModal();
      return;
    }
    if (m.baselineSnapshot === serializeWeekActivityMemoDraft(m.activityLinesDraft, m.generalMemoDraft)) {
      closeWeekActivityMemoModal();
      return;
    }
    const trimmedLines = [...m.activityLinesDraft.map((s) => s.trim())];
    while (trimmedLines.length > 0 && trimmedLines[trimmedLines.length - 1] === '') {
      trimmedLines.pop();
    }
    const key = weekMemoStateKey(m.termNumber, m.gradeId, m.mondayIso);
    const existingId = annualWeekMemosByKeyRef.current[key]?.id;
    try {
      const saved = await upsertAnnualTimetableWeekMemo({
        ...(existingId ? { id: existingId } : {}),
        workspace_id: workspaceId,
        grade_id: m.gradeId,
        academic_year: academicYear,
        term_number: m.termNumber,
        monday_iso: m.mondayIso,
        activity_lines: trimmedLines,
        general_memo: m.generalMemoDraft.trim(),
      });
      setAnnualWeekMemosByKey((prev) => ({
        ...prev,
        [key]: {
          id: saved.id,
          activityLines: saved.activity_lines,
          generalMemo: saved.general_memo,
        },
      }));
      closeWeekActivityMemoModal();
    } catch (e) {
      showError(e instanceof Error ? e.message : '주 메모 저장에 실패했습니다.');
    }
  }, [academicYear, closeWeekActivityMemoModal, workspaceId, showError]);

  const openWeekActivityMemoModal = useCallback(
    (params: {
      termKey: '1' | '2';
      termNumber: 1 | 2;
      gradeId: string;
      mondayIso: string;
      weekIndex: number;
      dateRangeDisplay: string;
    }) => {
      const { termKey, termNumber, gradeId, mondayIso, weekIndex, dateRangeDisplay } = params;
      clearAnnualCellMemoLongPressTimer();
      setAnnualCellMemoModal(null);
      setAnnualCellMemoAnchorStyle(null);
      setEditingCell(null);
      setDragState(null);
      dragStateRef.current = null;
      setWeekMemoHoverPreview(null);
      const key = weekMemoStateKey(termNumber, gradeId, mondayIso);
      const stored = annualWeekMemosByKey[key];
      const generalMemoDraft = stored?.generalMemo ?? '';
      const activityLinesDraft =
        stored?.activityLines && stored.activityLines.length > 0 ? [...stored.activityLines] : [''];
      setWeekActivityMemoModal({
        termKey,
        termNumber,
        gradeId,
        mondayIso,
        weekIndex,
        dateRangeDisplay,
        activityLinesDraft,
        generalMemoDraft,
        baselineSnapshot: serializeWeekActivityMemoDraft(activityLinesDraft, generalMemoDraft),
      });
    },
    [annualWeekMemosByKey, clearAnnualCellMemoLongPressTimer]
  );

  const commitEdit = useCallback(
    (gradeId: string, mondayIso: string, dayKey: string, periodIdx: number, value: string) => {
      const key = compoundCellKey(gradeId, mondayIso, dayKey, periodIdx);
      const current = effectiveCellsByKeyRef.current[key];
      if (!current || !current.isEditable) {
        setEditingCell(null);
        return;
      }
      applyCellUpdates([{ key, textValue: value }]);
      setEditingCell(null);
    },
    [applyCellUpdates]
  );

  /**
   * 사용자 편집·편집 중 초안만 이탈 확인 대상.
   * 학사일정 자동 정렬(화면≠DB baseline)은 저장 시 seed로 반영하며, 편집 없이 모달을 띄우지 않는다.
   */
  const hasUnsavedAnnualTimetableWork = useCallback((): boolean => {
    if (dirtyCellKeysRef.current.length > 0) return true;
    if (programColorsDirtyRef.current) return true;
    if (pendingLeaderAnnualOnlyPersistRef.current) return true;
    const ec = editingCellRef.current;
    if (ec) {
      const key = compoundCellKey(ec.gradeId, ec.mondayIso, ec.dayKey, ec.periodIdx);
      const cur = effectiveCellsByKeyRef.current[key];
      if (cur?.isEditable) {
        const live = editingInputRef.current?.value ?? ec.draft ?? '';
        if (live !== (cur.textValue ?? '')) return true;
      }
    }
    const mm = annualCellMemoModalRef.current;
    if (mm) {
      const cur = effectiveCellsByKeyRef.current[mm.compoundKey];
      if (mm.memoDraft.trim() !== (cur?.memo ?? '').trim()) return true;
    }
    const wm = weekActivityMemoModalRef.current;
    if (wm) {
      if (
        wm.baselineSnapshot !== serializeWeekActivityMemoDraft(wm.activityLinesDraft, wm.generalMemoDraft)
      ) {
        return true;
      }
    }
    return false;
  }, []);

  const dayKeysForSelection = React.useMemo(
    () => (showSaturday ? WEEKDAY_KEYS : WEEKDAY_KEYS.filter((w) => w.key !== 'sat')).map((w) => w.key),
    [showSaturday]
  );

  useEffect(() => {
    if (!workspaceId || classEntries.length === 0) {
      annualLocalUndoStackRef.current = [];
      annualLocalRedoStackRef.current = [];
      notifyAnnualUndoRedoStacksRef.current();
      // 이미 비어 있으면 setState 생략 — ttPageData.* 참조가 매 렌더 바뀌어도 루프 방지
      const clearIfNonEmpty = <T extends Record<string, unknown>>(prev: T): T =>
        Object.keys(prev).length === 0 ? prev : ({} as T);
      setCellData((prev) => clearIfNonEmpty(prev));
      setCellMemos((prev) => clearIfNonEmpty(prev));
      setCellColors((prev) => clearIfNonEmpty(prev));
      setCellSlotKinds((prev) => clearIfNonEmpty(prev));
      setCellRecordIds((prev) => clearIfNonEmpty(prev));
      setCellBaseExcluded((prev) => clearIfNonEmpty(prev));
      setDirtyCellKeys((prev) => (prev.length === 0 ? prev : []));
      annualCellSavedBaselineRef.current = {};
      setAnnualWeekMemosByKey((prev) => clearIfNonEmpty(prev));
      setLoadedCellScopeKey((prev) => (prev === '' ? prev : ''));
      return;
    }
    if (!ttPageData.isReady) return;

    const byKey = ttPageData.cellsByKey;
    const isScopeChange = loadedCellScopeKey !== expectedCellScopeKey;

    if (!isScopeChange) {
      const dirtySet = new Set(dirtyCellKeysRef.current);
      const keysToSync = Object.keys(byKey).filter((key) => {
        if (dirtySet.has(key)) return false;
        const cell = byKey[key];
        if (!cell) return false;
        if (!(key in cellRecordIdsRef.current)) return true;
        const baseline = annualCellSavedBaselineRef.current[key];
        const nextText = cell.text_value ?? '';
        const nextMemo = (cell.memo ?? '').trim();
        const nextColor = (cell.color_hex ?? '').trim();
        const nextKind = cell.slot_kind ?? 'class';
        const nextExcluded =
          typeof cell.is_base_excluded === 'boolean'
            ? cell.is_base_excluded
            : isBaseExcludeColor((cell.color_hex ?? '').trim(), baseExcludeColors);
        if (!baseline) return true;
        return (
          baseline.textValue !== nextText ||
          baseline.memo !== nextMemo ||
          baseline.colorHex !== nextColor ||
          baseline.slotKind !== nextKind ||
          baseline.isBaseExcluded !== nextExcluded
        );
      });
      if (keysToSync.length === 0) return;
      setCellData((prev) => {
        const next = { ...prev };
        keysToSync.forEach((key) => {
          next[key] = byKey[key]?.text_value ?? '';
        });
        return next;
      });
      setCellMemos((prev) => {
        const next = { ...prev };
        keysToSync.forEach((key) => {
          const memo = (byKey[key]?.memo ?? '').trim();
          if (memo) next[key] = memo;
          else delete next[key];
        });
        return next;
      });
      setCellColors((prev) => {
        const next = { ...prev };
        keysToSync.forEach((key) => {
          const color = byKey[key]?.color_hex ?? '';
          if (color) next[key] = color;
          else delete next[key];
        });
        return next;
      });
      setCellSlotKinds((prev) => {
        const next = { ...prev };
        keysToSync.forEach((key) => {
          next[key] = byKey[key]?.slot_kind ?? 'class';
        });
        return next;
      });
      setCellRecordIds((prev) => {
        const next = { ...prev };
        keysToSync.forEach((key) => {
          const id = byKey[key]?.id;
          if (id) next[key] = id;
        });
        return next;
      });
      setCellBaseExcluded((prev) => {
        const next = { ...prev };
        keysToSync.forEach((key) => {
          const cell = byKey[key];
          if (!cell) return;
          next[key] =
            typeof cell.is_base_excluded === 'boolean'
              ? cell.is_base_excluded
              : isBaseExcludeColor((cell.color_hex ?? '').trim(), baseExcludeColors);
        });
        return next;
      });
      const baseline = { ...annualCellSavedBaselineRef.current };
      keysToSync.forEach((key) => {
        const cell = byKey[key];
        if (!cell) return;
        baseline[key] = {
          textValue: cell.text_value ?? '',
          memo: (cell.memo ?? '').trim(),
          colorHex: (cell.color_hex ?? '').trim(),
          slotKind: cell.slot_kind ?? 'class',
          isBaseExcluded:
            typeof cell.is_base_excluded === 'boolean'
              ? cell.is_base_excluded
              : isBaseExcludeColor((cell.color_hex ?? '').trim(), baseExcludeColors),
        };
      });
      annualCellSavedBaselineRef.current = baseline;
      return;
    }

    annualLocalUndoStackRef.current = [];
    annualLocalRedoStackRef.current = [];
    notifyAnnualUndoRedoStacksRef.current();

    setAnnualWeekMemosByKey(ttPageData.weekMemosByKey);

    const nextData: Record<string, string> = {};
    const nextMemos: Record<string, string> = {};
    const nextColors: Record<string, string> = {};
    const nextKinds: Record<string, AnnualTimetableSlotKind> = {};
    const nextIds: Record<string, string> = {};
    const nextBaseExcluded: Record<string, boolean> = {};
    Object.entries(byKey).forEach(([key, cell]) => {
      nextData[key] = cell.text_value ?? '';
      nextMemos[key] = (cell.memo ?? '').trim();
      nextColors[key] = cell.color_hex ?? '';
      nextKinds[key] = cell.slot_kind;
      nextIds[key] = cell.id;
      const ex =
        typeof cell.is_base_excluded === 'boolean'
          ? cell.is_base_excluded
          : isBaseExcludeColor((cell.color_hex ?? '').trim(), baseExcludeColors);
      nextBaseExcluded[key] = ex;
    });
    setCellData(nextData);
    setCellMemos(nextMemos);
    setCellColors(nextColors);
    setCellSlotKinds(nextKinds);
    setCellRecordIds(nextIds);
    setCellBaseExcluded(nextBaseExcluded);
    setDirtyCellKeys([]);
    const baseline: Record<string, AnnualCellPersistSnapshot> = {};
    Object.keys(nextData).forEach((key) => {
      baseline[key] = {
        textValue: nextData[key] ?? '',
        memo: (nextMemos[key] ?? '').trim(),
        colorHex: (nextColors[key] ?? '').trim(),
        slotKind: nextKinds[key] ?? 'class',
        isBaseExcluded: nextBaseExcluded[key] ?? false,
      };
    });
    annualCellSavedBaselineRef.current = baseline;
    setLoadedCellScopeKey(expectedCellScopeKey);
  }, [
    baseExcludeColors,
    classEntries,
    expectedCellScopeKey,
    loadedCellScopeKey,
    ttPageData.cellsByKey,
    ttPageData.isReady,
    ttPageData.weekMemosByKey,
    workspaceId,
  ]);

  useEffect(() => {
    if (ttPageData.error) {
      setError(ttPageData.error);
    }
  }, [ttPageData.error]);

  const isCompoundCellEditable = useCallback(
    (compoundKey: string, weeks: WeekInfo[]) => {
      void weeks;
      return effectiveCellsByKeyRef.current[compoundKey]?.isEditable ?? false;
    },
    []
  );

  const handleCellMouseDown = useCallback(
    (compoundKey: string, isEditable: boolean, weeks: WeekInfo[], shiftKey: boolean) => {
      annualDateHeadActiveRef.current = false;
      setAnnualDateHeadPicker(null);

      if (
        shiftKey &&
        selectionRangeAnchorRef.current &&
        gradeIdFromCompound(selectionRangeAnchorRef.current) === gradeIdFromCompound(compoundKey)
      ) {
        const keys = weeklyRectKeysCompound(
          selectionRangeAnchorRef.current,
          compoundKey,
          weeks,
          dayKeysForSelection
        );
        dragStateRef.current = null;
        setDragState(null);
        setSelectedCells(keys);
        return;
      }

      selectionRangeAnchorRef.current = compoundKey;

      if (!isEditable) {
        setSelectedCells([compoundKey]);
        setDragState(null);
        return;
      }
      const isSameCell =
        editingCell &&
        compoundKey ===
          compoundCellKey(
            editingCell.gradeId,
            editingCell.mondayIso,
            editingCell.dayKey,
            editingCell.periodIdx
          );
      if (editingCell && !isSameCell) {
        commitEdit(
          editingCell.gradeId,
          editingCell.mondayIso,
          editingCell.dayKey,
          editingCell.periodIdx,
          editingInputRef.current?.value ?? editingCell.draft ?? ''
        );
      }
      const newDrag = { active: true, anchorKey: compoundKey, currentKey: compoundKey };
      dragStateRef.current = newDrag;
      setSelectedCells([compoundKey]);
      setDragState(newDrag);
    },
    [commitEdit, dayKeysForSelection, editingCell]
  );

  const handleCellMouseEnter = useCallback(
    (compoundKey: string, _weeks: WeekInfo[]) => {
      if (annualCellMemoLongPressTimerRef.current != null) {
        const anchor = annualCellMemoLongPressAnchorRef.current;
        if (anchor && compoundKey !== anchor) clearAnnualCellMemoLongPressTimer();
      }
      const state = dragStateRef.current;
      if (!state?.active) return;
      /* 사각형 안의 비편집 셀(휴일 등)도 키에 포함 → 시각적으로 한 덩어리.
       * 색/붙여넣기 등 실제 변경은 applyCellUpdates에서 isEditable만 처리. */
      const keys = weeklyRectKeysCompound(state.anchorKey, compoundKey, _weeks, dayKeysForSelection);
      setDragState((s) => (s ? { ...s, currentKey: compoundKey } : null));
      setSelectedCells(keys);
    },
    [clearAnnualCellMemoLongPressTimer, dayKeysForSelection]
  );

  /** 키로부터 편집 상태 구성 */
  const buildEditingCellFromCompoundKey = useCallback(
    (compoundKey: string) => {
      const gid = gradeIdFromCompound(compoundKey);
      const wk = weeklyPartFromCompound(compoundKey);
      if (!gid || !wk) return null;
      const parsed = parseWeeklyCellKey(wk);
      if (!parsed) return null;
      const currentValue = cellDataRef.current[compoundKey] ?? '';
      return {
        gradeId: gid,
        mondayIso: parsed.mondayIso,
        dayKey: parsed.dayKey,
        periodIdx: parsed.periodIdx,
        draft: currentValue,
      };
    },
    []
  );

  /** 단일 선택 셀을 기준으로 키보드 이동 (step: +1 오른쪽, -1 왼쪽, +N 아래, -N 위) */
  const moveSingleSelectionByFlatStep = useCallback(
    (step: number, options?: { skipAutoEditOnce?: boolean }): boolean => {
      const shouldSkipAutoEdit = options?.skipAutoEditOnce ?? true;
      const currentSel = selectedCellsRef.current;
      if (currentSel.length !== 1) return false;
      const currentKey = currentSel[0];
      const gradeId = gradeIdFromCompound(currentKey);
      const idx = weeklyKeyToIndicesCompound(currentKey, mergedWeeks, dayKeysForSelection);
      if (!gradeId || !idx) return false;
      const dayCount = dayKeysForSelection.length;
      if (dayCount <= 0 || mergedWeeks.length <= 0) return false;
      const perWeek = dayCount * MAX_PERIODS;
      const total = mergedWeeks.length * perWeek;
      const currentFlat = idx.weekIdx * perWeek + idx.dayIdx * MAX_PERIODS + idx.periodIdx;
      for (let flat = currentFlat + step; flat >= 0 && flat < total; flat += step) {
        const weekIdx = Math.floor(flat / perWeek);
        const inWeek = flat % perWeek;
        const dayIdx = Math.floor(inWeek / MAX_PERIODS);
        const periodIdx = inWeek % MAX_PERIODS;
        const week = mergedWeeks[weekIdx];
        const dayKey = dayKeysForSelection[dayIdx];
        if (!week || !dayKey) continue;
        const nextKey = compoundCellKey(gradeId, week.mondayIso, dayKey, periodIdx);
        const cell = effectiveCellsByKeyRef.current[nextKey];
        if (!cell?.isEditable) continue;
        skipAutoEditOnceRef.current = shouldSkipAutoEdit;
        setSelectedCells([nextKey]);
        setDragState(null);
        setCopiedSourceRegion(null);
        if (!shouldSkipAutoEdit) {
          const nextEditing = buildEditingCellFromCompoundKey(nextKey);
          if (nextEditing) setEditingCell(nextEditing);
        }
        return true;
      }
      // 이동 불가여도 확정 직후 자동 재편집은 막는다.
      skipAutoEditOnceRef.current = shouldSkipAutoEdit;
      return false;
    },
    [buildEditingCellFromCompoundKey, dayKeysForSelection, mergedWeeks]
  );

  useLayoutEffect(() => {
    if (!editingCell) return undefined;
    const input = editingInputRef.current;
    if (!input) return undefined;
    input.focus({ preventScroll: true });
    input.select();
    const raf = window.requestAnimationFrame(() => {
      input.select();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    editingCell?.gradeId,
    editingCell?.mondayIso,
    editingCell?.dayKey,
    editingCell?.periodIdx,
  ]);

  /**
   * 단일 선택: 클릭 한 번은 선택만, 편집은 더블클릭 또는 선택 상태에서 Enter.
   * 키보드로 셀만 옮긴 뒤(moveSingleSelectionByFlatStep) 즉시 편집할 때는 해당 함수에서 setEditingCell 처리.
   * 선택이 바뀌어 편집 중인 셀과 어긋나면 커밋 후 편집 종료.
   */
  useEffect(() => {
    if (selectedCells.length !== 1) {
      const ec = editingCellRef.current;
      if (ec) {
        commitEdit(
          ec.gradeId,
          ec.mondayIso,
          ec.dayKey,
          ec.periodIdx,
          editingInputRef.current?.value ?? ec.draft ?? ''
        );
      }
      setEditingCell(null);
      return;
    }
    if (dragState?.active) return;
    if (skipAutoEditOnceRef.current) {
      skipAutoEditOnceRef.current = false;
      setEditingCell(null);
      return;
    }
    const compoundKey = selectedCells[0];
    const ec = editingCellRef.current;
    if (!ec) return;
    const same =
      compoundKey ===
      compoundCellKey(ec.gradeId, ec.mondayIso, ec.dayKey, ec.periodIdx);
    if (same) return;
    commitEdit(
      ec.gradeId,
      ec.mondayIso,
      ec.dayKey,
      ec.periodIdx,
      editingInputRef.current?.value ?? ec.draft ?? ''
    );
    setEditingCell(null);
  }, [selectedCells, dragState?.active, commitEdit]);

  const handleCellDoubleClick = useCallback(
    (compoundKey: string, isEditable: boolean) => {
      if (!isEditable) return;
      clearAnnualCellMemoLongPressTimer();
      const ec = editingCellRef.current;
      const isSame =
        ec &&
        compoundKey === compoundCellKey(ec.gradeId, ec.mondayIso, ec.dayKey, ec.periodIdx);
      if (ec && !isSame) {
        commitEdit(
          ec.gradeId,
          ec.mondayIso,
          ec.dayKey,
          ec.periodIdx,
          editingInputRef.current?.value ?? ec.draft ?? ''
        );
      }
      setSelectedCells([compoundKey]);
      setDragState(null);
      const next = buildEditingCellFromCompoundKey(compoundKey);
      if (next) setEditingCell(next);
    },
    [buildEditingCellFromCompoundKey, clearAnnualCellMemoLongPressTimer, commitEdit]
  );

  /** 단일 선택·비편집(입력 포커스 없음): Tab/화살표는 선택 이동, Enter는 편집 모드 진입(편집 중 Tab/Enter는 확정 후 이동·더블클릭으로도 편집) */
  useEffect(() => {
    if (!isActive) return;
    const onDocKeyDownTrapTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const inAnnualTable = !!target.closest('.cp-annual-timetable-table-wrap');
      if (!inAnnualTable) return;
      // 브라우저 기본 탭 포커스(외부 버튼으로 이동)를 선차단
      e.preventDefault();
    };
    const onDocKeyDownMove = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === 'Tab' && suppressGlobalTabOnceRef.current) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const inAnnualTable = !!target.closest('.cp-annual-timetable-table-wrap');
      const inInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        (target instanceof HTMLElement && target.isContentEditable) ||
        !!target.closest('.cp-modal');
      // 연간 테이블 안에서는 Tab 기본 포커스 이동을 막고 셀 이동 로직만 사용한다.
      if (e.key === 'Tab' && inAnnualTable) {
        e.preventDefault();
      }
      if (inInput) return;
      if (editingCellRef.current) return;
      if (selectedCellsRef.current.length !== 1) return;

      const perWeekRow = dayKeysForSelection.length * MAX_PERIODS;

      if (
        (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'ArrowLeft') moveSingleSelectionByFlatStep(-1, { skipAutoEditOnce: true });
        else if (e.key === 'ArrowRight') moveSingleSelectionByFlatStep(1, { skipAutoEditOnce: true });
        else if (e.key === 'ArrowUp') moveSingleSelectionByFlatStep(-perWeekRow, { skipAutoEditOnce: true });
        else moveSingleSelectionByFlatStep(perWeekRow, { skipAutoEditOnce: true });
        return;
      }

      if (e.key === 'Tab') {
        e.stopPropagation();
        moveSingleSelectionByFlatStep(e.shiftKey ? -1 : 1, { skipAutoEditOnce: true });
        return;
      }
      if (e.key === 'Enter') {
        const compoundKey = selectedCellsRef.current[0];
        if (!compoundKey) return;
        if (!effectiveCellsByKeyRef.current[compoundKey]?.isEditable) return;
        e.preventDefault();
        e.stopPropagation();
        setDragState(null);
        const next = buildEditingCellFromCompoundKey(compoundKey);
        if (next) setEditingCell(next);
      }
    };
    document.addEventListener('keydown', onDocKeyDownTrapTab, true);
    document.addEventListener('keydown', onDocKeyDownMove);
    return () => {
      document.removeEventListener('keydown', onDocKeyDownTrapTab, true);
      document.removeEventListener('keydown', onDocKeyDownMove);
    };
  }, [buildEditingCellFromCompoundKey, dayKeysForSelection.length, isActive, moveSingleSelectionByFlatStep]);

  /** 선택 셀(비편집): Del/Backspace로 시간표명(텍스트)만 비움. 색·메모·슬롯종류는 유지 */
  useEffect(() => {
    if (!isActive) return;
    const onKeyDownClearText = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (editingCellRef.current) return;
      if (annualCellMemoModalRef.current) return;
      if (weekActivityMemoModalRef.current) return;
      const target = e.target;
      if (target instanceof Element) {
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          (target instanceof HTMLElement && target.isContentEditable) ||
          !!target.closest('.cp-modal, [role="dialog"]')
        ) {
          return;
        }
      }
      const keys = filterEditableSelectionKeys(selectedCellsRef.current).filter(
        (key) => (effectiveCellsByKeyRef.current[key]?.textValue ?? '').trim().length > 0
      );
      if (keys.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      applyCellUpdates(keys.map((key) => ({ key, textValue: '' })));
    };
    document.addEventListener('keydown', onKeyDownClearText, true);
    return () => document.removeEventListener('keydown', onKeyDownClearText, true);
  }, [applyCellUpdates, filterEditableSelectionKeys, isActive]);

  /** 바깥 클릭 시 선택 해제 및 편집 커밋 */
  useEffect(() => {
    if (!isActive) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // 사이드·상단 네비 클릭은 선택 해제 setState로 click이 삼켜지지 않게 그대로 통과
      if (
        target.closest(
          '.cp-sidebar, .cp-sidebar-shell, .cp-topnav, .cp-layout-root > .cp-modal-backdrop, [aria-modal="true"]'
        )
      ) {
        return;
      }
      if (target.closest('.cp-annual-timetable-class-section')) return;
      // 상단 액션(고정 프로그램/단축일 모달 열기) 클릭 시에는 기존 셀 선택을 유지한다.
      if (target.closest('.cp-annual-top-actions')) return;
      if (target.closest('.cp-annual-class-block-actions')) return;
      if (target.closest('.cp-annual-title-with-saturday')) return;
      const ec = editingCellRef.current;
      if (ec) {
        commitEdit(
          ec.gradeId,
          ec.mondayIso,
          ec.dayKey,
          ec.periodIdx,
          editingInputRef.current?.value ?? ec.draft ?? ''
        );
      }
      // setSelectedCells([])는 매번 새 []라 불필요 리렌더→사이드바 click 유실을 막기 위해 실제 변경 시에만
      setSelectedCells((prev) => (prev.length === 0 ? prev : []));
      setDragState((s) => (s == null ? s : null));
      setCopiedSourceRegion((s) => (s == null ? s : null));
    };
    const onDocMouseUp = () => {
      clearAnnualCellMemoLongPressTimer();
      setDragState((s) => {
        if (s) {
          dragStateRef.current = { ...s, active: false };
          return dragStateRef.current;
        }
        dragStateRef.current = null;
        return null;
      });
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('mouseup', onDocMouseUp, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('mouseup', onDocMouseUp, true);
    };
  }, [clearAnnualCellMemoLongPressTimer, commitEdit, isActive]);

  const getSelectionTopLeft = useCallback(
    (weeks: WeekInfo[], keys?: string[]) => {
      const keysToUse = keys ?? selectedCellsRef.current;
      if (keysToUse.length === 0) return null;
      const gradeId = gradeIdFromCompound(keysToUse[0]);
      if (!gradeId || keysToUse.some((k) => gradeIdFromCompound(k) !== gradeId)) return null;
      let minWi = weeks.length;
      let minDi = dayKeysForSelection.length;
      let minPi = MAX_PERIODS;
      for (const key of keysToUse) {
        const idx = weeklyKeyToIndicesCompound(key, weeks, dayKeysForSelection);
        if (!idx) continue;
        if (idx.weekIdx < minWi) minWi = idx.weekIdx;
        if (idx.dayIdx < minDi) minDi = idx.dayIdx;
        if (idx.periodIdx < minPi) minPi = idx.periodIdx;
      }
      if (minWi >= weeks.length || minDi >= dayKeysForSelection.length || minPi >= MAX_PERIODS) return null;
      const week = weeks[minWi];
      const dayKey = dayKeysForSelection[minDi];
      return week && dayKey
        ? {
            minWeekIdx: minWi,
            minDayIdx: minDi,
            minPeriodIdx: minPi,
            gradeId,
            originKey: compoundCellKey(gradeId, week.mondayIso, dayKey, minPi),
          }
        : null;
    },
    [dayKeysForSelection]
  );

  const getEffectiveCellValue = useCallback(
    (compoundKey: string): {
      value: string;
      memo: string;
      colorHex: string;
      slotKind: AnnualTimetableSlotKind;
      isBaseExcluded: boolean;
    } => {
      const ec = editingCellRef.current;
      const wk = weeklyPartFromCompound(compoundKey);
      const parsed = wk ? parseWeeklyCellKey(wk) : null;
      const gid = gradeIdFromCompound(compoundKey);
      const runtimeCell = effectiveCellsByKeyRef.current[compoundKey];
      const isEditing =
        parsed &&
        ec &&
        gid === ec.gradeId &&
        parsed.mondayIso === ec.mondayIso &&
        parsed.dayKey === ec.dayKey &&
        parsed.periodIdx === ec.periodIdx;
      return {
        value: isEditing
          ? (editingInputRef.current?.value ?? ec?.draft ?? '')
          : (runtimeCell?.textValue ?? ''),
        memo: runtimeCell?.memo ?? '',
        colorHex: runtimeCell?.colorHex ?? '',
        slotKind: runtimeCell?.slotKind ?? 'class',
        isBaseExcluded: runtimeCell?.isBaseExcluded ?? false,
      };
    },
    []
  );

  const handleCopy = useCallback(
    (weeks: WeekInfo[], keys?: string[]) => {
      const keysToUse = filterEditableSelectionKeys(keys ?? selectedCellsRef.current);
      if (keysToUse.length === 0) return;
      const origin = getSelectionTopLeft(weeks, keysToUse);
      if (!origin) return;
      const cells = keysToUse.map((key) => {
        const idx = weeklyKeyToIndicesCompound(key, weeks, dayKeysForSelection);
        if (!idx) return null;
        const { value, memo, colorHex, slotKind, isBaseExcluded } = getEffectiveCellValue(key);
        return {
          weekIdx: idx.weekIdx - origin.minWeekIdx,
          dayIdx: idx.dayIdx - origin.minDayIdx,
          periodIdx: idx.periodIdx - origin.minPeriodIdx,
          value: String(value).trim(),
          memo: String(memo).trim(),
          colorHex,
          slotKind,
          isBaseExcluded,
        };
      }).filter((c): c is NonNullable<typeof c> => c != null);
      copiedCellsRef.current = {
        cells,
        sourceGradeId: cells.length > 1 ? origin.gradeId : undefined,
      };
      setHasCopiedData(true);
      setCopiedSourceRegion([...keysToUse]);
    },
    [dayKeysForSelection, filterEditableSelectionKeys, getEffectiveCellValue, getSelectionTopLeft]
  );

  const handleCut = useCallback(
    (weeks: WeekInfo[], keys?: string[]) => {
      const keysToUse = filterEditableSelectionKeys(keys ?? selectedCellsRef.current);
      if (keysToUse.length === 0) return;
      const origin = getSelectionTopLeft(weeks, keysToUse);
      if (!origin) return;
      const cells = keysToUse.map((key) => {
        const idx = weeklyKeyToIndicesCompound(key, weeks, dayKeysForSelection);
        if (!idx) return null;
        const { value, memo, colorHex, slotKind, isBaseExcluded } = getEffectiveCellValue(key);
        return {
          weekIdx: idx.weekIdx - origin.minWeekIdx,
          dayIdx: idx.dayIdx - origin.minDayIdx,
          periodIdx: idx.periodIdx - origin.minPeriodIdx,
          value: String(value).trim(),
          memo: String(memo).trim(),
          colorHex,
          slotKind,
          isBaseExcluded,
        };
      }).filter((c): c is NonNullable<typeof c> => c != null);
      copiedCellsRef.current = {
        cells,
        isCut: true,
        sourceKeys: [...keysToUse],
        sourceGradeId: cells.length > 1 ? origin.gradeId : undefined,
      };
      setHasCopiedData(true);
      setCopiedSourceRegion([...keysToUse]);
    },
    [dayKeysForSelection, filterEditableSelectionKeys, getEffectiveCellValue, getSelectionTopLeft]
  );

  const handlePaste = useCallback(
    (weeks: WeekInfo[], pasteTargetKeys?: string[]) => {
      const copied = copiedCellsRef.current;
      const targetKeys = pasteTargetKeys ?? selectedCellsRef.current;
      if (!copied?.cells?.length || targetKeys.length === 0) return;
      const targetGradeId = gradeIdFromCompound(targetKeys[0]);
      if (
        copied.cells.length > 1 &&
        copied.sourceGradeId &&
        targetGradeId &&
        copied.sourceGradeId !== targetGradeId
      ) {
        return;
      }
      setCopiedSourceRegion(null);

      /** 붙여넣기는 표시(텍스트·메모·색·제외)만 반영하고, 활동/비수업(slotKind)은 대상 셀 유지 — 수업·활동·비수업 버튼으로만 변경 */
      let updates: {
        key: string;
        value: string;
        memo: string;
        colorHex: string;
        isBaseExcluded: boolean;
      }[];
      if (copied.cells.length === 1) {
        const single = copied.cells[0];
        updates = targetKeys.map((key) => ({
          key,
          value: single.value,
          memo: single.memo,
          colorHex: single.colorHex,
          isBaseExcluded: single.isBaseExcluded,
        }));
      } else {
        const origin = getSelectionTopLeft(weeks, targetKeys);
        if (!origin) return;
        updates = copied.cells
          .map((cell) => {
            const tw = weeks[origin.minWeekIdx + cell.weekIdx];
            const td = dayKeysForSelection[origin.minDayIdx + cell.dayIdx];
            const targetPeriodIdx = origin.minPeriodIdx + cell.periodIdx;
            if (!tw || !td || targetPeriodIdx < 0 || targetPeriodIdx >= MAX_PERIODS) return null;
            return {
              key: compoundCellKey(origin.gradeId, tw.mondayIso, td, targetPeriodIdx),
              value: cell.value,
              memo: cell.memo,
              colorHex: cell.colorHex,
              isBaseExcluded: cell.isBaseExcluded,
            };
          })
          .filter((cell): cell is NonNullable<typeof cell> => cell != null);
      }
      const finalUpdates = updates.map((update) => ({
        key: update.key,
        textValue: update.value,
        memo: update.memo,
        colorHex: update.colorHex,
        isBaseExcluded: update.isBaseExcluded,
      }));
      if (copied.isCut && copied.sourceKeys?.length) {
        const clearUpdates = copied.sourceKeys.map((key) => {
          const current = effectiveCellsByKeyRef.current[key];
          return {
            key,
            textValue: '',
            memo: '',
            colorHex: '',
            slotKind: current?.slotKind ?? 'class',
            isBaseExcluded: false,
          };
        });
        const merged = new Map<string, (typeof finalUpdates)[number]>();
        clearUpdates.forEach((u) => merged.set(u.key, u));
        finalUpdates.forEach((u) => merged.set(u.key, u));
        applyCellUpdates(Array.from(merged.values()));
      } else {
        applyCellUpdates(finalUpdates);
      }
      const ec = editingCellRef.current;
      if (ec) {
        const editingKey = compoundCellKey(ec.gradeId, ec.mondayIso, ec.dayKey, ec.periodIdx);
        const editingUpdate = updates.find((update) => update.key === editingKey);
        if (editingUpdate) {
          if (editingInputRef.current) editingInputRef.current.value = editingUpdate.value;
        }
      }
      if (copied.isCut && copied.sourceKeys) {
        copiedCellsRef.current = { cells: copied.cells, sourceGradeId: copied.sourceGradeId };
      }
    },
    [applyCellUpdates, dayKeysForSelection, getSelectionTopLeft]
  );

  /** 선택 셀: 시간표명·배경색(고정·전담·제외·프로젝트 등)·일반 메모만 제거. 공휴일·활동·비수업(slotKind) 표시는 유지. */
  const handleClearSelectedCellDisplayData = useCallback(
    (weeks: WeekInfo[]) => {
      const keys = filterEditableSelectionKeys(selectedCellsRef.current).filter((k) =>
        areAllKeysInWeeks([k], weeks, dayKeysForSelection)
      );
      if (keys.length === 0) return;
      const ec = editingCellRef.current;
      if (ec) {
        const editingKey = compoundCellKey(ec.gradeId, ec.mondayIso, ec.dayKey, ec.periodIdx);
        if (keys.includes(editingKey)) {
          commitEdit(
            ec.gradeId,
            ec.mondayIso,
            ec.dayKey,
            ec.periodIdx,
            editingInputRef.current?.value ?? ec.draft ?? ''
          );
        }
      }
      const mm = annualCellMemoModalRef.current;
      if (mm && keys.includes(mm.compoundKey)) {
        setAnnualCellMemoModal(null);
      }
      const updates = keys.map((key) => {
        const cur = effectiveCellsByKeyRef.current[key];
        const memoTrim = (cur?.memo ?? '').trim();
        const memoNext = isAnnualShortDayTailMemo(memoTrim) ? ANNUAL_SHORT_DAY_TAIL_MEMO : '';
        return { key, textValue: '', colorHex: '', isBaseExcluded: false, memo: memoNext };
      });
      applyCellUpdates(updates);
      setDragState(null);
    },
    [applyCellUpdates, commitEdit, dayKeysForSelection, filterEditableSelectionKeys]
  );

  /** 이 학기 주 목록에서 오늘이 속한 요일 열의 교시 10칸(한 날짜)을 모두 선택 */
  const handleTodayLessonInAnnualTimetable = useCallback(
    (wks: WeekInfo[], gradeId: string) => {
      if (wks.length === 0) return;
      const todayIso = toISODate(new Date());
      const weekdayKeysToShow = showSaturday ? WEEKDAY_KEYS : WEEKDAY_KEYS.filter((w) => w.key !== 'sat');
      const todayJs = new Date();
      if (todayJs.getDay() === 6 && !showSaturday) {
        showInfo('오늘이 토요일입니다. 상단에서 토요일 표시를 켜면 해당 열을 선택할 수 있습니다.');
        return;
      }
      for (const week of wks) {
        for (let dayIdx = 0; dayIdx < weekdayKeysToShow.length; dayIdx += 1) {
          const dateIso = week.datesByWeekday[dayIdx] ?? '';
          if (dateIso !== todayIso) continue;
          const dayKey = weekdayKeysToShow[dayIdx]!.key as AnnualWeekdayKey;
          const ec = editingCellRef.current;
          if (ec) {
            commitEdit(
              ec.gradeId,
              ec.mondayIso,
              ec.dayKey,
              ec.periodIdx,
              editingInputRef.current?.value ?? ec.draft ?? ''
            );
          }
          clearAnnualCellMemoLongPressTimer();
          setAnnualCellMemoModal(null);
          const keys = Array.from({ length: MAX_PERIODS }, (_, p) =>
            compoundCellKey(gradeId, week.mondayIso, dayKey, p)
          );
          setDragState(null);
          setCopiedSourceRegion(null);
          setSelectedCells(keys);
          window.setTimeout(() => {
            document.getElementById(annualCellDomId(keys[0]!))?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }, 0);
          return;
        }
      }
      showInfo('이 학기 시간표에서 오늘 날짜에 해당하는 주를 찾을 수 없습니다.');
    },
    [clearAnnualCellMemoLongPressTimer, commitEdit, showInfo, showSaturday]
  );

  /** 이 학기 주 목록에서 내일이 속한 요일 열의 교시 10칸(한 날짜)을 모두 선택 */
  const handleTomorrowLessonInAnnualTimetable = useCallback(
    (wks: WeekInfo[], gradeId: string) => {
      if (wks.length === 0) return;
      const tomorrowJs = addDays(new Date(), 1);
      const tomorrowIso = toISODate(tomorrowJs);
      const weekdayKeysToShow = showSaturday ? WEEKDAY_KEYS : WEEKDAY_KEYS.filter((w) => w.key !== 'sat');
      const tomorrowDow = tomorrowJs.getDay();
      if (tomorrowDow === 6 && !showSaturday) {
        showInfo('내일이 토요일입니다. 상단에서 토요일 표시를 켜면 해당 열을 선택할 수 있습니다.');
        return;
      }
      if (tomorrowDow === 0) {
        showInfo('내일이 일요일입니다. 연간시간표에는 일요일 열이 없어 해당 날짜를 선택할 수 없습니다.');
        return;
      }
      for (const week of wks) {
        for (let dayIdx = 0; dayIdx < weekdayKeysToShow.length; dayIdx += 1) {
          const dateIso = week.datesByWeekday[dayIdx] ?? '';
          if (dateIso !== tomorrowIso) continue;
          const dayKey = weekdayKeysToShow[dayIdx]!.key as AnnualWeekdayKey;
          const ec = editingCellRef.current;
          if (ec) {
            commitEdit(
              ec.gradeId,
              ec.mondayIso,
              ec.dayKey,
              ec.periodIdx,
              editingInputRef.current?.value ?? ec.draft ?? ''
            );
          }
          clearAnnualCellMemoLongPressTimer();
          setAnnualCellMemoModal(null);
          const keys = Array.from({ length: MAX_PERIODS }, (_, p) =>
            compoundCellKey(gradeId, week.mondayIso, dayKey, p)
          );
          setDragState(null);
          setCopiedSourceRegion(null);
          setSelectedCells(keys);
          window.setTimeout(() => {
            document.getElementById(annualCellDomId(keys[0]!))?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }, 0);
          return;
        }
      }
      showInfo('이 학기 시간표에서 내일 날짜에 해당하는 주를 찾을 수 없습니다.');
    },
    [clearAnnualCellMemoLongPressTimer, commitEdit, showInfo, showSaturday]
  );

  /** 이 학기 주 목록에서 월·일이 일치하는 요일 열의 교시 칸을 모두 선택 */
  const selectAnnualColumnByMonthDayInWeeks = useCallback(
    (wks: WeekInfo[], gradeId: string, month: number, day: number, periodCount: number): boolean => {
      if (wks.length === 0 || periodCount <= 0) return false;
      for (const week of wks) {
        for (let fullIdx = 0; fullIdx < week.datesByWeekday.length; fullIdx += 1) {
          const dateIso = week.datesByWeekday[fullIdx] ?? '';
          if (!/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) continue;
          const d = parseISODate(dateIso);
          if (Number.isNaN(d.getTime())) continue;
          if (d.getMonth() + 1 !== month || d.getDate() !== day) continue;
          const dayKey = WEEKDAY_KEYS[fullIdx]?.key as AnnualWeekdayKey | undefined;
          if (!dayKey) continue;
          if (dayKey === 'sat' && !showSaturday) {
            showInfo('해당 날짜가 토요일입니다. 상단에서 토요일 표시를 켜면 해당 열을 선택할 수 있습니다.');
            return false;
          }
          const dow = d.getDay();
          if (dow === 0) {
            showInfo('해당 날짜가 일요일입니다. 연간시간표에는 일요일 열이 없습니다.');
            return false;
          }
          const ec = editingCellRef.current;
          if (ec) {
            commitEdit(
              ec.gradeId,
              ec.mondayIso,
              ec.dayKey,
              ec.periodIdx,
              editingInputRef.current?.value ?? ec.draft ?? ''
            );
          }
          clearAnnualCellMemoLongPressTimer();
          setAnnualCellMemoModal(null);
          const n = Math.min(periodCount, MAX_PERIODS);
          const keys = Array.from({ length: n }, (_, p) => compoundCellKey(gradeId, week.mondayIso, dayKey, p));
          setDragState(null);
          setCopiedSourceRegion(null);
          setSelectedCells(keys);
          window.setTimeout(() => {
            document.getElementById(annualCellDomId(keys[0]!))?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }, 0);
          return true;
        }
      }
      return false;
    },
    [clearAnnualCellMemoLongPressTimer, commitEdit, showInfo, showSaturday]
  );

  const annualDateHeadPickerOpenKey =
    annualDateHeadPicker == null ? '' : `${annualDateHeadPicker.gradeId}|${annualDateHeadPicker.termKey}`;

  useEffect(() => {
    if (!annualDateHeadPickerOpenKey) return undefined;
    annualDateHeadActiveRef.current = true;
    const id = window.requestAnimationFrame(() => {
      const el = annualDateHeadInputRef.current;
      if (!el) return;
      el.focus();
      el.select();
    });
    return () => window.cancelAnimationFrame(id);
  }, [annualDateHeadPickerOpenKey]);

  /** 월·일 입력 모드: 해당 헤더 바깥을 누르면 입력만 닫음(Enter 확정과 구분) */
  useEffect(() => {
    if (!annualDateHeadPickerOpenKey) return undefined;
    const onDocMouseDownCapture = (e: MouseEvent) => {
      const p = annualDateHeadPickerRef.current;
      if (!p) return;
      const anchorSel = `[data-cp-annual-date-head-anchor="${p.gradeId}-${p.termKey}"]`;
      const t = e.target;
      if (t instanceof Element && t.closest(anchorSel)) return;
      annualDateHeadActiveRef.current = false;
      setAnnualDateHeadPicker(null);
    };
    document.addEventListener('mousedown', onDocMouseDownCapture, true);
    return () => document.removeEventListener('mousedown', onDocMouseDownCapture, true);
  }, [annualDateHeadPickerOpenKey]);

  const handleTodayLessonShortcut = useCallback(() => {
    let gid: string | null = null;
    const sel = selectedCellsRef.current;
    if (sel.length > 0) {
      const g0 = gradeIdFromCompound(sel[0]!);
      if (g0 && sel.every((k) => gradeIdFromCompound(k) === g0)) gid = g0;
    }
    if (!gid && visibleClassEntries.length > 0) {
      gid = visibleClassEntries[0]?.gradeId ?? null;
    }
    if (!gid) return;
    const combined = [...weeksTerm1, ...weeksTerm2];
    handleTodayLessonInAnnualTimetable(combined, gid);
  }, [handleTodayLessonInAnnualTimetable, visibleClassEntries, weeksTerm1, weeksTerm2]);

  const handleTomorrowLessonShortcut = useCallback(() => {
    let gid: string | null = null;
    const sel = selectedCellsRef.current;
    if (sel.length > 0) {
      const g0 = gradeIdFromCompound(sel[0]!);
      if (g0 && sel.every((k) => gradeIdFromCompound(k) === g0)) gid = g0;
    }
    if (!gid && visibleClassEntries.length > 0) {
      gid = visibleClassEntries[0]?.gradeId ?? null;
    }
    if (!gid) return;
    const combined = [...weeksTerm1, ...weeksTerm2];
    handleTomorrowLessonInAnnualTimetable(combined, gid);
  }, [handleTomorrowLessonInAnnualTimetable, visibleClassEntries, weeksTerm1, weeksTerm2]);

  const handleSetSwapRegionA = useCallback(() => {
    const currentSel = filterEditableSelectionKeys(selectedCellsRef.current);
    if (currentSel.length === 0) return;
    setSwapRegionA([...currentSel]);
    setSelectedCells([]);
    setCopiedSourceRegion(null);
  }, [filterEditableSelectionKeys]);

  const handleSetSwapRegionB = useCallback(() => {
    const currentSel = filterEditableSelectionKeys(selectedCellsRef.current);
    if (currentSel.length === 0) return;
    setSwapRegionB([...currentSel]);
    setSelectedCells([]);
    setCopiedSourceRegion(null);
  }, [filterEditableSelectionKeys]);

  const handleShiftSelectedCellsByStep = useCallback(
    (weeks: WeekInfo[], direction: 'left' | 'right' | 'up' | 'down') => {
      const selectedKeys = filterEditableSelectionKeys(selectedCellsRef.current).filter((key) =>
        areAllKeysInWeeks([key], weeks, dayKeysForSelection)
      );
      if (selectedKeys.length === 0) return;
      const gradeId = gradeIdFromCompound(selectedKeys[0]);
      if (!gradeId || selectedKeys.some((key) => gradeIdFromCompound(key) !== gradeId)) return;
      const dayCount = dayKeysForSelection.length;
      if (dayCount <= 0 || weeks.length <= 0) return;
      const ec = editingCellRef.current;
      if (ec) {
        commitEdit(
          ec.gradeId,
          ec.mondayIso,
          ec.dayKey,
          ec.periodIdx,
          editingInputRef.current?.value ?? ec.draft ?? ''
        );
      }
      clearAnnualCellMemoLongPressTimer();
      setAnnualCellMemoModal(null);
      const perWeek = dayCount * MAX_PERIODS;
      const step = (() => {
        if (direction === 'left') return -1;
        if (direction === 'right') return 1;
        if (direction === 'up') return -perWeek;
        return perWeek;
      })();
      const total = weeks.length * perWeek;
      const entries = selectedKeys
        .map((key) => {
          const idx = weeklyKeyToIndicesCompound(key, weeks, dayKeysForSelection);
          if (!idx) return null;
          const flat = idx.weekIdx * perWeek + idx.dayIdx * MAX_PERIODS + idx.periodIdx;
          return { key, flat };
        })
        .filter((item): item is { key: string; flat: number } => item != null);
      if (entries.length !== selectedKeys.length) return;
      const ordered = [...entries].sort((a, b) => (step > 0 ? b.flat - a.flat : a.flat - b.flat));
      const targetBySource = new Map<string, string>();
      for (const item of ordered) {
        const targetFlat = item.flat + step;
        if (targetFlat < 0 || targetFlat >= total) {
          showInfo('선택 영역이 표 경계를 벗어나 이동할 수 없습니다.');
          return;
        }
        const weekIdx = Math.floor(targetFlat / perWeek);
        const inWeek = targetFlat % perWeek;
        const dayIdx = Math.floor(inWeek / MAX_PERIODS);
        const periodIdx = inWeek % MAX_PERIODS;
        const targetWeek = weeks[weekIdx];
        const targetDayKey = dayKeysForSelection[dayIdx];
        if (!targetWeek || !targetDayKey) {
          showInfo('이동 대상 셀을 찾을 수 없습니다.');
          return;
        }
        const targetKey = compoundCellKey(gradeId, targetWeek.mondayIso, targetDayKey, periodIdx);
        const targetCell = effectiveCellsByKeyRef.current[targetKey];
        if (!targetCell?.isEditable) {
          showInfo('공휴일/비편집 셀은 이동할 수 없습니다.');
          return;
        }
        targetBySource.set(item.key, targetKey);
      }
      const involved = new Set<string>(selectedKeys);
      targetBySource.forEach((targetKey) => involved.add(targetKey));
      const nextByKey = new Map<
        string,
        { value: string; memo: string; colorHex: string; isBaseExcluded: boolean }
      >();
      involved.forEach((key) => {
        const cur = getEffectiveCellValue(key);
        nextByKey.set(key, {
          value: cur.value,
          memo: cur.memo,
          colorHex: cur.colorHex,
          isBaseExcluded: cur.isBaseExcluded,
        });
      });
      for (const item of ordered) {
        const sourceKey = item.key;
        const targetKey = targetBySource.get(sourceKey);
        if (!targetKey) continue;
        const sourceValue = nextByKey.get(sourceKey);
        const targetValue = nextByKey.get(targetKey);
        if (!sourceValue || !targetValue) continue;
        nextByKey.set(sourceKey, targetValue);
        nextByKey.set(targetKey, sourceValue);
      }
      applyCellUpdates(
        Array.from(involved).map((key) => {
          const value = nextByKey.get(key);
          return {
            key,
            textValue: value?.value ?? '',
            memo: value?.memo ?? '',
            colorHex: value?.colorHex ?? '',
            isBaseExcluded: value?.isBaseExcluded ?? false,
          };
        })
      );
      setSelectedCells(selectedKeys.map((key) => targetBySource.get(key) ?? key));
      setDragState(null);
      dragStateRef.current = null;
      setCopiedSourceRegion(null);
    },
    [
      applyCellUpdates,
      clearAnnualCellMemoLongPressTimer,
      commitEdit,
      dayKeysForSelection,
      filterEditableSelectionKeys,
      getEffectiveCellValue,
      showInfo,
    ]
  );

  const handleSwapRegions = useCallback(() => {
    if (!swapRegionA?.length || !swapRegionB?.length || swapRegionA.length !== swapRegionB.length) return;
    const ga = gradeIdFromCompound(swapRegionA[0]);
    if (
      !ga ||
      swapRegionA.some((k) => gradeIdFromCompound(k) !== ga) ||
      swapRegionB.some((k) => gradeIdFromCompound(k) !== ga)
    ) {
      return;
    }
    const valuesA = swapRegionA.map((key) => getEffectiveCellValue(key));
    const valuesB = swapRegionB.map((key) => getEffectiveCellValue(key));
    applyCellUpdates([
      ...swapRegionA.map((key, index) => ({
        key,
        textValue: valuesB[index]?.value ?? '',
        memo: valuesB[index]?.memo ?? '',
        colorHex: valuesB[index]?.colorHex ?? '',
        isBaseExcluded: valuesB[index]?.isBaseExcluded ?? false,
      })),
      ...swapRegionB.map((key, index) => ({
        key,
        textValue: valuesA[index]?.value ?? '',
        memo: valuesA[index]?.memo ?? '',
        colorHex: valuesA[index]?.colorHex ?? '',
        isBaseExcluded: valuesA[index]?.isBaseExcluded ?? false,
      })),
    ]);
    setSwapRegionA(null);
    setSwapRegionB(null);
  }, [applyCellUpdates, getEffectiveCellValue, swapRegionA, swapRegionB]);

  const swapRegionARef = useRef<string[] | null>(null);
  const swapRegionBRef = useRef<string[] | null>(null);
  const handleSwapRegionsRef = useRef(handleSwapRegions);
  swapRegionARef.current = swapRegionA;
  swapRegionBRef.current = swapRegionB;
  handleSwapRegionsRef.current = handleSwapRegions;

  const weeksRef = useRef<WeekInfo[]>([]);
  weeksRef.current = mergedWeeks;
  const handleCopyRef = useRef(handleCopy);
  const handleCutRef = useRef(handleCut);
  const handlePasteRef = useRef(handlePaste);
  handleCopyRef.current = handleCopy;
  handleCutRef.current = handleCut;
  handlePasteRef.current = handlePaste;

  useEffect(() => {
    const onDocKeyDownCopyPaste = (e: KeyboardEvent) => {
      const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const mod = isMac ? e.metaKey : e.ctrlKey;
      const key = (e.key || '').toLowerCase();
      let action: 'copy' | 'cut' | 'paste' | null = null;
      if (e.code === 'KeyC' || key === 'c') action = 'copy';
      else if (e.code === 'KeyX' || key === 'x') action = 'cut';
      else if (e.code === 'KeyV' || key === 'v') action = 'paste';
      if (!mod || !action) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const inWeeklyTable = target.closest('.cp-annual-timetable-table-wrap');
      const inInput = target.closest('input, textarea');
      if (inInput && !inWeeklyTable) return;
      const w = weeksRef.current;
      if (!w?.length) return;
      const currentSel = selectedCellsRef.current;
      const hasSelection = currentSel.length > 0;
      if ((action === 'copy' || action === 'cut') && !hasSelection) return;
      if (action === 'paste' && (!copiedCellsRef.current?.cells?.length || !hasSelection)) return;
      e.preventDefault();
      e.stopPropagation();
      if (action === 'copy') handleCopyRef.current(w, currentSel);
      else if (action === 'cut') handleCutRef.current(w, currentSel);
      else handlePasteRef.current(w, currentSel);
    };
    document.addEventListener('keydown', onDocKeyDownCopyPaste, true);
    return () => document.removeEventListener('keydown', onDocKeyDownCopyPaste, true);
  }, []);

  useEffect(() => {
    const onDocKeyDownSwap = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.repeat) return;
      if (e.isComposing) return;
      let swapAction: 'a' | 'b' | 'swap' | null = null;
      if (e.code === 'KeyQ') swapAction = 'a';
      else if (e.code === 'KeyW') swapAction = 'b';
      else if (e.code === 'KeyE') swapAction = 'swap';
      if (!swapAction) return;
      if (editingCellRef.current) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const inWeeklyTable = target.closest('.cp-annual-timetable-table-wrap');
      const inInput = target.closest('input, textarea');
      if (inInput && !inWeeklyTable) return;
      if (!weeksRef.current?.length) return;
      const currentSel = selectedCellsRef.current;
      if (swapAction === 'a') {
        const editableSel = filterEditableSelectionKeys(currentSel);
        if (!editableSel.length) return;
        e.preventDefault();
        e.stopPropagation();
        setSwapRegionA([...editableSel]);
        setSelectedCells([]);
        setCopiedSourceRegion(null);
        return;
      }
      if (swapAction === 'b') {
        const editableSel = filterEditableSelectionKeys(currentSel);
        if (!editableSel.length) return;
        e.preventDefault();
        e.stopPropagation();
        setSwapRegionB([...editableSel]);
        setSelectedCells([]);
        setCopiedSourceRegion(null);
        return;
      }
      const regionA = swapRegionARef.current;
      const regionB = swapRegionBRef.current;
      if (!regionA?.length || !regionB?.length || regionA.length !== regionB.length) return;
      e.preventDefault();
      e.stopPropagation();
      handleSwapRegionsRef.current();
    };
    document.addEventListener('keydown', onDocKeyDownSwap, true);
    return () => document.removeEventListener('keydown', onDocKeyDownSwap, true);
  }, [filterEditableSelectionKeys]);

  const applyColorToSelected = useCallback((colorHex: string, groupLabel?: string) => {
    const currentSel = selectedCellsRef.current;
    if (!currentSel.length) return;
    if (groupLabel === '고정(행사)' && colorHex) {
      const opened = openEventModalFromSelectedCellsRef.current(colorHex);
      if (opened) return;
    }
    const markExcluded = groupLabel === '라벨링 색상' && Boolean(colorHex);
    applyCellUpdates(
      currentSel.map((key) => {
        return {
          key,
          colorHex,
          isBaseExcluded: colorHex ? markExcluded : false,
        };
      })
    );
    setSelectedCells([]);
  }, [applyCellUpdates]);

  const applyProjectProgramToSelected = useCallback(
    (project: AnnualProjectProgram) => {
      const currentSel = selectedCellsRef.current;
      if (!currentSel.length) return;
      applyCellUpdates(
        currentSel.map((key) => {
          return {
            key,
            colorHex: project.colorHex,
            isBaseExcluded: false,
          };
        })
      );
      setSelectedCells([]);
    },
    [applyCellUpdates]
  );

  const openProjectCreateModal = useCallback((gradeId: string, defaultTerm?: 1 | 2) => {
    setProjectFormEditingId(null);
    setProjectFormTerms(defaultTerm === 1 || defaultTerm === 2 ? [defaultTerm] : []);
    setProjectFormName('');
    setProjectFormColorHex(null);
    setProjectCustomColorModalOpen(false);
    setProjectDuplicateColorWarnOpen(false);
    setProjectFormCustomColor(PROJECT_PROGRAM_COLOR_PALETTE[1] ?? '#4ead75');
    setProjectFormError(null);
    setProjectCreateModalGradeId(gradeId);
  }, []);

  const openProjectManageModal = useCallback((gradeId: string) => {
    setProjectDeleteTargetId(null);
    setProjectManageModalGradeId(gradeId);
  }, []);

  const closeProjectManageModal = useCallback(() => {
    setProjectManageModalGradeId(null);
    setProjectDeleteTargetId(null);
  }, []);

  const openProjectEditModal = useCallback((gradeId: string, project: AnnualProjectProgram) => {
    setProjectFormEditingId(project.id);
    setProjectFormTerms([project.termNumber]);
    setProjectFormName(project.name);
    setProjectFormColorHex(project.colorHex);
    setProjectFormCustomColor(project.colorHex);
    setProjectCustomColorModalOpen(false);
    setProjectDuplicateColorWarnOpen(false);
    setProjectFormError(null);
    setProjectCreateModalGradeId(gradeId);
    setProjectManageModalGradeId(null);
    setProjectDeleteTargetId(null);
  }, []);

  const toggleProjectFormTerm = useCallback(
    (term: 1 | 2) => {
      setProjectFormError(null);
      setProjectFormTerms((prev) => {
        // 수정 모드: 단일 학기만 (라디오)
        if (projectFormEditingId) {
          return [term];
        }
        if (prev.includes(term)) return prev.filter((t) => t !== term);
        return ([...prev, term] as (1 | 2)[]).sort((a, b) => a - b);
      });
    },
    [projectFormEditingId]
  );

  /** 학기 변경 등으로 현재 선택 색이 선택 학기 중 어디든 중복이면 선택 해제 */
  React.useEffect(() => {
    if (!projectCreateModalGradeId || projectFormTerms.length === 0 || !projectFormColorHex) return;
    const list = annualProjectProgramsByGradeId[projectCreateModalGradeId] ?? [];
    if (
      isOtherProjectUsingColorForAnyOfTerms(
        projectFormColorHex,
        list,
        projectFormTerms,
        projectFormEditingId
      )
    ) {
      setProjectFormColorHex(null);
      setProjectFormError(
        projectFormTerms.length > 1
          ? '선택한 학기 중 이미 사용 중인 색입니다. 다른 색을 선택해 주세요.'
          : '선택한 학기에는 이미 사용 중인 색입니다. 다른 색을 선택해 주세요.'
      );
    }
  }, [
    annualProjectProgramsByGradeId,
    projectCreateModalGradeId,
    projectFormColorHex,
    projectFormEditingId,
    projectFormTerms,
  ]);

  const closeProjectCreateModal = useCallback(() => {
    setProjectCreateModalGradeId(null);
    setProjectFormEditingId(null);
    setProjectCustomColorModalOpen(false);
    setProjectDuplicateColorWarnOpen(false);
    setProjectFormError(null);
  }, []);

  const handleRegisterProjectProgram = useCallback(async () => {
    if (!workspaceId || !projectCreateModalGradeId) return;
    const name = projectFormName.trim();
    const color = (projectFormColorHex ?? '').trim();
    if (projectFormTerms.length === 0) {
      setProjectFormError('1학기 또는 2학기를 선택해 주세요.');
      return;
    }
    if (!name) {
      setProjectFormError('프로젝트 명을 입력해 주세요.');
      return;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setProjectFormError('프로젝트 색상을 선택해 주세요.');
      return;
    }
    const prev = annualProjectProgramsByGradeId[projectCreateModalGradeId] ?? [];
    if (isOtherProjectUsingColorForAnyOfTerms(color, prev, projectFormTerms, projectFormEditingId)) {
      setProjectFormError(
        projectFormTerms.length > 1
          ? '선택한 학기 중 이미 다른 프로젝트가 사용 중인 색입니다.'
          : '이 학기에 이미 다른 프로젝트가 사용 중인 색입니다.'
      );
      return;
    }
    const editingId = projectFormEditingId;
    let list: AnnualProjectProgram[];
    let newProjects: AnnualProjectProgram[] = [];
    if (editingId) {
      const exists = prev.some((p) => p.id === editingId);
      if (!exists) {
        setProjectFormError('수정할 프로젝트를 찾을 수 없습니다. 목록을 새로고침해 주세요.');
        return;
      }
      const nextTerm = projectFormTerms[0];
      if (nextTerm !== 1 && nextTerm !== 2) {
        setProjectFormError('1학기 또는 2학기를 선택해 주세요.');
        return;
      }
      list = prev.map((p) =>
        p.id === editingId ? { ...p, name, colorHex: color, termNumber: nextTerm } : p
      );
    } else {
      const created: AnnualProjectProgram[] = projectFormTerms.map((termNumber) => {
        const id =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `proj_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        return { id, name, colorHex: color, termNumber };
      });
      newProjects = created;
      list = [...prev, ...created];
    }
    await saveAnnualProjectPrograms(workspaceId, projectCreateModalGradeId, academicYear, list);
    for (const newProject of newProjects) {
      try {
        await ensureProjectProgramScopedContentRows({
          workspaceId,
          gradeId: projectCreateModalGradeId,
          academicYear,
          termNumber: newProject.termNumber,
          projectId: newProject.id,
        });
      } catch {
        /* 프로젝트 등록은 유지, 스코프 행은 프로그램 페이지 진입 시 보완 */
      }
    }
    if (editingId) {
      const before = prev.find((p) => p.id === editingId);
      const oldHex = (before?.colorHex ?? '').trim();
      const oldTerm = before?.termNumber;
      if (oldHex && (oldTerm === 1 || oldTerm === 2)) {
        const oldNorm = normalizeHexColorForProjectCompare(oldHex);
        const newNorm = normalizeHexColorForProjectCompare(color);
        if (oldNorm && newNorm && oldNorm !== newNorm) {
          const paintUpdates = collectTimetableCellUpdatesForProjectPaintHexChange(effectiveCellsByKeyRef.current, {
            gradeId: projectCreateModalGradeId,
            termNumber: oldTerm,
            oldColorHex: oldHex,
            newColorHex: color,
          });
          if (paintUpdates.length) applyCellUpdates(paintUpdates);
        }
      }
    }
    setAnnualProjectProgramsByGradeId((s) => ({
      ...s,
      [projectCreateModalGradeId]: list,
    }));
    await syncProgramColorsFromAnnualProjects(projectCreateModalGradeId, list);
    closeProjectCreateModal();
  }, [
    academicYear,
    annualProjectProgramsByGradeId,
    applyCellUpdates,
    closeProjectCreateModal,
    projectCreateModalGradeId,
    projectFormColorHex,
    projectFormEditingId,
    projectFormName,
    projectFormTerms,
    syncProgramColorsFromAnnualProjects,
    workspaceId,
  ]);

  const handleConfirmDeleteProject = useCallback(async () => {
    if (!workspaceId || !projectManageModalGradeId || !projectDeleteTargetId) return;
    const prev = annualProjectProgramsByGradeId[projectManageModalGradeId] ?? [];
    const victim = prev.find((p) => p.id === projectDeleteTargetId);
    const oldHex = (victim?.colorHex ?? '').trim();
    const oldTerm = victim?.termNumber;
    const list = prev.filter((p) => p.id !== projectDeleteTargetId);
    await saveAnnualProjectPrograms(workspaceId, projectManageModalGradeId, academicYear, list);
    if (oldHex && (oldTerm === 1 || oldTerm === 2)) {
      const paintUpdates = collectTimetableCellUpdatesForProjectPaintHexChange(effectiveCellsByKeyRef.current, {
        gradeId: projectManageModalGradeId,
        termNumber: oldTerm,
        oldColorHex: oldHex,
        newColorHex: '',
      });
      if (paintUpdates.length) applyCellUpdates(paintUpdates);
    }
    setAnnualProjectProgramsByGradeId((s) => ({
      ...s,
      [projectManageModalGradeId]: list,
    }));
    await syncProgramColorsFromAnnualProjects(projectManageModalGradeId, list);
    setProjectDeleteTargetId(null);
  }, [
    academicYear,
    annualProjectProgramsByGradeId,
    applyCellUpdates,
    projectDeleteTargetId,
    projectManageModalGradeId,
    syncProgramColorsFromAnnualProjects,
    workspaceId,
  ]);

  const duplicateProjectProgram = useCallback(
    async (gradeId: string, project: AnnualProjectProgram) => {
      if (!workspaceId) return;
      const prev = annualProjectProgramsByGradeId[gradeId] ?? [];
      const used = getUsedProjectColorHexSetForTerm(prev, project.termNumber, null);
      const pickFreeColor = (): string | null => {
        for (const hex of PROJECT_PROGRAM_COLOR_PALETTE) {
          if (!used.has(hex.toLowerCase())) return hex;
        }
        for (const hex of REGULAR_PROGRAM_COLOR_PALETTE) {
          if (!used.has(hex.toLowerCase())) return hex;
        }
        return null;
      };
      const freeColor = pickFreeColor();
      if (!freeColor) {
        showInfo('이 학기에 배정 가능한 색이 없어 복제할 수 없습니다.');
        return;
      }
      const baseName = `${project.name} (복사)`;
      let name = baseName;
      let n = 2;
      const taken = new Set(prev.map((p) => p.name.trim()));
      while (taken.has(name)) {
        name = `${project.name} (복사 ${n})`;
        n += 1;
      }
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `proj_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const copy: AnnualProjectProgram = {
        id,
        name,
        colorHex: freeColor,
        termNumber: project.termNumber,
      };
      const list = [...prev, copy];
      await saveAnnualProjectPrograms(workspaceId, gradeId, academicYear, list);
      try {
        await ensureProjectProgramScopedContentRows({
          workspaceId,
          gradeId,
          academicYear,
          termNumber: copy.termNumber,
          projectId: copy.id,
        });
      } catch {
        /* 복제는 유지, 스코프 행은 프로그램 페이지 진입 시 보완 */
      }
      setAnnualProjectProgramsByGradeId((s) => ({
        ...s,
        [gradeId]: list,
      }));
      await syncProgramColorsFromAnnualProjects(gradeId, list);
    },
    [academicYear, annualProjectProgramsByGradeId, syncProgramColorsFromAnnualProjects, workspaceId, showInfo]
  );

  /** 수업·활동·비수업: 슬롯 종류(면 무늬)만 지정. 시간표명·메모·색상 등 기존 데이터는 유지 — 수업=무늬 없음, 활동·비수업=빗금 중첩. */
  const applySlotSurfaceToSelected = useCallback(
    (slotKind: 'class' | 'activity' | 'non_class') => {
      const keys = filterEditableSelectionKeys(selectedCellsRef.current);
      if (!keys.length) return;
      applyCellUpdates(keys.map((key) => ({ key, slotKind })));
      setSelectedCells([]);
    },
    [applyCellUpdates, filterEditableSelectionKeys]
  );

  const getWeekClassHours = useCallback(
    (gradeId: string, week: WeekInfo) => {
      return WEEKDAY_KEYS.reduce((total, { key }, weekdayIdx) => {
        if (key === 'sat' && !showSaturday) return total;
        const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
        for (let slotIndex = 0; slotIndex < MAX_PERIODS; slotIndex += 1) {
          const cell = effectiveCellsByKey[
            compoundCellKey(gradeId, week.mondayIso, key as AnnualWeekdayKey, slotIndex)
          ];
          if (cell?.slotKind === 'class' && cell.dateIso === dateIso) {
            total += 1;
          }
        }
        return total;
      }, 0);
    },
    [effectiveCellsByKey, showSaturday]
  );

  /** 운영계획 수업일수와 동일: 학기 범위 내 학사일정 `class` 요일 수 */
  const getWeekInstructionalDayCount = useCallback(
    (week: WeekInfo, termNum: 1 | 2) => {
      const termDef = termNum === 1 ? term1Def : term2Def;
      const termStarts = (termDef?.starts_on ?? '').trim();
      const termEnds = (termDef?.ends_on ?? '').trim();
      const weekdayKeys = showSaturday ? WEEKDAY_KEYS : WEEKDAY_KEYS.filter((w) => w.key !== 'sat');
      return weekdayKeys.reduce((count, _, weekdayIdx) => {
        const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
        const inTerm =
          !!dateIso && !!termStarts && !!termEnds && dateIso >= termStarts && dateIso <= termEnds;
        if (!inTerm) return count;
        const dayKind: DayKind = calendarDayKindByDate[dateIso] ?? 'class';
        if (dayKind !== 'class') return count;
        return count + 1;
      }, 0);
    },
    [calendarDayKindByDate, showSaturday, term1Def, term2Def]
  );

  const semesterHoursByGradeTerm1 = React.useMemo(() => {
    const next: Record<string, number> = {};
    classEntries.forEach((entry) => {
      next[entry.gradeId] = weeksTerm1.reduce(
        (total, week) => total + getWeekClassHours(entry.gradeId, week),
        0
      );
    });
    return next;
  }, [classEntries, getWeekClassHours, weeksTerm1]);

  const semesterHoursByGradeTerm2 = React.useMemo(() => {
    const next: Record<string, number> = {};
    classEntries.forEach((entry) => {
      next[entry.gradeId] = weeksTerm2.reduce(
        (total, week) => total + getWeekClassHours(entry.gradeId, week),
        0
      );
    });
    return next;
  }, [classEntries, getWeekClassHours, weeksTerm2]);

  const semesterInstructionalDaysTerm1 = React.useMemo(
    () => weeksTerm1.reduce((total, week) => total + getWeekInstructionalDayCount(week, 1), 0),
    [getWeekInstructionalDayCount, weeksTerm1]
  );

  const semesterInstructionalDaysTerm2 = React.useMemo(
    () => weeksTerm2.reduce((total, week) => total + getWeekInstructionalDayCount(week, 2), 0),
    [getWeekInstructionalDayCount, weeksTerm2]
  );

  const handleApplyBaseTimetable = useCallback(
    (entry: AnnualClassEntry, termNum: 1 | 2) => {
      const byTerm = baseTimetableByGrade[entry.gradeId];
      if (!byTerm) return;
      const grid = getBaseTimetableGridForTerm(byTerm, termNum);
      const weeksBlock = termNum === 1 ? weeksTerm1 : weeksTerm2;
      if (weeksBlock.length === 0) return;
      const updates: Array<{
        key: string;
        textValue?: string;
        colorHex?: string;
        slotKind?: AnnualTimetableSlotKind;
        isBaseExcluded?: boolean;
      }> = [];
      weeksBlock.forEach((week) => {
        WEEKDAY_KEYS.forEach(({ key }, weekdayIdx) => {
          const weekdayKey = key as AnnualWeekdayKey;
          if (weekdayKey === 'sat') return;
          for (let slotIndex = 0; slotIndex < MAX_PERIODS; slotIndex += 1) {
            const compoundKey = compoundCellKey(entry.gradeId, week.mondayIso, weekdayKey, slotIndex);
            const current = effectiveCellsByKeyRef.current[compoundKey];
            if (!current?.isEditable || current.slotKind !== 'class') continue;
            const baseIdx = baseGridClassPeriodIndexForSectionSlot(entry.sectionRows, slotIndex);
            if (baseIdx < 0) continue;
            const baseCell = getBaseGridCell(grid, weekdayKey, baseIdx);
            if (!baseCell) continue;
            if (isFixedProgramColor(current.colorHex, regularColors)) continue;
            const currentMemo = (current.memo ?? '').trim();
            if (currentMemo && !isAnnualShortDayTailMemo(currentMemo)) continue;
            if (current.isBaseExcluded) continue;
            if (baseCell.name.trim() === '' && baseCell.colorHex === '') continue;
            updates.push({
              key: compoundKey,
              textValue: baseCell.name,
              colorHex: baseCell.colorHex,
              isBaseExcluded: false,
              slotKind: 'class',
            });
          }
        });
      });
      applyCellUpdates(updates);
      showInfo(`${termNum}학기에 기초시간표가 반영되었습니다.`);
    },
    [applyCellUpdates, baseTimetableByGrade, regularColors, showInfo, weeksTerm1, weeksTerm2]
  );

  const closeTimetableSettingsModal = useCallback(() => {
    setTimetableSettingsModal(null);
    setTimetableSettingsCloseConfirm(false);
  }, []);

  const requestCloseTimetableSettingsModal = useCallback(() => {
    if (timetableSettingsDirty) {
      setTimetableSettingsCloseConfirm(true);
      return;
    }
    closeTimetableSettingsModal();
  }, [closeTimetableSettingsModal, timetableSettingsDirty]);

  const discardAndCloseTimetableSettingsModal = useCallback(() => {
    saveTimetableSettingsRef.current?.discard?.();
    closeTimetableSettingsModal();
  }, [closeTimetableSettingsModal, saveTimetableSettingsRef]);

  const saveAndCloseTimetableSettingsModal = useCallback(async () => {
    if (!saveTimetableSettingsRef.current?.save) return;
    setTimetableSettingsClosing(true);
    try {
      await saveTimetableSettingsRef.current.save();
      closeTimetableSettingsModal();
    } catch {
      // TimetableSettingsPage 토스트에서 오류 표시
    } finally {
      setTimetableSettingsClosing(false);
    }
  }, [closeTimetableSettingsModal, saveTimetableSettingsRef]);

  const eventDefaultColor = regularColors[0] ?? '#2e7d32';
  /** 모달 고정 프로그램 색 선택: 설정에 등록된 색만큼 버튼 표시 (없으면 기본 1색) */
  const eventModalFixedProgramColors = React.useMemo(() => {
    const raw = regularColors.filter((c) => (c ?? '').trim() !== '');
    const seen = new Set<string>();
    const out: string[] = [];
    raw.forEach((c) => {
      const k = c.trim().toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push(c.trim());
    });
    return out.length > 0 ? out : [eventDefaultColor];
  }, [regularColors, eventDefaultColor]);
  const availableEventClasses = classEntries;
  /** 고정 프로그램 모달: 클래스 미선택 시 시간표명 등록 여부를 볼 때 쓰는 학년 ID 목록 */
  const eventModalTimetableCheckGradeIds = React.useMemo(
    () => classEntries.map((e) => e.gradeId),
    [classEntries]
  );
  const fixedProgramSummaries = React.useMemo<FixedProgramSummary[]>(() => {
    const grouped = new Map<
      string,
      {
        dateIso: string;
        weekdayKey: AnnualWeekdayKey;
        mondayIso: string;
        eventName: string;
        colorHex: string;
        gradeIds: Set<string>;
        periodKeySet: Set<string>;
        periodTextCount: Record<string, Map<string, number>>;
        cellKeys: string[];
      }
    >();
    const gradeOrder = new Map(classEntries.map((e, i) => [e.gradeId, i]));
    const candidateKeys = new Set<string>([
      ...Object.keys(cellMemos),
      ...Object.keys(cellData),
      ...Object.keys(cellColors),
      ...Object.keys(cellSlotKinds),
      ...Object.keys(cellRecordIds),
      ...Object.keys(cellBaseExcluded),
    ]);
    candidateKeys.forEach((key) => {
      const parsed = parseAnnualCellKey(key);
      if (!parsed) return;
      const runtimeCell = effectiveCellsByKey[key];
      const weekdayIdx = WEEKDAY_KEYS.findIndex(({ key: weekdayKey }) => weekdayKey === parsed.weekdayKey);
      const fallbackDateIso =
        weekdayIdx >= 0 ? toISODate(addDays(parseISODate(parsed.mondayIso), weekdayIdx)) : '';
      const slotKind = cellSlotKinds[key] ?? runtimeCell?.slotKind ?? 'class';
      const colorHexRaw = (cellColors[key] ?? runtimeCell?.colorHex ?? '').trim();
      const eventName = (cellMemos[key] ?? runtimeCell?.memo ?? '').trim();
      if (isAnnualShortDayTailMemo(eventName)) return;
      if (!eventName) return;
      if (slotKind === 'out_of_term') return;
      const dateIso = (runtimeCell?.dateIso ?? fallbackDateIso).trim();
      if (!dateIso) return;
      const textValue = (cellData[key] ?? runtimeCell?.textValue ?? '').trim();
      const gradeId = parsed.gradeId;
      const colorKey = colorHexRaw.toLowerCase();
      const groupKey = `${dateIso}|${eventName}|${colorKey}`;
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          dateIso,
          weekdayKey: parsed.weekdayKey,
          mondayIso: parsed.mondayIso,
          eventName,
          colorHex: colorHexRaw,
          gradeIds: new Set<string>(),
          periodKeySet: new Set<string>(),
          periodTextCount: {},
          cellKeys: [],
        });
      }
      const group = grouped.get(groupKey)!;
      const entry = classEntries.find((e) => e.gradeId === gradeId);
      const periodKey = fixedProgramPeriodKeyFromCell(entry?.sectionRows ?? [], parsed.slotIndex);
      group.gradeIds.add(gradeId);
      group.periodKeySet.add(periodKey);
      group.cellKeys.push(key);
      if (!group.periodTextCount[periodKey]) group.periodTextCount[periodKey] = new Map<string, number>();
      const prev = group.periodTextCount[periodKey].get(textValue) ?? 0;
      group.periodTextCount[periodKey].set(textValue, prev + 1);
    });
    return Array.from(grouped.values())
      .map((group) => {
        const periodKeys = Array.from(group.periodKeySet).sort(compareFixedProgramPeriodKeys);
        const timetableByPeriodKey: Record<string, string> = {};
        periodKeys.forEach((pk) => {
          const counter = group.periodTextCount[pk];
          if (!counter) return;
          const best = Array.from(counter.entries()).sort((a, b) => b[1] - a[1])[0];
          timetableByPeriodKey[pk] = best?.[0] ?? '';
        });
        const colorHexResolved = group.colorHex || eventDefaultColor;
        return {
          id: `${group.dateIso}-${group.eventName || 'event'}-${group.colorHex}`,
          dateIso: group.dateIso,
          weekdayKey: group.weekdayKey,
          mondayIso: group.mondayIso,
          eventName: group.eventName,
          colorHex: colorHexResolved,
          isGeneralMemo: !isFixedProgramColor(group.colorHex || '', regularColors),
          gradeIds: Array.from(group.gradeIds).sort(
            (a, b) => (gradeOrder.get(a) ?? 999) - (gradeOrder.get(b) ?? 999)
          ),
          periodKeys,
          timetableByPeriodKey,
          cellKeys: group.cellKeys,
        };
      })
      .sort((a, b) => {
        const byDate = a.dateIso.localeCompare(b.dateIso);
        if (byDate !== 0) return byDate;
        return (a.eventName || '').localeCompare(b.eventName || '', 'ko');
      });
  }, [
    cellBaseExcluded,
    cellColors,
    cellData,
    cellMemos,
    cellRecordIds,
    cellSlotKinds,
    classEntries,
    effectiveCellsByKey,
    eventDefaultColor,
    regularColors,
  ]);

  const operationPlanAvailableTerms = React.useMemo<(1 | 2)[]>(() => {
    const out: Array<1 | 2> = [];
    if (term1Def?.starts_on && term1Def?.ends_on && weeksTerm1.length > 0) out.push(1);
    if (term2Def?.starts_on && term2Def?.ends_on && weeksTerm2.length > 0) out.push(2);
    return out;
  }, [term1Def?.ends_on, term1Def?.starts_on, term2Def?.ends_on, term2Def?.starts_on, weeksTerm1.length, weeksTerm2.length]);

  React.useEffect(() => {
    if (operationPlanTerm === 'all') return;
    if (!operationPlanAvailableTerms.includes(operationPlanTerm)) {
      setOperationPlanTerm(operationPlanAvailableTerms[0] ?? 1);
    }
  }, [operationPlanAvailableTerms, operationPlanTerm]);

  type OperationPlanTermMeta = {
    termNumber: 1 | 2;
    startsOn: string;
    endsOn: string;
    weeks: typeof weeksTerm1;
  };

  const operationPlanTermMetaByNumber = React.useMemo<Partial<Record<1 | 2, OperationPlanTermMeta>>>(() => {
    const map: Partial<Record<1 | 2, OperationPlanTermMeta>> = {};
    if (term1Def?.starts_on && term1Def?.ends_on && weeksTerm1.length > 0) {
      map[1] = {
        termNumber: 1,
        startsOn: term1Def.starts_on,
        endsOn: term1Def.ends_on,
        weeks: weeksTerm1,
      };
    }
    if (term2Def?.starts_on && term2Def?.ends_on && weeksTerm2.length > 0) {
      map[2] = {
        termNumber: 2,
        startsOn: term2Def.starts_on,
        endsOn: term2Def.ends_on,
        weeks: weeksTerm2,
      };
    }
    return map;
  }, [term1Def?.ends_on, term1Def?.starts_on, term2Def?.ends_on, term2Def?.starts_on, weeksTerm1, weeksTerm2]);

  const operationPlanTermMeta =
    operationPlanTerm === 'all' ? null : operationPlanTermMetaByNumber[operationPlanTerm] ?? null;

  const operationPlanWeekdays = React.useMemo(
    () => WEEKDAY_KEYS.filter((w) => w.key !== 'sat'),
    []
  );

  const operationPlanWeekdayLabelByKey = React.useMemo(() => {
    const map: Record<string, string> = {};
    operationPlanWeekdays.forEach((w) => {
      map[w.key] = w.label;
    });
    return map;
  }, [operationPlanWeekdays]);

  const operationPlanRepresentativeEntries = React.useMemo(() => {
    const entryById = new Map(visibleClassEntries.map((e) => [e.gradeId, e] as const));
    const byGradeNumber: Record<number, AnnualClassEntry[]> = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    visibleClassEntries.forEach((entry) => {
      inferGradeNumbersFromLabel(entry.label).forEach((n) => {
        if (n < 1 || n > 6) return;
        byGradeNumber[n].push(entry);
      });
    });
    const out: Array<{ gradeNumber: number; entry: AnnualClassEntry }> = [];
    for (let n = 1; n <= 6; n += 1) {
      const repId = representativeGradeIdByGradeNumber[n];
      if (repId) {
        const repEntry = entryById.get(repId);
        if (repEntry) {
          out.push({ gradeNumber: n, entry: repEntry });
          continue;
        }
      }
      const candidates = byGradeNumber[n];
      if (candidates.length === 0) continue;
      const preferredId = representativeGradeIdByGradeNumber[n];
      const preferred = preferredId ? candidates.find((c) => c.gradeId === preferredId) : null;
      out.push({ gradeNumber: n, entry: preferred ?? candidates[0]! });
    }
    return out;
  }, [representativeGradeIdByGradeNumber, visibleClassEntries]);

  const operationPlanClassHeaders = React.useMemo(() => {
    const headers = operationPlanRepresentativeEntries.map(({ gradeNumber, entry }) => {
      const parsed = parseGradeDisplayName(entry.label);
      const title = (parsed.title || entry.label || '').trim();
      return {
        columnKey: `${entry.gradeId}:${gradeNumber}`,
        gradeId: entry.gradeId,
        classLabel: title,
        gradeNumber: String(gradeNumber),
      };
    });
    if (headers.length > 0) return headers;
    return [{ columnKey: 'none', gradeId: 'none', classLabel: '클래스 없음', gradeNumber: '' }];
  }, [operationPlanRepresentativeEntries]);

  const operationPlanActiveClassHeaders = React.useMemo(() => {
    if (!operationPlanScopeGradeId) return operationPlanClassHeaders;
    const scoped = operationPlanClassHeaders.filter((h) => h.gradeId === operationPlanScopeGradeId);
    if (scoped.length > 0) return scoped;
    const entry = classEntryByGradeId[operationPlanScopeGradeId];
    if (!entry) {
      return [{ columnKey: 'none', gradeId: 'none', classLabel: '클래스 없음', gradeNumber: '' }];
    }
    const parsed = parseGradeDisplayName(entry.label);
    const title = (parsed.title || entry.label || '').trim();
    const gradeNums = inferGradeNumbersFromLabel(entry.label);
    if (gradeNums.length === 0) {
      return [
        {
          columnKey: `${entry.gradeId}:0`,
          gradeId: entry.gradeId,
          classLabel: title,
          gradeNumber: '',
        },
      ];
    }
    return gradeNums.map((n) => ({
      columnKey: `${entry.gradeId}:${n}`,
      gradeId: entry.gradeId,
      classLabel: title,
      gradeNumber: String(n),
    }));
  }, [operationPlanScopeGradeId, operationPlanClassHeaders, classEntryByGradeId]);

  const operationPlanScopeClassBlock = React.useMemo(() => {
    if (!operationPlanScopeGradeId) return null;
    const gradeRow = grades.find((g) => g.id === operationPlanScopeGradeId);
    if (gradeRow) return parseGradeDisplayName(gradeRow.name);
    const entry = classEntryByGradeId[operationPlanScopeGradeId];
    if (entry) return parseGradeDisplayName(entry.label);
    return null;
  }, [operationPlanScopeGradeId, grades, classEntryByGradeId]);

  const shortDaySummaries = React.useMemo<ShortDaySummary[]>(() => {
    const grouped = new Map<
      string,
      {
        dateIso: string;
        weekdayKey: AnnualWeekdayKey;
        mondayIso: string;
        gradeIds: Set<string>;
        increasePeriodSet: Set<string>;
        decreasePeriodSet: Set<string>;
        cellKeys: string[];
      }
    >();
    const gradeOrder = new Map(classEntries.map((e, i) => [e.gradeId, i]));
    Object.entries(effectiveCellsByKey).forEach(([key, cell]) => {
      if (!cell || !cell.isEditable) return;
      if (cell.slotKind !== 'class' && cell.slotKind !== 'non_class') return;
      const entry = classEntryByGradeId[cell.gradeId];
      if (!entry) return;
      const rowKind = entry.sectionRows[cell.slotIndex]?.kind;
      if (rowKind !== 'class' && rowKind !== 'after_school') return;
      const dayTarget = eventDateTargets[cell.dateIso];
      if (!dayTarget) return;
      const expectedKind = getSeedSlotKind({
        dateIso: cell.dateIso,
        weekdayKey: dayTarget.weekdayKey,
        slotIndex: cell.slotIndex,
        sectionRows: entry.sectionRows,
        slotLimitByDay: entry.slotLimitByDay,
        startsOn: cell.dateIso,
        endsOn: cell.dateIso,
        holidaySet: holidaySet,
        calendarDayKindByDate,
      });
      if (expectedKind !== 'class' && expectedKind !== 'non_class') return;
      if (cell.slotKind === expectedKind) return;
      const groupKey = cell.dateIso;
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          dateIso: cell.dateIso,
          weekdayKey: cell.weekdayKey,
          mondayIso: cell.mondayIso,
          gradeIds: new Set<string>(),
          increasePeriodSet: new Set<string>(),
          decreasePeriodSet: new Set<string>(),
          cellKeys: [],
        });
      }
      const group = grouped.get(groupKey)!;
      group.gradeIds.add(cell.gradeId);
      const periodKey = fixedProgramPeriodKeyFromCell(entry.sectionRows, cell.slotIndex);
      if (expectedKind === 'non_class' && cell.slotKind === 'class') {
        group.increasePeriodSet.add(periodKey);
      } else if (expectedKind === 'class' && cell.slotKind === 'non_class') {
        group.decreasePeriodSet.add(periodKey);
      }
      group.cellKeys.push(key);
    });
    return Array.from(grouped.values())
      .map((group) => {
        const sortedGradeIds = Array.from(group.gradeIds).sort(
          (a, b) => (gradeOrder.get(a) ?? 999) - (gradeOrder.get(b) ?? 999)
        );
        const expectedPeriodKeySet = new Set<string>();
        const runtimePeriodKeySet = new Set<string>();
        sortedGradeIds.forEach((gradeId) => {
          const entry = classEntryByGradeId[gradeId];
          if (!entry) return;
          for (let slotIndex = 0; slotIndex < MAX_PERIODS; slotIndex += 1) {
            const rowKind = entry.sectionRows[slotIndex]?.kind;
            if (rowKind !== 'class' && rowKind !== 'after_school') continue;
            const key = compoundCellKey(gradeId, group.mondayIso, group.weekdayKey, slotIndex);
            const runtime = effectiveCellsByKey[key];
            if (!runtime?.isEditable) continue;
            const periodKey = fixedProgramPeriodKeyFromCell(entry.sectionRows, slotIndex);
            const expectedKind = getSeedSlotKind({
              dateIso: group.dateIso,
              weekdayKey: group.weekdayKey,
              slotIndex,
              sectionRows: entry.sectionRows,
              slotLimitByDay: entry.slotLimitByDay,
              startsOn: group.dateIso,
              endsOn: group.dateIso,
              holidaySet,
              calendarDayKindByDate,
            });
            if (expectedKind === 'class') expectedPeriodKeySet.add(periodKey);
            if (runtime.slotKind === 'class') runtimePeriodKeySet.add(periodKey);
          }
        });
        const expectedKeys = Array.from(expectedPeriodKeySet);
        const runtimeKeys = Array.from(runtimePeriodKeySet);
        const increaseKeys = runtimeKeys.filter((k) => !expectedPeriodKeySet.has(k));
        const decreaseKeys = expectedKeys.filter((k) => !runtimePeriodKeySet.has(k));
        const hasIncreaseOnly = increaseKeys.length > 0 && decreaseKeys.length === 0;
        const hasDecreaseOnly = decreaseKeys.length > 0 && increaseKeys.length === 0;
        const displayKeySet = new Set<string>([...expectedKeys, ...runtimeKeys]);
        const displayKeys = Array.from(displayKeySet).sort(compareFixedProgramPeriodKeys);
        const periodChips = displayKeys.map((k) => {
          if (hasIncreaseOnly) {
            return { key: k, inactive: !increaseKeys.includes(k) };
          }
          if (hasDecreaseOnly) {
            return { key: k, inactive: !runtimePeriodKeySet.has(k) };
          }
          return { key: k, inactive: !runtimePeriodKeySet.has(k) };
        });
        const runtimeOrderedKeys = runtimeKeys.sort(compareFixedProgramPeriodKeys);
        const endSlotTarget =
          [...runtimeOrderedKeys]
            .reverse()
            .find((k): k is EventSlotTargetKey => parseEventSlotTargetKey(k) != null) ?? null;
        return {
          id: `${group.dateIso}-${displayKeys.join('|')}`,
          dateIso: group.dateIso,
          weekdayKey: group.weekdayKey,
          mondayIso: group.mondayIso,
          gradeIds: sortedGradeIds,
          periodChips,
          endSlotTarget,
          increaseCount: increaseKeys.length,
          decreaseCount: decreaseKeys.length,
          cellKeys: group.cellKeys,
        };
      })
      .sort((a, b) => a.dateIso.localeCompare(b.dateIso));
  }, [calendarDayKindByDate, classEntries, classEntryByGradeId, effectiveCellsByKey, eventDateTargets, holidaySet]);

  const buildOperationPlanRowsForTermMeta = useCallback(
    (termMeta: OperationPlanTermMeta): OperationPlanWeekRow[] => {
      const mondaySet = new Set(termMeta.weeks.map((w) => w.mondayIso));
      const fixedByDate: Record<string, Array<{ name: string; colorHex: string; isGeneralMemo: boolean }>> = {};
      fixedProgramSummaries.forEach((summary) => {
        if (summary.isGeneralMemo && !operationPlanShowGeneralMemos) return;
        if (!mondaySet.has(summary.mondayIso)) return;
        if (!fixedByDate[summary.dateIso]) fixedByDate[summary.dateIso] = [];
        fixedByDate[summary.dateIso]!.push({
          name: (summary.eventName || '').trim() || '고정 프로그램',
          colorHex: summary.colorHex || eventDefaultColor,
          isGeneralMemo: summary.isGeneralMemo,
        });
      });
      const termStarts = termMeta.startsOn;
      const termEnds = termMeta.endsOn;
      const registeredSchoolGradeNums = schoolWideGradeNumbersFromEntries(classEntries);
      type OpPlanActivityLine = {
        dedupKey: string;
        sortIso: string;
        datePart: string;
        name: string;
        kind: 'calendar' | 'fixed' | 'shortday';
        colorHex?: string;
        isGeneralMemo?: boolean;
        tagCommon: boolean;
        gradeNums: number[];
      };
      const pushActivityLine = (lines: OpPlanActivityLine[], set: Set<string>, line: OpPlanActivityLine) => {
        if (set.has(line.dedupKey)) return;
        set.add(line.dedupKey);
        lines.push(line);
      };
      const kindOrder: Record<OpPlanActivityLine['kind'], number> = { calendar: 0, fixed: 1, shortday: 2 };
      return termMeta.weeks.map((week) => {
        const weekdays = operationPlanWeekdays.map((weekday, weekdayIdx) => {
          const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
          const inTerm = !!dateIso && dateIso >= termStarts && dateIso <= termEnds;
          const dayKind: DayKind = inTerm ? (calendarDayKindByDate[dateIso] ?? 'class') : 'class';
          const dateObj = parseISODate(dateIso);
          const dayNumber = inTerm && !Number.isNaN(dateObj.getTime()) ? String(dateObj.getDate()) : '';
          const fixedEvents = inTerm ? (fixedByDate[dateIso] ?? []) : [];
          const nonGeneralEvent = fixedEvents.find((e) => !e.isGeneralMemo);
          const fixedDayGeneralMemoOnly =
            operationPlanShowGeneralMemos &&
            fixedEvents.length > 0 &&
            fixedEvents.every((e) => e.isGeneralMemo);
          const fixedDayTextColor = fixedDayGeneralMemoOnly
            ? ''
            : (nonGeneralEvent?.colorHex ?? fixedEvents[0]?.colorHex ?? '');
          return {
            key: weekday.key,
            dateIso,
            inTerm,
            dayKind,
            dayNumber,
            fixedEvents,
            fixedDayGeneralMemoOnly,
            fixedDayTextColor,
          };
        });
        const instructionalDayCount = weekdays.reduce((count, day) => {
          if (!day.inTerm) return count;
          if (day.dayKind !== 'class') return count;
          return count + 1;
        }, 0);
        const weekShortDays = shortDaySummaries.filter((summary) => {
          if (summary.mondayIso !== week.mondayIso) return false;
          if (operationPlanScopeGradeId && !summary.gradeIds.includes(operationPlanScopeGradeId)) return false;
          return true;
        });
        const adjustedGradeIdSet = new Set<string>();
        weekShortDays.forEach((summary) => {
          summary.gradeIds.forEach((gradeId) => adjustedGradeIdSet.add(gradeId));
        });
        const activityLines: OpPlanActivityLine[] = [];
        const activitySet = new Set<string>();
        weekdays.forEach((day) => {
          if (!day.inTerm || !day.dateIso) return;
          const weekdayLabel = operationPlanWeekdayLabelByKey[day.key] ?? day.key;
          const datePart = formatOperationPlanActivityDate(day.dateIso, weekdayLabel);
          if (day.dayKind === 'holiday' || day.dayKind === 'closure' || day.dayKind === 'vacation') {
            const note = (calendarDayNoteByDate[day.dateIso] ?? '').trim();
            const nameOnly = note || (day.dayKind === 'holiday' ? '공휴일' : '휴업일');
            const tagCommon = isOperationPlanCommonGradeTag(
              registeredSchoolGradeNums,
              registeredSchoolGradeNums
            );
            pushActivityLine(activityLines, activitySet, {
              dedupKey: `cal|${day.dateIso}|${nameOnly}`,
              sortIso: day.dateIso,
              datePart,
              name: nameOnly,
              kind: 'calendar',
              tagCommon,
              gradeNums: tagCommon ? [] : registeredSchoolGradeNums,
            });
          }
        });
        fixedProgramSummaries.forEach((summary) => {
          if (summary.mondayIso !== week.mondayIso) return;
          if (summary.dateIso < termStarts || summary.dateIso > termEnds) return;
          if (operationPlanScopeGradeId && !summary.gradeIds.includes(operationPlanScopeGradeId)) return;
          if (summary.isGeneralMemo && !operationPlanShowGeneralMemos) return;
          const weekdayLabel = operationPlanWeekdayLabelByKey[summary.weekdayKey] ?? summary.weekdayKey;
          const datePart = formatOperationPlanActivityDate(summary.dateIso, weekdayLabel);
          const eventName = (summary.eventName || '').trim() || '고정 프로그램';
          const colorHex = summary.colorHex || eventDefaultColor;
          const gradeNums = gradeNumbersFromClassGradeIds(summary.gradeIds, classEntries);
          const tagCommon = isOperationPlanCommonGradeTag(gradeNums, registeredSchoolGradeNums);
          pushActivityLine(activityLines, activitySet, {
            dedupKey: `fix|${summary.id}`,
            sortIso: summary.dateIso,
            datePart,
            name: eventName,
            kind: 'fixed',
            colorHex,
            isGeneralMemo: summary.isGeneralMemo,
            tagCommon,
            gradeNums: tagCommon ? [] : gradeNums,
          });
        });
        weekShortDays.forEach((summary) => {
          const weekdayLabel = operationPlanWeekdayLabelByKey[summary.weekdayKey] ?? summary.weekdayKey;
          const datePart = formatOperationPlanActivityDate(summary.dateIso, weekdayLabel);
          const targetLabel = formatShortDayTargetLabel(summary.endSlotTarget);
          if (!targetLabel) return;
          const isIncreaseOnly = summary.increaseCount > 0 && summary.decreaseCount === 0;
          const name = isIncreaseOnly ? `${targetLabel}로 증배` : `${targetLabel}로 단축`;
          const gradeNums = gradeNumbersFromClassGradeIds(summary.gradeIds, classEntries);
          const tagCommon = isOperationPlanCommonGradeTag(gradeNums, registeredSchoolGradeNums);
          pushActivityLine(activityLines, activitySet, {
            dedupKey: `sd|${summary.id}|${isIncreaseOnly ? 'inc' : 'dec'}`,
            sortIso: summary.dateIso,
            datePart,
            name,
            kind: 'shortday',
            tagCommon,
            gradeNums: tagCommon ? [] : gradeNums,
          });
        });
        activityLines.sort((a, b) => {
          const byDate = a.sortIso.localeCompare(b.sortIso);
          if (byDate !== 0) return byDate;
          return kindOrder[a.kind] - kindOrder[b.kind];
        });
        const classHours = operationPlanActiveClassHeaders
          .filter((header) => header.gradeId !== 'none')
          .map((header) => ({
            columnKey: header.columnKey,
            gradeId: header.gradeId,
            classLabel: header.classLabel,
            gradeNumber: header.gradeNumber,
            hours: getWeekClassHours(header.gradeId, week),
            adjusted: adjustedGradeIdSet.has(header.gradeId),
          }));
        const weekActivityRemarkTexts: string[] = [];
        classEntries.forEach((ce) => {
          if (operationPlanScopeGradeId && ce.gradeId !== operationPlanScopeGradeId) return;
          const memoKey = weekMemoStateKey(termMeta.termNumber, ce.gradeId, week.mondayIso);
          const wm = annualWeekMemosByKey[memoKey];
          (wm?.activityLines ?? []).forEach((raw) => {
            const t = (raw ?? '').trim();
            if (t) weekActivityRemarkTexts.push(t);
          });
        });
        return {
          monthLabel: week.monthLabel,
          weekIndex: week.weekIndex,
          instructionalDayCount,
          weekdays,
          classHours,
          activityLines,
          weekActivityRemarkTexts,
        };
      });
    },
    [
      annualWeekMemosByKey,
      classEntries,
      fixedProgramSummaries,
      eventDefaultColor,
      operationPlanWeekdays,
      calendarDayKindByDate,
      operationPlanWeekdayLabelByKey,
      calendarDayNoteByDate,
      operationPlanActiveClassHeaders,
      operationPlanScopeGradeId,
      getWeekClassHours,
      shortDaySummaries,
      operationPlanShowGeneralMemos,
    ]
  );

  const operationPlanView = React.useMemo(() => {
    const settings = calendarLinkedContext.sectionSettings;
    type OperationPlanTableBodyItem =
      | { kind: 'week'; key: string; row: OperationPlanWeekRow }
      | { kind: 'footer'; footer: OperationPlanFooterRow };

    if (operationPlanTerm === 'all') {
      const sections: Array<{
        termNumber: 1 | 2;
        rows: OperationPlanWeekRow[];
        totals: ReturnType<typeof computeOperationPlanTotals>;
        vacationText: string;
      }> = [];
      let grandTotals: ReturnType<typeof computeOperationPlanTotals> = {
        totalInstructionalDays: 0,
        totalHoursByColumnKey: {},
      };
      for (const termNo of [1, 2] as const) {
        const meta = operationPlanTermMetaByNumber[termNo];
        if (!meta) continue;
        const rows = buildOperationPlanRowsForTermMeta(meta);
        if (rows.length === 0) continue;
        const totals = computeOperationPlanTotals(rows);
        grandTotals = sections.length === 0 ? totals : mergeOperationPlanTotals(grandTotals, totals);
        sections.push({
          termNumber: termNo,
          rows,
          totals,
          vacationText: getOperationPlanVacationText(termNo, settings),
        });
      }
      const tableBodyItems: OperationPlanTableBodyItem[] = [];
      sections.forEach((section) => {
        section.rows.forEach((row) => {
          tableBodyItems.push({
            kind: 'week',
            key: `${section.termNumber}-${row.weekIndex}`,
            row,
          });
        });
        tableBodyItems.push({
          kind: 'footer',
          footer: {
            kind: 'subtotal',
            label: `${section.termNumber}학기 소계`,
            termNumber: section.termNumber,
            totalInstructionalDays: section.totals.totalInstructionalDays,
            totalHoursByColumnKey: section.totals.totalHoursByColumnKey,
            vacationText: section.vacationText,
          },
        });
      });
      if (sections.length > 0) {
        tableBodyItems.push({
          kind: 'footer',
          footer: {
            kind: 'grandTotal',
            label: '총계',
            totalInstructionalDays: grandTotals.totalInstructionalDays,
            totalHoursByColumnKey: grandTotals.totalHoursByColumnKey,
            vacationText: '',
          },
        });
      }
      return {
        tableBodyItems,
        rows: sections.flatMap((section) => section.rows),
        footerRows: tableBodyItems
          .filter((item): item is { kind: 'footer'; footer: OperationPlanFooterRow } => item.kind === 'footer')
          .map((item) => item.footer),
        hasData: sections.length > 0,
        totals: grandTotals,
        vacationText: '',
      };
    }
    const meta = operationPlanTermMeta;
    if (!meta) {
      return {
        tableBodyItems: [] as OperationPlanTableBodyItem[],
        rows: [] as OperationPlanWeekRow[],
        footerRows: [] as OperationPlanFooterRow[],
        hasData: false,
        totals: null,
        vacationText: '',
      };
    }
    const rows = buildOperationPlanRowsForTermMeta(meta);
    const totals = computeOperationPlanTotals(rows);
    const vacationText = getOperationPlanVacationText(meta.termNumber, settings);
    const grandFooter: OperationPlanFooterRow = {
      kind: 'grandTotal',
      label: '총계',
      termNumber: meta.termNumber,
      totalInstructionalDays: totals.totalInstructionalDays,
      totalHoursByColumnKey: totals.totalHoursByColumnKey,
      vacationText,
    };
    const tableBodyItems: OperationPlanTableBodyItem[] = [
      ...rows.map((row) => ({
        kind: 'week' as const,
        key: `${meta.termNumber}-${row.weekIndex}`,
        row,
      })),
      { kind: 'footer' as const, footer: grandFooter },
    ];
    return {
      tableBodyItems,
      rows,
      footerRows: [grandFooter],
      hasData: rows.length > 0,
      totals,
      vacationText,
    };
  }, [
    buildOperationPlanRowsForTermMeta,
    calendarLinkedContext.sectionSettings,
    operationPlanTerm,
    operationPlanTermMeta,
    operationPlanTermMetaByNumber,
  ]);

  const operationPlanTableBodyItems = operationPlanView.tableBodyItems;
  const operationPlanRows = operationPlanView.rows;
  const operationPlanFooterRows = operationPlanView.footerRows;
  const operationPlanTotals = operationPlanView.totals ?? {
    totalInstructionalDays: 0,
    totalHoursByColumnKey: {},
  };
  const operationPlanVacationText = operationPlanView.vacationText;

  const canExportOperationPlan = operationPlanView.hasData;

  const operationPlanExportSubtitle = React.useMemo(
    () =>
      buildOperationPlanExportSubtitle({
        academicYear,
        termSelection: operationPlanTerm,
        termMetaByNumber: {
          1: operationPlanTermMetaByNumber[1]
            ? {
                startsOn: operationPlanTermMetaByNumber[1]!.startsOn,
                endsOn: operationPlanTermMetaByNumber[1]!.endsOn,
              }
            : undefined,
          2: operationPlanTermMetaByNumber[2]
            ? {
                startsOn: operationPlanTermMetaByNumber[2]!.startsOn,
                endsOn: operationPlanTermMetaByNumber[2]!.endsOn,
              }
            : undefined,
        },
      }),
    [academicYear, operationPlanTerm, operationPlanTermMetaByNumber]
  );

  const buildOperationPlanExportFileBase = useCallback(() => {
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    const termPart =
      operationPlanTerm === 'all' ? '전체' : operationPlanTermMeta ? `${operationPlanTermMeta.termNumber}학기` : '';
    const scopePart = operationPlanScopeClassBlock?.title
      ? operationPlanScopeClassBlock.title.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48)
      : '전체';
    return `운영계획_${academicYear}학년도_${termPart}_${scopePart}_${dateLabel}`;
  }, [academicYear, operationPlanScopeClassBlock?.title, operationPlanTerm, operationPlanTermMeta]);

  const mapOperationPlanWeekRowToExport = useCallback(
    (row: OperationPlanWeekRow) => ({
      rowKind: 'week' as const,
      monthLabel: row.monthLabel,
      weekIndex: row.weekIndex,
      instructionalDayCount: row.instructionalDayCount,
      weekdayCells: row.weekdays.map((day) => ({
        value: day.dayNumber,
        inTerm: day.inTerm,
        dayKind: day.dayKind as 'class' | 'holiday' | 'closure' | 'vacation',
        fixedDayGeneralMemoOnly: day.fixedDayGeneralMemoOnly,
        fixedDayTextColor: day.fixedDayTextColor || undefined,
        hasFixedEvents: day.fixedEvents.length > 0,
      })),
      classHourCells: operationPlanActiveClassHeaders.map((header) => {
        if (header.gradeId === 'none') {
          return { value: '-', adjusted: false };
        }
        const match = row.classHours.find((classHour) => classHour.columnKey === header.columnKey);
        return {
          value: match ? match.hours : '-',
          adjusted: match?.adjusted ?? false,
        };
      }),
      activityLines: row.activityLines.map((line) => ({
        datePart: line.datePart,
        tagCommon: line.tagCommon,
        gradeNums: line.gradeNums,
        name: line.name,
        kind: line.kind,
        colorHex: line.colorHex,
        isGeneralMemo: line.isGeneralMemo,
      })),
      remarkTexts: row.weekActivityRemarkTexts,
    }),
    [operationPlanActiveClassHeaders]
  );

  const buildOperationPlanDocumentPayload = useCallback((): AnnualOperationPlanDocumentPayload | null => {
    if (!canExportOperationPlan) return null;
    return {
      title: '연간 수업 일수 및 시수 운영계획',
      metaSubtitle: operationPlanExportSubtitle,
      scopeClassTitle: operationPlanScopeClassBlock?.title,
      scopeGradeNumbers: operationPlanScopeClassBlock?.grades.map(String),
      weekdays: operationPlanWeekdays.map((weekday) => ({ label: weekday.label })),
      classHeaders: operationPlanActiveClassHeaders.map((header) => ({
        columnKey: header.columnKey,
        gradeNumber: header.gradeNumber,
        gradeId: header.gradeId,
      })),
      rows: operationPlanRows.map((row) => mapOperationPlanWeekRowToExport(row)),
      totalInstructionalDays: operationPlanTotals.totalInstructionalDays,
      totalHoursByColumnKey: operationPlanTotals.totalHoursByColumnKey,
      vacationText: operationPlanVacationText,
      footerRows: operationPlanFooterRows,
      tableBodyItems: operationPlanTableBodyItems.map((item) =>
        item.kind === 'week'
          ? { kind: 'week' as const, row: mapOperationPlanWeekRowToExport(item.row) }
          : { kind: 'footer' as const, footer: item.footer }
      ),
    };
  }, [
    canExportOperationPlan,
    mapOperationPlanWeekRowToExport,
    operationPlanActiveClassHeaders,
    operationPlanExportSubtitle,
    operationPlanFooterRows,
    operationPlanRows,
    operationPlanScopeClassBlock?.grades,
    operationPlanScopeClassBlock?.title,
    operationPlanTableBodyItems,
    operationPlanTotals.totalHoursByColumnKey,
    operationPlanTotals.totalInstructionalDays,
    operationPlanVacationText,
    operationPlanWeekdays,
  ]);

  const openOperationPlanPreviewTab = useCallback(
    (action: 'pdf' | 'print') => {
      const data = buildOperationPlanDocumentPayload();
      if (!data) return;
      const placeholder = openDocumentExportPreviewTabPlaceholder();
      const result = openDocumentExportPreviewInNewTab(
        {
          v: 1,
          kind: 'annual-operation-plan',
          fileNameBase: buildOperationPlanExportFileBase(),
          printTitle: `연간 수업 일수 및 시수 운영계획 — ${operationPlanExportSubtitle}`,
          data,
        },
        action,
        { preOpenedWindow: placeholder }
      );
      if (!result.ok) {
        alert(openDocumentExportPreviewFailureMessage(result.reason));
      }
    },
    [buildOperationPlanDocumentPayload, buildOperationPlanExportFileBase, operationPlanExportSubtitle]
  );

  const handleOperationPlanSavePdf = useCallback(() => {
    openOperationPlanPreviewTab('pdf');
  }, [openOperationPlanPreviewTab]);

  const handleOperationPlanSaveExcel = useCallback(async () => {
    if (!canExportOperationPlan) return;
    const payload = buildOperationPlanDocumentPayload();
    if (!payload) return;
    setOperationPlanExportBusy(true);
    try {
      await saveAnnualOperationPlanAsExcel({
        title: payload.title,
        metaSubtitle: payload.metaSubtitle,
        scopeClassTitle: payload.scopeClassTitle,
        weekdays: payload.weekdays,
        classHeaders: payload.classHeaders.map((header) => ({
          columnKey: header.columnKey,
          gradeNumber: header.gradeNumber,
        })),
        rows: payload.rows,
        totalInstructionalDays: payload.totalInstructionalDays,
        totalHoursByColumnKey: payload.totalHoursByColumnKey,
        vacationText: payload.vacationText,
        footerRows: payload.footerRows,
        tableBodyItems: payload.tableBodyItems,
        fileName: `${buildOperationPlanExportFileBase()}.xlsx`,
      });
    } catch (error) {
      console.error('운영계획 엑셀 저장 실패:', error);
      alert('엑셀 저장에 실패했습니다.');
    } finally {
      setOperationPlanExportBusy(false);
    }
  }, [buildOperationPlanDocumentPayload, buildOperationPlanExportFileBase, canExportOperationPlan]);

  const handleOperationPlanPrint = useCallback(() => {
    openOperationPlanPreviewTab('print');
  }, [openOperationPlanPreviewTab]);

  const syncEventDateFields = useCallback((iso: string) => {
    setEventDateIso(iso);
  }, []);

  const resetEventModalForm = useCallback(
    (initialDateIso?: string) => {
      const nextIso =
        initialDateIso !== undefined
          ? initialDateIso
          : (() => {
              const todayIso = toISODate(new Date());
              return eventDateTargets[todayIso]
                ? todayIso
                : Object.keys(eventDateTargets).sort((a, b) => a.localeCompare(b))[0] ?? '';
            })();
      syncEventDateFields(nextIso);
      setEventModalPeriodState({ selected: [], namesByTarget: {} });
      setEventMemoText('');
      setEventColorHex(eventDefaultColor);
      setEventEditingSourceKeys([]);
      setEventEditingProgramId(null);
      setEventSelectedGradeIds([]);
      setEventModalError(null);
    },
    [eventDateTargets, eventDefaultColor, syncEventDateFields]
  );

  const handleOpenEventModal = useCallback(
    (preferredGradeId?: string) => {
      const resolved = resolveAnnualModalInitialDateIso(
        selectedCellsRef.current,
        effectiveCellsByKeyRef.current,
        eventDateTargets,
        calendarStartsOn
      );
      resetEventModalForm(resolved);
      if (preferredGradeId && classEntryByGradeId[preferredGradeId]) {
        setEventSelectedGradeIds([preferredGradeId]);
      }
      setEventModalOpen(true);
    },
    [calendarStartsOn, classEntryByGradeId, eventDateTargets, resetEventModalForm]
  );

  /** '고정 프로그램 관리' → 일괄 관리 표. 열기 전에 시간표 셀(등록 모달이 넣은 행사)을 같은 목록으로 맞춘다. */
  const handleOpenManageFixedModal = useCallback(() => {
    const snapshots = fixedProgramSummaries
      .filter((sm) => !sm.isGeneralMemo)
      .map((sm) => ({
        dateIso: sm.dateIso,
        eventName: sm.eventName,
        colorHex: sm.colorHex,
        gradeIds: sm.gradeIds,
        periodKeys: sm.periodKeys,
        timetableByPeriodKey: sm.timetableByPeriodKey,
        cellKeys: sm.cellKeys,
      }));
    const merged = mergeFixedDraftsFromCellSnapshots(loadFixedDrafts(workspaceId), snapshots, {
      pageGradeIds: classEntries.map((entry) => entry.gradeId),
      liveCellKeys: new Set(Object.keys(effectiveCellsByKeyRef.current)),
    });
    if (merged.changed) saveFixedDrafts(workspaceId, merged.drafts);
    setBulkFixedModalOpen(true);
  }, [classEntries, fixedProgramSummaries, workspaceId]);

  const handleOpenManageShortDayModal = useCallback(() => {
    setManageShortDayModalOpen(true);
  }, []);

  const handleCloseManageShortDayModal = useCallback(() => {
    setManageShortDayModalOpen(false);
  }, []);

  const handleCloseEventModal = useCallback(() => {
    setEventModalOpen(false);
    setEventModalError(null);
  }, []);

  const handleOpenOperationPlanModal = useCallback(
    (scopeGradeId: string) => {
      const programTermRaw = searchParams.get('programTerm');
      const termFromUrl = programTermRaw === '1' || programTermRaw === '2' ? (Number(programTermRaw) as 1 | 2) : null;
      const nextTerm =
        termFromUrl && operationPlanAvailableTerms.includes(termFromUrl)
          ? termFromUrl
          : (operationPlanAvailableTerms[0] ?? 1);
      setOperationPlanScopeGradeId(scopeGradeId);
      setOperationPlanTerm(nextTerm);
      setOperationPlanShowGeneralMemos(false);
      setOperationPlanModalOpen(true);
    },
    [operationPlanAvailableTerms, searchParams]
  );

  const handleCloseOperationPlanModal = useCallback(() => {
    setOperationPlanModalOpen(false);
    setOperationPlanScopeGradeId(null);
    setOperationPlanShowGeneralMemos(false);
  }, []);

  const toggleEventSlotTarget = useCallback((target: EventSlotTargetKey) => {
    setEventModalPeriodState((prev) => {
      const namesByTarget = { ...prev.namesByTarget };
      if (prev.selected.includes(target)) {
        delete namesByTarget[target];
        return { selected: prev.selected.filter((t) => t !== target), namesByTarget };
      }
      return {
        selected: [...prev.selected, target],
        namesByTarget: { ...namesByTarget, [target]: namesByTarget[target] ?? '' },
      };
    });
  }, []);

  const updateEventModalNameForSlot = useCallback((target: EventSlotTargetKey, value: string) => {
    setEventModalPeriodState((prev) => ({
      ...prev,
      namesByTarget: { ...prev.namesByTarget, [target]: value },
    }));
  }, []);

  const toggleEventGrade = useCallback((gradeId: string) => {
    setEventSelectedGradeIds((prev) => {
      if (prev.includes(gradeId)) return prev.filter((id) => id !== gradeId);
      return [...prev, gradeId];
    });
  }, []);

  const selectAllEventClasses = useCallback(() => {
    if (availableEventClasses.length === 0) return;
    setEventSelectedGradeIds(availableEventClasses.map((e) => e.gradeId));
    setEventModalError(null);
  }, [availableEventClasses]);

  const clearAllEventClasses = useCallback(() => {
    setEventSelectedGradeIds([]);
    setEventModalError(null);
  }, []);

  const allEventClassesSelected =
    availableEventClasses.length > 0 &&
    availableEventClasses.every((e) => eventSelectedGradeIds.includes(e.gradeId));

  const isEventModalApplyReady = React.useMemo(() => {
    const resolvedDateIso = normalizeIsoDateInput(eventDateIso);
    if (!resolvedDateIso || !eventDateTargets[resolvedDateIso]) return false;
    if (eventSelectedSlotTargets.length === 0) return false;
    if (eventSelectedGradeIds.length === 0) return false;
    return true;
  }, [eventDateIso, eventDateTargets, eventSelectedGradeIds, eventSelectedSlotTargets]);

  const openEventModalFromSelectedCells = useCallback(
    (colorHex: string) => {
      const currentSel = selectedCellsRef.current;
      if (!currentSel.length) return false;
      const parsed = currentSel
        .map((key) => {
          const cell = effectiveCellsByKeyRef.current[key];
          if (!cell) return null;
          return { key, cell };
        })
        .filter((item): item is NonNullable<typeof item> => item != null);
      if (!parsed.length) return false;

      const first = parsed[0];
      const defaultGradeId = first.cell.gradeId;
      const targetDateIso = first.cell.dateIso;
      const sameGradeAndDate = parsed.filter(
        ({ cell }) => cell.gradeId === defaultGradeId && cell.dateIso === targetDateIso
      );
      const periodSource = sameGradeAndDate.length > 0 ? sameGradeAndDate : parsed;
      const sourceEntry = classEntries.find((entry) => entry.gradeId === defaultGradeId);
      const targets = Array.from(
        new Set(
          periodSource
            .map(({ cell }) => eventSlotTargetKeyBySlotIndex(sourceEntry?.sectionRows ?? [], cell.slotIndex))
            .filter((v): v is EventSlotTargetKey => v != null)
        )
      ).sort(compareFixedProgramPeriodKeys);
      const namesByTarget: Partial<Record<EventSlotTargetKey, string>> = {};
      periodSource.forEach(({ cell }) => {
        const t = eventSlotTargetKeyBySlotIndex(sourceEntry?.sectionRows ?? [], cell.slotIndex);
        if (!t) return;
        const tv = (cell.textValue ?? '').trim();
        namesByTarget[t] = tv || (cell.memo ?? '').trim();
      });
      const memoFromFirst = (first.cell.memo ?? '').trim();

      syncEventDateFields(targetDateIso);
      setEventModalPeriodState({ selected: targets, namesByTarget });
      setEventMemoText(memoFromFirst);
      setEventSelectedGradeIds([defaultGradeId]);
      setEventColorHex(colorHex || eventDefaultColor);
      setEventEditingSourceKeys([]);
      setEventEditingProgramId(null);
      setEventModalError(null);
      setEventModalOpen(true);

      if (
        parsed.some(({ cell }) => cell.dateIso !== targetDateIso) ||
        parsed.some(({ cell }) => cell.gradeId !== defaultGradeId)
      ) {
        showInfo('선택한 첫 셀 기준으로 날짜/클래스를 자동 입력했습니다.');
      }
      return true;
    },
    [classEntries, eventDefaultColor, showInfo, syncEventDateFields]
  );
  openEventModalFromSelectedCellsRef.current = openEventModalFromSelectedCells;


  const handleEventMemoInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      eventModalApplyBtnRef.current?.focus();
    }
  }, []);

  const handleApplyEventModal = useCallback(() => {
    const resolvedDateIso = normalizeIsoDateInput(eventDateIso);
    if (!resolvedDateIso) {
      setEventModalError('날짜를 선택해 주세요.');
      return;
    }
    const target = eventDateTargets[resolvedDateIso];
    if (!target) {
      setEventModalError('선택한 일자가 현재 학년도 학기 범위에 없습니다.');
      return;
    }
    if (eventSelectedSlotTargets.length === 0) {
      setEventModalError('반영할 수업/방과후를 1개 이상 선택해 주세요.');
      return;
    }
    if (eventSelectedGradeIds.length === 0) {
      setEventModalError('적용 클래스를 1개 이상 선택해 주세요.');
      return;
    }
    const updateMap = new Map<
      string,
      {
        key: string;
        textValue?: string;
        memo?: string;
        colorHex?: string;
        slotKind?: AnnualTimetableSlotKind;
        isBaseExcluded?: boolean;
      }
    >();
    eventEditingSourceKeys.forEach((key) => {
      updateMap.set(key, {
        key,
        textValue: '',
        memo: '',
        colorHex: '',
        isBaseExcluded: false,
      });
    });
    eventSelectedGradeIds.forEach((gradeId) => {
      const entry = classEntries.find((e) => e.gradeId === gradeId);
      if (!entry) return;
      eventSelectedSlotTargets.forEach((slotTarget) => {
        const slotIndex = findSlotIndexByEventSlotTarget(entry.sectionRows, slotTarget);
        if (slotIndex < 0) return;
        const parsed = parseEventSlotTargetKey(slotTarget);
        if (!parsed) return;
        const rawName = (eventNamesBySlotTarget[slotTarget] ?? '').trim();
        const resolvedName = rawName || DEFAULT_EVENT_TIMETABLE_CELL_LABEL;
        const memoTrim = eventMemoText.trim();
        updateMap.set(compoundCellKey(gradeId, target.mondayIso, target.weekdayKey, slotIndex), {
          key: compoundCellKey(gradeId, target.mondayIso, target.weekdayKey, slotIndex),
          textValue: resolvedName,
          memo: memoTrim,
          colorHex: eventColorHex || eventDefaultColor,
          isBaseExcluded: false,
          slotKind: parsed.kind === 'class' ? 'class' : 'non_class',
        });
      });
    });
    applyCellUpdates(Array.from(updateMap.values()));
    syncEventDateFields(resolvedDateIso);
    setEventModalOpen(false);
    setEventModalError(null);
    setEventEditingSourceKeys([]);
    setEventEditingProgramId(null);
    showInfo(
      `${resolvedDateIso} ${eventSelectedGradeIds.length}개 클래스에 행사(${eventSelectedSlotTargets.length}개 구간)가 반영되었습니다.`
    );
  }, [
    applyCellUpdates,
    classEntries,
    eventDateIso,
    eventDateTargets,
    eventEditingSourceKeys,
    eventColorHex,
    eventDefaultColor,
    eventMemoText,
    eventNamesBySlotTarget,
    eventSelectedGradeIds,
    eventSelectedSlotTargets,
    showInfo,
    syncEventDateFields,
  ]);

  /**
   * 고정 프로그램 단일 목록 ↔ 연간시간표 셀.
   * 1) 확정 항목 → 셀 반영(내용이 바뀌면 이전 셀을 비우고 다시 씀, 삭제 묘비는 셀 정리).
   * 2) 반영할 게 없을 때: 기존 '고정 프로그램' 모달로 등록/수정/삭제된 셀을 목록에 맞춰 넣음(편입·수정·삭제 동기화).
   */
  const [fixedDraftsTick, setFixedDraftsTick] = useState(0);
  useEffect(() => {
    if (!workspaceId) return undefined;
    return subscribeFixedDrafts(workspaceId, () => setFixedDraftsTick((n) => n + 1));
  }, [workspaceId]);
  useEffect(() => {
    if (!workspaceId || loading || classEntries.length === 0) return;
    const drafts = loadFixedDrafts(workspaceId);
    const color = eventModalFixedProgramColors[0] ?? eventDefaultColor;
    const updates: Parameters<typeof applyCellUpdates>[0] = [];
    let changed = false;
    let next = drafts.map((d) => {
      const applied = { ...(d.applied ?? {}) };
      const sig = draftApplySig(d);
      const confirmed = isDraftConfirmed(d);
      let touched = false;
      classEntries.forEach((entry) => {
        const gid = entry.gradeId;
        const prev = applied[gid];
        const wants = confirmed && d.gradeIds.includes(gid);
        if (prev && (!wants || prev.sig !== sig)) {
          prev.cellKeys.forEach((key) => {
            /** 기존 고정 프로그램 삭제와 같이: 색·메모만 지우고 시간표명은 유지 */
            if (effectiveCellsByKeyRef.current[key]?.isEditable) updates.push({ key, memo: '', colorHex: '', isBaseExcluded: false });
          });
          delete applied[gid];
          touched = true;
        }
        if (wants && !applied[gid]) {
          const target = eventDateTargets[d.date];
          if (!target) return;
          const keys: string[] = [];
          d.periods.forEach((pk) => {
            const parsed = parseEventSlotTargetKey(pk);
            if (!parsed) return;
            const slotIndex = findSlotIndexByEventSlotTarget(entry.sectionRows, pk as EventSlotTargetKey);
            if (slotIndex < 0) return;
            const key = compoundCellKey(gid, target.mondayIso, target.weekdayKey, slotIndex);
            if (!effectiveCellsByKeyRef.current[key]?.isEditable) return;
            keys.push(key);
            updates.push({
              key,
              textValue: draftPeriodName(d, pk),
              memo: d.name.trim(),
              colorHex: (d.colorHex ?? '').trim() || color,
              isBaseExcluded: false,
              slotKind: parsed.kind === 'class' ? 'class' : 'non_class',
            });
          });
          if (keys.length) {
            applied[gid] = { sig, cellKeys: keys };
            touched = true;
          }
        }
      });
      if (touched) changed = true;
      return touched ? { ...d, applied } : d;
    });
    if (updates.length) {
      applyCellUpdates(updates);
    }

    /* 등록 모달이 셀에 넣은 행사 → 관리 목록. 이번 턴에 다시 쓰는 칸은 옛 값으로 덮지 않는다. */
    const mergedFromCells = mergeFixedDraftsFromCellSnapshots(next, fixedProgramSummaries.filter((sm) => !sm.isGeneralMemo).map((sm) => ({
      dateIso: sm.dateIso,
      eventName: sm.eventName,
      colorHex: sm.colorHex,
      gradeIds: sm.gradeIds,
      periodKeys: sm.periodKeys,
      timetableByPeriodKey: sm.timetableByPeriodKey,
      cellKeys: sm.cellKeys,
    })), {
      pageGradeIds: classEntries.map((entry) => entry.gradeId),
      skipCellKeys: new Set(updates.map((update) => update.key)),
      liveCellKeys: updates.length ? undefined : new Set(Object.keys(effectiveCellsByKeyRef.current)),
    });
    if (changed || mergedFromCells.changed) saveFixedDrafts(workspaceId, mergedFromCells.drafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, loading, classEntries, eventDateTargets, fixedDraftsTick, fixedProgramSummaries]);

  const handleOpenShortDayModal = useCallback(
    (preferredGradeId?: string) => {
      const resolved = resolveAnnualModalInitialDateIso(
        selectedCellsRef.current,
        effectiveCellsByKeyRef.current,
        eventDateTargets,
        calendarStartsOn
      );
      setShortDayDateIso(resolved);
      setShortDaySelectedGradeIds(
        preferredGradeId && classEntryByGradeId[preferredGradeId]
          ? [preferredGradeId]
          : classEntries.map((e) => e.gradeId)
      );
      setShortDayModalError(null);
      setShortDayModalOpen(true);
    },
    [calendarStartsOn, classEntries, classEntryByGradeId, eventDateTargets]
  );

  const handleEditShortDaySummary = useCallback(
    (summary: ShortDaySummary) => {
      const resolved = normalizeIsoDateInput(summary.dateIso);
      if (!resolved || !eventDateTargets[resolved]) {
        showInfo('해당 일자는 현재 학년도 학기 범위에 없습니다.');
        return;
      }
      setShortDayDateIso(resolved);
      setShortDaySelectedGradeIds([...summary.gradeIds]);
      setShortDayEndSlotTarget(summary.endSlotTarget ?? shortDayOrderedSlotTargets[0] ?? null);
      setShortDayModalError(null);
      setManageShortDayModalOpen(false);
      setShortDayModalOpen(true);
    },
    [eventDateTargets, shortDayOrderedSlotTargets, showInfo]
  );

  const handleCloseShortDayModal = useCallback(() => {
    setShortDayModalOpen(false);
    setShortDayModalError(null);
  }, []);

  const toggleShortDayGrade = useCallback((gradeId: string) => {
    setShortDaySelectedGradeIds((prev) => {
      if (prev.includes(gradeId)) return prev.filter((id) => id !== gradeId);
      return [...prev, gradeId];
    });
    setShortDayModalError(null);
  }, []);

  const selectAllShortDayClasses = useCallback(() => {
    if (classEntries.length === 0) return;
    setShortDaySelectedGradeIds(classEntries.map((e) => e.gradeId));
    setShortDayModalError(null);
  }, [classEntries]);

  const clearAllShortDayClasses = useCallback(() => {
    setShortDaySelectedGradeIds([]);
    setShortDayModalError(null);
  }, []);

  const allShortDayClassesSelected =
    classEntries.length > 0 && classEntries.every((e) => shortDaySelectedGradeIds.includes(e.gradeId));

  const isShortDayModalApplyReady = React.useMemo(() => {
    const resolved = normalizeIsoDateInput(shortDayDateIso);
    if (!resolved || !eventDateTargets[resolved]) return false;
    if (!shortDayEndSlotTarget || !shortDayOrderedSlotTargets.includes(shortDayEndSlotTarget)) return false;
    return shortDaySelectedGradeIds.length > 0;
  }, [
    eventDateTargets,
    shortDayDateIso,
    shortDayEndSlotTarget,
    shortDayOrderedSlotTargets,
    shortDaySelectedGradeIds,
  ]);

  const handleApplyShortDayModal = useCallback(() => {
    const resolvedDateIso = normalizeIsoDateInput(shortDayDateIso);
    if (!resolvedDateIso) {
      setShortDayModalError('날짜를 선택해 주세요.');
      return;
    }
    const target = eventDateTargets[resolvedDateIso];
    if (!target) {
      setShortDayModalError('선택한 일자가 현재 학년도 학기 범위에 없습니다.');
      return;
    }
    if (shortDaySelectedGradeIds.length === 0) {
      setShortDayModalError('적용 클래스를 1개 이상 선택해 주세요.');
      return;
    }
    const endKey = shortDayEndSlotTarget;
    if (!endKey || !shortDayOrderedSlotTargets.includes(endKey)) {
      setShortDayModalError('마지막으로 포함할 수업·방과후 구간을 선택해 주세요.');
      return;
    }
    const endIdxInOrder = shortDayOrderedSlotTargets.indexOf(endKey);

    const updates: Array<{
      key: string;
      textValue?: string;
      memo?: string;
      colorHex?: string;
      slotKind?: AnnualTimetableSlotKind;
      isBaseExcluded?: boolean;
    }> = [];

    shortDaySelectedGradeIds.forEach((gradeId) => {
      const entry = classEntries.find((e) => e.gradeId === gradeId);
      if (!entry) return;
      const shortDayTermNum = target.termNumber as 1 | 2;
      const baseGrid = getBaseTimetableGridForTerm(baseTimetableByGrade[gradeId] ?? null, shortDayTermNum);
      const sectionRows = entry.sectionRows;
      let endSlotIndex = -1;
      for (let i = 0; i <= endIdxInOrder; i += 1) {
        const k = shortDayOrderedSlotTargets[i];
        if (!k) continue;
        const si = findSlotIndexByEventSlotTarget(sectionRows, k);
        if (si >= 0) endSlotIndex = Math.max(endSlotIndex, si);
      }
      if (endSlotIndex < 0) return;
      /** 선택 구간(0..endSlotIndex) 안에서 편제상 마지막 수업(class) 행 인덱스. 없으면 -1 */
      let lastClassTemplateIdx = -1;
      for (let i = 0; i <= endSlotIndex; i += 1) {
        if (sectionRows[i]?.kind === 'class') lastClassTemplateIdx = i;
      }
      for (let slotIndex = 0; slotIndex < MAX_PERIODS; slotIndex += 1) {
        const key = compoundCellKey(gradeId, target.mondayIso, target.weekdayKey, slotIndex);
        const current = effectiveCellsByKeyRef.current[key];
        if (!current?.isEditable) continue;
        const row = sectionRows[slotIndex];
        const rowKind = row?.kind;

        if (slotIndex <= endSlotIndex) {
          if (rowKind === 'activity') {
            const beforeOrAtLastClass =
              lastClassTemplateIdx < 0 || slotIndex <= lastClassTemplateIdx;
            if (beforeOrAtLastClass) {
              updates.push({
                key,
                textValue: '',
                memo: '',
                colorHex: '',
                isBaseExcluded: false,
                slotKind: 'activity',
              });
              continue;
            }
            const baseIdxAct = baseGridClassPeriodIndexForSectionSlot(sectionRows, slotIndex);
            const baseCell =
              baseIdxAct >= 0 ? getBaseGridCell(baseGrid, target.weekdayKey, baseIdxAct) : null;
            const name = (baseCell?.name ?? '').trim();
            const bg = (baseCell?.colorHex ?? '').trim();
            updates.push({
              key,
              textValue: name,
              memo: '',
              colorHex: bg,
              isBaseExcluded: false,
              slotKind: 'class',
            });
            continue;
          }
          const baseIdx = baseGridClassPeriodIndexForSectionSlot(sectionRows, slotIndex);
          const baseCell = baseIdx >= 0 ? getBaseGridCell(baseGrid, target.weekdayKey, baseIdx) : null;
          const name = (baseCell?.name ?? '').trim();
          const bg = (baseCell?.colorHex ?? '').trim();
          updates.push({
            key,
            textValue: name,
            memo: '',
            colorHex: bg,
            isBaseExcluded: false,
            slotKind: 'class',
          });
        } else {
          updates.push({
            key,
            textValue: '',
            memo: ANNUAL_SHORT_DAY_TAIL_MEMO,
            colorHex: '',
            isBaseExcluded: false,
            slotKind: 'non_class',
          });
        }
      }
    });

    applyCellUpdates(updates);
    setShortDayModalOpen(false);
    setShortDayModalError(null);
    const endLabel = (() => {
      const p = parseEventSlotTargetKey(endKey);
      return p ? eventSlotTargetLabel(p.kind, p.order) : endKey;
    })();
    showInfo(
      `${resolvedDateIso} 단축·증배: 처음~${endLabel}까지 반영, 그 뒤는 미운영 표시했습니다. (저장하기로 DB 반영)`
    );
  }, [
    applyCellUpdates,
    baseTimetableByGrade,
    classEntries,
    eventDateTargets,
    shortDayDateIso,
    shortDayEndSlotTarget,
    shortDayOrderedSlotTargets,
    shortDaySelectedGradeIds,
    showInfo,
  ]);

  /** 화면(학사일정·편제 반영)과 DB 마지막 저장본이 다름 — 저장 버튼만 (이탈 확인에는 쓰지 않음) */
  const hasPendingCalendarBaselineAlign = React.useMemo(() => {
    const baselineMap = annualCellSavedBaselineRef.current;
    const recordIds = cellRecordIds;
    for (const key of Object.keys(effectiveCellsByKey)) {
      const cell = effectiveCellsByKey[key];
      if (!cell) continue;
      const saved = baselineMap[key];
      if (!saved && !recordIds[key]) continue;
      const targetSnap = annualPersistSnapshotFromEffective(cell);
      if (!saved || !annualCellSnapshotsEqual(targetSnap, saved)) return true;
    }
    return false;
  }, [cellRecordIds, effectiveCellsByKey]);

  /** 사이드바·이탈 모달: 사용자가 직접 바꾼 칸·색상 팔레트 */
  const isLeaveDirty = dirtyCellKeys.length > 0 || programColorsDirty;
  /** 저장 버튼: 사용자 편집 + 학사일정 자동 정렬 미반영 */
  const isDirty = isLeaveDirty || hasPendingCalendarBaselineAlign;

  const syncFixedProgramDefaultRows = useCallback(
    async (savedRows: AnnualTimetableCell[]) => {
      if (!workspaceId || savedRows.length === 0) return;
      const weekdayOrder = Object.fromEntries(
        WEEKDAY_KEYS.map(({ key }, index) => [key, index] as const)
      ) as Record<AnnualWeekdayKey, number>;
      type FixedCandidate = {
        annualKey: string;
        workspaceId: string;
        gradeId: string;
        academicYear: number;
        termNumber: 1 | 2;
        mondayIso: string;
        weekdayKey: AnnualWeekdayKey;
        slotIndex: number;
        timetableText: string;
        eventName: string;
        colorHex: string;
      };
      const affectedScopeKeys = new Set(
        savedRows.map((row) => `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`)
      );
      const candidatesByScope = new Map<string, FixedCandidate[]>();
      /** 행사 색이 아닌 시간표가 들어간 칸 — 이 칸에 묶였던 행사 내용은 삭제 */
      const reassignedAnnualKeysByScope = new Map<string, Set<string>>();
      Object.values(effectiveCellsByKeyRef.current).forEach((cell) => {
        const scopeKey = `${cell.workspaceId}|${cell.academicYear}|${cell.termNumber}|${cell.gradeId}`;
        if (!affectedScopeKeys.has(scopeKey)) return;
        const cellColorHex = (cell.colorHex ?? '').trim();
        if (
          annualSlotHasTimetableProgramAssignment((cell.textValue ?? '').trim(), cellColorHex) &&
          !isFixedProgramColor(cellColorHex, regularColors)
        ) {
          const reassigned = reassignedAnnualKeysByScope.get(scopeKey) ?? new Set<string>();
          reassigned.add(buildAnnualCellKey(cell.gradeId, cell.mondayIso, cell.weekdayKey, cell.slotIndex));
          reassignedAnnualKeysByScope.set(scopeKey, reassigned);
        }
        const eventName = (cell.memo ?? '').trim();
        if (!eventName || isAnnualShortDayTailMemo(eventName)) return;
        const colorHex = (cell.colorHex ?? '').trim();
        const annualKey = buildAnnualCellKey(cell.gradeId, cell.mondayIso, cell.weekdayKey, cell.slotIndex);
        const list = candidatesByScope.get(scopeKey) ?? [];
        list.push({
          annualKey,
          workspaceId: cell.workspaceId,
          gradeId: cell.gradeId,
          academicYear: cell.academicYear,
          termNumber: cell.termNumber,
          mondayIso: cell.mondayIso,
          weekdayKey: cell.weekdayKey,
          slotIndex: cell.slotIndex,
          timetableText: cell.textValue ?? '',
          eventName,
          colorHex,
        });
        candidatesByScope.set(scopeKey, list);
      });

      for (const scopeKey of Array.from(affectedScopeKeys)) {
        const scope = parseGradeTermScopeKey(scopeKey);
        if (!scope) continue;
        const candidates = candidatesByScope.get(scopeKey) ?? [];
        const first = candidates[0] ?? {
          workspaceId: scope.workspaceId,
          academicYear: scope.academicYear,
          termNumber: scope.termNumber,
          gradeId: scope.gradeId,
        };
        const [existingFixedRows, existingProgramCells] = await Promise.all([
          getProgramFixedContentRows(first.workspaceId, first.academicYear, first.termNumber, first.gradeId),
          getProgramProgressCells(first.workspaceId, first.academicYear, first.termNumber, first.gradeId, {
            forceRefresh: true,
          }),
        ]);
        const orderedCandidates = candidates
          .filter((c) => isFixedProgramColor(c.colorHex, regularColors))
          .slice()
          .sort((a, b) => {
            const byDate = a.mondayIso.localeCompare(b.mondayIso);
            if (byDate !== 0) return byDate;
            const byWeekday = (weekdayOrder[a.weekdayKey] ?? 0) - (weekdayOrder[b.weekdayKey] ?? 0);
            if (byWeekday !== 0) return byWeekday;
            return a.slotIndex - b.slotIndex;
          });
        const existingFixedBySource = new Map(
          existingFixedRows
            .map((row) => [((row.source_annual_cell_key ?? '').trim() || ''), row] as const)
            .filter(([key]) => !!key)
        );
        const existingProgramCellByKey = existingProgramCells.reduce<Record<string, (typeof existingProgramCells)[number]>>(
          (acc, row) => {
            acc[buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index)] = row;
            return acc;
          },
          {}
        );
        const claimedFixedIds = new Set<string>();
        const pickExistingFixedRow = (candidate: FixedCandidate) => {
          const byKey = existingFixedBySource.get(candidate.annualKey);
          if (byKey && !claimedFixedIds.has(byKey.id)) {
            claimedFixedIds.add(byKey.id);
            return byKey;
          }
          const ev = normalizeFixedEventName(candidate.eventName);
          const byEvent = existingFixedRows.find((row) => {
            if (claimedFixedIds.has(row.id)) return false;
            const source = (row.source_annual_cell_key ?? '').trim();
            if (source && source !== candidate.annualKey) return false;
            return normalizeFixedEventName(row.event_name ?? '') === ev;
          });
          if (byEvent) claimedFixedIds.add(byEvent.id);
          return byEvent;
        };

        const fixedRowsToUpsert = orderedCandidates.flatMap<ProgramFixedContentRowUpsert>((candidate, index) => {
          const existing = pickExistingFixedRow(candidate);
          const defaultUnitText = `◆${candidate.eventName}`;
          const defaultLearningText = `◆${candidate.eventName}-`;
          if (!existing) {
            if (
              hasSubstantialFixedEventContentElsewhere(
                candidate.eventName,
                candidate.annualKey,
                existingProgramCells,
                existingFixedRows
              )
            ) {
              return [];
            }
            return [
              {
                workspace_id: candidate.workspaceId,
                grade_id: candidate.gradeId,
                academic_year: candidate.academicYear,
                term_number: candidate.termNumber,
                order_index: existingFixedRows.length + index,
                source_annual_cell_key: candidate.annualKey,
                event_name: candidate.eventName,
                timetable_text: '',
                unit_text: defaultUnitText,
                learning_text: defaultLearningText,
                learning_note: '',
                presentation_ref_id: null,
                worksheet_ref_id: null,
                page_text: '',
                assistant_page_text: '',
                current_period_text: '',
                total_period_text: '',
                material_text: '',
                record_cross_curricular_text: '',
                record_memo_text: '',
                record_evaluation_text: '',
                record_class_text: '',
                record_member_group_id: null,
                bg_color_by_field: {},
              },
            ];
          }
          const aligned = alignFixedContentRowToScheduleSlot(
            candidate.annualKey,
            candidate.eventName,
            existing
          );
          if (
            (aligned.event_name ?? '') === (existing.event_name ?? '') &&
            (aligned.unit_text ?? '') === (existing.unit_text ?? '') &&
            (aligned.learning_text ?? '') === (existing.learning_text ?? '') &&
            (aligned.source_annual_cell_key ?? '') === (existing.source_annual_cell_key ?? '')
          ) {
            return [];
          }
          return [
            {
              id: existing.id,
              workspace_id: existing.workspace_id,
              grade_id: existing.grade_id,
              academic_year: existing.academic_year,
              term_number: existing.term_number,
              order_index: existing.order_index,
              source_annual_cell_key: aligned.source_annual_cell_key ?? candidate.annualKey,
              event_name: aligned.event_name ?? candidate.eventName,
              timetable_text: existing.timetable_text ?? '',
              unit_text: aligned.unit_text ?? defaultUnitText,
              learning_text: aligned.learning_text ?? defaultLearningText,
              learning_note: existing.learning_note ?? '',
              presentation_ref_id: existing.presentation_ref_id ?? null,
              worksheet_ref_id: existing.worksheet_ref_id ?? null,
              page_text: existing.page_text ?? '',
              assistant_page_text: existing.assistant_page_text ?? '',
              current_period_text: existing.current_period_text ?? '',
              total_period_text: existing.total_period_text ?? '',
              material_text: existing.material_text ?? '',
              record_cross_curricular_text: existing.record_cross_curricular_text ?? '',
              record_memo_text: existing.record_memo_text ?? '',
              record_evaluation_text: existing.record_evaluation_text ?? '',
              record_class_text: existing.record_class_text ?? '',
              record_member_group_id: existing.record_member_group_id ?? null,
              bg_color_by_field: existing.bg_color_by_field ?? {},
            },
          ];
        });

        const stillFixedAnnualKeys = new Set(orderedCandidates.map((c) => c.annualKey));
        const reassignedAnnualKeys = reassignedAnnualKeysByScope.get(scopeKey) ?? new Set<string>();
        const leftoversChanged = await applyFixedProgramUnfixedSlotCleanup({
          stillFixedAnnualKeys,
          reassignedAnnualKeys,
          existingProgramCells,
          existingFixedRows,
        });

        const programRowsToUpsert: ProgramProgressCellUpsert[] = orderedCandidates.flatMap((candidate) => {
          const existing = existingProgramCellByKey[candidate.annualKey];
          const defaultUnitText = `◆${candidate.eventName}`;
          const defaultLearningText = `◆${candidate.eventName}-`;
          const nextUnitText = (existing?.unit_text ?? '').trim() || defaultUnitText;
          const nextLearningText = (existing?.learning_text ?? '').trim() || defaultLearningText;
          if (
            existing &&
            (existing.unit_text ?? '').trim() &&
            (existing.learning_text ?? '').trim()
          ) {
            return [];
          }
          const wouldSeedDefaultLearning = !(existing?.learning_text ?? '').trim();
          if (
            wouldSeedDefaultLearning &&
            hasSubstantialFixedEventContentElsewhere(
              candidate.eventName,
              candidate.annualKey,
              existingProgramCells,
              existingFixedRows
            )
          ) {
            return [];
          }
          return [
            {
              id: existing?.id,
              workspace_id: candidate.workspaceId,
              grade_id: candidate.gradeId,
              academic_year: candidate.academicYear,
              term_number: candidate.termNumber,
              monday_iso: candidate.mondayIso,
              weekday_key: candidate.weekdayKey,
              slot_index: candidate.slotIndex,
              timetable_text: existing?.timetable_text ?? candidate.timetableText,
              unit_text: nextUnitText,
              learning_text: nextLearningText,
              learning_note: existing?.learning_note ?? '',
              presentation_ref_id: existing?.presentation_ref_id ?? null,
              worksheet_ref_id: existing?.worksheet_ref_id ?? null,
              page_text: existing?.page_text ?? '',
              assistant_page_text: existing?.assistant_page_text ?? '',
              current_period_text: existing?.current_period_text ?? '',
              total_period_text: existing?.total_period_text ?? '',
              material_text: existing?.material_text ?? '',
              record_cross_curricular_text: existing?.record_cross_curricular_text ?? '',
              record_memo_text: existing?.record_memo_text ?? '',
              record_evaluation_text: existing?.record_evaluation_text ?? '',
              record_class_text: existing?.record_class_text ?? '',
              bg_color_by_field: existing?.bg_color_by_field ?? {},
            },
          ];
        });

        await Promise.all([
          fixedRowsToUpsert.length ? upsertProgramFixedContentRows(fixedRowsToUpsert) : Promise.resolve([]),
          programRowsToUpsert.length ? upsertProgramProgressCells(programRowsToUpsert) : Promise.resolve([]),
        ]);

        if (fixedRowsToUpsert.length || programRowsToUpsert.length || leftoversChanged) {
          emitProgramProgressSaved({
            workspaceId: first.workspaceId,
            academicYear: first.academicYear,
            termNumber: first.termNumber,
            gradeId: first.gradeId,
          });
        }
      }
    },
    [regularColors, workspaceId]
  );

  const handleSaveAll = useCallback(
    async (): Promise<boolean> => {
      const wm0 = weekActivityMemoModalRef.current;
      const hadWeekMemoDirty =
        !!wm0 &&
        wm0.baselineSnapshot !== serializeWeekActivityMemoDraft(wm0.activityLinesDraft, wm0.generalMemoDraft);
      const weekFlushed = await flushWeekActivityMemoModalIfDirty();
      const dirty = dirtyCellKeysRef.current;
      /** 학사 달력 공휴일 등으로 화면은 공휴인데 DB·마지막 저장본에 과목이 남은 칸 — dirty에 없어도 반드시 한 줄로 덮어써야 함 */
      const effective = effectiveCellsByKeyRef.current;
      const baselineMap = annualCellSavedBaselineRef.current;
      const seedCalendarAlignKeys: string[] = [];
      Object.keys(effective).forEach((key) => {
        const cell = effective[key];
        if (!cell) return;
        const saved = baselineMap[key];
        if (!saved && !cellRecordIdsRef.current[key]) return;
        const targetSnap = annualPersistSnapshotFromEffective(cell);
        if (!saved || !annualCellSnapshotsEqual(targetSnap, saved)) {
          seedCalendarAlignKeys.push(key);
        }
      });
      const keys = Array.from(new Set([...dirty, ...seedCalendarAlignKeys]));
      const colorsDirty = programColorsDirtyRef.current;
      const pendingColors = programColorSettingsFullRef.current;
      if (keys.length === 0 && !pendingLeaderAnnualOnlyPersistRef.current && !colorsDirty) {
        if (hadWeekMemoDirty && !weekFlushed) {
          return false;
        }
        if (weekFlushed) {
          showSuccess();
        } else {
          showInfo('변경사항 없음');
        }
        return true;
      }
      setSavingAll(true);
      showSaving();
      try {
        const pendingLeader = pendingLeaderAnnualOnlyPersistRef.current;
        if (pendingLeader) {
          await saveProgramColorSettings(workspaceId, pendingLeader.programSettings);
          adoptProgramColorSettingsFromServer(pendingLeader.programSettings);
          setBaseTimetableByGrade(pendingLeader.programSettings.baseTimetableByGrade);
          await saveAnnualProjectPrograms(
            workspaceId,
            pendingLeader.gradeId,
            academicYear,
            pendingLeader.projects
          );
          try {
            await ensureProjectProgramScopedContentRowsForProjects(
              workspaceId,
              pendingLeader.gradeId,
              academicYear,
              pendingLeader.projects
            );
          } catch {
            /* 연간셀 저장은 유지 */
          }
          pendingLeaderAnnualOnlyPersistRef.current = null;
          emitAnnualPrerequisiteRefresh(workspaceId, ['timetable_settings']);
        } else if (colorsDirty && pendingColors) {
          await saveProgramColorSettings(workspaceId, pendingColors);
          programColorSettingsBaselineRef.current = structuredClone(pendingColors);
          setProgramColorsDirty(false);
          emitAnnualPrerequisiteRefresh(workspaceId, ['timetable_settings']);
        }
        if (keys.length === 0) {
          showSuccess();
          return true;
        }
        const rows: AnnualTimetableCellUpsert[] = keys
          .map((key) => {
            const cell = effectiveCellsByKeyRef.current[key];
            if (!cell) return null;
            return createAnnualCellUpsert({
              existingId: cellRecordIdsRef.current[key],
              workspaceId,
              gradeId: cell.gradeId,
              academicYear,
              termNumber: cell.termNumber,
              mondayIso: cell.mondayIso,
              weekdayKey: cell.weekdayKey,
              slotIndex: cell.slotIndex,
              textValue: cell.textValue,
              memo: cell.memo,
              colorHex: cell.colorHex,
              slotKind: annualPersistSlotKindFromEffective(cell),
              dedicatedColors,
              isBaseExcluded: cell.isBaseExcluded,
            });
          })
          .filter((row): row is AnnualTimetableCellUpsert => row != null);
        if (rows.length === 0) {
          showSuccess();
          return true;
        }
        const savedRows = await upsertAnnualTimetableCells(rows);
        const clearedProgramProgressScopes = await deleteProgramProgressCellsForClearedAnnualSlots(savedRows);
        /**
         * 편제·프로젝트 학습내용은 시간표 저장에서 건드리지 않는다(삭제·연결 해제 없음).
         * 일정이 바뀌면 화면에서 "i번째 일정 = i번째 학습내용"으로 다시 짝지어 보여 준다.
         */
        const progressEmitScopes = new Map<
          string,
          { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string }
        >();
        const mergeEmitScope = (
          list: { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string }[]
        ) => {
          list.forEach((d) =>
            progressEmitScopes.set(`${d.workspaceId}|${d.academicYear}|${d.termNumber}|${d.gradeId}`, d)
          );
        };
        mergeEmitScope(clearedProgramProgressScopes);
        progressEmitScopes.forEach((d) => emitProgramProgressSaved(d));
        await syncFixedProgramDefaultRows(savedRows);
        const savedMap = buildAnnualCellMap(savedRows);
        const savedGradeIds = Array.from(new Set(savedRows.map((row) => row.grade_id).filter(Boolean)));
        const savedTermNumbers = Array.from(
          new Set(
            savedRows
              .map((row) => row.term_number)
              .filter((value): value is 1 | 2 => value === 1 || value === 2)
          )
        );
        setCellRecordIds((prev) => {
          const next = { ...prev };
          Object.entries(savedMap).forEach(([key, cell]) => {
            next[key] = cell.id;
          });
          return next;
        });
        setCellBaseExcluded((prev) => {
          const next = { ...prev };
          Object.entries(savedMap).forEach(([key, cell]) => {
            next[key] = Boolean(cell.is_base_excluded);
          });
          return next;
        });
        setCellData((prev) => {
          const next = { ...prev };
          Object.entries(savedMap).forEach(([key, cell]) => {
            next[key] = cell.text_value ?? '';
          });
          return next;
        });
        setCellMemos((prev) => {
          const next = { ...prev };
          Object.entries(savedMap).forEach(([key, cell]) => {
            next[key] = (cell.memo ?? '').trim();
          });
          return next;
        });
        setCellColors((prev) => {
          const next = { ...prev };
          Object.entries(savedMap).forEach(([key, cell]) => {
            next[key] = (cell.color_hex ?? '').trim();
          });
          return next;
        });
        setCellSlotKinds((prev) => {
          const next = { ...prev };
          Object.entries(savedMap).forEach(([key, cell]) => {
            const eff = effectiveCellsByKeyRef.current[key];
            const persistKind = cell.slot_kind;
            if (
              persistKind === 'class' &&
              (!eff || eff.nonClassSource === 'calendar_schedule' || eff.slotKind === 'class')
            ) {
              delete next[key];
            } else {
              next[key] = persistKind;
            }
          });
          return next;
        });
        if (savedGradeIds.length > 0 && savedTermNumbers.length > 0) {
          emitAnnualTimetableSaved({
            workspaceId,
            academicYear,
            termNumbers: savedTermNumbers,
            gradeIds: savedGradeIds,
          });
        }
        Object.entries(savedMap).forEach(([key, cell]) => {
          annualCellSavedBaselineRef.current[key] = {
            textValue: cell.text_value ?? '',
            memo: (cell.memo ?? '').trim(),
            colorHex: (cell.color_hex ?? '').trim(),
            slotKind: cell.slot_kind,
            isBaseExcluded: Boolean(cell.is_base_excluded),
          };
        });
        setDirtyCellKeys([]);
        annualLocalUndoStackRef.current = [];
        annualLocalRedoStackRef.current = [];
        notifyAnnualUndoRedoStacksRef.current();
        showSuccess();
        return true;
      } catch (e) {
        showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
        return false;
      } finally {
        setSavingAll(false);
      }
    },
    [
      academicYear,
      adoptProgramColorSettingsFromServer,
      dedicatedColors,
      flushWeekActivityMemoModalIfDirty,
      trackNameByTimetableTextByGrade,
      wildcardCharByGradeTrackName,
      workspaceId,
      showError,
      showInfo,
      showSaving,
      showSuccess,
      syncFixedProgramDefaultRows,
    ]
  );

  /** 미저장 변경이 있을 때 연간진도표 탐색 전 저장 확인 */
  const [annualProgressBrowseGuardTarget, setAnnualProgressBrowseGuardTarget] = useState<string | null>(null);

  const openAnnualProgressBrowseWithGuard = useCallback(
    (target: string | null | undefined) => {
      const t = (target ?? '').trim();
      if (!t) return;
      if (!hasUnsavedAnnualTimetableWork()) {
        markAnnualEntryFocusClick();
        navigate(t);
        return;
      }
      setAnnualProgressBrowseGuardTarget(t);
    },
    [hasUnsavedAnnualTimetableWork, navigate]
  );

  const primeAnnualProgressBrowseGuard = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, target: string | null | undefined) => {
      const t = (target ?? '').trim();
      if (!t) return;
      if (!hasUnsavedAnnualTimetableWork()) return;
      e.preventDefault();
      e.stopPropagation();
      setAnnualProgressBrowseGuardTarget(t);
    },
    [hasUnsavedAnnualTimetableWork]
  );

  const dismissAnnualProgressBrowseGuard = useCallback(() => {
    setAnnualProgressBrowseGuardTarget(null);
  }, []);

  const saveAndNavigateToAnnualProgress = useCallback(async () => {
    const target = annualProgressBrowseGuardTarget;
    if (!target) return;
    flushSync(() => {
      const ec = editingCellRef.current;
      if (ec) {
        const key = compoundCellKey(ec.gradeId, ec.mondayIso, ec.dayKey, ec.periodIdx);
        const cur = effectiveCellsByKeyRef.current[key];
        if (cur?.isEditable) {
          const live = editingInputRef.current?.value ?? ec.draft ?? '';
          const nextKind = cur.slotKind === 'non_class' && live.trim() !== '' ? 'class' : cur.slotKind;
          if (live !== (cur.textValue ?? '') || nextKind !== cur.slotKind) {
            commitEdit(ec.gradeId, ec.mondayIso, ec.dayKey, ec.periodIdx, live);
          }
        }
      }
      const mm = annualCellMemoModalRef.current;
      if (mm) {
        const cur = effectiveCellsByKeyRef.current[mm.compoundKey];
        if (mm.memoDraft.trim() !== (cur?.memo ?? '').trim()) {
          applyCellUpdates([{ key: mm.compoundKey, memo: mm.memoDraft }]);
        }
        setAnnualCellMemoModal(null);
      }
    });
    const ok = await handleSaveAll();
    if (ok) {
      setAnnualProgressBrowseGuardTarget(null);
      markAnnualEntryFocusClick();
      navigate(target);
    }
  }, [
    annualProgressBrowseGuardTarget,
    applyCellUpdates,
    commitEdit,
    handleSaveAll,
    navigate,
  ]);

  const navigateAnnualProgressWithoutSave = useCallback(() => {
    const target = annualProgressBrowseGuardTarget;
    if (!target || savingAll) return;
    setAnnualProgressBrowseGuardTarget(null);
    setEditingCell(null);
    setAnnualCellMemoModal(null);
    setWeekActivityMemoModal(null);
    setDirtyCellKeys([]);
    annualLocalUndoStackRef.current = [];
    annualLocalRedoStackRef.current = [];
    notifyAnnualUndoRedoStacksRef.current();
    setLoadedCellScopeKey('');
    markAnnualEntryFocusClick();
    navigate(target);
  }, [annualProgressBrowseGuardTarget, navigate, savingAll]);

  useEffect(() => {
    if (!annualProgressBrowseGuardTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (savingAll) return;
      e.preventDefault();
      dismissAnnualProgressBrowseGuard();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [annualProgressBrowseGuardTarget, dismissAnnualProgressBrowseGuard, savingAll]);

  useEffect(() => {
    if (routerLocation.pathname !== '/annual-timetable') return;
    const onKey = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyG') return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (annualProgressBrowseGuardTarget || savingAll) return;
      const target = primaryAnnualProgressBrowseTarget?.trim() ?? '';
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      openAnnualProgressBrowseWithGuard(target);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [
    annualProgressBrowseGuardTarget,
    openAnnualProgressBrowseWithGuard,
    primaryAnnualProgressBrowseTarget,
    routerLocation.pathname,
    savingAll,
  ]);

  useEffect(() => {
    if (routerLocation.pathname !== '/annual-timetable') return;
    const onKey = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyT' && e.code !== 'KeyY') return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (annualProgressBrowseGuardTarget || savingAll) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'KeyY') {
        handleTomorrowLessonShortcut();
      } else {
        handleTodayLessonShortcut();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [
    annualProgressBrowseGuardTarget,
    handleTodayLessonShortcut,
    handleTomorrowLessonShortcut,
    routerLocation.pathname,
    savingAll,
  ]);

  useEffect(() => {
    setAnnualTimetableDirty(isLeaveDirty);
    return () => setAnnualTimetableDirty(false);
  }, [isLeaveDirty, setAnnualTimetableDirty]);

  const handleDiscardUnsaved = useCallback(() => {
    setEditingCell(null);
    setAnnualCellMemoModal(null);
    setWeekActivityMemoModal(null);
    setAnnualProgressBrowseGuardTarget(null);
    setDirtyCellKeys([]);
    pendingLeaderAnnualOnlyPersistRef.current = null;
    annualLocalUndoStackRef.current = [];
    annualLocalRedoStackRef.current = [];
    notifyAnnualUndoRedoStacksRef.current();
    const baseline = programColorSettingsBaselineRef.current;
    if (baseline) {
      setProgramColorSettingsFull(structuredClone(baseline));
      setBaseTimetableByGrade(structuredClone(baseline.baseTimetableByGrade));
    }
    setProgramColorsDirty(false);
    /** keep-alive라도 DB 로드 스냅샷으로 되돌리도록 scope hydrate를 다시 탄다 */
    setLoadedCellScopeKey('');
  }, []);

  useEffect(() => {
    saveAnnualTimetableRef.current = {
      save: async () => {
        await handleSaveAll();
      },
      discard: handleDiscardUnsaved,
    };
    return () => {
      saveAnnualTimetableRef.current = null;
    };
  }, [handleDiscardUnsaved, handleSaveAll, saveAnnualTimetableRef]);

  const selectedSet = React.useMemo(() => new Set(selectedCells), [selectedCells]);
  /** 한 칸만 골랐을 때 같은 학년·같은 주(월요일) — 선택 칸 제외하고 연한 회색 표시 */
  const singleSelectWeekContext = React.useMemo(() => {
    if (selectedCells.length !== 1) return null;
    const parsed = parseAnnualCellKey(selectedCells[0] ?? '');
    if (!parsed) return null;
    return { gradeId: parsed.gradeId, mondayIso: parsed.mondayIso };
  }, [selectedCells]);
  const copiedSourceSet = React.useMemo(() => new Set(copiedSourceRegion ?? []), [copiedSourceRegion]);
  const swapASet = React.useMemo(() => new Set(swapRegionA ?? []), [swapRegionA]);
  const swapBSet = React.useMemo(() => new Set(swapRegionB ?? []), [swapRegionB]);

  const showMainContentLoading =
    loading || ((loadingCells || waitingForCellScopeHydration) && hasAnyTermWeeks && classEntries.length > 0);

  const renderClassActionBar = (gradeId: string, hasScheduleEntry: boolean) => (
    <AnnualTimetableClassActionBar
      gradeId={gradeId}
      hasScheduleEntry={hasScheduleEntry}
      loading={loading}
      loadingCells={loadingCells}
      operationPlanDisabled={operationPlanAvailableTerms.length === 0}
      onOpenOperationPlan={handleOpenOperationPlanModal}
      onOpenEventModal={handleOpenEventModal}
      onOpenManageFixedModal={handleOpenManageFixedModal}
      onOpenShortDayModal={handleOpenShortDayModal}
      onOpenManageShortDayModal={handleOpenManageShortDayModal}
    />
  );

  const renderTeamLeaderDownloadBtn = (
    gradeId: string,
    classTitle: string,
    classGrades: number[]
  ) =>
    teamIdForLeaderImport ? (
      <button
        type="button"
        className="cp-program-color-section-copy-btn cp-annual-timetable-section-download-btn"
        onClick={() => void handleOpenTeamLeaderImportModal(gradeId, classTitle, classGrades)}
        disabled={loading || loadingCells || teamLeaderImportBusy}
        aria-label="팀장 데이터 다운로드"
        title="팀장 데이터 다운로드"
      >
        <img src="/Download.svg" alt="" className="track-icon" />
      </button>
    ) : null;

  if (error) {
    return (
      <section>
        <h2>연간시간표</h2>
        <p className="page-desc" role="alert">
          {error}
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="cp-page-title-row">
        <div className="cp-annual-page-title-block">
          <div className="cp-annual-title-with-saturday">
            <h2>연간시간표</h2>
            <div className="cp-annual-top-view-toggles">
              <div className="cp-annual-top-saturday-toggle" aria-label="토요일 보기 토글">
                <span className="cp-weekly-schedule-saturday-toggle-label">토요일 보기</span>
                <button
                  type="button"
                  className={'team-auto-approve-toggle' + (showSaturday ? ' on' : '')}
                  onClick={() =>
                    setShowSaturday((v) => {
                      const next = !v;
                      saveAnnualShowSaturday(workspaceId, next);
                      return next;
                    })
                  }
                  aria-pressed={showSaturday}
                  aria-label="토요일 열 표시"
                  disabled={loading || loadingCells || classEntries.length === 0}
                >
                  <span className="team-auto-approve-toggle-slider" data-on={!showSaturday}>
                    OFF
                  </span>
                  <span className="team-auto-approve-toggle-slider" data-on={showSaturday}>
                    ON
                  </span>
                </button>
              </div>
              <div className="cp-annual-top-saturday-toggle" aria-label="모든 클래스 동시보기 토글">
                <span className="cp-weekly-schedule-saturday-toggle-label">모든 클래스 동시보기</span>
                <button
                  type="button"
                  className={'team-auto-approve-toggle' + (showAllClasses ? ' on' : '')}
                  onClick={() =>
                    setShowAllClasses((v) => {
                      const next = !v;
                      saveAnnualShowAllClasses(workspaceId, next);
                      return next;
                    })
                  }
                  aria-pressed={showAllClasses}
                  aria-label="모든 클래스 섹션 동시 표시"
                  disabled={loading || loadingCells || classEntries.length === 0}
                >
                  <span className="team-auto-approve-toggle-slider" data-on={!showAllClasses}>
                    OFF
                  </span>
                  <span className="team-auto-approve-toggle-slider" data-on={showAllClasses}>
                    ON
                  </span>
                </button>
              </div>
              <div className="cp-annual-top-saturday-toggle" aria-label="1,2학기 위치 바꾸기 토글">
                <span className="cp-weekly-schedule-saturday-toggle-label">1,2학기 위치 바꾸기</span>
                <button
                  type="button"
                  className={'team-auto-approve-toggle' + (swapTermOrder ? ' on' : '')}
                  onClick={() => {
                    const next = !swapTermOrder;
                    saveAnnualSwapTermOrder(workspaceId, next);
                    setSwapTermOrder(next);
                  }}
                  aria-pressed={swapTermOrder}
                  aria-label="1학기 2학기 위치 바꾸기"
                >
                  <span className="team-auto-approve-toggle-slider" data-on={!swapTermOrder}>
                    OFF
                  </span>
                  <span className="team-auto-approve-toggle-slider" data-on={swapTermOrder}>
                    ON
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="cp-workspace-learning-title-actions" role="group" aria-label="연간시간표 저장 도구">
          <ProgramToolbarUndoRedoButtons
            canUndo={annualUndoRedoAvail.canUndo}
            canRedo={annualUndoRedoAvail.canRedo}
            disabled={loading || loadingCells || savingAll}
            onUndo={() => {
              tryAnnualUndo();
            }}
            onRedo={() => {
              tryAnnualRedo();
            }}
          />
          <button
            type="button"
            className="cp-btn-save-all"
            onClick={handleSaveAll}
            disabled={loading || loadingCells || !isDirty || savingAll}
          >
            {isDirty ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      {showMainContentLoading ? (
        <article className="cp-card cp-weekly-class-hours-card">
          <p className="page-desc">불러오는 중…</p>
        </article>
      ) : grades.length === 0 ? (
        <AnnualTimetableShellPreview
          academicYear={academicYear}
          showSaturday={showSaturday}
          swapTermOrder={swapTermOrder}
        />
      ) : (
        <div className="cp-base-timetable-sections cp-base-timetable-page-classes cp-annual-timetable-classes-stack">
          {visibleAnnualBlocks.map(({ grade, entry }) => {
            const parsedClass = parseGradeDisplayName(grade.name);
            if (!entry || !hasAnyTermWeeks) {
              return (
                <div key={grade.id} className="cp-annual-timetable-class-block-wrap">
                  {renderClassActionBar(grade.id, Boolean(entry))}
                  <AnnualTimetableClassShellPreview
                    academicYear={academicYear}
                    showSaturday={showSaturday}
                    swapTermOrder={swapTermOrder}
                    gradeId={grade.id}
                    classTitle={parsedClass.title}
                    classGrades={parsedClass.grades}
                    headerTrailing={renderTeamLeaderDownloadBtn(
                      grade.id,
                      parsedClass.title,
                      parsedClass.grades
                    )}
                  />
                </div>
              );
            }
              const sectionRows = classSchedule?.sectionRows?.[entry.sectionId] ?? [];
              const periodLabels = buildPeriodLabels(sectionRows);
              const periodsPerDay = periodLabels.length > 0 ? periodLabels.length : MAX_PERIODS;
              const headingId = `cp-annual-class-${entry.gradeId}`;
              const programGroup = programGroupsByGrade[entry.gradeId];
              const orderedTrackNames = getOrderedTrackNamesForGrade(
                workspaceId,
                entry.gradeId,
                tracks,
                gradeTrackSections
              );
              const trackOrderIndex = new Map(
                orderedTrackNames.map((trackName, index) => [trackName, index])
              );
              const trackGroups = (() => {
                const rows = tracks.filter((t) => (t.grade_id ?? '') === entry.gradeId);
                const map = new Map<
                  string,
                  {
                    trackName: string;
                    aliases: string[];
                    criteriaHours: number;
                    isOtherTrack: boolean;
                    wildcardChar?: string;
                  }
                >();
                rows.forEach((t) => {
                  const trackName = (t.name ?? '').trim();
                  if (!trackName) return;
                  const alias = (t.description ?? '').trim();
                  const h = Math.min(999, Math.max(0, Number(t.criteria_hours) || 0));
                  if (!map.has(trackName)) {
                    map.set(trackName, {
                      trackName,
                      aliases: [],
                      criteriaHours: h,
                      isOtherTrack: t.track_category === 'other',
                      wildcardChar:
                        sanitizeWildcardChar(
                          wildcardCharByGradeTrackName[entry.gradeId]?.[trackName] ?? ''
                        ) || undefined,
                    });
                  } else if (t.track_category === 'other') {
                    map.get(trackName)!.isOtherTrack = true;
                  }
                  if (alias) map.get(trackName)!.aliases.push(alias);
                });
                return Array.from(map.values())
                  .map((g) => {
                    const aliasSet = Array.from(new Set(g.aliases)).filter(Boolean);
                    const orderedAliases = orderedTrackAliasesForGrade(
                      workspaceId,
                      entry.gradeId,
                      g.trackName,
                      tracks
                    );
                    return {
                      trackName: g.trackName,
                      aliases: orderedAliases.length > 0 ? orderedAliases : aliasSet,
                      criteriaHours: g.criteriaHours,
                      isOtherTrack: g.isOtherTrack,
                      wildcardChar: g.wildcardChar,
                    };
                  })
                  .sort((a, b) => {
                    const orderA = trackOrderIndex.get(a.trackName) ?? 9999;
                    const orderB = trackOrderIndex.get(b.trackName) ?? 9999;
                    if (orderA !== orderB) return orderA - orderB;
                    return a.trackName.localeCompare(b.trackName, 'ko');
                  });
              })();
              type SummaryTrackGroup = {
                trackName: string;
                aliases: string[];
                criteriaHours: number;
                isOtherTrack: boolean;
                wildcardChar?: string;
              };
              const summaryColGroups: SummaryTrackGroup[] =
                trackGroups.length === 0
                  ? [{ trackName: '—', aliases: [], criteriaHours: 0, isOtherTrack: false, wildcardChar: undefined }]
                  : trackGroups.map((g) => ({
                      trackName: g.trackName,
                      aliases: [...g.aliases],
                      criteriaHours: g.criteriaHours,
                      isOtherTrack: g.isOtherTrack,
                      wildcardChar: g.wildcardChar,
                    }));
              const summaryGroupColCount = (g: { aliases: string[] }) =>
                g.aliases.length === 0 ? 1 : g.aliases.length;
              const summaryAliasListPerGroup = (g: { aliases: string[] }) =>
                g.aliases.length === 0 ? ['—'] : g.aliases;
              const selHere =
                selectedCells.length > 0 &&
                selectedCells.every((k) => gradeIdFromCompound(k) === entry.gradeId);
              const swapAHere =
                swapRegionA &&
                swapRegionA.length > 0 &&
                gradeIdFromCompound(swapRegionA[0]) === entry.gradeId;
              const swapBHere =
                swapRegionB &&
                swapRegionB.length > 0 &&
                gradeIdFromCompound(swapRegionB[0]) === entry.gradeId;
              return (
                <div key={entry.gradeId} className="cp-annual-timetable-class-block-wrap">
                {renderClassActionBar(entry.gradeId, true)}
                <article className="cp-card cp-annual-timetable-per-class-card">
                <section
                  className="cp-annual-timetable-class-section"
                  aria-labelledby={headingId}
                >
                  <div className="cp-annual-timetable-section-top">
                    <div className="cp-annual-timetable-section-class-row" id={headingId}>
                      <AnnualTimetableClassSectionTitle
                        classTitle={parsedClass.title}
                        classGrades={parsedClass.grades}
                      />
                      <div className="cp-annual-timetable-section-class-row-right">
                        <div className="cp-annual-timetable-action-set cp-annual-timetable-action-set-base">
                          <button
                            type="button"
                            className="cp-btn-secondary cp-annual-base-timetable-apply-btn cp-btn-annual-timetable-settings"
                            onClick={() =>
                              setTimetableSettingsModal({
                                gradeId: entry.gradeId,
                                classTitle: parsedClass.title,
                              })
                            }
                            disabled={loading || loadingCells}
                            title="기초 시간표 설정 열기"
                          >
                            기초 시간표 설정
                          </button>
                          <button
                            type="button"
                            className="cp-btn-secondary cp-annual-base-timetable-apply-btn"
                            onClick={() => setBaseTimetableApplyConfirm({ entry, termNum: 1 })}
                            disabled={
                              !gradeHasBaseTimetableForApply(baseTimetableByGrade, entry.gradeId) ||
                              weeksTerm1.length === 0
                            }
                            title={
                              !gradeHasBaseTimetableForApply(baseTimetableByGrade, entry.gradeId)
                                ? '사전 설정에서 이 클래스의 기초시간표를 저장한 뒤 사용할 수 있습니다'
                                : weeksTerm1.length === 0
                                  ? '학사일정 1학기 구간이 없어 주차를 만들 수 없습니다'
                                  : '1학기에 기초시간표 반영'
                            }
                          >
                            1학기 기초시간표 반영
                          </button>
                          <button
                            type="button"
                            className="cp-btn-secondary cp-annual-base-timetable-apply-btn"
                            onClick={() => setBaseTimetableApplyConfirm({ entry, termNum: 2 })}
                            disabled={
                              !gradeHasBaseTimetableForApply(baseTimetableByGrade, entry.gradeId) ||
                              weeksTerm2.length === 0
                            }
                            title={
                              !gradeHasBaseTimetableForApply(baseTimetableByGrade, entry.gradeId)
                                ? '사전 설정에서 이 클래스의 기초시간표를 저장한 뒤 사용할 수 있습니다'
                                : weeksTerm2.length === 0
                                  ? '학사일정 2학기 구간이 없어 주차를 만들 수 없습니다'
                                  : '2학기에 기초시간표 반영'
                            }
                          >
                            2학기 기초시간표 반영
                          </button>
                          <button
                            type="button"
                            className="cp-btn-secondary cp-annual-base-timetable-apply-btn cp-annual-hours-adjust-open-btn"
                            onClick={() =>
                              setHoursAdjustModal({
                                gradeId: entry.gradeId,
                                classTitle: parsedClass.title,
                              })
                            }
                            disabled={loading || loadingCells}
                            title="편제별 시수·학습내용 시수를 비교·조정"
                          >
                            <img
                              src={`${process.env.PUBLIC_URL || ''}/MagicWand.svg`}
                              alt=""
                              className="cp-annual-hours-adjust-btn-icon"
                              width={14}
                              height={14}
                            />
                            기준시수 맞춤 시간표 자동 조정
                          </button>
                        </div>
                        {renderTeamLeaderDownloadBtn(
                          entry.gradeId,
                          parsedClass.title,
                          parsedClass.grades
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="cp-annual-timetable-table-panel">
                    {(() => {
                      const weekdayKeysToShow = showSaturday ? WEEKDAY_KEYS : WEEKDAY_KEYS.filter((w) => w.key !== 'sat');
                      const term1Set = new Set(weeksTerm1.map((w) => w.mondayIso));
                      const term2Set = new Set(weeksTerm2.map((w) => w.mondayIso));
                      const countForAliasInGroup = (
                        g: (typeof summaryColGroups)[number],
                        aliasName: string,
                        termNum: 1 | 2
                      ): number => {
                        if (aliasName === '—') return 0;
                        const set = termNum === 1 ? term1Set : term2Set;
                        const seen = new Set<string>();
                        const addCell = (cell: AnnualEffectiveCell) => {
                          if (cell.termNumber !== termNum) return;
                          if (!set.has(cell.mondayIso)) return;
                          if (!showSaturday && cell.weekdayKey === 'sat') return;
                          if (cell.slotKind !== 'class' && cell.slotKind !== 'non_class') return;
                          seen.add(`${cell.mondayIso}|${cell.weekdayKey}|${cell.slotIndex}`);
                        };
                        (programGroup?.byTextValue?.[aliasName] ?? []).forEach(addCell);
                        const wildcard = sanitizeWildcardChar(g.wildcardChar ?? '');
                        const defaultAlias = (g.aliases[0] ?? '').trim();
                        if (wildcard && defaultAlias && aliasName === defaultAlias) {
                          Object.entries(programGroup?.byTextValue ?? {}).forEach(([textValue, cells]) => {
                            if (!matchesTrackWildcardSuffix(textValue, wildcard)) return;
                            cells.forEach(addCell);
                          });
                        }
                        return seen.size;
                      };
                      const countForGroup = (
                        g: (typeof summaryColGroups)[number],
                        termNum: 1 | 2
                      ): number => {
                        const set = termNum === 1 ? term1Set : term2Set;
                        const seen = new Set<string>();
                        const addCell = (cell: AnnualEffectiveCell) => {
                          if (cell.termNumber !== termNum) return;
                          if (!set.has(cell.mondayIso)) return;
                          if (!showSaturday && cell.weekdayKey === 'sat') return;
                          if (cell.slotKind !== 'class' && cell.slotKind !== 'non_class') return;
                          seen.add(`${cell.mondayIso}|${cell.weekdayKey}|${cell.slotIndex}`);
                        };
                        const names = summaryAliasListPerGroup(g);
                        names.forEach((name) => {
                          if (name === '—') return;
                          (programGroup?.byTextValue?.[name] ?? []).forEach(addCell);
                        });
                        const wildcard = sanitizeWildcardChar(g.wildcardChar ?? '');
                        if (wildcard) {
                          Object.entries(programGroup?.byTextValue ?? {}).forEach(([textValue, cells]) => {
                            if (!matchesTrackWildcardSuffix(textValue, wildcard)) return;
                            cells.forEach(addCell);
                          });
                        }
                        return seen.size;
                      };
                      void trackProgramPrefsRev;
                      const sumTermHoursForGroup = (g: (typeof summaryColGroups)[number]) => {
                        const mode = resolveTrackProgramModeForSummary(workspaceId, entry.gradeId, g.trackName);
                        if (mode === 'sync' && g.aliases.length > 1) {
                          const rep = resolveSyncRepresentativeAlias(
                            workspaceId,
                            entry.gradeId,
                            g.trackName,
                            g.aliases
                          );
                          if (!rep) return 0;
                          return countForGroup(
                            { ...g, aliases: [rep] },
                            1
                          ) + countForGroup({ ...g, aliases: [rep] }, 2);
                        }
                        return countForGroup(g, 1) + countForGroup(g, 2);
                      };
                      const syncMirrorSuffixForCell = (
                        g: (typeof summaryColGroups)[number],
                        timetableName: string
                      ): string => {
                        if (timetableName === '—') return '';
                        const mode = resolveTrackProgramModeForSummary(workspaceId, entry.gradeId, g.trackName);
                        if (mode !== 'sync' || g.aliases.length <= 1) return '';
                        const rep = resolveSyncRepresentativeAlias(
                          workspaceId,
                          entry.gradeId,
                          g.trackName,
                          g.aliases
                        );
                        return timetableName !== rep ? ' cp-annual-term-summary-sync-mirror' : '';
                      };
                      const isSummaryMirrorCell = (
                        g: (typeof summaryColGroups)[number],
                        timetableName: string
                      ): boolean => syncMirrorSuffixForCell(g, timetableName) !== '';
                      const wrapSummaryMirrorContent = (
                        isMirror: boolean,
                        content: React.ReactNode
                      ): React.ReactNode =>
                        isMirror ? (
                          <span className="cp-annual-term-summary-mirror-fade">{content}</span>
                        ) : (
                          content
                        );
                      const summaryTrackGroupEdgeClassForGroup = (groupIndex: number) =>
                        groupIndex > 0 ? ' cp-annual-term-summary-track-group-edge' : '';
                      const summaryTrackGroupEdgeClassForAlias = (groupIndex: number, aliasIndex: number) =>
                        groupIndex > 0 && aliasIndex === 0
                          ? ' cp-annual-term-summary-track-group-edge'
                          : '';
                      const countTrackTermHoursForGroup = (
                        g: (typeof summaryColGroups)[number],
                        termNum: 1 | 2
                      ): number => {
                        const mode = resolveTrackProgramModeForSummary(workspaceId, entry.gradeId, g.trackName);
                        if (mode === 'sync' && g.aliases.length > 1) {
                          const rep = resolveSyncRepresentativeAlias(
                            workspaceId,
                            entry.gradeId,
                            g.trackName,
                            g.aliases
                          );
                          if (!rep) return 0;
                          return countForGroup({ ...g, aliases: [rep] }, termNum);
                        }
                        return countForGroup(g, termNum);
                      };
                      const countForGroupInWeek = (
                        g: (typeof summaryColGroups)[number],
                        week: WeekInfo,
                        termNum: 1 | 2
                      ): number => {
                        const weekSet = new Set([week.mondayIso]);
                        const seen = new Set<string>();
                        const addCell = (cell: AnnualEffectiveCell) => {
                          if (cell.termNumber !== termNum) return;
                          if (!weekSet.has(cell.mondayIso)) return;
                          if (!showSaturday && cell.weekdayKey === 'sat') return;
                          if (cell.slotKind !== 'class' && cell.slotKind !== 'non_class') return;
                          seen.add(`${cell.mondayIso}|${cell.weekdayKey}|${cell.slotIndex}`);
                        };
                        const names = summaryAliasListPerGroup(g);
                        names.forEach((name) => {
                          if (name === '—') return;
                          (programGroup?.byTextValue?.[name] ?? []).forEach(addCell);
                        });
                        const wildcard = sanitizeWildcardChar(g.wildcardChar ?? '');
                        if (wildcard) {
                          Object.entries(programGroup?.byTextValue ?? {}).forEach(([textValue, cells]) => {
                            if (!matchesTrackWildcardSuffix(textValue, wildcard)) return;
                            cells.forEach(addCell);
                          });
                        }
                        return seen.size;
                      };
                      const countTrackHoursInWeek = (
                        g: (typeof summaryColGroups)[number],
                        week: WeekInfo,
                        termNum: 1 | 2
                      ): number => countForGroupInWeek(g, week, termNum);
                      const buildWeekTrackHoursBreakdown = (week: WeekInfo, termNum: 1 | 2) =>
                        summaryColGroups
                          .filter((g) => g.trackName !== '—')
                          .map((g) => ({
                            trackName: g.trackName,
                            hours: countTrackHoursInWeek(g, week, termNum),
                          }));
                      const buildTermTrackHoursBreakdown = (termNum: 1 | 2) =>
                        summaryColGroups
                          .filter((g) => g.trackName !== '—')
                          .map((g) => ({
                            trackName: g.trackName,
                            hours: countForGroup(g, termNum),
                          }));
                      const summaryTermRowsSplit = summaryColGroups.some((g) => g.aliases.length >= 2);
                      const renderTermBlock = (params: {
                        termKey: '1' | '2';
                        termLabel: string;
                        wks: WeekInfo[];
                        termHours: Record<string, number>;
                        termDays: number;
                      }) => {
                        const { termKey, termLabel, wks, termHours, termDays } = params;
                        const selInTerm = selHere && areAllKeysInWeeks(selectedCells, wks, dayKeysForSelection);
                        const canClearDataInTerm =
                          selInTerm &&
                          filterEditableSelectionKeys(selectedCells).some((k) =>
                            areAllKeysInWeeks([k], wks, dayKeysForSelection)
                          );
                        const swapAInTerm = swapAHere && areAllKeysInWeeks(swapRegionA ?? [], wks, dayKeysForSelection);
                        const swapBInTerm = swapBHere && areAllKeysInWeeks(swapRegionB ?? [], wks, dayKeysForSelection);
                        const canSwap =
                          swapAInTerm &&
                          swapBInTerm &&
                          !!swapRegionA &&
                          !!swapRegionB &&
                          swapRegionA.length === swapRegionB.length &&
                          gradeIdFromCompound(swapRegionA[0]) === gradeIdFromCompound(swapRegionB[0]);
                        const termNum = termKey === '1' ? 1 : 2;
                        const termColorCounts =
                          annualTermColorCellCounts[`${entry.gradeId}|${termNum}`] ?? {
                            labeling: {},
                            project: {},
                          };
                        const selectedHeadDateLabel = (() => {
                          if (!selInTerm || selectedCells.length === 0) return '월   일';
                          const firstKey = selectedCells[0];
                          const iso = (firstKey ? effectiveCellsByKey[firstKey]?.dateIso : '')?.trim() ?? '';
                          if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '월   일';
                          const d = parseISODate(iso);
                          if (Number.isNaN(d.getTime())) return '월   일';
                          return `${d.getMonth() + 1}월 ${d.getDate()}일`;
                        })();
                        const isDateHeadPicker =
                          annualDateHeadPicker?.gradeId === entry.gradeId &&
                          annualDateHeadPicker.termKey === termKey;
                        const periodCountForJump = Math.min(
                          periodLabels.length > 0 ? periodLabels.length : MAX_PERIODS,
                          MAX_PERIODS
                        );
                        const commitAnnualDateHeadJump = (raw: string) => {
                          const parsed = parseMonthDayDotInput(raw);
                          if (!parsed) {
                            showInfo('월.일 형식으로 입력해 주세요. (예: 3.15)');
                            return;
                          }
                          const ok = selectAnnualColumnByMonthDayInWeeks(
                            wks,
                            entry.gradeId,
                            parsed.month,
                            parsed.day,
                            periodCountForJump
                          );
                          if (!ok) {
                            showInfo('이 학기 시간표에서 해당 월·일에 해당하는 교시 열을 찾을 수 없습니다.');
                          }
                        };
                        return wks.length === 0 ? (
                          <div
                            key={termKey}
                            id={`cp-annual-fixed-term-${entry.gradeId}-${termKey}`}
                            className="cp-annual-term-table-block"
                          >
                            <div
                              className="cp-program-progress-toolbar cp-annual-term-program-toolbar"
                              role="toolbar"
                              aria-label="교환·복사·붙여넣기·데이터 삭제"
                            >
                              <div className="cp-program-progress-toolbar-leading">
                                <span
                                  className={
                                    'cp-annual-term-title-badge' +
                                    (termKey === '1'
                                      ? ' cp-annual-term-title-badge--term1'
                                      : ' cp-annual-term-title-badge--term2')
                                  }
                                >
                                  {termLabel}
                                </span>
                                <button
                                  type="button"
                                  className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-annual-progress-browse-left"
                                  data-tutorial-browse="annual-progress"
                                  onMouseDown={(e) =>
                                    primeAnnualProgressBrowseGuard(
                                      e,
                                      selectedAnnualProgressBrowseTargetByTerm[termNum]
                                    )
                                  }
                                  onClick={() =>
                                    openAnnualProgressBrowseWithGuard(selectedAnnualProgressBrowseTargetByTerm[termNum])
                                  }
                                  disabled={!selectedAnnualProgressBrowseTargetByTerm[termNum]}
                                  title="선택영역 좌상단 셀의 연간진도표 행으로 이동 (Shift+G)"
                                >
                                  <span className="cp-btn-program-browse-nav-icon" aria-hidden="true">
                                    <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
                                      <path
                                        d="M8.1 2v5.5L10 5.6l1.6 1.6L7 11.8 2.4 7.2 4 5.6l1.9 1.9V2h2.2z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </span>
                                  <span className="cp-btn-label-desktop">선택셀 연간진도표 탐색</span>
                                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                                    탐색
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className="cp-btn-copy-paste cp-btn-program-browse-nav"
                                  onClick={() => handleTodayLessonInAnnualTimetable(wks, entry.gradeId)}
                                  disabled={wks.length === 0}
                                  title="이 학기 표에서 오늘 날짜에 해당하는 교시 10칸을 모두 선택합니다. (Shift+T)"
                                >
                                  <span className="cp-btn-label-desktop">오늘 학습내용 보기</span>
                                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                                    오늘 학습내용
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className="cp-btn-copy-paste cp-btn-program-browse-nav"
                                  onClick={() => handleTomorrowLessonInAnnualTimetable(wks, entry.gradeId)}
                                  disabled={wks.length === 0}
                                  title="이 학기 표에서 내일 날짜에 해당하는 교시 10칸을 모두 선택합니다. (Shift+Y)"
                                >
                                  <span className="cp-btn-label-desktop">내일 학습내용 보기</span>
                                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                                    내일 학습내용
                                  </span>
                                </button>
                              </div>
                              <div className="cp-program-progress-toolbar-trailing cp-annual-term-title-inline-actions">
                                <div className="cp-program-progress-toolbar-group" role="group" aria-label="교환 영역">
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'left')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 왼쪽으로 한 칸 이동"
                                    aria-label="선택 셀 왼쪽 이동"
                                  >
                                    <span className="cp-btn-label-desktop">←</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      ←
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'right')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 오른쪽으로 한 칸 이동"
                                    aria-label="선택 셀 오른쪽 이동"
                                  >
                                    <span className="cp-btn-label-desktop">→</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      →
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'up')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 위로 한 칸 이동"
                                    aria-label="선택 셀 위로 이동"
                                  >
                                    <span className="cp-btn-label-desktop">↑</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      ↑
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'down')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 아래로 한 칸 이동"
                                    aria-label="선택 셀 아래로 이동"
                                  >
                                    <span className="cp-btn-label-desktop">↓</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      ↓
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={handleSetSwapRegionA}
                                    disabled={!selInTerm}
                                    title="선택 영역을 교환영역 A로 지정 (Shift+Q)"
                                  >
                                    <span className="cp-btn-label-desktop">
                                      {swapAInTerm ? `A: 셀 ${swapRegionA!.length}개` : '교환영역 A'}
                                    </span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      A
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={handleSetSwapRegionB}
                                    disabled={!selInTerm}
                                    title="선택 영역을 교환영역 B로 지정 (Shift+W)"
                                  >
                                    <span className="cp-btn-label-desktop">
                                      {swapBInTerm ? `B: 셀 ${swapRegionB!.length}개` : '교환영역 B'}
                                    </span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      B
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={handleSwapRegions}
                                    disabled={!canSwap}
                                    title="영역 A와 B 맞교환 (Shift+E)"
                                  >
                                    <span className="cp-btn-label-desktop">영역 바꾸기</span>
                                    <span className="cp-btn-label-mobile-icon cp-btn-mobile-swap-icon" aria-hidden>
                                      <span>A</span>
                                      <svg viewBox="0 0 20 20" className="cp-btn-mobile-action-svg">
                                        <path d="M3 6h11.2l-2.4-2.3 1.1-1.1L17.1 7l-4.2 4.4-1.1-1.1L14.2 8H3z" fill="currentColor" />
                                        <path d="M17 14H5.8l2.4 2.3-1.1 1.1L2.9 13l4.2-4.4 1.1 1.1L5.8 12H17z" fill="currentColor" />
                                      </svg>
                                      <span>B</span>
                                    </span>
                                  </button>
                                </div>
                                <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드">
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handleCopy(wks)}
                                    disabled={!selInTerm}
                                    title="선택한 셀 복사 (Ctrl+C)"
                                  >
                                    <span className="cp-btn-label-desktop">복사</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handleCut(wks)}
                                    disabled={!selInTerm}
                                    title="선택한 셀 잘라내기 (Ctrl+X)"
                                  >
                                    <span className="cp-btn-label-desktop">잘라내기</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handlePaste(wks)}
                                    disabled={!hasCopiedData || !selInTerm}
                                    title="붙여넣기 (Ctrl+V)"
                                  >
                                    <span className="cp-btn-label-desktop">붙여넣기</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handleClearSelectedCellDisplayData(wks)}
                                    disabled={!canClearDataInTerm}
                                    title="선택한 셀의 시간표명·배경색(고정·전담·제외)·메모만 지웁니다. 공휴일·활동·비수업 표시는 유지됩니다."
                                  >
                                    <span className="cp-btn-label-desktop">데이터 삭제</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/del.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            <p className="page-desc cp-annual-term-empty">{termLabel} 일정이 없습니다.</p>
                            <div className="cp-annual-timetable-preview-readonly" aria-hidden="true">
                              <AnnualTimetablePreviewMainTable
                                termKey={termKey}
                                anchorGradeId={entry.gradeId}
                                weekdayKeysToShow={weekdayKeysToShow}
                                periodLabels={periodLabels}
                                sectionRows={sectionRows}
                                weeks={createAnnualShellWeekRows(4, showSaturday)}
                                termHoursCell={String(termHours[entry.gradeId] ?? '')}
                                termDaysCell={String(termDays)}
                              />
                            </div>
                          </div>
                        ) : (
                          <div
                            key={termKey}
                            id={`cp-annual-fixed-term-${entry.gradeId}-${termKey}`}
                            className="cp-annual-term-table-block"
                          >
                            <div
                              className="cp-program-progress-toolbar cp-annual-term-program-toolbar"
                              role="toolbar"
                              aria-label="교환·복사·붙여넣기·데이터 삭제"
                            >
                              <div className="cp-program-progress-toolbar-leading">
                                <span
                                  className={
                                    'cp-annual-term-title-badge' +
                                    (termKey === '1'
                                      ? ' cp-annual-term-title-badge--term1'
                                      : ' cp-annual-term-title-badge--term2')
                                  }
                                >
                                  {termLabel}
                                </span>
                                <button
                                  type="button"
                                  className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-annual-progress-browse-left"
                                  data-tutorial-browse="annual-progress"
                                  onMouseDown={(e) =>
                                    primeAnnualProgressBrowseGuard(
                                      e,
                                      selectedAnnualProgressBrowseTargetByTerm[termNum]
                                    )
                                  }
                                  onClick={() =>
                                    openAnnualProgressBrowseWithGuard(selectedAnnualProgressBrowseTargetByTerm[termNum])
                                  }
                                  disabled={!selectedAnnualProgressBrowseTargetByTerm[termNum]}
                                  title="선택영역 좌상단 셀의 연간진도표 행으로 이동 (Shift+G)"
                                >
                                  <span className="cp-btn-program-browse-nav-icon" aria-hidden="true">
                                    <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
                                      <path
                                        d="M8.1 2v5.5L10 5.6l1.6 1.6L7 11.8 2.4 7.2 4 5.6l1.9 1.9V2h2.2z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </span>
                                  <span className="cp-btn-label-desktop">선택셀 연간진도표 탐색</span>
                                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                                    탐색
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className="cp-btn-copy-paste cp-btn-program-browse-nav"
                                  onClick={() => handleTodayLessonInAnnualTimetable(wks, entry.gradeId)}
                                  disabled={wks.length === 0}
                                  title="이 학기 표에서 오늘 날짜에 해당하는 교시 10칸을 모두 선택합니다. (Shift+T)"
                                >
                                  <span className="cp-btn-label-desktop">오늘 학습내용 보기</span>
                                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                                    오늘 학습내용
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className="cp-btn-copy-paste cp-btn-program-browse-nav"
                                  onClick={() => handleTomorrowLessonInAnnualTimetable(wks, entry.gradeId)}
                                  disabled={wks.length === 0}
                                  title="이 학기 표에서 내일 날짜에 해당하는 교시 10칸을 모두 선택합니다. (Shift+Y)"
                                >
                                  <span className="cp-btn-label-desktop">내일 학습내용 보기</span>
                                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                                    내일 학습내용
                                  </span>
                                </button>
                              </div>
                              <div className="cp-program-progress-toolbar-trailing cp-annual-term-title-inline-actions">
                                <div className="cp-program-progress-toolbar-group" role="group" aria-label="교환 영역">
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'left')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 왼쪽으로 한 칸 이동"
                                    aria-label="선택 셀 왼쪽 이동"
                                  >
                                    <span className="cp-btn-label-desktop">←</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      ←
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'right')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 오른쪽으로 한 칸 이동"
                                    aria-label="선택 셀 오른쪽 이동"
                                  >
                                    <span className="cp-btn-label-desktop">→</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      →
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'up')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 위로 한 칸 이동"
                                    aria-label="선택 셀 위로 이동"
                                  >
                                    <span className="cp-btn-label-desktop">↑</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      ↑
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={() => handleShiftSelectedCellsByStep(wks, 'down')}
                                    disabled={!selInTerm}
                                    title="선택 셀을 아래로 한 칸 이동"
                                    aria-label="선택 셀 아래로 이동"
                                  >
                                    <span className="cp-btn-label-desktop">↓</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      ↓
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={handleSetSwapRegionA}
                                    disabled={!selInTerm}
                                    title="선택 영역을 교환영역 A로 지정 (Shift+Q)"
                                  >
                                    <span className="cp-btn-label-desktop">
                                      {swapAInTerm ? `A: 셀 ${swapRegionA!.length}개` : '교환영역 A'}
                                    </span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      A
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={handleSetSwapRegionB}
                                    disabled={!selInTerm}
                                    title="선택 영역을 교환영역 B로 지정 (Shift+W)"
                                  >
                                    <span className="cp-btn-label-desktop">
                                      {swapBInTerm ? `B: 셀 ${swapRegionB!.length}개` : '교환영역 B'}
                                    </span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      B
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste cp-btn-swap-region"
                                    onClick={handleSwapRegions}
                                    disabled={!canSwap}
                                    title="영역 A와 B 맞교환 (Shift+E)"
                                  >
                                    <span className="cp-btn-label-desktop">영역 바꾸기</span>
                                    <span className="cp-btn-label-mobile-icon cp-btn-mobile-swap-icon" aria-hidden>
                                      <span>A</span>
                                      <svg viewBox="0 0 20 20" className="cp-btn-mobile-action-svg">
                                        <path d="M3 6h11.2l-2.4-2.3 1.1-1.1L17.1 7l-4.2 4.4-1.1-1.1L14.2 8H3z" fill="currentColor" />
                                        <path d="M17 14H5.8l2.4 2.3-1.1 1.1L2.9 13l4.2-4.4 1.1 1.1L5.8 12H17z" fill="currentColor" />
                                      </svg>
                                      <span>B</span>
                                    </span>
                                  </button>
                                </div>
                                <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드">
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handleCopy(wks)}
                                    disabled={!selInTerm}
                                    title="선택한 셀 복사 (Ctrl+C)"
                                  >
                                    <span className="cp-btn-label-desktop">복사</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handleCut(wks)}
                                    disabled={!selInTerm}
                                    title="선택한 셀 잘라내기 (Ctrl+X)"
                                  >
                                    <span className="cp-btn-label-desktop">잘라내기</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handlePaste(wks)}
                                    disabled={!hasCopiedData || !selInTerm}
                                    title="붙여넣기 (Ctrl+V)"
                                  >
                                    <span className="cp-btn-label-desktop">붙여넣기</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-btn-copy-paste"
                                    onClick={() => handleClearSelectedCellDisplayData(wks)}
                                    disabled={!canClearDataInTerm}
                                    title="선택한 셀의 시간표명·배경색(고정·전담·제외)·메모만 지웁니다. 공휴일·활동·비수업 표시는 유지됩니다."
                                  >
                                    <span className="cp-btn-label-desktop">데이터 삭제</span>
                                    <span className="cp-btn-label-mobile-icon" aria-hidden>
                                      <img src="/del.svg" alt="" className="cp-btn-mobile-action-svg" />
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="cp-annual-term-toolbar" role="toolbar">
                              <div
                                className="cp-weekly-schedule-color-palettes cp-annual-timetable-toolbar-palettes"
                                aria-label={`${entry.label} ${termLabel} 색상`}
                              >
                {buildColorPaletteGroups(entry.gradeId).map((group) => (
                  <React.Fragment key={`${termKey}-${group.label}`}>
                    {group.label === '고정(행사)' ? (
                      <div
                        className="cp-annual-table-slot-legend"
                        role="group"
                        aria-label="수업·활동·비수업 셀 적용"
                      >
                        <div className="cp-annual-slot-legend-item">
                          <button
                            type="button"
                            className="cp-annual-slot-legend-swatch cp-annual-slot-legend-swatch--class"
                            aria-label="선택 셀을 수업(흰 배경)으로 지정"
                            disabled={!selInTerm}
                            title="수업"
                            onClick={() => applySlotSurfaceToSelected('class')}
                          >
                            <span className="cp-annual-slot-legend-swatch-label" aria-hidden>
                              수
                            </span>
                          </button>
                          <span className="cp-annual-slot-legend-tooltip" role="tooltip">
                            수업
                          </span>
                        </div>
                        <div className="cp-annual-slot-legend-item">
                          <button
                            type="button"
                            className="cp-annual-slot-legend-swatch cp-annual-slot-legend-swatch--activity"
                            aria-label="선택 셀에 활동(베이지 줄무늬) 적용. 기존 배경색은 유지됩니다."
                            disabled={!selInTerm}
                            title="활동"
                            onClick={() => applySlotSurfaceToSelected('activity')}
                          >
                            <span className="cp-annual-slot-legend-swatch-label" aria-hidden>
                              활
                            </span>
                          </button>
                          <span className="cp-annual-slot-legend-tooltip" role="tooltip">
                            활동
                          </span>
                        </div>
                        <div className="cp-annual-slot-legend-item">
                          <button
                            type="button"
                            className="cp-annual-slot-legend-swatch cp-annual-slot-legend-swatch--nonclass"
                            aria-label="선택 셀에 비수업(회색 줄무늬) 적용. 기존 배경색은 유지됩니다."
                            disabled={!selInTerm}
                            title="비수업"
                            onClick={() => applySlotSurfaceToSelected('non_class')}
                          >
                            <span className="cp-annual-slot-legend-swatch-label" aria-hidden>
                              비
                            </span>
                          </button>
                          <span className="cp-annual-slot-legend-tooltip" role="tooltip">
                            비수업
                          </span>
                        </div>
                      </div>
                    ) : null}
                    <div
                      className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group"
                      data-tutorial-annual-color-group={
                        group.label === '고정(행사)'
                          ? 'fixed-event'
                          : group.label === '라벨링 색상'
                            ? 'labeling'
                            : undefined
                      }
                    >
                    <span className="cp-base-timetable-dedicated-label">{group.label}</span>
                    <button
                      type="button"
                      className="cp-dedicated-color-btn cp-dedicated-color-none-btn"
                      onClick={() => applyColorToSelected('', group.label)}
                                      disabled={!selInTerm}
                      title={`${group.label} 색상없음`}
                      aria-label={`${group.label} 색상없음`}
                    />
                    {group.colors.map((hex) => {
                      const colorLabel = getProgramColorLabel(entry.gradeId, group.modalType, hex);
                      const showCellCount = group.label === '라벨링 색상';
                      const colorNorm = normalizeHexColorForProjectCompare(hex);
                      const cellCount =
                        showCellCount && colorNorm ? (termColorCounts.labeling[colorNorm] ?? 0) : null;
                      return (
                        <span
                          key={`${entry.gradeId}-${termKey}-${group.label}-${hex}`}
                          className="cp-annual-toolbar-color-wrap"
                        >
                          <span className="cp-annual-project-toolbar-swatch-wrap">
                            <button
                              type="button"
                              className="cp-dedicated-color-btn"
                              onClick={() => applyColorToSelected(hex, group.label)}
                              disabled={!selInTerm}
                              title={colorLabel ? undefined : hex}
                              aria-label={
                                colorLabel
                                  ? `${group.label} ${colorLabel}${cellCount != null ? ` ${cellCount}칸` : ''}`
                                  : `${group.label} ${hex}${cellCount != null ? ` ${cellCount}칸` : ''}`
                              }
                            >
                              <span
                                className="cp-dedicated-color-btn-inner"
                                style={{ backgroundColor: hex }}
                                aria-hidden
                              />
                              {cellCount != null ? (
                                <span
                                  className="cp-dedicated-color-btn-count"
                                  style={{ color: contrastInkForBackgroundHex(hex) }}
                                  aria-hidden
                                >
                                  {cellCount}
                                </span>
                              ) : null}
                            </button>
                            {colorLabel ? (
                              <span className="cp-annual-project-toolbar-tooltip" role="tooltip">
                                {colorLabel}
                              </span>
                            ) : null}
                          </span>
                          <button
                            type="button"
                            className="cp-annual-toolbar-color-action cp-annual-toolbar-color-action-edit"
                            onClick={() =>
                              setProgramColorPickModal({
                                gradeId: entry.gradeId,
                                type: group.modalType,
                                mode: 'edit',
                                initialHex: hex,
                                initialLabel: colorLabel,
                              })
                            }
                            aria-label={`${group.label} 색상 수정`}
                            title="색상/라벨 수정"
                          >
                            <img src="/edit.svg" alt="" aria-hidden />
                          </button>
                        </span>
                      );
                    })}
                    <button
                      type="button"
                      className="cp-dedicated-color-btn cp-dedicated-color-add-btn"
                      onClick={() =>
                        setProgramColorPickModal({
                          gradeId: entry.gradeId,
                          type: group.modalType,
                          mode: 'create',
                        })
                      }
                      title={`${group.label} 색상 추가`}
                      aria-label={`${group.label} 색상 추가`}
                    >
                      +
                    </button>
                  </div>
                  </React.Fragment>
                ))}
                {(() => {
                  const termProjects = (annualProjectProgramsByGradeId[entry.gradeId] ?? []).filter(
                    (p) => p.termNumber === termNum
                  );
                  return (
                    <div
                      className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group cp-annual-project-palette"
                      data-tutorial-annual-color-group="project"
                    >
                      <span className="cp-base-timetable-dedicated-label">프로젝트</span>
                      <button
                        type="button"
                        className="cp-dedicated-color-btn cp-annual-project-manage-gear-btn"
                        onClick={() => openProjectManageModal(entry.gradeId)}
                        disabled={loading || loadingCells}
                        title="프로젝트 관리"
                        aria-label="프로젝트 관리"
                      >
                        <BasicSettingIcon />
                      </button>
                      <button
                        type="button"
                        className="cp-dedicated-color-btn cp-dedicated-color-none-btn"
                        onClick={() => applyColorToSelected('')}
                        disabled={!selInTerm}
                        title="프로젝트 색상없음"
                        aria-label="프로젝트 색상없음"
                      />
                      {termProjects.map((p) => {
                        const projectNorm = normalizeHexColorForProjectCompare(p.colorHex);
                        const cellCount = projectNorm
                          ? (termColorCounts.project[projectNorm] ?? 0)
                          : 0;
                        return (
                        <span key={p.id} className="cp-annual-project-toolbar-swatch-wrap">
                          <button
                            type="button"
                            className="cp-dedicated-color-btn"
                            onClick={() => applyProjectProgramToSelected(p)}
                            disabled={!selInTerm}
                            title={!selInTerm ? p.name : undefined}
                            aria-label={`프로젝트 ${p.name} (${p.colorHex}) ${cellCount}칸`}
                          >
                            <span
                              className="cp-dedicated-color-btn-inner cp-annual-project-grid-fill"
                              style={{ backgroundColor: p.colorHex }}
                              aria-hidden
                            />
                            <span
                              className="cp-dedicated-color-btn-count"
                              style={{ color: contrastInkForBackgroundHex(p.colorHex) }}
                              aria-hidden
                            >
                              {cellCount}
                            </span>
                          </button>
                          <span className="cp-annual-project-toolbar-tooltip" role="tooltip">
                            {p.name}
                          </span>
                        </span>
                        );
                      })}
                      <button
                        type="button"
                        className="cp-dedicated-color-btn cp-dedicated-color-add-btn"
                        onClick={() => openProjectCreateModal(entry.gradeId, termNum)}
                        disabled={loading || loadingCells}
                        title="프로젝트 프로그램 생성"
                        aria-label="프로젝트 프로그램 생성"
                      >
                        +
                      </button>
                    </div>
                  );
                })()}
              </div>
                              <AnnualTermToolbarSelectionInput
                                gradeId={entry.gradeId}
                                selInTerm={selInTerm}
                                selectedCells={selectedCells}
                                wks={wks}
                                dayKeysForSelection={dayKeysForSelection}
                                effectiveCellsByKey={effectiveCellsByKey}
                                editingCell={editingCell}
                                editingInputRef={editingInputRef}
                                filterEditableSelectionKeys={filterEditableSelectionKeys}
                                applyCellUpdates={applyCellUpdates}
                                setEditingCell={setEditingCell}
                                moveSingleSelectionByFlatStep={moveSingleSelectionByFlatStep}
                              />
                            </div>
                            <div className="cp-annual-timetable-table-wrap">
                              <table
                                className={
                                  'cp-weekly-schedule-table cp-annual-timetable-fixed-table ' +
                                  (termKey === '1'
                                    ? 'cp-annual-term-table-term1'
                                    : 'cp-annual-term-table-term2')
                                }
                              >
              <thead>
                <tr>
                  <th className="cp-weekly-schedule-th-month">월</th>
                  <th className="cp-weekly-schedule-th-week">주</th>
                  <th className="cp-weekly-schedule-th-daterange">일자</th>
                  {weekdayKeysToShow.map(({ key, label }) => (
                    <th key={key} colSpan={periodsPerDay} className="cp-weekly-schedule-th-weekday">
                      {label}
                    </th>
                  ))}
                  <th className="cp-weekly-schedule-th-hours">
                    수업
                    <br />
                    시수
                  </th>
                  <th className="cp-weekly-schedule-th-days">
                    수업
                    <br />
                    일수
                  </th>
                </tr>
                <tr className="cp-annual-timetable-period-head-row">
                  <th
                    colSpan={3}
                    className="cp-weekly-schedule-th-daterange cp-annual-selected-date-headcell"
                    data-cp-annual-date-head-anchor={`${entry.gradeId}-${termKey}`}
                  >
                    {isDateHeadPicker && annualDateHeadPicker ? (
                      <input
                        ref={annualDateHeadInputRef}
                        type="text"
                        inputMode="decimal"
                        className="cp-annual-selected-date-headcell-input"
                        value={annualDateHeadPicker.draft}
                        onChange={(e) =>
                          setAnnualDateHeadPicker((prev) =>
                            prev ? { ...prev, draft: e.target.value } : null
                          )
                        }
                        placeholder="월.일"
                        aria-label="월·일 입력 후 Enter로 해당 날짜 교시 열 선택"
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            annualDateHeadActiveRef.current = false;
                            setAnnualDateHeadPicker(null);
                            return;
                          }
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const raw = (e.currentTarget as HTMLInputElement).value.trim();
                            annualDateHeadActiveRef.current = false;
                            setAnnualDateHeadPicker(null);
                            if (raw) commitAnnualDateHeadJump(raw);
                          }
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="cp-annual-selected-date-headcell-btn"
                        title="클릭 후 월.일(예: 3.15) 입력, Enter로 해당 날짜의 교시 열 전체 선택"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          let draft = '';
                          if (selInTerm && selectedCells.length > 0) {
                            const firstKey = selectedCells[0];
                            const iso =
                              (firstKey ? effectiveCellsByKey[firstKey]?.dateIso : '')?.trim() ?? '';
                            if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
                              const d = parseISODate(iso);
                              if (!Number.isNaN(d.getTime())) {
                                draft = `${d.getMonth() + 1}.${d.getDate()}`;
                              }
                            }
                          }
                          annualDateHeadActiveRef.current = true;
                          setAnnualDateHeadPicker({
                            gradeId: entry.gradeId,
                            termKey,
                            draft,
                          });
                        }}
                      >
                        {selectedHeadDateLabel}
                      </button>
                    )}
                  </th>
                  {weekdayKeysToShow.map(({ key }) =>
                    periodLabels.map((label, p) => {
                      const scheduleRow = sectionRows[p];
                      const timeRangeTip = formatPeriodHeaderTimeRange(scheduleRow);
                      return (
                        <th
                          key={`${key}-${p}`}
                          className="cp-weekly-schedule-th-period cp-annual-period-head-th"
                        >
                          <AnnualPeriodHeadLabel label={label} />
                          <span className="cp-annual-period-head-th-tooltip" role="tooltip">
                            <span className="cp-annual-period-head-th-tooltip-inner">{timeRangeTip}</span>
                          </span>
                        </th>
                      );
                    })
                  )}
                  <th
                    id={annualTermHoursAnchorDomId(termKey, entry.gradeId)}
                    className="cp-weekly-schedule-th-hours cp-weekly-schedule-semester-hours cp-annual-term-hours-cell"
                    onMouseEnter={(e) => {
                      const tracks = buildTermTrackHoursBreakdown(termNum);
                      if (tracks.length === 0) return;
                      setTermHoursHoverPreview({
                        anchorId: annualTermHoursAnchorDomId(termKey, entry.gradeId),
                        style: computeWeekTrackHoursHoverTooltipStyle(
                          e.currentTarget.getBoundingClientRect()
                        ),
                        tracks,
                      });
                    }}
                    onMouseLeave={() => setTermHoursHoverPreview(null)}
                  >
                    {termHours[entry.gradeId] ?? ''}
                  </th>
                  <th className="cp-weekly-schedule-th-days cp-weekly-schedule-semester-days">
                    {termDays}
                  </th>
                </tr>
              </thead>
              <tbody>
                {wks.map((week, weekRowIdx) => {
                  const weeklyHours = getWeekClassHours(entry.gradeId, week);
                  const weeklyInstructionalDays = getWeekInstructionalDayCount(week, termNum);
                  const dateRangeDisplay = showSaturday
                    ? week.dateRangeLabel
                    : `${String(parseISODate(week.datesByWeekday[0]).getMonth() + 1).padStart(2, '0')}.${String(parseISODate(week.datesByWeekday[0]).getDate()).padStart(2, '0')}-${String(parseISODate(week.datesByWeekday[4]).getMonth() + 1).padStart(2, '0')}.${String(parseISODate(week.datesByWeekday[4]).getDate()).padStart(2, '0')}`;
                  const weekMemoLookupKey = weekMemoStateKey(termNum, entry.gradeId, week.mondayIso);
                  const weekMemoRow = annualWeekMemosByKey[weekMemoLookupKey];
                  const weekMemoHasContent =
                    (weekMemoRow?.generalMemo ?? '').trim().length > 0 ||
                    (weekMemoRow?.activityLines ?? []).some((l) => l.trim().length > 0);
                  const periodsPerDay = periodLabels.length;
                  const dayCols = weekdayKeysToShow;
                  return (
                    <tr key={week.mondayIso}>
                      <td className="cp-weekly-schedule-td-month">{week.monthLabel}</td>
                      <td className="cp-weekly-schedule-td-week">{week.weekIndex}</td>
                      <td
                        id={annualWeekMemoAnchorDomId(termKey, entry.gradeId, week.mondayIso)}
                        className={
                          'cp-weekly-schedule-td-daterange cp-annual-week-memo-anchor-cell' +
                          (weekMemoHasContent ? ' cp-annual-week-memo-anchor-cell--has' : '')
                        }
                        role="button"
                        tabIndex={0}
                        aria-label={`${dateRangeDisplay} 주 활동 메모 편집`}
                        title={weekMemoHasContent ? undefined : '주 활동 메모'}
                        onMouseEnter={(e) => {
                          if (!weekMemoRow || !weekMemoHasContent) return;
                          const anchorId = annualWeekMemoAnchorDomId(termKey, entry.gradeId, week.mondayIso);
                          const r = e.currentTarget.getBoundingClientRect();
                          const { activityLines: al, generalMemo: gm } = weekMemoRowToHoverPayload(weekMemoRow);
                          setWeekMemoHoverPreview({
                            anchorId,
                            style: computeProgramProgressHoverTooltipStyle(r, 'memo_narrow'),
                            activityLines: al,
                            generalMemo: gm,
                          });
                        }}
                        onMouseLeave={() => setWeekMemoHoverPreview(null)}
                        onMouseDown={(e) => {
                          setWeekMemoHoverPreview(null);
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openWeekActivityMemoModal({
                            termKey,
                            termNumber: termNum,
                            gradeId: entry.gradeId,
                            mondayIso: week.mondayIso,
                            weekIndex: week.weekIndex,
                            dateRangeDisplay,
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault();
                          e.stopPropagation();
                          openWeekActivityMemoModal({
                            termKey,
                            termNumber: termNum,
                            gradeId: entry.gradeId,
                            mondayIso: week.mondayIso,
                            weekIndex: week.weekIndex,
                            dateRangeDisplay,
                          });
                        }}
                      >
                        {dateRangeDisplay}
                      </td>
                      {weekdayKeysToShow.map(({ key: dayKey }, dayIdx) => {
                        const dateIsoForDay = week.datesByWeekday[dayIdx] ?? '';
                        const clampedHolidayNote = clampCalendarHolidayNote(
                          (calendarDayNoteByDate[dateIsoForDay] ?? '').trim()
                        );
                        const dayKindForCol = calendarDayKindByDate[dateIsoForDay];
                        const holidayNameFallback =
                          dayKindForCol === 'closure' ? '휴업일' : '공휴일';
                        const holidaySpreadSource =
                          clampedHolidayNote ||
                          (holidaySet.has(dateIsoForDay) ? holidayNameFallback : '');
                        const holidayNoteChars = Array.from(holidaySpreadSource);
                        const isSchoolBreakDay = isDateInSchoolBreak(
                          calendarLinkedContext.sectionSettings,
                          dateIsoForDay
                        );
                        return periodLabels.map((_, p) => {
                          const compoundKey = compoundCellKey(entry.gradeId, week.mondayIso, dayKey, p);
                          const runtimeCell = effectiveCellsByKey[compoundKey];
                          const isOutOfRange = runtimeCell?.slotKind === 'out_of_term';
                          const isHoliday = runtimeCell?.slotKind === 'holiday';
                          const isSchoolBreakCell = isSchoolBreakDay && isHoliday;
                          const isCalendarHolidayCell = isHoliday && !isSchoolBreakCell;
                          const isActivitySlot = runtimeCell?.slotKind === 'activity';
                          const isCalendarScheduleNonClass =
                            runtimeCell?.nonClassSource === 'calendar_schedule';
                          const isImportConflictCell =
                            (isCalendarHolidayCell || isSchoolBreakCell || isCalendarScheduleNonClass) &&
                            Boolean(
                              (runtimeCell?.textValue ?? '').trim() ||
                                (runtimeCell?.memo ?? '').trim() ||
                                (runtimeCell?.colorHex ?? '').trim()
                            );
                          const isUserNonClassSlot =
                            runtimeCell?.slotKind === 'non_class' && !isCalendarScheduleNonClass;
                          const isUnusedKindSlot = isUserNonClassSlot;
                          const isEditable = isCompoundCellEditable(compoundKey, wks);
                          const isEditing =
                            editingCell?.gradeId === entry.gradeId &&
                            editingCell?.mondayIso === week.mondayIso &&
                            editingCell?.dayKey === dayKey &&
                            editingCell?.periodIdx === p;
                          const cellMemo = (runtimeCell?.memo ?? '').trim();
                          const userVisibleMemo = cellMemo && !isAnnualShortDayTailMemo(cellMemo) ? cellMemo : '';
                          const hasMemo = userVisibleMemo.length > 0;
                          const storedTextValue = (runtimeCell?.textValue ?? '').trim();
                          const displayValue =
                            isEditing && editingCell
                              ? editingCell.draft
                              : isCalendarHolidayCell && !isImportConflictCell
                                ? (holidayNoteChars[p] ?? '')
                                : storedTextValue ||
                                  (isUserNonClassSlot && userVisibleMemo ? userVisibleMemo : '') ||
                                  (runtimeCell?.textValue ?? '');
                          const timetableTextInvalid =
                            isEditable &&
                            isAnnualTimetableNameUnregistered(
                              entry.gradeId,
                              displayValue,
                              registeredNamesByGrade,
                              wildcardCharsByGrade
                            );
                          const colorHex = runtimeCell?.colorHex ?? '';
                          const isProjectProgramCell =
                            isEditable &&
                            !!colorHex &&
                            matchesAnnualProjectProgramCell(
                              colorHex,
                              annualProjectProgramsByGradeId[entry.gradeId] ?? [],
                              termNum,
                              runtimeCell?.isBaseExcluded
                            );
                          const isTrackMismatchAgainstBase = (() => {
                            if (!isEditable) return false;
                            if (runtimeCell?.slotKind !== 'class') return false;
                            // 고정/전담/기초 제외/프로젝트 등 색이 있는 셀은 제외 (기본 시간표 셀만)
                            if ((colorHex ?? '').trim()) return false;
                            const byTermData = baseTimetableByGrade[entry.gradeId];
                            if (!byTermData) return false;
                            const baseGrid = getBaseTimetableGridForTerm(byTermData, termNum);
                            const baseIdx = baseGridClassPeriodIndexForSectionSlot(entry.sectionRows, p);
                            if (baseIdx < 0) return false;
                            const baseCell = getBaseGridCell(baseGrid, dayKey as AnnualWeekdayKey, baseIdx);
                            return isAnnualTrackMismatchAgainstBase(
                              entry.gradeId,
                              baseCell?.name ?? '',
                              displayValue,
                              trackNameByTimetableTextByGrade,
                              wildcardCharByGradeTrackName
                            );
                          })();
                          const selected = selectedSet.has(compoundKey);
                          const selectedSingle = selectedCells.length === 1 && selected;
                          const selectedMulti = selectedCells.length > 1 && selected;
                          const weekContext =
                            !!singleSelectWeekContext &&
                            !selected &&
                            singleSelectWeekContext.gradeId === entry.gradeId &&
                            singleSelectWeekContext.mondayIso === week.mondayIso;
                          const copiedSource = copiedSourceSet.has(compoundKey);
                          const copiedSourceSingle = copiedSource && copiedSourceSet.size === 1;
                          const copiedSourceMulti = copiedSource && copiedSourceSet.size > 1;
                          const inSwapA = swapASet.has(compoundKey);
                          const inSwapB = swapBSet.has(compoundKey);
                          /* 한 주 행 안에서 요일·교시를 하나의 가로 띠(flatCol)로 보고 이웃을 잡는다. */
                          const flatCol = dayIdx * periodsPerDay + p;
                          const flatMax = dayCols.length * periodsPerDay - 1;
                          const leftKey =
                            flatCol > 0
                              ? compoundCellKey(
                                  entry.gradeId,
                                  week.mondayIso,
                                  dayCols[Math.floor((flatCol - 1) / periodsPerDay)]!.key,
                                  (flatCol - 1) % periodsPerDay
                                )
                              : null;
                          const rightKey =
                            flatCol < flatMax
                              ? compoundCellKey(
                                  entry.gradeId,
                                  week.mondayIso,
                                  dayCols[Math.floor((flatCol + 1) / periodsPerDay)]!.key,
                                  (flatCol + 1) % periodsPerDay
                                )
                              : null;
                          const prevWeekRow = weekRowIdx > 0 ? wks[weekRowIdx - 1] : null;
                          const nextWeekRow = weekRowIdx < wks.length - 1 ? wks[weekRowIdx + 1] : null;
                          const topKey = prevWeekRow
                            ? compoundCellKey(
                                entry.gradeId,
                                prevWeekRow.mondayIso,
                                dayCols[Math.floor(flatCol / periodsPerDay)]!.key,
                                flatCol % periodsPerDay
                              )
                            : null;
                          const bottomKey = nextWeekRow
                            ? compoundCellKey(
                                entry.gradeId,
                                nextWeekRow.mondayIso,
                                dayCols[Math.floor(flatCol / periodsPerDay)]!.key,
                                flatCol % periodsPerDay
                              )
                            : null;
                          const selectedEdgeTop = selectedMulti && (!topKey || !selectedSet.has(topKey));
                          const selectedEdgeBottom = selectedMulti && (!bottomKey || !selectedSet.has(bottomKey));
                          const selectedEdgeLeft = selectedMulti && (!leftKey || !selectedSet.has(leftKey));
                          const selectedEdgeRight = selectedMulti && (!rightKey || !selectedSet.has(rightKey));
                          const copiedEdgeTop = copiedSourceMulti && (!topKey || !copiedSourceSet.has(topKey));
                          const copiedEdgeBottom = copiedSourceMulti && (!bottomKey || !copiedSourceSet.has(bottomKey));
                          const copiedEdgeLeft = copiedSourceMulti && (!leftKey || !copiedSourceSet.has(leftKey));
                          const copiedEdgeRight = copiedSourceMulti && (!rightKey || !copiedSourceSet.has(rightKey));
                          const swapAEdgeTop = inSwapA && (!topKey || !swapASet.has(topKey));
                          const swapAEdgeBottom = inSwapA && (!bottomKey || !swapASet.has(bottomKey));
                          const swapAEdgeLeft = inSwapA && (!leftKey || !swapASet.has(leftKey));
                          const swapAEdgeRight = inSwapA && (!rightKey || !swapASet.has(rightKey));
                          const swapBEdgeTop = inSwapB && (!topKey || !swapBSet.has(topKey));
                          const swapBEdgeBottom = inSwapB && (!bottomKey || !swapBSet.has(bottomKey));
                          const swapBEdgeLeft = inSwapB && (!leftKey || !swapBSet.has(leftKey));
                          const swapBEdgeRight = inSwapB && (!rightKey || !swapBSet.has(rightKey));
                          const cellClass = [
                            'cp-weekly-schedule-cell',
                            isOutOfRange ? 'cp-weekly-cell-out-of-range' : '',
                            isSchoolBreakCell ? 'cp-weekly-cell-out-of-range' : '',
                            isCalendarHolidayCell ? 'cp-weekly-cell-holiday' : '',
                            isCalendarScheduleNonClass ? 'cp-weekly-cell-calendar-non-class' : '',
                            !isHoliday && !isOutOfRange && runtimeCell?.slotKind === 'non_class'
                              ? 'cp-weekly-cell-no-class'
                              : '',
                            isActivitySlot ? 'cp-weekly-cell-activity-hatch' : '',
                            runtimeCell?.slotKind === 'non_class' ? 'cp-weekly-cell-unused-kind' : '',
                            isImportConflictCell ? 'cp-annual-import-conflict-cell' : '',
                            isImportConflictCell && (isCalendarHolidayCell || isSchoolBreakCell)
                              ? 'cp-annual-import-conflict-cell--non-class-day'
                              : '',
                            isImportConflictCell && isCalendarScheduleNonClass
                              ? 'cp-annual-import-conflict-cell--calendar-non-class'
                              : '',
                            isTrackMismatchAgainstBase ? 'cp-annual-timetable-cell-track-mismatch' : '',
                            isEditable && colorHex ? 'cp-base-timetable-cell-has-bg' : '',
                            isProjectProgramCell ? 'cp-annual-project-cell' : '',
                            hasMemo ? 'cp-annual-cell-has-memo' : '',
                            selectedSingle ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-single' : '',
                            selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
                            weekContext ? 'cp-annual-timetable-week-context' : '',
                            selectedEdgeTop ? 'cp-base-timetable-cell-edge-top' : '',
                            selectedEdgeBottom ? 'cp-base-timetable-cell-edge-bottom' : '',
                            selectedEdgeLeft ? 'cp-base-timetable-cell-edge-left' : '',
                            selectedEdgeRight ? 'cp-base-timetable-cell-edge-right' : '',
                            copiedSource ? 'cp-base-timetable-cell-copied-source' : '',
                            copiedSourceSingle ? 'cp-base-timetable-cell-copied-source-single' : '',
                            copiedSourceMulti ? 'cp-base-timetable-cell-copied-source-multi' : '',
                            copiedEdgeTop ? 'cp-base-timetable-cell-copied-edge-top' : '',
                            copiedEdgeBottom ? 'cp-base-timetable-cell-copied-edge-bottom' : '',
                            copiedEdgeLeft ? 'cp-base-timetable-cell-copied-edge-left' : '',
                            copiedEdgeRight ? 'cp-base-timetable-cell-copied-edge-right' : '',
                            inSwapA ? 'cp-base-timetable-cell-swap-a' : '',
                            swapAEdgeTop ? 'cp-base-timetable-cell-swap-a-edge-top' : '',
                            swapAEdgeBottom ? 'cp-base-timetable-cell-swap-a-edge-bottom' : '',
                            swapAEdgeLeft ? 'cp-base-timetable-cell-swap-a-edge-left' : '',
                            swapAEdgeRight ? 'cp-base-timetable-cell-swap-a-edge-right' : '',
                            inSwapB ? 'cp-base-timetable-cell-swap-b' : '',
                            swapBEdgeTop ? 'cp-base-timetable-cell-swap-b-edge-top' : '',
                            swapBEdgeBottom ? 'cp-base-timetable-cell-swap-b-edge-bottom' : '',
                            swapBEdgeLeft ? 'cp-base-timetable-cell-swap-b-edge-left' : '',
                            swapBEdgeRight ? 'cp-base-timetable-cell-swap-b-edge-right' : '',
                          ]
                            .filter(Boolean)
                            .join(' ');
                          return (
                            <td
                              key={`${dayIdx}-${p}`}
                              id={annualCellDomId(compoundKey)}
                              className={cellClass}
                              onMouseDown={(e) => {
                                setCellColorHoverPreview(null);
                                if (isEditable) e.preventDefault();
                                if (isEditable && e.button === 0) {
                                  clearAnnualCellMemoLongPressTimer();
                                  annualCellMemoLongPressAnchorRef.current = compoundKey;
                                  annualCellMemoLongPressTimerRef.current = window.setTimeout(() => {
                                    annualCellMemoLongPressTimerRef.current = null;
                                    annualCellMemoLongPressAnchorRef.current = null;
                                    openAnnualCellMemoModalForKey(compoundKey);
                                  }, 450);
                                }
                                handleCellMouseDown(compoundKey, isEditable, wks, e.shiftKey);
                              }}
                              onDoubleClick={(e) => {
                                if (!isEditable) return;
                                e.preventDefault();
                                e.stopPropagation();
                                handleCellDoubleClick(compoundKey, isEditable);
                              }}
                              onMouseEnter={(e) => {
                                handleCellMouseEnter(compoundKey, wks);
                                if (dragStateRef.current?.active) {
                                  setCellColorHoverPreview(null);
                                  return;
                                }
                                if (!isEditable || !colorHex.trim()) {
                                  setCellColorHoverPreview(null);
                                  return;
                                }
                                const gradeColors = programColorSettingsFull
                                  ? resolveGradeProgramColors(programColorSettingsFull, entry.gradeId)
                                  : {
                                      ...createDefaultGradeProgramColors(),
                                      regular: regularColors,
                                      dedicated: dedicatedColors,
                                      baseExclude: baseExcludeColors,
                                    };
                                const projectMatch = isProjectProgramCell
                                  ? findAnnualProjectProgramForCell(
                                      colorHex,
                                      annualProjectProgramsByGradeId[entry.gradeId] ?? [],
                                      termNum
                                    )
                                  : undefined;
                                let hoverLabel = '';
                                let hoverKind: 'project' | 'plain' = 'plain';
                                if (projectMatch?.name.trim()) {
                                  hoverLabel = projectMatch.name.trim();
                                  hoverKind = 'project';
                                } else if (
                                  runtimeCell?.isDedicated ||
                                  isDedicatedColor(colorHex, gradeColors.dedicated) ||
                                  colorHexInPalette(colorHex, gradeColors.dedicated)
                                ) {
                                  hoverLabel = '전담';
                                } else if (
                                  runtimeCell?.isBaseExcluded ||
                                  isBaseExcludeColor(colorHex, gradeColors.baseExclude) ||
                                  colorHexInPalette(colorHex, gradeColors.baseExclude)
                                ) {
                                  hoverLabel = getProgramColorLabel(
                                    entry.gradeId,
                                    'baseExclude',
                                    colorHex
                                  ).trim();
                                } else if (
                                  colorHexInPalette(colorHex, gradeColors.project ?? [])
                                ) {
                                  hoverLabel = getProgramColorLabel(
                                    entry.gradeId,
                                    'project',
                                    colorHex
                                  ).trim();
                                }
                                if (!hoverLabel) {
                                  setCellColorHoverPreview(null);
                                  return;
                                }
                                setCellColorHoverPreview({
                                  anchorId: annualCellDomId(compoundKey),
                                  style: computeAnnualCellColorHoverTooltipStyle(
                                    e.currentTarget.getBoundingClientRect()
                                  ),
                                  label: hoverLabel,
                                  kind: hoverKind,
                                });
                              }}
                              onMouseLeave={() => setCellColorHoverPreview(null)}
                            >
                              {weekContext ? (
                                <span className="cp-annual-timetable-week-context-wash" aria-hidden />
                              ) : null}
                              {isEditable && colorHex ? (
                                <div
                                  className={
                                    'cp-base-timetable-cell-bg' +
                                    (isProjectProgramCell ? ' cp-annual-project-grid-fill' : '') +
                                    (isImportConflictCell ? ' cp-base-timetable-cell-bg--import-conflict-overlay' : '') +
                                    (isUnusedKindSlot
                                      ? ' cp-base-timetable-cell-bg--non-class-stripes-over-fill'
                                      : isActivitySlot
                                        ? ' cp-base-timetable-cell-bg--activity-hatch-over-fill'
                                        : isFixedProgramColor(colorHex, regularColors)
                                          ? ' cp-base-timetable-cell-bg--fixed-program-hatch-over-fill'
                                          : '')
                                  }
                                  style={{ backgroundColor: colorHex }}
                                  aria-hidden
                                />
                              ) : null}
                              {!isEditable && (!isHoliday || isSchoolBreakCell) ? (
                                <span className="cp-base-timetable-disabled-mark">-</span>
                              ) : isEditing ? (
                                <input
                                  key={compoundKey}
                                  ref={editingInputRef}
                                  type="text"
                                  className={
                                    'cp-base-timetable-cell-input cp-annual-timetable-cell-input' +
                                    (timetableTextInvalid
                                      ? ' cp-annual-timetable-cell-invalid-timetable-name'
                                      : '')
                                  }
                                  style={{ fontSize: annualCellDisplayFontSize(editingCell?.draft ?? '') }}
                                  value={editingCell?.draft ?? ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEditingCell((prev) =>
                                      prev &&
                                      prev.gradeId === entry.gradeId &&
                                      prev.mondayIso === week.mondayIso &&
                                      prev.dayKey === dayKey &&
                                      prev.periodIdx === p
                                        ? { ...prev, draft: value }
                                        : prev
                                    );
                                  }}
                                  onCompositionStart={() => {
                                    isComposingRef.current = true;
                                  }}
                                  onCompositionEnd={() => {
                                    isComposingRef.current = false;
                                  }}
                                  onBlur={() => {
                                    // 키보드(Enter/Tab)와 마우스 경로에서 이미 커밋을 수행하므로
                                    // blur에서는 중복 커밋을 하지 않는다. (값 복제/2칸 이동 방지)
                                    if (suppressInputBlurCommitOnceRef.current) {
                                      suppressInputBlurCommitOnceRef.current = false;
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      if (isComposingRef.current) return;
                                      e.preventDefault();
                                      (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                                      e.stopPropagation();
                                      suppressInputBlurCommitOnceRef.current = true;
                                      window.setTimeout(() => {
                                        suppressInputBlurCommitOnceRef.current = false;
                                      }, 0);
                                      // Enter: 확정 후 아래 셀은 선택만(다시 Enter로 편집). Tab은 옆 셀 즉시 편집 유지.
                                      skipAutoEditOnceRef.current = false;
                                      if (editingCell)
                                        commitEdit(
                                          entry.gradeId,
                                          week.mondayIso,
                                          dayKey,
                                          p,
                                          editingInputRef.current?.value ?? editingCell.draft ?? ''
                                        );
                                      moveSingleSelectionByFlatStep(dayKeysForSelection.length * MAX_PERIODS, {
                                        skipAutoEditOnce: true,
                                      });
                                      return;
                                    }
                                    if (e.key === 'Tab') {
                                      if (isComposingRef.current) return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                                      suppressInputBlurCommitOnceRef.current = true;
                                      window.setTimeout(() => {
                                        suppressInputBlurCommitOnceRef.current = false;
                                      }, 0);
                                      suppressGlobalTabOnceRef.current = true;
                                      window.setTimeout(() => {
                                        suppressGlobalTabOnceRef.current = false;
                                      }, 0);
                                      // Enter 이동과 동일: 옆 칸으로 이동한 뒤 즉시 편집 모드 진입
                                      skipAutoEditOnceRef.current = false;
                                      if (editingCell)
                                        commitEdit(
                                          entry.gradeId,
                                          week.mondayIso,
                                          dayKey,
                                          p,
                                          editingInputRef.current?.value ?? editingCell.draft ?? ''
                                        );
                                      moveSingleSelectionByFlatStep(e.shiftKey ? -1 : 1, {
                                        skipAutoEditOnce: false,
                                      });
                                      return;
                                    }
                                    if (e.key === 'Escape') setEditingCell(null);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label={`${week.dateRangeLabel} ${dayKey} ${p + 1}교시`}
                                  aria-invalid={timetableTextInvalid}
                                />
                              ) : (
                                <div
                                  className={
                                    'cp-base-timetable-cell-inner cp-annual-timetable-cell-inner' +
                                    (timetableTextInvalid
                                      ? ' cp-annual-timetable-cell-invalid-timetable-name'
                                      : '')
                                  }
                                  style={{ fontSize: annualCellDisplayFontSize(displayValue) }}
                                  aria-label={`${week.dateRangeLabel} ${dayKey} ${p + 1}교시`}
                                >
                                  {displayValue.trim() === '' ? (
                                    <span className="cp-base-timetable-empty"> </span>
                                  ) : (
                                    displayValue
                                  )}
                                </div>
                              )}
                              {hasMemo ? (
                                <div className="cp-annual-cell-memo-tooltip" role="note">
                                  {userVisibleMemo}
                                </div>
                              ) : null}
                            </td>
                          );
                        });
                      })}
                      <td
                        id={annualWeekHoursAnchorDomId(termKey, entry.gradeId, week.mondayIso)}
                        className="cp-weekly-schedule-td-hours cp-annual-week-hours-cell"
                        onMouseEnter={(e) => {
                          const tracks = buildWeekTrackHoursBreakdown(week, termNum);
                          if (tracks.length === 0) return;
                          setWeekHoursHoverPreview({
                            anchorId: annualWeekHoursAnchorDomId(termKey, entry.gradeId, week.mondayIso),
                            style: computeWeekTrackHoursHoverTooltipStyle(
                              e.currentTarget.getBoundingClientRect()
                            ),
                            tracks,
                          });
                        }}
                        onMouseLeave={() => setWeekHoursHoverPreview(null)}
                      >
                        {weeklyHours}
                      </td>
                      <td className="cp-weekly-schedule-td-days cp-annual-week-days-cell">
                        {weeklyInstructionalDays}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
                          </div>
                        );
                      };

                      const formatDeviation = (counted: number, baseline: number) => {
                        const d = counted - baseline;
                        if (d > 0) return `+${d}`;
                        if (d < 0) return String(d);
                        return '0';
                      };
                      const totalBaselineHours = summaryColGroups.reduce(
                        (sum, g) => sum + (g.isOtherTrack ? 0 : (g.criteriaHours ?? 0)),
                        0
                      );
                      const totalCountedHours = summaryColGroups.reduce(
                        (sum, g) => sum + (g.isOtherTrack ? 0 : sumTermHoursForGroup(g)),
                        0
                      );
                      const totalDeviationText = formatDeviation(totalCountedHours, totalBaselineHours);
                      const totalTermHoursFor = (term: 1 | 2) =>
                        summaryColGroups.reduce(
                          (sum, g) =>
                            sum + (g.isOtherTrack ? 0 : countTrackTermHoursForGroup(g, term)),
                          0
                        );
                      const totalTerm1Hours = totalTermHoursFor(1);
                      const totalTerm2Hours = totalTermHoursFor(2);

                      const summaryBlock = (
                        <div className="cp-annual-term-summary-block" aria-label="편제별 학기 시수 카운팅">
                          <div className="cp-annual-timetable-table-wrap">
                            <table className="cp-annual-term-summary-table">
                            <tbody>
                              <tr className="cp-annual-term-summary-row-shaded cp-annual-term-summary-row-trackname">
                                <th className="cp-annual-term-summary-rowhead">편제명</th>
                                {summaryColGroups.map((g, gi) => (
                                  <td
                                    key={`pn-${gi}`}
                                    colSpan={summaryGroupColCount(g)}
                                    className={
                                      'cp-annual-term-summary-cell cp-annual-term-summary-trackhead' +
                                      summaryTrackGroupEdgeClassForGroup(gi) +
                                      (g.isOtherTrack ? ' cp-annual-term-summary-other-muted' : '')
                                    }
                                  >
                                    <span className="cp-annual-term-summary-trackhead-name">{g.trackName}</span>
                                    {g.isOtherTrack ? (
                                      <span className="cp-class-representative-badge cp-track-other-badge">기타</span>
                                    ) : null}
                                  </td>
                                ))}
                              </tr>
                              <tr className="cp-annual-term-summary-row-shaded">
                                <th className="cp-annual-term-summary-rowhead">시간표명</th>
                                {summaryColGroups.flatMap((g, gi) => {
                                  const cellCls =
                                    'cp-annual-term-summary-cell cp-annual-term-summary-timetable-names';
                                  const names = summaryAliasListPerGroup(g);
                                  const mode = resolveTrackProgramModeForSummary(
                                    workspaceId,
                                    entry.gradeId,
                                    g.trackName
                                  );
                                  const isSync = mode === 'sync';
                                  const rep =
                                    isSync && g.aliases.length > 0
                                      ? resolveSyncRepresentativeAlias(
                                          workspaceId,
                                          entry.gradeId,
                                          g.trackName,
                                          g.aliases
                                        )
                                      : '';
                                  if (g.aliases.length === 0) {
                                    return [
                                      <td key={`tn-${gi}`} className={cellCls + summaryTrackGroupEdgeClassForGroup(gi)}>
                                        —
                                        {g.isOtherTrack ? (
                                          <span className="cp-class-representative-badge cp-track-other-badge">기타</span>
                                        ) : null}
                                      </td>,
                                    ];
                                  }
                                  return names.map((name, ai) => {
                                    const isMirror =
                                      isSync && g.aliases.length > 1 && name !== '—' && name !== rep;
                                    const showRepBadge = isSync && name !== '—' && name === rep;
                                    return (
                                      <td
                                        key={`tn-${gi}-${ai}`}
                                        className={
                                          cellCls +
                                          summaryTrackGroupEdgeClassForAlias(gi, ai) +
                                          (isMirror ? ' cp-annual-term-summary-sync-mirror' : '') +
                                          (g.isOtherTrack ? ' cp-annual-term-summary-other-muted' : '')
                                        }
                                      >
                                        <span
                                          className={
                                            'cp-annual-term-summary-timetable-name-wrap' +
                                            (showRepBadge ? ' cp-annual-term-summary-timetable-name-wrap--rep' : '') +
                                            (isMirror ? ' cp-annual-term-summary-mirror-fade' : '')
                                          }
                                        >
                                          {name === '—' ? (
                                            '—'
                                          ) : (
                                            <span className="cp-annual-term-summary-timetable-name-text">
                                              {name}
                                            </span>
                                          )}
                                          {g.isOtherTrack ? (
                                            <span className="cp-class-representative-badge cp-track-other-badge">기타</span>
                                          ) : null}
                                          {showRepBadge ? (
                                            <span className="cp-class-representative-badge track-alias-rep-badge">
                                              대표
                                            </span>
                                          ) : null}
                                        </span>
                                      </td>
                                    );
                                  });
                                })}
                              </tr>
                              {orderedAcademicTerms(swapTermOrder).flatMap((termNum) => {
                                const termRowClass =
                                  termNum === 1
                                    ? 'cp-annual-summary-hours-row-term1'
                                    : 'cp-annual-summary-hours-row-term2';
                                const termLabel = termNum === 1 ? '1학기 시수' : '2학기 시수';
                                const termTotal = termNum === 1 ? totalTerm1Hours : totalTerm2Hours;
                                const trackCells = summaryColGroups.flatMap((g, gi) => {
                                  const cellCls =
                                    'cp-annual-term-summary-cell cp-annual-term-summary-track-term-hours' +
                                    (g.isOtherTrack ? ' cp-annual-term-summary-other-muted' : '');
                                  if (summaryTermRowsSplit && g.aliases.length >= 2) {
                                    return [
                                      <td
                                        key={`t${termNum}-track-${gi}`}
                                        colSpan={summaryGroupColCount(g)}
                                        className={cellCls + summaryTrackGroupEdgeClassForGroup(gi)}
                                      >
                                        {countTrackTermHoursForGroup(g, termNum)}
                                      </td>,
                                    ];
                                  }
                                  return summaryAliasListPerGroup(g).map((name, ai) => (
                                    <td key={`t${termNum}-track-${gi}-${ai}`} className={cellCls + summaryTrackGroupEdgeClassForAlias(gi, ai)}>
                                      {countTrackTermHoursForGroup(g, termNum)}
                                    </td>
                                  ));
                                });
                                const timetableCells = summaryColGroups.flatMap((g, gi) =>
                                  summaryAliasListPerGroup(g).map((name, ai) => (
                                    <td
                                      key={`t${termNum}-alias-${gi}-${ai}`}
                                      className={
                                        'cp-annual-term-summary-cell' +
                                        summaryTrackGroupEdgeClassForAlias(gi, ai) +
                                        syncMirrorSuffixForCell(g, name) +
                                        (g.isOtherTrack ? ' cp-annual-term-summary-other-muted' : '')
                                      }
                                    >
                                      {wrapSummaryMirrorContent(
                                        isSummaryMirrorCell(g, name),
                                        countForAliasInGroup(g, name, termNum)
                                      )}
                                    </td>
                                  ))
                                );
                                const headCell = (
                                  <th
                                    className="cp-annual-term-summary-rowhead"
                                    rowSpan={summaryTermRowsSplit ? 2 : 1}
                                  >
                                    <div className="cp-annual-term-summary-rowhead-main">
                                      <span>{termLabel}</span>
                                      <span className="cp-annual-term-summary-total-pill">{termTotal}</span>
                                    </div>
                                  </th>
                                );
                                if (!summaryTermRowsSplit) {
                                  return [
                                    <tr
                                      key={`term-hours-${termNum}`}
                                      className={`cp-annual-summary-hours-row ${termRowClass}`}
                                    >
                                      {headCell}
                                      {timetableCells}
                                    </tr>,
                                  ];
                                }
                                return [
                                  <tr
                                    key={`term-hours-${termNum}-track`}
                                    className={`cp-annual-summary-hours-row ${termRowClass} cp-annual-summary-hours-row-track`}
                                  >
                                    {headCell}
                                    {trackCells}
                                  </tr>,
                                  <tr
                                    key={`term-hours-${termNum}-timetable`}
                                    className={`cp-annual-summary-hours-row ${termRowClass} cp-annual-summary-hours-row-timetable`}
                                  >
                                    {timetableCells}
                                  </tr>,
                                ];
                              })}
                              <tr className="cp-annual-term-summary-row-total">
                                <th className="cp-annual-term-summary-rowhead">
                                  <div className="cp-annual-term-summary-rowhead-main">
                                    <span>총 시수</span>
                                    <span className="cp-annual-term-summary-total-pill">
                                      {totalCountedHours}
                                    </span>
                                  </div>
                                </th>
                                {summaryColGroups.map((g, gi) => {
                                  const total = sumTermHoursForGroup(g);
                                  return (
                                    <td
                                      key={`pt-${gi}`}
                                      colSpan={summaryGroupColCount(g)}
                                      className={
                                        'cp-annual-term-summary-cell' +
                                        summaryTrackGroupEdgeClassForGroup(gi) +
                                        (g.isOtherTrack ? ' cp-annual-term-summary-other-muted' : '')
                                      }
                                    >
                                      {total}
                                    </td>
                                  );
                                })}
                              </tr>
                              <tr>
                                <th className="cp-annual-term-summary-rowhead">
                                  <div className="cp-annual-term-summary-rowhead-main">
                                    <span>기준 시수</span>
                                    <span className="cp-annual-term-summary-total-pill">
                                      {totalBaselineHours}
                                    </span>
                                  </div>
                                </th>
                                {summaryColGroups.map((g, gi) => (
                                  <td
                                    key={`ch-${gi}`}
                                    colSpan={summaryGroupColCount(g)}
                                    className={
                                      'cp-annual-term-summary-cell cp-annual-term-summary-baseline' +
                                      summaryTrackGroupEdgeClassForGroup(gi) +
                                      (g.isOtherTrack ? ' cp-annual-term-summary-other-muted' : '')
                                    }
                                  >
                                    {g.criteriaHours ?? 0}
                                  </td>
                                ))}
                              </tr>
                              <tr>
                                <th className="cp-annual-term-summary-rowhead">
                                  <div className="cp-annual-term-summary-rowhead-main">
                                    <span>편차</span>
                                    <span
                                      className={
                                        'cp-annual-term-summary-total-pill cp-annual-term-summary-total-pill-deviation' +
                                        (totalDeviationText.startsWith('+')
                                          ? ' cp-annual-term-summary-total-pill-deviation-pos'
                                          : '') +
                                        (totalDeviationText.startsWith('-')
                                          ? ' cp-annual-term-summary-total-pill-deviation-neg'
                                          : '')
                                      }
                                    >
                                      {totalDeviationText}
                                    </span>
                                  </div>
                                </th>
                                {summaryColGroups.map((g, gi) => {
                                  const total = sumTermHoursForGroup(g);
                                  const base = g.criteriaHours ?? 0;
                                  const dev = formatDeviation(total, base);
                                  const pos = dev.startsWith('+');
                                  const neg = dev.startsWith('-');
                                  return (
                                    <td
                                      key={`dv-${gi}`}
                                      colSpan={summaryGroupColCount(g)}
                                      className={
                                        'cp-annual-term-summary-cell cp-annual-term-summary-deviation' +
                                        summaryTrackGroupEdgeClassForGroup(gi) +
                                        (pos ? ' cp-annual-term-summary-deviation-pos' : '') +
                                        (neg ? ' cp-annual-term-summary-deviation-neg' : '') +
                                        (g.isOtherTrack ? ' cp-annual-term-summary-other-muted' : '')
                                      }
                                    >
                                      {dev}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                          </div>
                        </div>
                      );

                      return (
                        <>
                          {swapTermOrder ? (
                            <>
                              {renderTermBlock({
                                termKey: '2',
                                termLabel: '2학기',
                                wks: weeksTerm2,
                                termHours: semesterHoursByGradeTerm2,
                                termDays: semesterInstructionalDaysTerm2,
                              })}
                              {summaryBlock}
                              {renderTermBlock({
                                termKey: '1',
                                termLabel: '1학기',
                                wks: weeksTerm1,
                                termHours: semesterHoursByGradeTerm1,
                                termDays: semesterInstructionalDaysTerm1,
                              })}
                            </>
                          ) : (
                            <>
                              {renderTermBlock({
                                termKey: '1',
                                termLabel: '1학기',
                                wks: weeksTerm1,
                                termHours: semesterHoursByGradeTerm1,
                                termDays: semesterInstructionalDaysTerm1,
                              })}
                              {summaryBlock}
                              {renderTermBlock({
                                termKey: '2',
                                termLabel: '2학기',
                                wks: weeksTerm2,
                                termHours: semesterHoursByGradeTerm2,
                                termDays: semesterInstructionalDaysTerm2,
                              })}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </section>
      </article>
                </div>
              );
            })}
        </div>
      )}

      {annualProgressBrowseGuardTarget ? (
        <div
          className="cp-modal-backdrop"
          onClick={() => {
            if (!savingAll) dismissAnnualProgressBrowseGuard();
          }}
        >
          <div
            className="cp-modal cp-annual-browse-guard-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-annual-browse-guard-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-annual-browse-guard-title">연간진도표로 이동</h3>
            <div className="cp-modal-body">
              <p className="page-desc">
                저장하지 않은 연간시간표 변경이 있습니다. 저장한 뒤 이동할까요?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={navigateAnnualProgressWithoutSave}
                disabled={savingAll}
              >
                저장하지 않고 이동
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void saveAndNavigateToAnnualProgress()}
                disabled={savingAll}
              >
                저장 후 이동
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {annualCellMemoModal && annualCellMemoAnchorStyle
        ? createPortal(
            <>
              <div
                className="cp-annual-cell-memo-portal-backdrop"
                aria-hidden
                onClick={closeAnnualCellMemoModal}
              />
              <div
                className="cp-program-table-floating-popup cp-annual-cell-memo-floating-wrap"
                style={annualCellMemoAnchorStyle}
              >
                <div
                  className="cp-program-note-popup cp-annual-cell-memo-floating-inner"
                  role="dialog"
                  aria-modal="true"
                  aria-label="시간표 셀 메모"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-note-popup-stack">
                    <p className="cp-annual-cell-memo-floating-title">시간표 셀 메모</p>
                    <div className="cp-program-note-popup-field">
                      <input
                        readOnly
                        tabIndex={-1}
                        value={
                          annualCellMemoModal.cellDisplay.trim() === ''
                            ? '(시간표명 없음)'
                            : annualCellMemoModal.cellDisplay
                        }
                        title={annualCellMemoModal.cellDisplay}
                        aria-label="시간표명"
                      />
                    </div>
                    <div className="cp-program-note-popup-field">
                      <input
                        type="text"
                        value={annualCellMemoModal.memoDraft}
                        onChange={(e) =>
                          setAnnualCellMemoModal((prev) =>
                            prev ? { ...prev, memoDraft: e.target.value } : prev
                          )
                        }
                        placeholder="메모"
                        aria-label="메모"
                        autoFocus
                      />
                    </div>
                    <div className="cp-program-note-popup-actions">
                      <button
                        type="button"
                        className="cp-program-note-confirm-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={closeAnnualCellMemoModal}
                      >
                        닫기
                      </button>
                      <button type="button" className="cp-program-note-confirm-btn" onClick={saveAnnualCellMemoModal}>
                        저장
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>,
            document.body
          )
        : null}

      {weekMemoHoverPreview
        ? createPortal(
            <div
              className="cp-annual-week-memo-hover-preview"
              style={weekMemoHoverPreview.style}
              role="tooltip"
            >
              <div className="cp-annual-week-memo-hover-preview-inner">
                {weekMemoHoverPreview.activityLines.map((line, i) => (
                  <div key={`wh-act-${i}`} className="cp-annual-week-memo-hover-preview-activity-row">
                    <span className="cp-annual-week-memo-week-mark" aria-hidden>
                      주
                    </span>
                    <span className="cp-annual-week-memo-hover-preview-activity-text">{line}</span>
                  </div>
                ))}
                {weekMemoHoverPreview.generalMemo ? (
                  <div
                    className={
                      'cp-annual-week-memo-hover-preview-general' +
                      (weekMemoHoverPreview.activityLines.length > 0
                        ? ' cp-annual-week-memo-hover-preview-general--after-activity'
                        : '')
                    }
                  >
                    {weekMemoHoverPreview.generalMemo}
                  </div>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}

      {weekHoursHoverPreview
        ? createPortal(
            <div
              className="cp-annual-week-hours-hover-preview"
              style={weekHoursHoverPreview.style}
              role="tooltip"
              aria-label="편제별 주간 시수"
            >
              <div className="cp-annual-week-hours-hover-grid">
                {weekHoursHoverPreview.tracks.map((t) => (
                  <div key={`wh-${t.trackName}`} className="cp-annual-week-hours-hover-col">
                    <span className="cp-annual-week-hours-hover-label" title={t.trackName}>
                      {t.trackName}
                    </span>
                    <span className="cp-annual-week-hours-hover-value">{t.hours}</span>
                  </div>
                ))}
                <div className="cp-annual-week-hours-hover-col cp-annual-week-hours-hover-col--total">
                  <span className="cp-annual-week-hours-hover-label">합계</span>
                  <span className="cp-annual-week-hours-hover-value cp-annual-week-hours-hover-value--total">
                    {weekHoursHoverPreview.tracks.reduce((sum, t) => sum + t.hours, 0)}
                  </span>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {termHoursHoverPreview
        ? createPortal(
            <div
              className="cp-annual-week-hours-hover-preview"
              style={termHoursHoverPreview.style}
              role="tooltip"
              aria-label="편제별 학기 시수"
            >
              <div className="cp-annual-week-hours-hover-grid">
                {termHoursHoverPreview.tracks.map((t) => (
                  <div key={`th-${t.trackName}`} className="cp-annual-week-hours-hover-col">
                    <span className="cp-annual-week-hours-hover-label" title={t.trackName}>
                      {t.trackName}
                    </span>
                    <span className="cp-annual-week-hours-hover-value">{t.hours}</span>
                  </div>
                ))}
                <div className="cp-annual-week-hours-hover-col cp-annual-week-hours-hover-col--total">
                  <span className="cp-annual-week-hours-hover-label">합계</span>
                  <span className="cp-annual-week-hours-hover-value cp-annual-week-hours-hover-value--total">
                    {termHoursHoverPreview.tracks.reduce((sum, t) => sum + t.hours, 0)}
                  </span>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {cellColorHoverPreview
        ? createPortal(
            <div
              className="cp-annual-cell-color-hover-preview"
              style={cellColorHoverPreview.style}
              role="tooltip"
            >
              {cellColorHoverPreview.kind === 'project' ? (
                <span className="cp-annual-cell-color-hover-project-mark" aria-hidden>
                  프
                </span>
              ) : null}
              {cellColorHoverPreview.label}
            </div>,
            document.body
          )
        : null}

      {weekActivityMemoModal && weekActivityMemoAnchorStyle
        ? createPortal(
            <>
              <div
                className="cp-annual-cell-memo-portal-backdrop"
                aria-hidden
                onClick={closeWeekActivityMemoModal}
              />
              <div
                className="cp-program-table-floating-popup cp-annual-cell-memo-floating-wrap cp-annual-week-memo-floating-wrap"
                style={weekActivityMemoAnchorStyle}
              >
                <div
                  className="cp-program-note-popup cp-annual-cell-memo-floating-inner cp-annual-week-memo-floating-inner"
                  role="dialog"
                  aria-modal="true"
                  aria-label="주간 활동 메모"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-note-popup-stack cp-annual-week-memo-popup-stack">
                    <p className="cp-annual-cell-memo-floating-title cp-annual-week-memo-floating-title">
                      주간 활동 메모
                    </p>
                    <p className="cp-annual-week-memo-section-label">
                      {weekActivityMemoModal.weekIndex}주차 {weekActivityMemoModal.dateRangeDisplay}
                    </p>
                    <div className="cp-annual-week-memo-section">
                      <div className="cp-annual-week-memo-section-heading">주간 교육활동</div>
                      {weekActivityMemoModal.activityLinesDraft.map((line, idx) => (
                        <div className="cp-program-note-popup-field" key={`wl-${idx}`}>
                          <input
                            type="text"
                            value={line}
                            autoFocus={idx === 0}
                            onChange={(e) => {
                              const v = e.target.value;
                              setWeekActivityMemoModal((prev) => {
                                if (!prev) return prev;
                                const nextLines = [...prev.activityLinesDraft];
                                nextLines[idx] = v;
                                return { ...prev, activityLinesDraft: nextLines };
                              });
                            }}
                            placeholder="주간 교육활동"
                            aria-label={`주간 교육활동 ${idx + 1}행`}
                          />
                        </div>
                      ))}
                      <div className="cp-program-note-popup-field cp-annual-week-memo-add-row">
                        <button
                          type="button"
                          className="cp-program-note-confirm-btn cp-annual-week-memo-add-btn"
                          aria-label="주간 교육활동 입력 줄 추가"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={() =>
                            setWeekActivityMemoModal((prev) =>
                              prev ? { ...prev, activityLinesDraft: [...prev.activityLinesDraft, ''] } : prev
                            )
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="cp-annual-week-memo-section">
                      <div className="cp-annual-week-memo-section-heading">일반 메모</div>
                      <div className="cp-program-note-popup-field cp-annual-week-memo-general-field">
                        <textarea
                          value={weekActivityMemoModal.generalMemoDraft}
                          onChange={(e) =>
                            setWeekActivityMemoModal((prev) =>
                              prev ? { ...prev, generalMemoDraft: e.target.value } : prev
                            )
                          }
                          rows={3}
                          placeholder="일반 메모"
                          aria-label="일반 메모"
                        />
                      </div>
                    </div>
                    <div className="cp-program-note-popup-actions">
                      <button
                        type="button"
                        className="cp-program-note-confirm-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={closeWeekActivityMemoModal}
                      >
                        닫기
                      </button>
                      <button
                        type="button"
                        className="cp-program-note-confirm-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={() => void saveWeekActivityMemoModal()}
                      >
                        저장
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>,
            document.body
          )
        : null}

      {baseTimetableApplyConfirm && (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-annual-base-timetable-apply-confirm-title"
          onClick={() => setBaseTimetableApplyConfirm(null)}
        >
          <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="cp-annual-base-timetable-apply-confirm-title" className="cp-leave-modal-title">
              {baseTimetableApplyConfirm.termNum}학기 기초시간표 반영
            </h3>
            <p className="cp-leave-modal-desc">
              고정 프로그램, 라벨링 색상 시간표를 제외한 영역들이 기초시간표로 입력됩니다. 진행하시겠습니까?
            </p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setBaseTimetableApplyConfirm(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  const p = baseTimetableApplyConfirm;
                  setBaseTimetableApplyConfirm(null);
                  handleApplyBaseTimetable(p.entry, p.termNum);
                }}
              >
                진행
              </button>
            </div>
          </div>
        </div>
      )}

      {teamLeaderImportModal ? (
        <div
          className="cp-modal-backdrop cp-annual-team-import-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-team-leader-annual-import-title"
          onClick={() => {
            if (!teamLeaderImportBusy) {
              setTeamLeaderImportModal(null);
              setTeamLeaderImportError(null);
              setTeamLeaderImportAllConfirmOpen(false);
            }
          }}
        >
          <div className="cp-annual-team-import-backdrop-scrim" aria-hidden="true" />
          <div className="cp-annual-team-import-backdrop-scroll">
          <div className="cp-modal cp-leave-modal-box cp-annual-team-import-modal" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const sourceCandidates = teamLeaderImportModal.sourceCandidates;
              const sourceActiveIndex = Math.max(
                0,
                sourceCandidates.findIndex(
                  (row) => row.gradeId === teamLeaderImportModal.selectedSourceGradeId
                )
              );
              const sourceNavDisabled =
                teamLeaderImportBusy || sourceCandidates.length <= 1;
              const goPrevSource = () => {
                if (sourceCandidates.length === 0) return;
                const nextIndex =
                  sourceActiveIndex <= 0 ? sourceCandidates.length - 1 : sourceActiveIndex - 1;
                setTeamLeaderImportModal((prev) =>
                  prev
                    ? { ...prev, selectedSourceGradeId: sourceCandidates[nextIndex].gradeId }
                    : prev
                );
              };
              const goNextSource = () => {
                if (sourceCandidates.length === 0) return;
                const nextIndex =
                  sourceActiveIndex >= sourceCandidates.length - 1 ? 0 : sourceActiveIndex + 1;
                setTeamLeaderImportModal((prev) =>
                  prev
                    ? { ...prev, selectedSourceGradeId: sourceCandidates[nextIndex].gradeId }
                    : prev
                );
              };
              return (
                <>
            <div className="cp-leader-import-modal-title-row">
              <button
                type="button"
                className="cp-leader-import-modal-nav-btn"
                aria-label="이전 팀장 클래스"
                disabled={sourceNavDisabled}
                onClick={goPrevSource}
              >
                &lt;
              </button>
              <h3 id="cp-team-leader-annual-import-title" className="cp-leader-import-modal-title-text">
                팀장 연간시간표 데이터 가져오기
              </h3>
              <button
                type="button"
                className="cp-leader-import-modal-nav-btn"
                aria-label="다음 팀장 클래스"
                disabled={sourceNavDisabled}
                onClick={goNextSource}
              >
                &gt;
              </button>
            </div>
            <div className="cp-annual-team-import-source-section">
              {sourceCandidates.length === 0 ? (
                <p className="cp-leave-modal-desc">팀장 클래스 목록을 불러오는 중…</p>
              ) : (
                <AnnualTimetableLeaderImportSourcePreview
                  candidates={sourceCandidates}
                  selectedGradeId={teamLeaderImportModal.selectedSourceGradeId}
                  importMode={teamLeaderImportModal.mode}
                />
              )}
            </div>
            <div className="cp-annual-team-import-mode-grid" role="radiogroup" aria-label="가져오기 옵션">
              <button
                type="button"
                role="radio"
                aria-checked={teamLeaderImportModal.mode === 'annual_all'}
                className={
                  'cp-annual-team-import-mode-btn' +
                  (teamLeaderImportModal.mode === 'annual_all' ? ' is-active' : '')
                }
                onClick={() =>
                  setTeamLeaderImportModal((prev) => (prev ? { ...prev, mode: 'annual_all' } : prev))
                }
                disabled={teamLeaderImportBusy}
              >
                연간시간표 데이터 구성 모든 데이터 가져오기 (데이터 가져오기 즉시 저장)
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={teamLeaderImportModal.mode === 'annual_only'}
                className={
                  'cp-annual-team-import-mode-btn' +
                  (teamLeaderImportModal.mode === 'annual_only' ? ' is-active' : '')
                }
                onClick={() =>
                  setTeamLeaderImportModal((prev) => (prev ? { ...prev, mode: 'annual_only' } : prev))
                }
                disabled={teamLeaderImportBusy}
              >
                연간시간표 데이터만 가져오기
              </button>
            </div>
                </>
              );
            })()}
            {teamLeaderImportError ? <p className="cp-grade-track-leader-import-error">{teamLeaderImportError}</p> : null}
            <div className="cp-modal-footer cp-leave-modal-actions cp-annual-team-import-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => {
                  setTeamLeaderImportModal(null);
                  setTeamLeaderImportError(null);
                  setTeamLeaderImportAllConfirmOpen(false);
                }}
                disabled={teamLeaderImportBusy}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  if (teamLeaderImportModal.mode === 'annual_all') {
                    setTeamLeaderImportAllConfirmOpen(true);
                    return;
                  }
                  void handleExecuteTeamLeaderImport();
                }}
                disabled={
                  teamLeaderImportBusy ||
                  teamLeaderImportModal.sourceCandidates.length === 0 ||
                  !teamLeaderImportModal.selectedSourceGradeId
                }
              >
                {teamLeaderImportBusy ? '가져오는 중…' : '가져오기'}
              </button>
            </div>
          </div>
          </div>
        </div>
      ) : null}

      {teamLeaderImportAllConfirmOpen ? (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-team-leader-annual-all-confirm-title"
          onClick={() => {
            if (!teamLeaderImportBusy) setTeamLeaderImportAllConfirmOpen(false);
          }}
        >
          <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="cp-pin-confirm-warn-icon" aria-hidden>
              !
            </div>
            <h3 id="cp-team-leader-annual-all-confirm-title" className="cp-leave-modal-title">
              모든 데이터 가져오기
            </h3>
            <p className="cp-leave-modal-desc">
              현재 클래스 단독 데이터인
              <br />
              편제 및 시간표 입력 양식, 연간시간표의
              <br />
              기존 데이터가 있다면
              <br />
              가져오는 데이터로 덮어쓰여집니다.
              <br />
              <br />
              계속 진행하시겠습니까?
            </p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setTeamLeaderImportAllConfirmOpen(false)}
                disabled={teamLeaderImportBusy}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  setTeamLeaderImportAllConfirmOpen(false);
                  void handleExecuteTeamLeaderImport();
                }}
                disabled={teamLeaderImportBusy}
              >
                진행
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {hoursAdjustModal ? (
        <AnnualTimetableHoursAdjustModal
          open
          onClose={() => setHoursAdjustModal(null)}
          workspaceId={workspaceId}
          academicYear={academicYear}
          gradeId={hoursAdjustModal.gradeId}
          classTitle={hoursAdjustModal.classTitle}
          tracks={tracks}
          gradeTrackSections={gradeTrackSections}
          programGroup={programGroupsByGrade[hoursAdjustModal.gradeId]}
          regularColors={
            programColorSettingsFull
              ? resolveGradeProgramColors(programColorSettingsFull, hoursAdjustModal.gradeId).regular
              : regularColors
          }
          term1MondayIsos={weeksTerm1.map((w) => w.mondayIso)}
          term2MondayIsos={weeksTerm2.map((w) => w.mondayIso)}
          showSaturday={showSaturday}
          wildcardCharByTrackName={wildcardCharByGradeTrackName[hoursAdjustModal.gradeId]}
          onChecklistReady={(payload) => {
            setHoursAdjustChecklistFloat({
              gradeId: hoursAdjustModal.gradeId,
              classTitle: hoursAdjustModal.classTitle,
              checklist: payload.checklist,
              phase2: payload.phase2,
              applySkipColorHexes: payload.applySkipColorHexes,
              checklistChecked: payload.checklistChecked,
              fillFromCurriculumEventCells: payload.fillFromCurriculumEventCells,
            });
            setHoursAdjustModal(null);
          }}
        />
      ) : null}

      {hoursAdjustChecklistFloat ? (
        <HoursAdjustChecklistFloatingModal
          open
          onClose={() => setHoursAdjustChecklistFloat(null)}
          workspaceId={workspaceId}
          gradeId={hoursAdjustChecklistFloat.gradeId}
          classTitle={hoursAdjustChecklistFloat.classTitle}
          checklist={hoursAdjustChecklistFloat.checklist}
          phase2={hoursAdjustChecklistFloat.phase2}
          dedicatedColors={
            programColorSettingsFull
              ? resolveGradeProgramColors(
                  programColorSettingsFull,
                  hoursAdjustChecklistFloat.gradeId
                ).dedicated
              : dedicatedColors
          }
          baseExcludeColors={
            programColorSettingsFull
              ? resolveGradeProgramColors(
                  programColorSettingsFull,
                  hoursAdjustChecklistFloat.gradeId
                ).baseExclude
              : baseExcludeColors
          }
          programColorLabels={
            programColorSettingsFull
              ? (() => {
                  const c = resolveGradeProgramColors(
                    programColorSettingsFull,
                    hoursAdjustChecklistFloat.gradeId
                  );
                  return {
                    dedicated: c.labels?.dedicated ?? {},
                    baseExclude: c.labels?.baseExclude ?? {},
                  };
                })()
              : undefined
          }
          initialSkipColorHexes={hoursAdjustChecklistFloat.applySkipColorHexes}
          initialChecklistChecked={hoursAdjustChecklistFloat.checklistChecked}
          onApplyChecklist={(phase2, options) => {
            const gradeId = hoursAdjustChecklistFloat.gradeId;
            const colors = programColorSettingsFull
              ? resolveGradeProgramColors(programColorSettingsFull, gradeId)
              : {
                  regular: regularColors,
                  dedicated: dedicatedColors,
                  baseExclude: baseExcludeColors,
                };
            const plan = planHoursAdjustChecklistCellUpdates({
              workspaceId,
              gradeId,
              tracks,
              gradeTrackSections,
              cells: Object.values(effectiveCellsByKeyRef.current),
              phase2,
              term1MondayIsos: weeksTerm1.map((w) => w.mondayIso),
              term2MondayIsos: weeksTerm2.map((w) => w.mondayIso),
              showSaturday,
              regularColors: colors.regular,
              wildcardCharByTrackName: wildcardCharByGradeTrackName[gradeId],
              skipColorHexes: options?.skipColorHexes,
              checklistTransfers: options?.checklistTransfers,
              fillFromCurriculumEventCells:
                hoursAdjustChecklistFloat.fillFromCurriculumEventCells,
            });
            if (plan.updates.length > 0) {
              applyCellUpdates(plan.updates);
            }
            return plan;
          }}
        />
      ) : null}

      {timetableSettingsModal ? (
        <div
          className="cp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-annual-timetable-settings-modal-title"
          onClick={() => requestCloseTimetableSettingsModal()}
        >
          <div
            className="cp-modal cp-annual-timetable-settings-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-annual-timetable-settings-modal-header">
              <h3 id="cp-annual-timetable-settings-modal-title">
                시간표 설정 · {timetableSettingsModal.classTitle}
              </h3>
              <div
                className="cp-annual-timetable-settings-modal-header-actions"
                ref={setTimetableSettingsToolbarEl}
              />
              <button
                type="button"
                className="cp-annual-timetable-settings-modal-close"
                onClick={() => requestCloseTimetableSettingsModal()}
                aria-label="시간표 설정 닫기"
              >
                ×
              </button>
            </div>
            <div className="cp-annual-timetable-settings-modal-body">
              <TimetableSettingsPage
                workspaceId={workspaceId}
                grades={grades}
                currentGradeId={timetableSettingsModal.gradeId}
                academicYear={academicYear}
                annualProjectProgramsByGradeId={annualProjectProgramsByGradeId}
                onOpenProjectManage={(gradeId) => openProjectManageModal(gradeId)}
                onOpenProjectCreate={(gradeId, term) => openProjectCreateModal(gradeId, term)}
                embeddedInModal
                toolbarPortalTarget={timetableSettingsToolbarEl}
              />
            </div>
          </div>
        </div>
      ) : null}

      {timetableSettingsCloseConfirm ? (
        <div
          className="cp-modal-backdrop cp-annual-timetable-settings-close-confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-annual-timetable-settings-close-confirm-title"
          onClick={() => {
            if (!timetableSettingsClosing) setTimetableSettingsCloseConfirm(false);
          }}
        >
          <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="cp-annual-timetable-settings-close-confirm-title" className="cp-leave-modal-title">
              변경사항이 저장되지 않았습니다
            </h3>
            <p className="cp-leave-modal-desc">저장하지 않고 나가시겠습니까?</p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={discardAndCloseTimetableSettingsModal}
                disabled={timetableSettingsClosing}
              >
                저장하지 않고 나가기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void saveAndCloseTimetableSettingsModal()}
                disabled={timetableSettingsClosing}
              >
                {timetableSettingsClosing ? '저장 중…' : '저장 하기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {eventModalOpen && (
        <div className="cp-modal-backdrop" onClick={handleCloseEventModal}>
          <div className="cp-modal cp-annual-event-modal" onClick={(e) => e.stopPropagation()}>
            <h3>고정 프로그램(행사) 등록</h3>
            {eventEditingProgramId ? <p className="page-desc">기존 등록 항목을 수정 중입니다.</p> : null}
            <div className="cp-modal-body cp-annual-event-modal-body">
              <section
                className="cp-annual-event-modal-section"
                aria-label="날짜·교시·색상·시간표 이름·행사명"
              >
                <div className="cp-annual-event-field">
                  <div className="form-row holiday-register-row cp-annual-event-date-row">
                    <input
                      type="date"
                      value={eventDateIso}
                      onChange={(e) => {
                        const next = normalizeIsoDateInput(e.target.value) ?? '';
                        syncEventDateFields(next);
                        setEventModalError(null);
                      }}
                      placeholder="날짜"
                      aria-label="행사 날짜"
                      tabIndex={-1}
                    />
                  </div>
                </div>

                <div className="cp-annual-event-field cp-annual-event-field-periods">
                  <div className="cp-annual-event-period-band">
                    <div
                      className="cp-annual-event-period-modal-grid"
                      style={
                        eventOrderedSlotTargets.length > 0
                          ? {
                              gridTemplateColumns: `repeat(${eventOrderedSlotTargets.length}, minmax(0, 1fr))`,
                            }
                          : undefined
                      }
                    >
                      {eventOrderedSlotTargets.map((slotKey) => {
                        const parsed = parseEventSlotTargetKey(slotKey);
                        const label = parsed ? eventSlotTargetLabel(parsed.kind, parsed.order) : slotKey;
                        const selected = eventSelectedSlotTargets.includes(slotKey);
                        const slotRaw = eventNamesBySlotTarget[slotKey] ?? '';
                        const slotNameInvalid = isEventModalTimetableTextUnregistered(
                          slotRaw,
                          eventSelectedGradeIds,
                          registeredNamesByGrade,
                          eventModalTimetableCheckGradeIds,
                          wildcardCharsByGrade
                        );
                        return (
                          <div
                            key={slotKey}
                            className="cp-annual-event-period-block cp-annual-event-period-modal-cell"
                          >
                            <button
                              type="button"
                              className={
                                'cp-annual-event-period-btn' + (selected ? ' cp-annual-event-period-btn-selected' : '')
                              }
                              ref={(el) => {
                                if (el) eventPeriodLabelBtnRefs.current.set(slotKey, el);
                                else eventPeriodLabelBtnRefs.current.delete(slotKey);
                              }}
                              onClick={() => toggleEventSlotTarget(slotKey)}
                              aria-pressed={selected}
                              aria-label={label}
                              tabIndex={-1}
                            >
                              {label}
                            </button>
                            {selected ? (
                              <input
                                type="text"
                                className={
                                  'cp-annual-event-slot-name-input' +
                                  (slotNameInvalid ? ' cp-annual-event-slot-name-input-invalid' : '')
                                }
                                value={slotRaw}
                                onChange={(e) => {
                                  updateEventModalNameForSlot(slotKey, e.target.value);
                                  setEventModalError(null);
                                }}
                                placeholder={DEFAULT_EVENT_TIMETABLE_CELL_LABEL}
                                aria-label={`${label} 시간표 이름`}
                                aria-invalid={slotNameInvalid}
                              />
                            ) : null}
                          </div>
                        );
                    })}
                  </div>
                  <div
                    className="cp-annual-event-fixed-color-row"
                    role="group"
                    aria-label="고정 프로그램 색상"
                  >
                    {eventModalFixedProgramColors.map((hex) => {
                      const resolved = (eventColorHex || eventDefaultColor).trim().toLowerCase();
                      const selected = resolved === hex.trim().toLowerCase();
                      return (
                        <button
                          key={hex}
                          type="button"
                          className={
                            'cp-annual-event-fixed-color-btn' +
                            (selected ? ' cp-annual-event-fixed-color-btn-selected' : '')
                          }
                          onClick={() => {
                            setEventColorHex(hex);
                            setEventModalError(null);
                          }}
                          aria-pressed={selected}
                          title={hex}
                          aria-label={`고정 프로그램 색 ${hex}`}
                          tabIndex={-1}
                        >
                          <span
                            className="cp-annual-event-fixed-color-btn-inner"
                            style={{ backgroundColor: hex }}
                          />
                        </button>
                      );
                    })}
                  </div>
                  <label className="cp-annual-event-name-in-period-band">
                    <input
                      type="text"
                      value={eventMemoText}
                      onChange={(e) => {
                        setEventMemoText(e.target.value);
                        setEventModalError(null);
                      }}
                      onKeyDown={handleEventMemoInputKeyDown}
                      placeholder="행사명"
                      aria-label="행사명"
                    />
                  </label>
                </div>
                </div>
              </section>

              <section className="cp-annual-event-modal-section" aria-label="적용 클래스">
                <div className="cp-annual-event-field">
                  <div
                    className="cp-annual-event-class-select-all-row"
                    role="group"
                    aria-label="클래스 전체 선택·해제"
                  >
                    <button
                      type="button"
                      className="cp-btn-secondary cp-annual-event-class-select-compact-btn"
                      onClick={selectAllEventClasses}
                      disabled={availableEventClasses.length === 0 || allEventClassesSelected}
                      tabIndex={-1}
                    >
                      전체 선택
                    </button>
                    <button
                      type="button"
                      className="cp-btn-secondary cp-annual-event-class-select-compact-btn"
                      onClick={clearAllEventClasses}
                      disabled={
                        availableEventClasses.length === 0 || eventSelectedGradeIds.length === 0
                      }
                      tabIndex={-1}
                    >
                      선택 해제
                    </button>
                  </div>
                  <div
                    className="program-color-class-blocks-wrap cp-annual-event-class-blocks-wrap"
                    role="group"
                    aria-label="적용 클래스"
                  >
                    {availableEventClasses.map((entry) => {
                      const selected = eventSelectedGradeIds.includes(entry.gradeId);
                      const gradeRawName =
                        grades.find((g) => g.id === entry.gradeId)?.name ?? entry.label;
                      const parsed = parseGradeDisplayName(gradeRawName);
                      return (
                        <button
                          key={entry.gradeId}
                          type="button"
                          className={
                            'cp-grade-track-class-block' +
                            (selected
                              ? ' cp-annual-event-class-block-selected'
                              : ' cp-annual-event-class-block-unselected')
                          }
                          onClick={() => toggleEventGrade(entry.gradeId)}
                          aria-pressed={selected}
                          title={gradeLabelById[entry.gradeId] ?? entry.label}
                          tabIndex={-1}
                        >
                          <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                          {parsed.grades.map((num) => (
                            <span key={num} className="class-grade-chip-display">
                              {num}
                            </span>
                          ))}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              {eventModalError ? (
                <p className="cp-base-timetable-invalid cp-annual-event-modal-error" role="alert">
                  {eventModalError}
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={handleCloseEventModal}
                tabIndex={-1}
              >
                닫기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                ref={eventModalApplyBtnRef}
                onClick={handleApplyEventModal}
                disabled={!isEventModalApplyReady}
              >
                반영
              </button>
            </div>
          </div>
        </div>
      )}
      {operationPlanModalOpen && (
        <div className="cp-modal-backdrop" onClick={handleCloseOperationPlanModal}>
          <div
            className="cp-modal cp-annual-operation-plan-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="연간 수업 일수 및 시수 운영계획"
          >
            <div className="cp-modal-document-header cp-annual-operation-plan-header">
              <h3>연간 수업 일수 및 시수 운영계획</h3>
              <ModalDocumentActionButtons
                onSavePdf={() => void handleOperationPlanSavePdf()}
                onSaveExcel={() => void handleOperationPlanSaveExcel()}
                onPrint={() => void handleOperationPlanPrint()}
                onClose={handleCloseOperationPlanModal}
                saveDisabled={!canExportOperationPlan}
                saveExcelDisabled={!canExportOperationPlan}
                printDisabled={!canExportOperationPlan}
                busy={operationPlanExportBusy}
              />
            </div>
            <div className="cp-modal-body cp-annual-operation-plan-modal-body">
              {!operationPlanView.hasData ? (
                <p className="page-desc">표시할 학기 데이터가 없습니다.</p>
              ) : (
                <div ref={operationPlanExportRootRef} className="cp-annual-operation-plan-export-root">
                  <div className="cp-annual-operation-plan-export-meta" data-pdf-element="header">
                    <p className="cp-annual-operation-plan-export-title">연간 수업 일수 및 시수 운영계획</p>
                    <div className="cp-annual-operation-plan-intro">
                      {operationPlanScopeClassBlock ? (
                        <div className="cp-annual-operation-plan-scope-class">
                          <div className="cp-grade-track-class-block" aria-label="대상 클래스">
                            <span className="cp-grade-track-class-block-label">{operationPlanScopeClassBlock.title}</span>
                            {operationPlanScopeClassBlock.grades.map((num) => (
                              <span key={num} className="class-grade-chip-display">
                                {num}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {operationPlanAvailableTerms.length > 1 ? (
                        <div
                          className="cp-annual-operation-plan-term-toggle cp-export-exclude"
                          role="tablist"
                          aria-label="학기 선택"
                        >
                          <button
                            type="button"
                            role="tab"
                            aria-selected={operationPlanTerm === 'all'}
                            className={`cp-annual-progress-tab ${operationPlanTerm === 'all' ? 'is-active' : ''}`}
                            onClick={() => setOperationPlanTerm('all')}
                          >
                            전체
                          </button>
                          {operationPlanAvailableTerms.map((termNo) => (
                            <button
                              key={termNo}
                              type="button"
                              role="tab"
                              aria-selected={operationPlanTerm === termNo}
                              className={`cp-annual-progress-tab ${operationPlanTerm === termNo ? 'is-active' : ''}`}
                              onClick={() => setOperationPlanTerm(termNo)}
                            >
                              {termNo}학기
                            </button>
                          ))}
                        </div>
                      ) : null}
                      <div className="cp-annual-operation-plan-subtitle-row">
                        <div className="cp-annual-operation-plan-memo-inline cp-export-exclude">
                          <div className="cp-annual-top-saturday-toggle" aria-label="일반 메모 표시">
                            <span className="cp-weekly-schedule-saturday-toggle-label">일반 메모 표시</span>
                            <button
                              type="button"
                              className={'team-auto-approve-toggle' + (operationPlanShowGeneralMemos ? ' on' : '')}
                              onClick={() => setOperationPlanShowGeneralMemos((v) => !v)}
                              aria-pressed={operationPlanShowGeneralMemos}
                              aria-label="일반 메모를 주요 교육 활동에 표시"
                              title="연간 시간표에서 고정 프로그램 색이 아닌 셀 메모를 주요 교육 활동에 포함합니다."
                            >
                              <span className="team-auto-approve-toggle-slider" data-on={!operationPlanShowGeneralMemos}>
                                OFF
                              </span>
                              <span className="team-auto-approve-toggle-slider" data-on={operationPlanShowGeneralMemos}>
                                ON
                              </span>
                            </button>
                          </div>
                        </div>
                        <p className="cp-annual-operation-plan-subtitle">{operationPlanExportSubtitle}</p>
                      </div>
                    </div>
                  </div>
                  <div className="cp-annual-operation-plan-table-wrap">
                      <table className="cp-annual-operation-plan-table">
                    <AnnualOperationPlanTableColGroup
                      weekdayCount={operationPlanWeekdays.length}
                      classHeaderCount={operationPlanActiveClassHeaders.length}
                    />
                    <thead>
                      <tr>
                        <th rowSpan={2}>월</th>
                        <th rowSpan={2}>주</th>
                        <th rowSpan={2}>수업일수</th>
                        <th colSpan={5}>연간 수업 일수</th>
                        <th colSpan={Math.max(1, operationPlanActiveClassHeaders.length)}>클래스별 수업시수</th>
                        <th rowSpan={2}>주요 교육 활동</th>
                        <th rowSpan={2}>교육주간 및 비고</th>
                      </tr>
                      <tr>
                        {operationPlanWeekdays.map((weekday) => (
                          <th key={`weekday-head-${weekday.key}`}>{weekday.label}</th>
                        ))}
                        {operationPlanActiveClassHeaders.map((entry) => (
                          <th
                            key={`class-head-${entry.columnKey}`}
                            className="cp-annual-operation-plan-class-head"
                            title={entry.gradeId !== 'none' ? entry.classLabel || undefined : undefined}
                          >
                            <span className="cp-annual-operation-plan-class-head-wrap">
                              {entry.gradeId === 'none' ? (
                                <span className="cp-annual-operation-plan-class-head-empty">-</span>
                              ) : entry.gradeNumber ? (
                                <span className="cp-annual-operation-plan-grade-badge">{entry.gradeNumber}</span>
                              ) : null}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {operationPlanTableBodyItems.map((item) =>
                        item.kind === 'week' ? (
                        <tr key={`operation-plan-${item.key}`}>
                          <td>{item.row.monthLabel}</td>
                          <td>{item.row.weekIndex}</td>
                          <td className="cp-annual-operation-plan-count-cell">{item.row.instructionalDayCount}</td>
                          {item.row.weekdays.map((day) => (
                            <td
                              key={`operation-day-${item.key}-${day.key}`}
                              className={[
                                'cp-annual-operation-plan-day-cell',
                                !day.inTerm ? 'cp-annual-operation-plan-day-inactive' : '',
                                day.dayKind === 'holiday' ? 'cp-weekly-cell-holiday' : '',
                                day.dayKind === 'closure' || day.dayKind === 'vacation'
                                  ? 'cp-annual-operation-plan-day-closure'
                                  : '',
                                day.fixedEvents.length > 0 ? 'cp-annual-operation-plan-day-fixed' : '',
                                day.fixedDayGeneralMemoOnly ? 'cp-annual-operation-plan-day-memo-general' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              style={
                                day.fixedEvents.length > 0 &&
                                !day.fixedDayGeneralMemoOnly &&
                                day.fixedDayTextColor
                                  ? { color: day.fixedDayTextColor }
                                  : undefined
                              }
                            >
                              {day.dayNumber}
                            </td>
                          ))}
                          {item.row.classHours.length > 0 ? (
                            item.row.classHours.map((classHour) => (
                              <td
                                key={`operation-hours-${item.key}-${classHour.columnKey}`}
                                className={[
                                  'cp-annual-operation-plan-hours-cell',
                                  classHour.adjusted ? 'cp-annual-operation-plan-hours-cell-adjusted' : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                {classHour.hours}
                              </td>
                            ))
                          ) : (
                            <td className="cp-annual-operation-plan-hours-cell">-</td>
                          )}
                          <td className="cp-annual-operation-plan-activity-cell">
                            {item.row.activityLines.length > 0
                              ? item.row.activityLines.map((line, idx) => (
                                  <React.Fragment key={`activity-${item.key}-${idx}`}>
                                    <span
                                      className={[
                                        'cp-annual-operation-plan-activity-line',
                                        line.kind === 'calendar'
                                          ? 'cp-annual-operation-plan-activity-daykind'
                                          : line.kind === 'fixed'
                                            ? 'cp-annual-operation-plan-activity-fixed'
                                            : line.kind === 'shortday'
                                              ? 'cp-annual-operation-plan-activity-shortday'
                                              : '',
                                        line.isGeneralMemo ? 'cp-annual-operation-plan-activity-memo-general' : '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      style={
                                        line.kind === 'fixed' &&
                                        line.colorHex &&
                                        !line.isGeneralMemo
                                          ? { color: line.colorHex }
                                          : undefined
                                      }
                                    >
                                      <span className="cp-annual-operation-plan-activity-datetime">{line.datePart}</span>
                                      <span className="cp-annual-operation-plan-activity-tags">
                                        {line.tagCommon ? (
                                          <span className="cp-annual-operation-plan-activity-tag cp-annual-operation-plan-activity-tag--common">
                                            공통
                                          </span>
                                        ) : (
                                          line.gradeNums.map((g) => (
                                            <span
                                              key={`${line.dedupKey}-g${g}`}
                                              className="cp-annual-operation-plan-activity-tag"
                                            >
                                              {g}
                                            </span>
                                          ))
                                        )}
                                      </span>
                                      <span className="cp-annual-operation-plan-activity-name">{line.name}</span>
                                    </span>
                                    {idx < item.row.activityLines.length - 1 ? <br /> : null}
                                  </React.Fragment>
                                ))
                              : ''}
                          </td>
                          <td className="cp-annual-operation-plan-remark-cell">
                            {item.row.weekActivityRemarkTexts.map((text, idx) => (
                              <div key={`op-wk-rmk-${item.key}-${idx}`} className="cp-annual-operation-plan-remark-week-row">
                                <span className="cp-annual-week-memo-week-mark" aria-hidden>
                                  주
                                </span>
                                <span className="cp-annual-operation-plan-remark-week-text">{text}</span>
                              </div>
                            ))}
                          </td>
                        </tr>
                        ) : (
                        <tr
                          key={`operation-plan-footer-${item.footer.kind}-${item.footer.label}`}
                          className={getOperationPlanFooterRowClassName(item.footer)}
                        >
                          <td colSpan={2}>{item.footer.label}</td>
                          <td className="cp-annual-operation-plan-count-cell">
                            {item.footer.totalInstructionalDays}
                          </td>
                          <td colSpan={operationPlanWeekdays.length} className="cp-annual-operation-plan-total-hours-label">
                            수업 시수
                          </td>
                          {operationPlanActiveClassHeaders.map((header) => (
                            <td
                              key={`operation-total-hours-${item.footer.label}-${header.columnKey}`}
                              className="cp-annual-operation-plan-hours-cell"
                            >
                              {header.gradeId === 'none'
                                ? '-'
                                : (item.footer.totalHoursByColumnKey[header.columnKey] ?? 0)}
                            </td>
                          ))}
                          <td className="cp-annual-operation-plan-activity-cell">
                            {item.footer.vacationText ?? ''}
                          </td>
                          <td className="cp-annual-operation-plan-remark-cell"></td>
                        </tr>
                        )
                      )}
                    </tbody>
                      </table>
                    </div>
                </div>
              )}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={handleCloseOperationPlanModal}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {projectCreateModalGradeId ? (
        <div className="cp-modal-backdrop" onClick={closeProjectCreateModal}>
          <div
            className="cp-modal program-color-modal cp-annual-project-create-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-annual-project-create-title"
          >
            <h3 id="cp-annual-project-create-title">
              {projectFormEditingId ? '프로젝트 프로그램 수정' : '프로젝트 프로그램 생성'}
            </h3>
            <div className="cp-modal-body">
              <div className="cp-annual-project-term-field">
                <span className="cp-annual-project-term-label" id="cp-annual-project-term-label">
                  학기 선택
                </span>
                <div
                  className="cp-annual-progress-tabs cp-annual-project-term-tabs"
                  role="group"
                  aria-labelledby="cp-annual-project-term-label"
                >
                  <button
                    type="button"
                    className={`cp-annual-progress-tab${projectFormTerms.includes(1) ? ' is-active' : ''}`}
                    aria-pressed={projectFormTerms.includes(1)}
                    onClick={() => toggleProjectFormTerm(1)}
                  >
                    1학기
                  </button>
                  <button
                    type="button"
                    className={`cp-annual-progress-tab${projectFormTerms.includes(2) ? ' is-active' : ''}`}
                    aria-pressed={projectFormTerms.includes(2)}
                    onClick={() => toggleProjectFormTerm(2)}
                  >
                    2학기
                  </button>
                </div>
                {!projectFormEditingId ? (
                  <p className="page-desc cp-annual-project-term-multi-hint">
                    1학기·2학기를 함께 선택하면 같은 이름·색으로 두 학기에 동시에 등록됩니다.
                  </p>
                ) : null}
              </div>
              <label className="cp-annual-project-name-label">
                프로젝트 명
                <input
                  type="text"
                  value={projectFormName}
                  onChange={(e) => {
                    setProjectFormName(e.target.value);
                    setProjectFormError(null);
                  }}
                  className="cp-annual-project-name-input"
                  placeholder="예: 가을 탐구 프로젝트"
                  autoComplete="off"
                />
              </label>
              <section className="cp-annual-project-color-section" aria-label="프로젝트 색상 등록">
                <h4 className="cp-annual-project-color-heading">프로젝트 색상 등록</h4>
                <p className="page-desc">
                  프로젝트 전용 팔레트(12색) 또는 기타 색상 선택 모달에서 색을 선택해 주세요. 선택한
                  학기에서는 서로 다른 색만 사용할 수 있습니다. 1·2학기를 함께 선택하면 어느
                  학기에서든 이미 쓰인 색은 선택할 수 없습니다.
                </p>
                {projectFormTerms.length === 0 ? (
                  <p className="page-desc cp-annual-project-color-term-hint">
                    색을 고르려면 먼저 <strong>학기</strong>를 선택해 주세요.
                  </p>
                ) : null}
                <div className="program-color-palette program-color-palette-3x4">
                  {PROJECT_PROGRAM_COLOR_PALETTE.map((hex) => {
                    const modalList =
                      projectCreateModalGradeId != null
                        ? annualProjectProgramsByGradeId[projectCreateModalGradeId] ?? []
                        : [];
                    const colorTakenInTerms =
                      projectFormTerms.length > 0 &&
                      isOtherProjectUsingColorForAnyOfTerms(
                        hex,
                        modalList,
                        projectFormTerms,
                        projectFormEditingId
                      );
                    const swatchDisabled = projectFormTerms.length === 0 || colorTakenInTerms;
                    return (
                      <button
                        key={hex}
                        type="button"
                        disabled={swatchDisabled}
                        className={
                          'program-color-swatch program-color-swatch-btn' +
                          (projectFormColorHex === hex && !swatchDisabled ? ' is-selected' : '') +
                          (swatchDisabled ? ' program-color-swatch-btn-unavailable' : '')
                        }
                        onClick={() => {
                          setProjectFormColorHex(hex);
                          setProjectFormError(null);
                        }}
                        title={
                          projectFormTerms.length === 0
                            ? '먼저 학기를 선택해 주세요'
                            : colorTakenInTerms
                              ? projectFormTerms.length > 1
                                ? '1학기 또는 2학기에 이미 사용 중인 색입니다'
                                : '이 학기에 이미 사용 중인 색입니다'
                              : hex
                        }
                        aria-label={
                          colorTakenInTerms ? `${hex} (사용 불가)` : `색상 ${hex}`
                        }
                      >
                        <span
                          className="program-color-swatch-inner cp-annual-project-grid-fill"
                          style={{ backgroundColor: hex }}
                          aria-hidden
                        />
                      </button>
                    );
                  })}
                </div>
                <div className="program-color-modal-custom-row">
                  <button
                    type="button"
                    className="cp-btn-secondary program-color-custom-btn program-color-custom-btn-block"
                    onClick={() => {
                      setProjectFormCustomColor(projectFormColorHex ?? '#4ead75');
                      setProjectCustomColorModalOpen(true);
                    }}
                  >
                    기타 색상 선택
                  </button>
                </div>
                {projectFormColorHex ? (
                  <p className="cp-annual-project-color-preview" aria-label="선택된 프로젝트 색상">
                    <span
                      className="cp-annual-project-color-preview-swatch cp-annual-project-grid-fill"
                      style={{ backgroundColor: projectFormColorHex }}
                      aria-hidden
                    />
                  </p>
                ) : null}
              </section>
              {projectFormError ? (
                <p className="cp-base-timetable-invalid" role="alert">
                  {projectFormError}
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={closeProjectCreateModal}>
                닫기
              </button>
              <button type="button" className="cp-btn-primary" onClick={handleRegisterProjectProgram}>
                {projectFormEditingId ? '저장' : '등록'}
              </button>
            </div>

            {projectCustomColorModalOpen ? (
              <div
                className="cp-modal-backdrop cp-annual-project-custom-color-backdrop"
                onClick={() => setProjectCustomColorModalOpen(false)}
              >
                <div className="cp-modal program-color-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>기타 색상 선택</h3>
                  <p className="page-desc">원하는 색을 고른 뒤 적용을 누르세요.</p>
                  <div className="program-color-picker-inline">
                    <input
                      type="color"
                      value={projectFormCustomColor}
                      onChange={(e) => setProjectFormCustomColor(e.target.value)}
                      className="program-color-picker-input cp-annual-project-color-input-grid"
                      aria-label="프로젝트 사용자 지정 색"
                    />
                  </div>
                  <div className="cp-modal-footer">
                    <button
                      type="button"
                      className="cp-btn-secondary"
                      onClick={() => setProjectCustomColorModalOpen(false)}
                    >
                      닫기
                    </button>
                    <button
                      type="button"
                      className="cp-btn-primary"
                      onClick={() => {
                        if (projectFormTerms.length === 0) {
                          setProjectFormError('학기를 먼저 선택해 주세요.');
                          return;
                        }
                        if (!projectCreateModalGradeId) return;
                        const modalList = annualProjectProgramsByGradeId[projectCreateModalGradeId] ?? [];
                        if (
                          isOtherProjectUsingColorForAnyOfTerms(
                            projectFormCustomColor,
                            modalList,
                            projectFormTerms,
                            projectFormEditingId
                          )
                        ) {
                          setProjectDuplicateColorWarnOpen(true);
                          return;
                        }
                        setProjectFormColorHex(projectFormCustomColor);
                        setProjectFormError(null);
                        setProjectCustomColorModalOpen(false);
                      }}
                    >
                      선택
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {projectDuplicateColorWarnOpen ? (
              <div
                className="cp-modal-backdrop cp-annual-project-dup-color-warn-backdrop"
                onClick={() => setProjectDuplicateColorWarnOpen(false)}
              >
                <div
                  className="cp-modal program-color-modal cp-annual-project-dup-color-warn-modal"
                  onClick={(e) => e.stopPropagation()}
                  role="alertdialog"
                  aria-modal="true"
                  aria-labelledby="cp-annual-project-dup-color-title"
                >
                  <h3 id="cp-annual-project-dup-color-title">색상 사용 불가</h3>
                  <p className="page-desc">
                    선택한 학기 중 <strong>이미 같은 색</strong>을 쓰는 프로젝트가 있습니다. 다른 색을
                    골라 주세요.
                  </p>
                  <div className="cp-modal-footer">
                    <button
                      type="button"
                      className="cp-btn-primary"
                      onClick={() => setProjectDuplicateColorWarnOpen(false)}
                    >
                      확인
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {projectManageModalGradeId ? (
        <div className="cp-modal-backdrop" onClick={closeProjectManageModal}>
          <div
            className="cp-modal program-color-modal cp-annual-project-manage-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-annual-project-manage-title"
          >
            <h3 id="cp-annual-project-manage-title">프로젝트 관리</h3>
            <p className="page-desc cp-annual-project-manage-subtitle">
              {parseGradeDisplayName(
                grades.find((g) => g.id === projectManageModalGradeId)?.name ?? ''
              ).title}{' '}
              · {academicYear}학년도
            </p>
            <div className="cp-modal-body cp-annual-project-manage-body">
              {(() => {
                const gid = projectManageModalGradeId;
                const list = annualProjectProgramsByGradeId[gid] ?? [];
                const sorted = [...list].sort((a, b) => {
                  if (a.termNumber !== b.termNumber) return a.termNumber - b.termNumber;
                  return a.name.localeCompare(b.name, 'ko');
                });
                if (sorted.length === 0) {
                  return (
                    <p className="page-desc cp-annual-project-manage-empty">
                      등록된 프로젝트가 없습니다. 프로젝트의 「+」로 추가해 주세요.
                    </p>
                  );
                }
                return (
                  <ul className="cp-annual-project-manage-list">
                    {sorted.map((p) => (
                      <li key={p.id} className="cp-annual-project-manage-row">
                        <span
                          className="cp-annual-project-manage-swatch cp-annual-project-grid-fill"
                          style={{ backgroundColor: p.colorHex }}
                          aria-hidden
                        />
                        <div className="cp-annual-project-manage-row-main">
                          <span className="cp-annual-project-manage-name">{p.name}</span>
                          <span className="cp-annual-project-manage-term">{p.termNumber}학기</span>
                        </div>
                        <span className="cp-annual-project-manage-actions">
                          <button
                            type="button"
                            className="track-icon-button"
                            onClick={() => openProjectEditModal(gid, p)}
                            aria-label={`${p.name} 수정`}
                            title="수정"
                          >
                            <img src="/edit.svg" alt="" className="track-icon" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="track-icon-button"
                            onClick={() => duplicateProjectProgram(gid, p)}
                            aria-label={`${p.name} 복제`}
                            title="복제"
                          >
                            <img src="/Copy.svg" alt="" className="track-icon" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="track-icon-button"
                            onClick={() => setProjectDeleteTargetId(p.id)}
                            aria-label={`${p.name} 삭제`}
                            title="삭제"
                          >
                            <img src="/Trash.svg" alt="" className="track-icon" aria-hidden />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={closeProjectManageModal}>
                닫기
              </button>
            </div>
            {projectDeleteTargetId ? (
              <div
                className="cp-annual-project-delete-confirm-overlay"
                role="presentation"
                onClick={() => setProjectDeleteTargetId(null)}
              >
                <div
                  className="cp-annual-project-delete-confirm-box"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="cp-annual-project-delete-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h4 id="cp-annual-project-delete-title" className="cp-annual-project-delete-title">
                    프로젝트 삭제
                  </h4>
                  <p className="page-desc">
                    &quot;
                    {(annualProjectProgramsByGradeId[projectManageModalGradeId] ?? []).find(
                      (x) => x.id === projectDeleteTargetId
                    )?.name ?? ''}
                    &quot; 프로젝트를 삭제할까요?
                  </p>
                  <div className="cp-modal-footer cp-annual-project-delete-footer">
                    <button
                      type="button"
                      className="cp-btn-secondary"
                      onClick={() => setProjectDeleteTargetId(null)}
                    >
                      취소
                    </button>
                    <button type="button" className="cp-btn-primary" onClick={handleConfirmDeleteProject}>
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {shortDayModalOpen && (
        <div className="cp-modal-backdrop" onClick={handleCloseShortDayModal}>
          <div className="cp-modal cp-annual-event-modal cp-annual-shortday-modal" onClick={(e) => e.stopPropagation()}>
            <h3>증배 및 단축일 등록</h3>
            <div className="cp-modal-body cp-annual-event-modal-body">
              <section className="cp-annual-event-modal-section" aria-label="날짜·교시">
                <div className="cp-annual-event-field">
                  <div className="form-row holiday-register-row cp-annual-event-date-row">
                    <input
                      type="date"
                      value={shortDayDateIso}
                      onChange={(e) => {
                        const next = normalizeIsoDateInput(e.target.value) ?? '';
                        setShortDayDateIso(next);
                        setShortDayModalError(null);
                      }}
                      placeholder="날짜"
                      aria-label="단축·증배 적용 날짜"
                    />
                  </div>
                </div>
                <div className="cp-annual-event-field cp-annual-event-field-periods">
                  <div className="cp-annual-event-period-band">
                    <p className="page-desc cp-annual-shortday-period-hint">
                      마지막으로 포함할 구간을 한 번 클릭하세요. (처음부터 해당 버튼까지 연속 선택)
                    </p>
                    <div className="cp-annual-event-period-unified-grid cp-annual-event-period-inline-row cp-annual-shortday-period-grid">
                      {shortDayOrderedSlotTargets.map((slotKey, idx) => {
                        const parsed = parseEventSlotTargetKey(slotKey);
                        const label = parsed ? eventSlotTargetLabel(parsed.kind, parsed.order) : slotKey;
                        const endIdx = shortDayEndSlotTarget
                          ? shortDayOrderedSlotTargets.indexOf(shortDayEndSlotTarget)
                          : -1;
                        const selected = endIdx >= 0 && idx <= endIdx;
                        return (
                          <div key={slotKey} className="cp-annual-event-period-block">
                            <button
                              type="button"
                              className={
                                'cp-annual-event-period-btn' +
                                (selected ? ' cp-annual-event-period-btn-selected' : '')
                              }
                              onClick={() => {
                                setShortDayEndSlotTarget(slotKey);
                                setShortDayModalError(null);
                              }}
                              aria-pressed={selected}
                              aria-label={`처음부터 ${label}까지 선택`}
                            >
                              {label}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>

              <section className="cp-annual-event-modal-section" aria-label="적용 클래스">
                <div className="cp-annual-event-field">
                  <div
                    className="cp-annual-event-class-select-all-row"
                    role="group"
                    aria-label="클래스 전체 선택·해제"
                  >
                    <button
                      type="button"
                      className="cp-btn-secondary cp-annual-event-class-select-compact-btn"
                      onClick={selectAllShortDayClasses}
                      disabled={classEntries.length === 0 || allShortDayClassesSelected}
                    >
                      전체 선택
                    </button>
                    <button
                      type="button"
                      className="cp-btn-secondary cp-annual-event-class-select-compact-btn"
                      onClick={clearAllShortDayClasses}
                      disabled={classEntries.length === 0 || shortDaySelectedGradeIds.length === 0}
                    >
                      선택 해제
                    </button>
                  </div>
                  <div
                    className="program-color-class-blocks-wrap cp-annual-event-class-blocks-wrap"
                    role="group"
                    aria-label="적용 클래스"
                  >
                    {classEntries.map((entry) => {
                      const selected = shortDaySelectedGradeIds.includes(entry.gradeId);
                      const gradeRawName =
                        grades.find((g) => g.id === entry.gradeId)?.name ?? entry.label;
                      const parsed = parseGradeDisplayName(gradeRawName);
                      return (
                        <button
                          key={entry.gradeId}
                          type="button"
                          className={
                            'cp-grade-track-class-block' +
                            (selected
                              ? ' cp-annual-event-class-block-selected'
                              : ' cp-annual-event-class-block-unselected')
                          }
                          onClick={() => toggleShortDayGrade(entry.gradeId)}
                          aria-pressed={selected}
                          title={gradeLabelById[entry.gradeId] ?? entry.label}
                        >
                          <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                          {parsed.grades.map((num) => (
                            <span key={num} className="class-grade-chip-display">
                              {num}
                            </span>
                          ))}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              {shortDayModalError ? (
                <p className="cp-base-timetable-invalid cp-annual-event-modal-error" role="alert">
                  {shortDayModalError}
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={handleCloseShortDayModal}>
                닫기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleApplyShortDayModal}
                disabled={!isShortDayModalApplyReady}
              >
                반영
              </button>
            </div>
          </div>
        </div>
      )}

      {manageShortDayModalOpen && (
        <div className="cp-modal-backdrop" onClick={handleCloseManageShortDayModal}>
          <div
            className="cp-modal cp-annual-event-modal cp-fixed-manage-modal cp-shortday-manage-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="cp-fixed-manage-modal-title">증배 및 단축일 관리</h3>
            <div className="cp-modal-body cp-fixed-manage-modal-body">
              {shortDaySummaries.length === 0 ? (
                <p className="page-desc">등록된 증배·단축 변경이 없습니다.</p>
              ) : (
                <div className="cp-fixed-manage-list" role="list">
                  {shortDaySummaries.map((summary) => (
                    <button
                      key={summary.id}
                      type="button"
                      className="cp-fixed-manage-item"
                      onClick={() => handleEditShortDaySummary(summary)}
                    >
                      <div className="cp-fixed-manage-item-headline">
                        <span className="cp-fixed-manage-item-date-lg">
                          {formatFixedProgramManageDate(summary.dateIso)}
                        </span>
                        <div className="cp-fixed-manage-period-chips" role="list" aria-label="운영·기준 구간">
                          {summary.periodChips.map((chip) => (
                            <span
                              key={chip.key}
                              className={
                                'cp-fixed-manage-period-chip' +
                                (chip.inactive ? ' cp-shortday-manage-period-chip-inactive' : '')
                              }
                              role="listitem"
                            >
                              {formatFixedProgramPeriodLabel(chip.key)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="cp-fixed-manage-item-event-row cp-shortday-manage-delta-row">
                        <div className="cp-shortday-manage-delta-wrap">
                          {summary.increaseCount > 0 ? (
                            <div className="cp-fixed-manage-item-event-name cp-shortday-manage-delta-plus">
                              증배 +{summary.increaseCount}
                            </div>
                          ) : null}
                          {summary.decreaseCount > 0 ? (
                            <div className="cp-fixed-manage-item-event-name cp-shortday-manage-delta-minus">
                              단축 -{summary.decreaseCount}
                            </div>
                          ) : null}
                        </div>
                        <div className="cp-fixed-manage-item-class-row">
                          {summary.gradeIds.map((gid) => {
                            const gradeRaw = grades.find((g) => g.id === gid)?.name ?? gid;
                            const parsed = parseGradeDisplayName(gradeRaw);
                            return (
                              <span
                                key={gid}
                                className="cp-fixed-manage-class-block cp-fixed-manage-class-block-compact"
                                title={gradeLabelById[gid] ?? gradeRaw}
                              >
                                <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                                {parsed.grades.map((num) => (
                                  <span key={num} className="class-grade-chip-display">
                                    {num}
                                  </span>
                                ))}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={handleCloseManageShortDayModal}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkFixedModalOpen && (
        <div className="cp-modal-backdrop" onClick={() => (bulkFixedDirty ? setBulkFixedCloseAsk(true) : setBulkFixedModalOpen(false))}>
          <div className="cp-modal cp-aip-modal cp-fpb-modal" role="dialog" aria-modal="true" aria-label="고정 프로그램 관리" onClick={(e) => e.stopPropagation()}>
            <div className="cp-aip-head">
              <h3>고정 프로그램 관리</h3>
              <button
                type="button"
                className="cp-annual-timetable-settings-modal-close cp-fpb-x"
                aria-label="닫기"
                onClick={() => (bulkFixedDirty ? setBulkFixedCloseAsk(true) : setBulkFixedModalOpen(false))}
              >
                ×
              </button>
            </div>
            <div className="cp-modal-body cp-aip-body">
              <FixedProgramBulkTable
                ref={bulkFixedRef}
                workspaceId={workspaceId}
                seedFromCellCache={false}
                onDirtyChange={setBulkFixedDirty}
                classOptions={classEntries.map((e) => {
                  const parsed = parseGradeDisplayName(grades.find((g) => g.id === e.gradeId)?.name ?? e.gradeId);
                  return { id: e.gradeId, label: parsed.title, grades: parsed.grades.map(String) };
                })}
              />
              <p className="cp-aip-sub cp-aip-center">
                ‘고정 프로그램(행사) 등록’으로 넣은 행사도 이 표에 보여요. 여기서 고친 내용은 ‘저장하기’를 눌러야 시간표에 반영돼요.
              </p>
            </div>
            <div className="cp-modal-footer cp-aip-footer cp-fpb-footer">
              <button
                type="button"
                className="cp-fpb-close"
                onClick={() => (bulkFixedDirty ? setBulkFixedCloseAsk(true) : setBulkFixedModalOpen(false))}
              >
                닫기
              </button>
              <button
                type="button"
                className="cp-btn-save-all cp-fpb-save"
                disabled={!bulkFixedDirty}
                onClick={() => {
                  bulkFixedRef.current?.save();
                  showInfo('고정 프로그램을 저장했습니다. 시간표에 반영됩니다.');
                }}
              >
                {bulkFixedDirty ? '저장하기' : '변경사항 없음'}
              </button>
            </div>
            {bulkFixedCloseAsk ? (
              <div className="cp-annual-project-delete-confirm-overlay" role="presentation" onClick={() => setBulkFixedCloseAsk(false)}>
                <div className="cp-annual-project-delete-confirm-box" role="dialog" aria-modal="true" aria-labelledby="cp-fpb-close-title" onClick={(e) => e.stopPropagation()}>
                  <h4 id="cp-fpb-close-title" className="cp-annual-project-delete-title">저장하지 않은 변경사항</h4>
                  <p className="page-desc">고정 프로그램에 저장하지 않은 변경이 있어요. 저장할까요?</p>
                  <div className="cp-modal-footer cp-annual-project-delete-footer">
                    <button type="button" className="cp-btn-secondary" onClick={() => setBulkFixedCloseAsk(false)}>
                      취소
                    </button>
                    <button
                      type="button"
                      className="cp-btn-secondary"
                      onClick={() => {
                        bulkFixedRef.current?.discard();
                        setBulkFixedCloseAsk(false);
                        setBulkFixedModalOpen(false);
                      }}
                    >
                      저장 안 함
                    </button>
                    <button
                      type="button"
                      className="cp-btn-primary"
                      onClick={() => {
                        bulkFixedRef.current?.save();
                        setBulkFixedCloseAsk(false);
                        setBulkFixedModalOpen(false);
                      }}
                    >
                      저장 후 닫기
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {programColorPickModal && programColorSettingsFull ? (
        <ProgramColorPickModal
          type={programColorPickModal.type}
          mode={programColorPickModal.mode}
          initialHex={programColorPickModal.initialHex}
          initialLabel={programColorPickModal.initialLabel}
          existingColors={
            programColorPickModal.type === 'regular'
              ? resolveGradeProgramColors(programColorSettingsFull, programColorPickModal.gradeId).regular
              : programColorPickModal.type === 'dedicated'
                ? resolveGradeProgramColors(programColorSettingsFull, programColorPickModal.gradeId).dedicated
                : resolveGradeProgramColors(programColorSettingsFull, programColorPickModal.gradeId).baseExclude
          }
          onSubmit={(payload) =>
            handleSubmitProgramColorFromToolbar(
              programColorPickModal.gradeId,
              programColorPickModal.type,
              payload
            )
          }
          onDelete={
            programColorPickModal.mode === 'edit' && programColorPickModal.initialHex
              ? () =>
                  handleDeleteProgramColorFromToolbar(
                    programColorPickModal.gradeId,
                    programColorPickModal.type,
                    programColorPickModal.initialHex!
                  )
              : undefined
          }
          onClose={() => setProgramColorPickModal(null)}
        />
      ) : null}

      {hasAnyTermWeeks && classEntries.length > 0 && (
        <>
          <button
            type="button"
            className="cp-fab-save"
            onClick={handleSaveAll}
            disabled={loading || loadingCells || !isDirty || savingAll}
            aria-label="저장하기"
            title="저장하기"
          >
            <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
          </button>
          <AutoStatusToast state={saveToastState} />
        </>
      )}
    </section>
  );
};

export default AnnualTimetablePage;
